import { useEffect, useMemo, useRef, useState } from "react";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  bg: "#09071A", surface: "#110D26", card: "#18133A", border: "#2A204E",
  purple: "#7C5CBF", purpleLight: "#A07EE0", purpleDim: "#2A204E",
  gold: "#C9A84C", goldLight: "#E8C96A", goldDim: "#3A2A10",
  orange: "#D4751A", green: "#3D9E6A", red: "#B84040",
  text: "#EDE8FF", muted: "#7A6A9A", dim: "#3A2D5A",
};


// v191: ตารางนัดมาตรฐานใช้ช่องละ 30 นาทีทั้งลูกค้าและหมอดู
// บริการ 15 นาทีจะกัน 1 ช่อง, 30 นาที 1 ช่อง, 60 นาที 2 ช่องติดกัน
const makeBookingSlotsV191 = (start, end) => {
  const pad = (n) => String(n).padStart(2,"0");
  const format = (value) => {
    const n = ((Number(value)%1440)+1440)%1440;
    return `${pad(Math.floor(n/60))}:${pad(n%60)}`;
  };
  const rows=[];
  for(let minute=start; minute<end; minute+=30) rows.push(`${format(minute)}-${format(minute+30)}`);
  return rows;
};
const BOOKING_TIME_SLOTS_V92 = makeBookingSlotsV191(9*60, 24*60);
// ช่วงหลังเที่ยงคืนเป็นเวลาพิเศษ ปิดโดยค่าเริ่มต้น และเปิดแยกตามวัน
const SPECIAL_NIGHT_TIME_SLOTS_V180 = makeBookingSlotsV191(0, 4*60);
const SERVICE_DURATION_OPTIONS_V191 = [15,30,60];
function slotStartMinutesV191(slot="") {
  const m=String(slot||"").match(/(\d{1,2}):(\d{2})/);
  return m ? Number(m[1])*60+Number(m[2]) : null;
}
function timeTextV191(value) {
  const n=((Number(value||0)%1440)+1440)%1440;
  return `${String(Math.floor(n/60)).padStart(2,"0")}:${String(n%60).padStart(2,"0")}`;
}
function serviceDurationV191(service={}) {
  const numeric=Number(service?.service_duration_minutes || service?.duration_minutes || service?.minutes || service?.duration || 0);
  if(SERVICE_DURATION_OPTIONS_V191.includes(numeric)) return numeric;
  const matched=String(`${service?.name||""} ${service?.service_name||""} ${service?.desc||""} ${service?.service_detail||""}`).match(/(15|30|60)\s*(?:นาที|min)/i);
  return matched ? Number(matched[1]) : 30;
}
function reservedSlotCountV191(service={}) { return Math.max(1, Math.ceil(serviceDurationV191(service)/30)); }
function reservedSlotCellsV191(slot="", service={}) {
  const start=slotStartMinutesV191(slot); if(start===null) return [];
  return Array.from({length:reservedSlotCountV191(service)},(_,i)=>`${timeTextV191(start+i*30)}-${timeTextV191(start+(i+1)*30)}`);
}
function bookingDisplayRangeV191(slot="", service={}) {
  const start=slotStartMinutesV191(slot); if(start===null) return String(slot||"");
  return `${timeTextV191(start)}-${timeTextV191(start+reservedSlotCountV191(service)*30)}`;
}
function bookingActualRangeV191(date="", slot="", service={}) {
  const start=slotStartMinutesV191(slot); if(!date || start===null) return [date,slot].filter(Boolean).join(" ");
  return `${String(date).slice(0,10)} ${timeTextV191(start)}-${timeTextV191(start+serviceDurationV191(service))}`;
}
function canSelectBookingStartV191({date,slot,service,openSlots=[],blockedKeys=new Set()}) {
  const cells=reservedSlotCellsV191(slot, service);
  if(!cells.length) return false;
  const open=new Set(openSlots);
  return cells.every(cell=>open.has(cell) && !blockedKeys.has(slotKeyV113(date,cell)));
}

function localDateInputValue(date = new Date()) {
  const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 10);
}

const DUANGWANNEE_LOGO = "data:image/png;base64,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";

// Cloudflare D1 API จริงผ่าน Pages Functions
// ใช้ path เดียวกับเว็บ เช่น /api/reading/tarot, /api/seer-applications, /api/seers
const API_BASE = "";
const SYSTEM_API_URL = API_BASE;

async function apiGet(path) {
  const res = await fetch(API_BASE + path, { headers: { "accept": "application/json" } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.message || `API error: ${res.status}`);
  return data;
}

async function apiPost(path, body) {
  const res = await fetch(API_BASE + path, {
    method: "POST",
    headers: { "content-type": "application/json", "accept": "application/json" },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.message || `API error: ${res.status}`);
  return data;
}

// ─── v3.9.26 Admin LIFF route recovery ─────────────────────────────────────
// The configured LIFF endpoint is the site root. In an external browser, LINE
// first returns to that root during authentication. Keep the intended admin
// route as a root query (`screen=admin`) so the LIFF callback cannot strand the
// user in the customer home page after LINE Login.
let adminLiffInitPromiseV3926 = null;
const ADMIN_LIFF_RETURN_KEY_V3926 = "DW_ADMIN_LIFF_RETURN_V3926";

function getAdminInviteTokenForReturnV3926() {
  try {
    const url = new URL(window.location.href);
    const direct = String(url.searchParams.get("admin_invite") || "").trim();
    if (direct) return direct;
    const state = getLiffStateParamsV142B();
    return String(state.get("admin_invite") || "").trim();
  } catch {
    return "";
  }
}

function buildAdminReturnUrlV3926() {
  const target = new URL("/", window.location.origin);
  target.searchParams.set("screen", "admin");
  const inviteToken = getAdminInviteTokenForReturnV3926();
  if (inviteToken) target.searchParams.set("admin_invite", inviteToken);
  return target.toString();
}

function isAdminRouteV3926() {
  try {
    const url = new URL(window.location.href);
    if (url.pathname.toLowerCase().startsWith("/admin")) return true;
    if (String(url.searchParams.get("screen") || "").toLowerCase() === "admin") return true;
    if (url.searchParams.has("admin_invite")) return true;
    const state = getLiffStateParamsV142B();
    return String(state.get("screen") || "").toLowerCase() === "admin" || state.has("admin_invite");
  } catch {
    return false;
  }
}

async function getAdminLiffCredentialsV3926() {
  if (!adminLiffInitPromiseV3926) {
    adminLiffInitPromiseV3926 = (async () => {
      const liff = await loadLiffSdk();
      if (!liff) throw new Error("โหลด LINE LIFF ไม่สำเร็จ");

      // Do not enable automatic external-browser login here. That callback uses
      // the LIFF endpoint root and previously discarded the admin route. We
      // explicitly provide a stable admin return URL below instead.
      await liff.init({ liffId: LIFF_MAIN_ID });

      const savedReturnUrl = (() => {
        try { return String(sessionStorage.getItem(ADMIN_LIFF_RETURN_KEY_V3926) || "").trim(); }
        catch { return ""; }
      })();
      if (savedReturnUrl && liff.isLoggedIn() && !isAdminRouteV3926()) {
        try { sessionStorage.removeItem(ADMIN_LIFF_RETURN_KEY_V3926); } catch {}
        window.location.replace(savedReturnUrl);
        throw new Error("กำลังกลับเข้าสู่แดชแอดมิน");
      }

      if (!liff.isLoggedIn()) {
        const redirectUri = buildAdminReturnUrlV3926();
        try { sessionStorage.setItem(ADMIN_LIFF_RETURN_KEY_V3926, redirectUri); } catch {}
        liff.login({ redirectUri });
        throw new Error("กำลังเข้าสู่ระบบ LINE เพื่อเปิดแดชแอดมิน");
      }
      try { sessionStorage.removeItem(ADMIN_LIFF_RETURN_KEY_V3926); } catch {}
      return liff;
    })().catch((error) => {
      adminLiffInitPromiseV3926 = null;
      throw error;
    });
  }

  const liff = await adminLiffInitPromiseV3926;
  const idToken = typeof liff.getIDToken === "function" ? String(liff.getIDToken() || "").trim() : "";
  const accessToken = typeof liff.getAccessToken === "function" ? String(liff.getAccessToken() || "").trim() : "";
  if (!idToken && !accessToken) throw new Error("LINE ยังไม่ส่งข้อมูลยืนยันตัวตน กรุณาปิดลิงก์นี้แล้วเปิดใหม่จากแชต LINE");
  return { idToken, accessToken };
}

async function adminAccessApiV3924(action, body = {}) {
  const { idToken, accessToken } = await getAdminLiffCredentialsV3926();
  const headers = { "content-type": "application/json", "accept": "application/json" };
  if (idToken) headers.authorization = `Bearer ${idToken}`;
  if (accessToken) headers["x-line-access-token"] = accessToken;
  const response = await fetch(API_BASE + "/api/admin-access", {
    method: "POST",
    headers,
    body: JSON.stringify({ action, ...(body || {}) }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) throw new Error(data.message || `API error: ${response.status}`);
  return data;
}

function getAdminInviteTokenV3923() {
  try {
    const url = new URL(window.location.href);
    const direct = String(url.searchParams.get("admin_invite") || "").trim();
    if (direct) return direct;
    const state = getLiffStateParamsV142B();
    return String(state.get("admin_invite") || "").trim();
  } catch {
    return "";
  }
}

function buildAdminInviteUrlV3926(token) {
  const raw = String(token || "").trim();
  if (!raw) return "";
  // The LIFF endpoint is the site root. Keep the application route in a query
  // parameter that LINE preserves throughout the primary/secondary redirect.
  return `${LIFF_MAIN_URL}/?screen=admin&admin_invite=${encodeURIComponent(raw)}`;
}

// ─── v3.9.14 Cloudflare RealtimeKit — audio-only booking call ─────────────
// Core SDK receives only a short-lived RealtimeKit participant token returned by
// the backend. Cloudflare / LINE secrets are never bundled into this app.
// The UI bridge is required for remote audio playback in a custom UI.
const REALTIMEKIT_BROWSER_SDK_V391 = "https://cdn.jsdelivr.net/npm/@cloudflare/realtimekit@2.0.0/dist/browser.js";
const REALTIMEKIT_UI_LOADER_V391 = "https://cdn.jsdelivr.net/npm/@cloudflare/realtimekit-ui@2.0.0/loader/index.es2017.js";
let realtimeKitLoaderV391 = null;
let realtimeKitUiLoaderV391 = null;
let voiceLiffInitPromiseV391 = null;

function loadRealtimeKitV391() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("เปิดระบบโทรได้จากเบราว์เซอร์เท่านั้น"));
  }
  if (window.RealtimeKitClient) return Promise.resolve(window.RealtimeKitClient);
  if (realtimeKitLoaderV391) return realtimeKitLoaderV391;

  realtimeKitLoaderV391 = new Promise((resolve, reject) => {
    const previous = document.querySelector("script[data-dw-realtimekit='v391']");
    if (previous) {
      previous.addEventListener(
        "load",
        () => window.RealtimeKitClient
          ? resolve(window.RealtimeKitClient)
          : reject(new Error("โหลดระบบโทรไม่สำเร็จ")),
        { once: true },
      );
      previous.addEventListener(
        "error",
        () => reject(new Error("โหลดระบบโทรไม่สำเร็จ โปรดตรวจสอบอินเทอร์เน็ต")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = REALTIMEKIT_BROWSER_SDK_V391;
    script.async = true;
    script.dataset.dwRealtimekit = "v391";
    script.onload = () => window.RealtimeKitClient
      ? resolve(window.RealtimeKitClient)
      : reject(new Error("ไม่พบระบบโทรหลังโหลดเสร็จ"));
    script.onerror = () => reject(new Error("โหลดระบบโทรไม่สำเร็จ โปรดตรวจสอบอินเทอร์เน็ต"));
    document.head.appendChild(script);
  }).catch((error) => {
    realtimeKitLoaderV391 = null;
    throw error;
  });

  return realtimeKitLoaderV391;
}

function loadRealtimeKitUiV391() {
  if (typeof window === "undefined" || !window.customElements) {
    return Promise.reject(new Error("เบราว์เซอร์นี้ไม่รองรับระบบเสียงของห้องโทร"));
  }

  const requiredElements = ["rtk-ui-provider", "rtk-participants-audio", "rtk-dialog-manager", "rtk-notifications"];
  if (requiredElements.every((tagName) => window.customElements.get(tagName))) {
    return Promise.resolve();
  }
  if (realtimeKitUiLoaderV391) return realtimeKitUiLoaderV391;

  realtimeKitUiLoaderV391 = import(/* @vite-ignore */ REALTIMEKIT_UI_LOADER_V391)
    .then(async (uiModule) => {
      if (typeof uiModule?.defineCustomElements !== "function") {
        throw new Error("โหลดส่วนรับเสียงของระบบโทรไม่สำเร็จ");
      }
      uiModule.defineCustomElements();
      await Promise.all(requiredElements.map((tagName) => window.customElements.whenDefined(tagName)));
    })
    .catch((error) => {
      realtimeKitUiLoaderV391 = null;
      throw error;
    });

  return realtimeKitUiLoaderV391;
}

// IMPORTANT: Do not bind the UI provider to a meeting before the Core SDK join
// has resolved. The UI provider owns UI state transitions; binding it while the
// app calls meeting.join() can cause a second, concurrent join on mobile.
// The audio elements are still created before join so they remain eligible for
// playback from the same user gesture, but they receive the meeting only after
// the Core SDK has joined successfully.
async function prepareRealtimeKitAudioBridgeV391(host) {
  if (!host) throw new Error("เตรียมระบบรับเสียงไม่สำเร็จ");
  await loadRealtimeKitUiV391();

  const provider = document.createElement("rtk-ui-provider");
  provider.style.cssText = "position:fixed;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden;pointer-events:none;";
  provider.setAttribute("aria-hidden", "true");
  provider.showSetupScreen = false;

  const participantsAudio = document.createElement("rtk-participants-audio");
  const dialogManager = document.createElement("rtk-dialog-manager");
  const notifications = document.createElement("rtk-notifications");
  provider.append(participantsAudio, dialogManager, notifications);
  host.replaceChildren(provider);

  let bound = false;
  return {
    bind(meeting) {
      if (!meeting || bound) return;
      bound = true;
      provider.meeting = meeting;
    },
    dispose() {
      if (host.contains(provider)) host.removeChild(provider);
    },
  };
}

async function getVoiceLiffV391() {
  if (voiceLiffInitPromiseV391) return voiceLiffInitPromiseV391;

  voiceLiffInitPromiseV391 = (async () => {
    const liff = await loadLiffSdk();
    if (!liff) throw new Error("โหลด LINE LIFF ไม่สำเร็จ");

    // v3.9.4: A voice-room URL may be opened from a LINE notification, a
    // LIFF URL, or a normal browser. Ask the LIFF SDK to complete the LINE
    // login flow automatically when the current browser is not a LIFF browser.
    // This does not store a token and does not change the existing LIFF IDs.
    await liff.init({
      liffId: getCurrentLiffId(),
      withLoginOnExternalBrowser: true,
    });
    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri: window.location.href });
      throw new Error("กรุณาเข้าสู่ระบบ LINE ก่อนใช้โทรเสียง");
    }
    return liff;
  })().catch((error) => {
    voiceLiffInitPromiseV391 = null;
    throw error;
  });

  return voiceLiffInitPromiseV391;
}

async function getCurrentVoiceLiffCredentialsV394() {
  const liff = await getVoiceLiffV391();
  const idToken = typeof liff.getIDToken === "function" ? String(liff.getIDToken() || "").trim() : "";
  const accessToken = typeof liff.getAccessToken === "function" ? String(liff.getAccessToken() || "").trim() : "";
  if (!idToken && !accessToken) {
    throw new Error("LINE ยังไม่ส่งข้อมูลยืนยันตัวตน กรุณากลับไปเปิดห้องดูดวงจาก LINE แล้วลองอีกครั้ง");
  }
  // Both values stay in memory only and are sent straight to the backend over HTTPS.
  // They are never put in localStorage, sessionStorage, logs, or UI.
  return { idToken, accessToken };
}

async function voiceCallApiV391(bookingId, action) {
  const { idToken, accessToken } = await getCurrentVoiceLiffCredentialsV394();
  return apiPost("/api/voice-call", {
    booking_id: String(bookingId || ""),
    action,
    liff_id_token: idToken,
    liff_access_token: accessToken,
  });
}


// ─── v3.9.21 LINE Meeting five-minute access ─────────────────────────────
// The meeting URL remains in D1. Five minutes before an eligible confirmed
// appointment, both verified participants can open the same assigned room.
let lineMeetingTestLiffPromiseV3920 = null;

async function getLineMeetingTestCredentialsV3920() {
  if (lineMeetingTestLiffPromiseV3920) return lineMeetingTestLiffPromiseV3920;

  lineMeetingTestLiffPromiseV3920 = (async () => {
    const liff = await loadLiffSdk();
    if (!liff) throw new Error("โหลด LINE LIFF ไม่สำเร็จ");

    await liff.init({
      liffId: getCurrentLiffId(),
      withLoginOnExternalBrowser: true,
    });
    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri: window.location.href });
      throw new Error("กรุณาเข้าสู่ระบบ LINE ก่อนเข้าห้องนัด");
    }

    const idToken = typeof liff.getIDToken === "function" ? String(liff.getIDToken() || "").trim() : "";
    const accessToken = typeof liff.getAccessToken === "function" ? String(liff.getAccessToken() || "").trim() : "";
    if (!idToken && !accessToken) {
      throw new Error("LINE ยังไม่ส่งข้อมูลยืนยันตัวตน กรุณาเปิดห้องนัดใหม่จาก LINE");
    }
    return { idToken, accessToken };
  })().catch((error) => {
    lineMeetingTestLiffPromiseV3920 = null;
    throw error;
  });

  return lineMeetingTestLiffPromiseV3920;
}

async function lineMeetingTestApiV3920(bookingId) {
  const { idToken, accessToken } = await getLineMeetingTestCredentialsV3920();
  return apiPost("/api/line-meeting-test", {
    booking_id: String(bookingId || ""),
    liff_id_token: idToken,
    liff_access_token: accessToken,
  });
}

function lineMeetingStartDateV3920(booking = {}) {
  try {
    return parseBookingScheduleDateV112(booking);
  } catch {
    return null;
  }
}

function BookingLineMeetingAccessV3920({ booking, isSeer = false, fallbackToWebCall = false, compact = false }) {
  const bookingId = String(booking?.booking_id || "");
  const startAt = lineMeetingStartDateV3920(booking);
  const [nowTick, setNowTick] = useState(Date.now());
  const [state, setState] = useState({ loading: false, assigned: false, meetingUrl: "", error: "", poolEmpty: false });
  const eligibleStatus = ["confirmed", "in_session"].includes(String(booking?.status || "").toLowerCase());
  const meetingOpenAtMs = startAt ? startAt.getTime() - 5 * 60 * 1000 : 0;
  const roomOpen = !startAt || nowTick >= meetingOpenAtMs;

  useEffect(() => {
    if (!startAt || roomOpen) return undefined;
    const wait = Math.max(0, meetingOpenAtMs - Date.now()) + 350;
    const timer = setTimeout(() => setNowTick(Date.now()), Math.min(wait, 2147480000));
    return () => clearTimeout(timer);
  }, [bookingId, startAt?.getTime?.(), meetingOpenAtMs, roomOpen]);

  useEffect(() => {
    let active = true;
    if (!bookingId || !eligibleStatus || !roomOpen) {
      setState({ loading: false, assigned: false, meetingUrl: "", error: "", poolEmpty: false });
      return () => { active = false; };
    }

    setState((prev) => ({ ...prev, loading: true, error: "" }));
    (async () => {
      try {
        const data = await lineMeetingTestApiV3920(bookingId);
        if (!active) return;
        const meetingUrl = String(data?.meeting_url || "").trim();
        setState({
          loading: false,
          assigned: Boolean(data?.assigned && meetingUrl),
          meetingUrl,
          error: "",
          poolEmpty: String(data?.code || "") === "LINE_MEETING_POOL_EMPTY",
        });
      } catch (error) {
        if (!active) return;
        setState({
          loading: false,
          assigned: false,
          meetingUrl: "",
          error: String(error?.message || "ตรวจสอบห้อง LINE Meeting ทดสอบไม่สำเร็จ"),
          poolEmpty: false,
        });
      }
    })();

    return () => { active = false; };
  }, [bookingId, eligibleStatus, roomOpen]);

  const openMeeting = () => {
    if (!state.meetingUrl) return;
    try {
      window.location.assign(state.meetingUrl);
    } catch {
      window.open(state.meetingUrl, "_blank", "noopener,noreferrer");
    }
  };

  const fallback = () => (fallbackToWebCall ? <BookingVoiceCallPanelV39 booking={booking} isSeer={isSeer} /> : null);

  if (!eligibleStatus) return fallback();

  if (!roomOpen) {
    const minutes = Math.max(0, Math.ceil((meetingOpenAtMs - nowTick) / 60000));
    return (
      <div className="warn-box" style={{ marginBottom: compact ? 8 : 12, borderColor: `${C.green}55`, background: `${C.green}0d`, color: C.text }}>
        🟢 ห้องนัดผ่าน LINE จะเปิดอัตโนมัติก่อนเวลานัด 5 นาที{minutes > 0 ? ` อีกประมาณ ${minutes} นาที` : ""}
      </div>
    );
  }

  if (state.loading) {
    return <div className="empty" style={{ padding: compact ? 10 : 12, marginBottom: compact ? 8 : 12, fontSize: 13 }}>กำลังเตรียมห้องนัด LINE ให้รายการนี้...</div>;
  }

  if (state.assigned) {
    return (
      <div className="card" style={{ padding: compact ? 13 : (isSeer ? 18 : 14), marginBottom: compact ? 8 : 12, borderColor: `${C.green}77`, background: `${C.green}10` }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: compact ? 16 : (isSeer ? 20 : 18), fontWeight: 900, color: C.green }}>🟢 ห้องนัด LINE Meeting พร้อมแล้ว</div>
            <div style={{ fontSize: 12, color: C.text, lineHeight: 1.7, marginTop: 5 }}>
              ลูกค้าและหมอดูของนัดนี้จะได้รับลิงก์ห้องเดียวกัน กดปุ่มเพื่อเปิด LINE และเข้าร่วมการสนทนา
            </div>
          </div>
          <span className="badge b-green" style={{ whiteSpace: "nowrap" }}>พร้อมเข้า</span>
        </div>
        <button type="button" className="btn btn-gold" style={{ width: "100%", minHeight: compact ? 48 : 54, marginTop: 13, fontSize: 16, fontWeight: 900 }} onClick={openMeeting}>
          เข้าห้องนัดผ่าน LINE
        </button>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.65, marginTop: 9 }}>
          ใช้เสียงใน LINE Meeting โดยตรง • ปิดกล้องได้ก่อนเข้าห้อง • เมื่อหมดเวลา กรุณากดออกจาก Meeting
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <>
        <div className="warn-box" style={{ marginBottom: compact ? 8 : 12, borderColor: `${C.orange}55`, background: `${C.orange}11`, color: C.orange }}>
          🟢 ห้อง LINE Meeting ยังไม่พร้อม: {state.error}
        </div>
        {fallback()}
      </>
    );
  }

  if (state.poolEmpty) {
    return (
      <>
        <div className="warn-box" style={{ marginBottom: compact ? 8 : 12, borderColor: `${C.orange}55`, background: `${C.orange}11`, color: C.orange }}>
          🟢 ห้อง LINE Meeting ยังไม่ว่างสำหรับนัดนี้ กรุณารอแอดมินเพิ่มห้องสำรอง
        </div>
        {fallback()}
      </>
    );
  }

  return fallback();
}

function voiceStatusLabelV391(status = "") {
  const map = {
    idle: "รอเริ่มโทร",
    starting: "กำลังเตรียมห้อง",
    ringing: "กำลังเรียกลูกค้า",
    active: "กำลังสนทนา",
    ended: "สายสิ้นสุดแล้ว",
  };
  return map[String(status || "")] || "รอเริ่มโทร";
}

function voiceStatusColorV391(status = "") {
  if (status === "active") return C.green;
  if (status === "ringing" || status === "starting") return C.orange;
  if (status === "ended") return C.muted;
  return C.purpleLight;
}

function BookingVoiceCallPanelV39({ booking, isSeer = false }) {
  const bookingId = String(booking?.booking_id || "");
  const meetingRef = useRef(null);
  const audioBridgeHostRef = useRef(null);
  const disposeAudioBridgeRef = useRef(null);
  const joinPromiseRef = useRef(null);
  const mountedRef = useRef(true);
  const busyRef = useRef(false);
  const leavingRef = useRef(false);

  const [call, setCall] = useState(null);
  const [serverRole, setServerRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState("");

  const effectiveRole = serverRole || (isSeer ? "seer" : "customer");
  const isSeerRole = effectiveRole === "seer";
  const callStatus = String(call?.status || "idle");
  const callWindow = call?.window || {};
  const participantState = call?.participants || {};
  const remoteRole = isSeerRole ? "customer" : "seer";
  const remote = participantState?.[remoteRole] || null;
  const seerPanelStyle = isSeerRole
    ? {
        background: "#fffdf8",
        borderColor: "#6d28a8aa",
        boxShadow: "0 6px 22px rgba(109,40,168,.12)",
        color: "#2d163f",
      }
    : {
        borderColor: `${C.purple}88`,
        boxShadow: `0 6px 22px ${C.purple}18`,
      };
  const seerTitleStyle = isSeerRole
    ? { color: "#5b258a", textShadow: "-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff" }
    : { color: C.purpleLight };

  const applyServerState = (data) => {
    if (!data) return;
    if (data.call) setCall(data.call);
    if (data.role) setServerRole(String(data.role));
    setError("");
  };

  const refreshNow = async ({ quiet = false } = {}) => {
    if (!bookingId || busyRef.current) return;
    if (!quiet && mountedRef.current) setLoading(true);
    try {
      const data = await voiceCallApiV391(bookingId, "status");
      if (!mountedRef.current) return;
      applyServerState(data);
    } catch (err) {
      if (!quiet && mountedRef.current) {
        setError(err?.message || "โหลดสถานะโทรเสียงไม่สำเร็จ");
      }
    } finally {
      if (!quiet && mountedRef.current) setLoading(false);
    }
  };

  const disposeAudioBridge = () => {
    const dispose = disposeAudioBridgeRef.current;
    disposeAudioBridgeRef.current = null;
    try { dispose?.(); } catch {}
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      disposeAudioBridge();
      const meeting = meetingRef.current;
      meetingRef.current = null;
      joinPromiseRef.current = null;
      try { meeting?.leave?.(); } catch {}
    };
  }, []);

  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const refresh = async (quiet = false) => {
      if (cancelled || busyRef.current) return;
      if (!quiet && mountedRef.current) setLoading(true);
      try {
        const data = await voiceCallApiV391(bookingId, "status");
        if (cancelled || !mountedRef.current) return;
        applyServerState(data);
      } catch (err) {
        if (!quiet && !cancelled && mountedRef.current) {
          setError(err?.message || "โหลดสถานะโทรเสียงไม่สำเร็จ");
        }
      } finally {
        if (!quiet && !cancelled && mountedRef.current) setLoading(false);
      }
    };

    refresh(false);
    const timer = window.setInterval(() => refresh(true), 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [bookingId]);

  const reportJoined = async () => {
    try {
      const data = await voiceCallApiV391(bookingId, "joined");
      if (mountedRef.current) applyServerState(data);
    } catch (err) {
      // The RealtimeKit participant token already proved meeting access. Keep
      // audio connected and let the next status refresh surface any API issue.
      console.warn("voice joined state update failed", err);
    }
  };

  const reportLeft = async () => {
    try {
      const data = await voiceCallApiV391(bookingId, "leave");
      if (mountedRef.current) applyServerState(data);
    } catch (err) {
      console.warn("voice leave state update failed", err);
    }
  };

  const joinRealtime = async (authToken) => {
    const RealtimeKitClient = await loadRealtimeKitV391();
    const meeting = await RealtimeKitClient.init({
      authToken,
      defaults: {
        audio: true,
        video: false,
        // Voice-first settings for mobile WebRTC. Keep video off and ask the
        // browser for echo cancellation, noise suppression, and automatic gain.
        mediaConfiguration: {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            enableStereo: false,
            enableHighBitrate: false,
          },
        },
        autoSwitchAudioDevice: true,
      },
      onError: (sdkError) => {
        if (!mountedRef.current) return;
        const sdkCode = String(sdkError?.code || "").trim();
        const currentMessage = String(sdkError?.message || "").toLowerCase();
        if (/permission|microphone|audio/.test(currentMessage)) {
          setError("กรุณาอนุญาตไมโครโฟน แล้วกดเข้าร่วมสายอีกครั้ง");
        } else if (/network|socket|ice|turn|connection/.test(currentMessage)) {
          setError("การเชื่อมต่อเสียงไม่เสถียร กรุณาอยู่หน้าโทรและกดกลับเข้าสายอีกครั้ง");
        } else if (sdkCode) {
          setError(`ระบบโทรมีปัญหาชั่วคราว (${sdkCode}) กรุณากดกลับเข้าสายอีกครั้ง`);
        }
      },
    });

    meetingRef.current = meeting;

    meeting?.self?.on?.("roomJoined", () => {
      if (meetingRef.current !== meeting || !mountedRef.current) return;
      setJoined(true);
      setMuted(Boolean(meeting?.self?.audioEnabled === false));
    });

    meeting?.self?.on?.("roomLeft", ({ state } = {}) => {
      if (meetingRef.current !== meeting) return;
      meetingRef.current = null;
      disposeAudioBridge();
      if (mountedRef.current) {
        setJoined(false);
        setMuted(false);
        if (state === "ended" || state === "kicked") {
          setError("สายสิ้นสุดแล้ว");
          refreshNow({ quiet: true });
        } else if (["disconnected", "failed"].includes(String(state || ""))) {
          setError("การเชื่อมต่อสายหลุด กรุณากดกลับเข้าสายอีกครั้ง");
          refreshNow({ quiet: true });
        }
      }
      if (!leavingRef.current && ["left", "disconnected", "failed"].includes(String(state || ""))) {
        reportLeft();
      }
    });

    try {
      // Create the hidden audio elements now, but bind their provider only after
      // Core SDK join completes. This gives one owner to meeting.join().
      disposeAudioBridge();
      const audioBridge = await prepareRealtimeKitAudioBridgeV391(audioBridgeHostRef.current);
      disposeAudioBridgeRef.current = () => audioBridge.dispose();

      if (!meeting?.self?.roomJoined) {
        if (joinPromiseRef.current) {
          await joinPromiseRef.current;
        } else {
          const joinPromise = Promise.resolve().then(() => meeting.join());
          joinPromiseRef.current = joinPromise;
          try {
            await joinPromise;
          } finally {
            if (joinPromiseRef.current === joinPromise) joinPromiseRef.current = null;
          }
        }
      }

      audioBridge.bind(meeting);
      if (meeting?.self && typeof meeting.self.enableAudio === "function" && meeting.self.audioEnabled === false) {
        await meeting.self.enableAudio();
      }
    } catch (error) {
      if (meetingRef.current === meeting) meetingRef.current = null;
      joinPromiseRef.current = null;
      disposeAudioBridge();
      try { await meeting.leave?.(); } catch {}
      const message = String(error?.message || "");
      if (/permission|microphone|audio/i.test(message)) {
        throw new Error("กรุณาอนุญาตไมโครโฟน แล้วกดเข้าร่วมสายอีกครั้ง");
      }
      throw error;
    }

    if (!mountedRef.current) {
      if (meetingRef.current === meeting) meetingRef.current = null;
      joinPromiseRef.current = null;
      disposeAudioBridge();
      try { await meeting.leave?.(); } catch {}
      return;
    }

    setJoined(true);
    setMuted(Boolean(meeting?.self?.audioEnabled === false));
    await reportJoined();
  };

  const runCallAction = async (action) => {
    if (!bookingId || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      const data = await voiceCallApiV391(bookingId, action);
      applyServerState(data);
      if (data.auth_token) await joinRealtime(data.auth_token);
    } catch (err) {
      setError(err?.message || "ดำเนินการโทรเสียงไม่สำเร็จ");
    } finally {
      busyRef.current = false;
      if (mountedRef.current) setBusy(false);
    }
  };

  const toggleMute = async () => {
    const meeting = meetingRef.current;
    if (!meeting?.self || busy) return;

    setBusy(true);
    setError("");
    try {
      if (meeting.self.audioEnabled) {
        await meeting.self.disableAudio();
        setMuted(true);
      } else {
        await meeting.self.enableAudio();
        setMuted(false);
      }
    } catch {
      setError("เปลี่ยนสถานะไมโครโฟนไม่สำเร็จ กรุณาตรวจสอบสิทธิ์ไมโครโฟนของเครื่อง");
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const leaveCall = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    leavingRef.current = true;

    const meeting = meetingRef.current;
    meetingRef.current = null;
    joinPromiseRef.current = null;
    disposeAudioBridge();
    try { await meeting?.leave?.(); } catch {}
    try {
      await reportLeft();
    } finally {
      leavingRef.current = false;
      if (mountedRef.current) {
        setJoined(false);
        setMuted(false);
        setBusy(false);
      }
    }
  };

  const endCall = async () => {
    if (!isSeerRole || busy) return;
    setBusy(true);
    setError("");
    leavingRef.current = true;

    const meeting = meetingRef.current;
    meetingRef.current = null;
    joinPromiseRef.current = null;
    disposeAudioBridge();
    try { await meeting?.leave?.(); } catch {}
    try {
      const data = await voiceCallApiV391(bookingId, "end");
      applyServerState(data);
      setJoined(false);
      setMuted(false);
    } catch (err) {
      setError(err?.message || "จบสายไม่สำเร็จ");
    } finally {
      leavingRef.current = false;
      if (mountedRef.current) setBusy(false);
    }
  };

  const Button = ({ children, onClick, className = "btn btn-purple", disabled = false, style = {} }) => (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        minHeight: isSeerRole ? 58 : 52,
        fontSize: isSeerRole ? 18 : 16,
        fontWeight: 900,
        ...(isSeerRole && className === "btn btn-purple" ? { background: "#5b258a", color: "#fff", borderColor: "#5b258a" } : {}),
        ...style,
      }}
    >
      {busy ? "กำลังดำเนินการ..." : children}
    </button>
  );

  return (
    <div className="card" style={{ padding: isSeerRole ? 18 : 14, marginBottom: 12, ...seerPanelStyle }}>
      <div ref={audioBridgeHostRef} aria-hidden="true" style={{ position: "fixed", left: -10000, top: "auto", width: 1, height: 1, overflow: "hidden", pointerEvents: "none" }} />
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: isSeerRole ? 21 : 18, fontWeight: 900, ...seerTitleStyle }}>📞 โทรเสียงในระบบ</div>
          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.65, marginTop: 4 }}>
            เสียงเท่านั้น • ไม่มีวิดีโอ • ไม่บันทึกเสียง • ไม่มีข้อความถอดเสียง
          </div>
        </div>
        <span style={{ border: `1px solid ${voiceStatusColorV391(callStatus)}66`, color: voiceStatusColorV391(callStatus), borderRadius: 999, padding: "5px 9px", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
          {voiceStatusLabelV391(callStatus)}
        </span>
      </div>

      {!bookingId ? (
        <div className="warn-box">ไม่พบเลขรายการสำหรับห้องโทรนี้</div>
      ) : loading && !call ? (
        <div className="empty" style={{ padding: 12, fontSize: 13 }}>กำลังตรวจสอบสถานะห้องโทร...</div>
      ) : callWindow.before ? (
        <div className="warn-box" style={{ borderColor: `${C.orange}55`, background: `${C.orange}11`, color: C.orange }}>
          ⏳ ยังไม่ถึงเวลานัด ระบบจะเปิดโทรเสียงเมื่อถึงเวลานัด{callWindow.wait_minutes ? ` อีกประมาณ ${callWindow.wait_minutes} นาที` : ""}
        </div>
      ) : callWindow.expired ? (
        <div className="warn-box">⌛ หมดเวลาสำหรับห้องโทรของนัดนี้แล้ว ระบบปิดสิทธิ์เข้าห้องเรียบร้อย</div>
      ) : callStatus === "ended" ? (
        <div className="warn-box">✅ สายนี้สิ้นสุดแล้ว สามารถกลับไปสรุปงานได้</div>
      ) : call && call.configured === false ? (
        <div className="warn-box" style={{ borderColor: `${C.orange}55`, background: `${C.orange}11`, color: C.orange }}>
          ระบบโทรเสียงยังรอผู้ดูแลตั้งค่า Cloudflare RealtimeKit และ LINE Login verification ก่อนเปิดใช้งานจริง
        </div>
      ) : (
        <>
          {callStatus === "starting" && (
            <div className="warn-box" style={{ color: C.orange, borderColor: `${C.orange}55`, background: `${C.orange}11` }}>
              กำลังเตรียมห้องโทร โปรดรอสักครู่
            </div>
          )}

          {callStatus === "idle" && isSeerRole && (
            <Button onClick={() => runCallAction("start")}>🔔 เริ่มโทรเสียง</Button>
          )}
          {callStatus === "idle" && !isSeerRole && (
            <div className="empty" style={{ padding: 12, fontSize: 13 }}>
              กำลังรอหมอดูเริ่มโทร ระบบจะแจ้งให้คุณรับสายทันที
            </div>
          )}

          {callStatus === "ringing" && !isSeerRole && !joined && (
            <Button onClick={() => runCallAction("accept")}>📞 รับสาย</Button>
          )}
          {callStatus === "ringing" && isSeerRole && !joined && (
            <Button onClick={() => runCallAction("rejoin")}>📞 กลับเข้าสาย</Button>
          )}
          {callStatus === "ringing" && isSeerRole && joined && (
            <div className="warn-box" style={{ color: C.orange, borderColor: `${C.orange}55`, background: `${C.orange}11` }}>
              กำลังเรียกลูกค้า รออีกฝ่ายกดรับสาย
            </div>
          )}

          {callStatus === "active" && !joined && (
            <Button onClick={() => runCallAction("rejoin")}>
              📞 {isSeerRole ? "กลับเข้าสาย" : "เข้าร่วมสาย"}
            </Button>
          )}

          {callStatus === "active" && remote?.has_joined && !remote?.is_present && (
            <div className="warn-box" style={{ marginTop: 9, borderColor: `${C.orange}55`, background: `${C.orange}11`, color: C.orange }}>
              {isSeerRole ? "ลูกค้าออกจากสายแล้ว" : "หมอดูออกจากสายแล้ว"}
            </div>
          )}

          {joined && (
            <div style={{ display: "grid", gridTemplateColumns: isSeerRole ? "1fr 1fr" : "1fr", gap: 8, marginTop: 10 }}>
              <Button className="btn btn-outline" onClick={toggleMute}>
                {muted ? "🎙️ เปิดไมโครโฟน" : "🔇 ปิดไมโครโฟน"}
              </Button>
              {isSeerRole ? (
                <Button className="btn btn-danger" onClick={endCall}>☎️ จบสาย</Button>
              ) : (
                <Button className="btn btn-outline" onClick={leaveCall}>ออกจากสาย</Button>
              )}
              {isSeerRole && (
                <Button className="btn btn-outline" onClick={leaveCall} style={{ gridColumn: "1 / -1" }}>
                  ออกจากสายชั่วคราว
                </Button>
              )}
            </div>
          )}

          {!joined && ["ringing", "active"].includes(callStatus) && !isSeerRole && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.muted, lineHeight: 1.55 }}>
              เมื่อพร้อมแล้วกด “รับสาย” และอนุญาตไมโครโฟนของเครื่อง
            </div>
          )}
          {joined && (
            <div style={{ marginTop: 10, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              🔊 เสียงจะออกตามอุปกรณ์/ลำโพงที่โทรศัพท์หรือเบราว์เซอร์ตั้งไว้
            </div>
          )}
        </>
      )}

      {error && (
        <div className="warn-box" style={{ marginTop: 10, borderColor: `${C.red}66`, background: `${C.red}11`, color: C.red }}>
          {error}
          <br />
          <button
            type="button"
            onClick={() => refreshNow({ quiet: false })}
            style={{ border: 0, background: "transparent", color: "inherit", textDecoration: "underline", padding: 0, marginTop: 5, fontWeight: 900, cursor: "pointer" }}
          >
            โหลดสถานะโทรอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}


// ─── v91 Booking / Fortune Room helpers ─────────────────────────────────────
const DW_BOOKING_STATUS_LABEL = {
  pending_request: "รอหมอดูตอบรับ",
  paid_waiting_seer: "ชำระแล้ว รอหมอดูรับงาน",
  seer_confirmed: "หมอดูยืนยันเวลาแล้ว",
  seer_proposed_new_time: "หมอดูเสนอเวลาใหม่",
  rejected: "หมอดูปฏิเสธ",
  waiting_payment: "รอลูกค้าโอนเงิน",
  waiting_slip_review: "รอตรวจสลิป",
  slot_unavailable: "เวลาที่เลือกไม่ว่างแล้ว",
  confirmed: "ยืนยันคิวแล้ว / รอถึงเวลานัด",
  in_session: "กำลังดูดวง",
  completed: "เสร็จสิ้น",
  cancelled: "ยกเลิก",
};
function dwBookingStatusLabel(status) {
  return DW_BOOKING_STATUS_LABEL[status] || status || "-";
}
function dwBookingBadgeClass(status) {
  if (["confirmed", "in_session", "completed", "seer_confirmed", "waiting_payment", "paid_waiting_seer"].includes(status)) return "b-green";
  if (["rejected", "cancelled", "slot_unavailable"].includes(status)) return "b-red";
  if (["waiting_slip_review", "seer_proposed_new_time"].includes(status)) return "b-orange";
  return "b-purple";
}

const PLATFORM_COMMISSION_PERCENT_V109 = 0; // ยังไม่ได้กำหนดหักเปอร์เซ็นต์แพลตฟอร์มในระบบจริง
const REFERRAL_COMMISSION_PERCENT_V109 = 3; // ค่าคอมแนะนำหมอดูที่มีอยู่เดิมในระบบ invite
function calcSeerNetIncomeV109(price, commissionPercent = PLATFORM_COMMISSION_PERCENT_V109) {
  const gross = Number(price || 0);
  const fee = Math.round(gross * Number(commissionPercent || 0) / 100);
  return { gross, fee, net: Math.max(0, gross - fee), commissionPercent:Number(commissionPercent || 0) };
}
function dwBookingScheduledText(b = {}) {
  return b.scheduled_at || [b.requested_date, b.requested_time].filter(Boolean).join(" ") || "ยังไม่ระบุ";
}
function parseBookingScheduleDateV112(b = {}) {
  const raw = String(b.scheduled_at || [b.requested_date, b.requested_time].filter(Boolean).join(" ") || "").trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{1,2}:\d{2}))?/);
  if (m) return new Date(`${m[1]}T${m[2] || "00:00"}:00+07:00`);
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}
function isBookingRoomOpenV112(b = {}) {
  const d = parseBookingScheduleDateV112(b);
  if (!d) return true;
  return Date.now() >= d.getTime();
}
function bookingRoomWaitTextV112(b = {}) {
  const d = parseBookingScheduleDateV112(b);
  if (!d) return "";
  const mins = Math.max(0, Math.ceil((d.getTime() - Date.now()) / 60000));
  const when = dwBookingScheduledText(b);
  if (mins <= 0) return "";
  if (mins < 60) return `ห้องนี้จะเข้าได้เมื่อถึงเวลานัด ${when} อีกประมาณ ${mins} นาที`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return `ห้องนี้จะเข้าได้เมื่อถึงเวลานัด ${when} อีกประมาณ ${hrs} ชม.${rem ? ` ${rem} นาที` : ""}`;
}
// v3.9.42: ทุกงานต้องผ่านจากเวลาเริ่มนัดอย่างน้อย 15 นาทีจึงปิดงานได้
// UI ใช้ข้อความเดียวกับ API แต่ไม่เปิดเผยเหตุผลภายในของกติกานี้แก่หมอดู
const BOOKING_COMPLETION_DELAY_MINUTES_V3942 = 15;
const BOOKING_COMPLETION_WAIT_MESSAGE_V3942 = "ไม่สามารถกดปิดงานได้ ต้องรอให้พ้น 15นาทีจากที่ลูกค้านัดเวลา ถึงกดปิดงานได้";
function bookingCompletionAvailableAtV3942(b = {}) {
  const start = parseBookingScheduleDateV112(b);
  return start ? new Date(start.getTime() + BOOKING_COMPLETION_DELAY_MINUTES_V3942 * 60000) : null;
}
function isBookingCloseReadyV3942(b = {}) {
  const availableAt = bookingCompletionAvailableAtV3942(b);
  return Boolean(availableAt) && Date.now() >= availableAt.getTime();
}
function parseIsoOrLocalV113(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d;
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{1,2}:\d{2}))?/);
  if (m) return new Date(`${m[1]}T${m[2] || "00:00"}:00+07:00`);
  return null;
}
// v183: ทุกคำขอที่ยังไม่ชำระมีเวลาชำระ 15 นาทีเท่านั้น
// ใช้ deadline ที่เร็วกว่าเสมอ เพื่อให้รายการเก่าซึ่งเคยตั้งไว้ 30 นาที
// ถูกนับตามกติกาใหม่ 15 นาทีด้วย โดยไม่ต้องแก้ข้อมูลย้อนหลังด้วย SQL
function paymentDeadlineDateV113(b = {}) {
  const explicit = parseIsoOrLocalV113(b.payment_deadline_at || b.paymentDeadlineAt || "");
  const base = parseIsoOrLocalV113(b.updated_at || b.created_at || b.seer_confirmed_at || "");
  const policyDeadline = base ? new Date(base.getTime() + 15 * 60000) : null;
  if (explicit && policyDeadline) return explicit.getTime() <= policyDeadline.getTime() ? explicit : policyDeadline;
  return explicit || policyDeadline;
}
function paymentCountdownTextV113(b = {}, nowMs = Date.now()) {
  if (!["waiting_payment","seer_confirmed"].includes(String(b.status || ""))) return "";
  const d = paymentDeadlineDateV113(b);
  if (!d) return "กรุณาโอนเงินภายใน 15 นาที";
  const secs = Math.max(0, Math.floor((d.getTime() - nowMs) / 1000));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  if (secs <= 0) return "หมดเวลาโอนเงินแล้ว ระบบจะยกเลิกอัตโนมัติ";
  return `เหลือเวลาโอนเงิน ${String(mm).padStart(2,"0")}:${String(ss).padStart(2,"0")} นาที`;
}
function slotKeyV113(date, time) {
  return `${String(date || "").trim()}|${String(time || "").trim()}`;
}
function bookingSlotKeyV113(b = {}) {
  const date = b.requested_date || String(b.scheduled_at || "").slice(0,10);
  const time = b.requested_time || (String(b.scheduled_at || "").match(/(\d{1,2}:\d{2})/)?.[1] || "");
  return slotKeyV113(date, time);
}
function bookingSlotKeysV191(b = {}, dateOverride = "", timeOverride = "") {
  const date = String(dateOverride || b.requested_date || String(b.scheduled_at || "").slice(0,10)).slice(0,10);
  const slot = String(timeOverride || b.requested_time || b.time_slot || "");
  return reservedSlotCellsV191(slot, b).map(cell=>slotKeyV113(date,cell));
}
function isSlotBlockedStatusV113(status) {
  // v176: an unpaid booking is only a payment draft, not a permanent slot hold.
  // Slots become unavailable once a slip is submitted, payment is approved, or a paid reschedule is proposed.
  return ["waiting_slip_review","paid_waiting_seer","seer_proposed_new_time","confirmed","in_session","completed"].includes(String(status || ""));
}
function blockedSlotKeysForSeerV176(list = [], seerIdKeys = []) {
  const keys = new Set();
  for (const b of (list || [])) {
    const sameSeer = seerIdKeys.includes(String(b.seer_id || "")) || seerIdKeys.includes(String(b.seer_line_user_id || "")) || seerIdKeys.includes(String(b.seer_name || ""));
    if (!sameSeer) continue;
    if (isSlotBlockedStatusV113(b.status)) bookingSlotKeysV191(b).forEach(key=>keys.add(key));
    // While the customer decides on a paid reschedule, reserve both the original and proposed 30-minute cells.
    if (String(b.status || "") === "seer_proposed_new_time" && b.proposed_date && b.proposed_time) {
      bookingSlotKeysV191(b, b.proposed_date, b.proposed_time).forEach(key=>keys.add(key));
    }
  }
  return keys;
}
function bookingAvailabilityKeyV176(seer = {}, date = "") {
  return `${String(seer?.seerId || seer?.id || seer?.seer_id || seer?.lineUserId || seer?.line_user_id || seer?.name || "").trim()}|${String(date || "").slice(0,10)}`;
}

function bookingSlotStartDateV120(date, slot) {
  const d = String(date || "").trim();
  const s = String(slot || "").trim();
  const start = (s.match(/(\d{1,2}:\d{2})/) || [])[1] || "";
  if (!d || !start) return null;
  const dt = new Date(`${d}T${start}:00+07:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function isTodayDateV120(date) {
  const d = String(date || "").slice(0,10);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  return d === today;
}
function isSlotTooCloseForTodayV120(date, slot, nowMs = Date.now()) {
  if (!isTodayDateV120(date)) return false;
  const start = bookingSlotStartDateV120(date, slot);
  if (!start) return false;
  // ต้องมีเวลามากกว่า 30 นาทีก่อนเริ่มนัด
  // เช่น 16:30 ยังเห็น 17:00 ได้ แต่ 16:31 จะไม่เห็น 17:00 แล้ว
  return nowMs > (start.getTime() - 30 * 60000);
}

function lineContactUrlV112(value = "") {
  const v = String(value || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v) || /^line:\/\//i.test(v)) return v;
  if (v.startsWith("@")) return `https://line.me/R/ti/p/${encodeURIComponent(v)}`;
  if (/^U[a-f0-9]/i.test(v)) return ""; // LINE userId ใช้เปิดโทรโดยตรงไม่ได้
  return `https://line.me/R/ti/p/${encodeURIComponent(v)}`;
}
function roomLineInviteTextV121(b = {}) {
  const roomUrl = createCustomerBookingRoomLiffUrlV394(b.booking_id || "");
  return `นัดดูดวง ดวงวันนี้\nบริการ: ${b.service_name || "-"}\nเวลา: ${dwBookingScheduledText(b) || "-"}\nห้อง: ${b.room_id || "-"}\nกดเข้าแอพ: ${roomUrl}`;
}
async function copyTextV121(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch { return false; }
  }
}
function lineShareUrlV121(text = "") {
  return `https://line.me/R/share?text=${encodeURIComponent(text)}`;
}
function openLineFallbackV121(text = "") {
  const share = lineShareUrlV121(text);
  try { window.open(share, "_blank"); }
  catch { location.href = share; }
}
async function uploadFileToR2(file, { ownerType = "general", ownerId = "draft", fileRole = "file" } = {}) {
  if (!file) throw new Error("missing file");
  const form = new FormData();
  form.append("file", file);
  form.append("ownerType", ownerType);
  form.append("ownerId", ownerId);
  form.append("fileRole", fileRole);
  const res = await fetch(API_BASE + "/api/upload", { method: "POST", body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.message || `Upload error: ${res.status}`);
  return data;
}

function fileToCompressedDataUrlV110(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("missing file"));
    if (!String(file.type || "").startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("อ่านไฟล์ไม่สำเร็จ"));
      reader.readAsDataURL(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        const scale = Math.min(1, max / Math.max(img.width || 1, img.height || 1));
        const w = Math.max(1, Math.round((img.width || 1) * scale));
        const h = Math.max(1, Math.round((img.height || 1) * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.72));
      };
      img.onerror = () => resolve(String(reader.result || ""));
      img.src = String(reader.result || "");
    };
    reader.onerror = () => reject(reader.error || new Error("อ่านไฟล์ไม่สำเร็จ"));
    reader.readAsDataURL(file);
  });
}

async function uploadSlipDirectV110(file, bookingId, lineUserId = "", customerContact = {}) {
  const form = new FormData();
  form.append("file", file);
  form.append("booking_id", bookingId || "");
  form.append("line_user_id", lineUserId || "");
  form.append("customer_phone", customerContact.phone || "");
  form.append("customer_line_id", customerContact.lineId || customerContact.line_id || "");
  form.append("customer_contact_preference", Array.isArray(customerContact.preferredMethods) ? customerContact.preferredMethods.join(",") : (customerContact.customer_contact_preference || ""));
  form.append("marketing_consent", customerContact.consent ? "1" : "0");
  const res = await fetch(API_BASE + "/api/upload-slip", { method: "POST", body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) {
    const err = new Error(data.message || `Upload slip error: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

function parseMoneyNumber(text, fallback = 199) {
  const n = Number(String(text || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// v18: D1 approval polling + no base64. v17: ตอนยังไม่ได้เปิด R2 ห้ามส่งไฟล์ base64 เข้า D1 เพราะรูปจากมือถือมักใหญ่เกินและทำให้ใบสมัครไม่เข้าแอดมิน
function stripFileBodyForD1(fileLike) {
  if (!fileLike) return { name: "", url: "", note: "" };
  if (typeof fileLike === "string") return { name: fileLike, url: "", note: "" };
  return {
    name: fileLike.name || fileLike.fileName || "ไฟล์แนบ",
    url: fileLike.url && !String(fileLike.url).startsWith("data:") ? fileLike.url : "",
    note: fileLike.url && String(fileLike.url).startsWith("data:") ? "รอเปิด R2 เพื่อดูไฟล์จริง" : "",
  };
}

function safePhotoForD1(photoUrl) {
  const value = String(photoUrl || "");
  // v133: profile_image_url ต้องเป็น URL จริงจาก R2 เท่านั้น ไม่เก็บ data URL เป็นค่าหลัก
  return value.startsWith("data:") ? "" : value;
}

function getPhotoNoteForD1(photoUrl, photoName) {
  const value = String(photoUrl || "");
  return {
    name: photoName || "ภาพถ่ายหมอดู",
    url: value && !value.startsWith("data:") ? value : "",
    note: value.startsWith("data:") ? "แนบจากมือถือแล้ว แต่ยังไม่เก็บไฟล์จริงจนกว่าจะเปิด R2" : "",
  };
}

function parseJsonMaybe(text, fallback = {}) {
  try {
    if (!text || typeof text !== "string") return fallback;
    const t = text.trim();
    if (!t.startsWith("{") && !t.startsWith("[")) return fallback;
    return JSON.parse(t);
  } catch {
    return fallback;
  }
}

function applicationMeta(row = {}) {
  const meta = parseJsonMaybe(row.admin_note, null);
  if (meta && typeof meta === "object") return meta;
  const note = row.admin_note || "";
  return { rawNote: note, services: [], documents: [] };
}

function mapApiSeer(row = {}) {
  const packagePricesV134 = String(row.price_text || row.priceText || "").split(/\n+/).map(x => Number(String(x).match(/(?:ราคา\s*)?(\d{2,6})\s*(?:บาท|บ\.?|฿)/i)?.[1] || String(x).match(/฿\s*(\d{2,6})/i)?.[1] || 0)).filter(n => Number.isFinite(n) && n > 0);
  const minPrice = packagePricesV134.length ? Math.min(...packagePricesV134) : parseMoneyNumber(row.price_text, 199);
  const spec = row.specialties || "หมอดูออนไลน์";
  return {
    id: row.seer_id || row.id || `seer_${Date.now()}`,
    seerId: row.seer_id || row.id || "",
    lineUserId: row.line_user_id || "",
    lineId: row.line_id || row.lineId || "",
    lineUrl: row.line_url || row.lineUrl || "",
    buttonText: row.button_text || row.buttonText || "ปรึกษาหมอดูคนนี้",
    callType: row.call_type || row.callType || "line",
    availableTime: row.available_time || row.availableTime || "",
    commissionRate: Number(row.commission_rate ?? row.commissionRate ?? 0.3),
    loginRole: row.login_role || row.loginRole || "seer",
    profileStatus: row.profile_status || row.profileStatus || "approved",
    bankName: row.bank_name || row.bankName || "",
    bankAccountName: row.bank_account_name || row.bankAccountName || "",
    bankAccountNumber: row.bank_account_number || row.bankAccountNumber || "",
    termsAccepted: row.terms_accepted || row.termsAccepted || "yes",
    termsAcceptedAt: row.terms_accepted_at || row.termsAcceptedAt || "",
    termsVersion: row.terms_version || row.termsVersion || "v1.0",
    sourceApplicationId: row.source_application_id || row.sourceApplicationId || "",
    emoji: "🔮",
    name: row.display_name || row.name || "หมอดู",
    spec,
    bio: row.short_bio || "หมอดูที่ผ่านการอนุมัติจากระบบดวงวันนี้",
    photoUrl: row.profile_image_url || "",
    photoName: row.profile_image_url ? "profile" : "",
    rating: Number(row.rating || 5),
    reviews: Number(row.review_count || 0),
    status: row.live_status || row.presence_status || (row.status === "active" ? "offline" : (row.status || "offline")),
    adminStatus: row.status || "active",
    lastSeenAt: row.last_seen_at || row.lastSeenAt || "",
    lastOnline: formatSeerLastSeenV136(row.last_seen_at || row.lastSeenAt || ""),
    avgReply: (row.live_status || row.presence_status) === "online" ? "ภายใน 10 นาที" : "เมื่อออนไลน์",
    answered: 0, latestViews: 0, chatCount: 0,
    minPrice,
    priceText: row.price_text || row.priceText || "",
    price_text: row.price_text || row.priceText || "",
    bookingPackageText: row.price_text || row.priceText || "",
    booking_packages_text: row.price_text || row.priceText || "",
    adminNote: row.admin_note || row.adminNote || "",
    profileLocked: Number(row.profile_locked || 0) === 1 || row.profile_locked === true,
    platformLevel: normalizeSeerPlatformLevel(row.platform_level || row.platformLevel || "standard"),
    referralStatus: String(row.referral_status || row.referralStatus || "none"),
    referralId: row.referral_id || row.referralId || "",
    referrerSeerId: row.referrer_seer_id || row.referrerSeerId || "",
    referrerName: row.referrer_name || row.referrerName || "",
    referralCommissionPercent: Number(row.referral_commission_percent || row.referralCommissionPercent || 0),
    incomeSummary: row.income_summary || row.incomeSummary || { completed_count:0, gross_amount:0, seer_income_amount:0, platform_net_income_amount:0, last_completed_at:"" },
    scheduleSummary: row.schedule_summary || row.scheduleSummary || { open_slot_count:0, next_available_date:"", last_available_date:"" },
    joined: row.created_at || row.updated_at || new Date().toISOString(),
    approvedAt: row.created_at || row.updated_at || new Date().toLocaleString("th-TH"),
    sortOrder: Number(row.sort_order || 999),
    feedBoost: Number(row.sort_order || 999) <= 1,
    services: [
      {id:`${row.seer_id || "seer"}_live`,name:"จองคิวดูดวงสด",type:"live",price:minPrice,ico:"🎯",desc:"คุยกับหมอดูแบบสด",input:"booking"},
      {id:`${row.seer_id || "seer"}_tarot`,name:"ดูไพ่ Tarot",type:"tarot",price:minPrice,ico:"🃏",desc:"ดูไพ่และให้คำแนะนำ",input:"question"},
      ...(String(spec).includes("เบอร์") ? [{id:`${row.seer_id || "seer"}_phone`,name:"ดูเบอร์โทร",type:"phone",price:minPrice,ico:"📱",desc:"วิเคราะห์เบอร์โทรศัพท์",input:"phone"}] : []),
    ],
  };
}

function mapApiReview(row = {}) {
  return {
    id: row.review_id || row.id || `REV_${Date.now()}`,
    seer_id: row.seer_id || "",
    seer_name: row.seer_name || "",
    n: row.customer_name || "ลูกค้าจริง",
    t: row.review_text || "",
    st: Number(row.rating || 5),
    verified: true,
    createdAt: row.created_at || "",
    source: row.booking_id === "ADMIN" ? "admin" : "customer",
  };
}

function mapApiApplication(row = {}) {
  const statusMap = { pending:"รอตรวจสอบ", approved:"อนุมัติแล้ว", rejected:"ปฏิเสธ" };
  const meta = applicationMeta(row);
  const price = row.start_price ? `${row.start_price} บาท` : "-";
  const bank = [row.bank_name, row.bank_account_name, row.bank_account_number].filter(Boolean).join(" / ") || row.bank_account_number || "-";
  const documents = Array.isArray(meta.documents) ? meta.documents : [row.book_file_url, row.id_file_url, row.extra_file_url].filter(Boolean).map(url => ({name:url,url}));
  const profilePhoto = meta.photo && typeof meta.photo === "object" ? meta.photo : {};
  const services = Array.isArray(meta.services) && meta.services.length ? meta.services : String(row.specialties || "").split(",").map(x=>x.trim()).filter(Boolean);
  return {
    id: row.application_id || row.id || `APP_${Date.now()}`,
    applicationId: row.application_id || row.id || "",
    name: row.display_name || row.name || "หมอดูสมัครใหม่",
    spec: row.specialties || row.spec || "",
    phone: row.phone || row.line_display_name || "",
    contactPhone: row.contact_phone || row.contactPhone || meta.contactPhone || meta.contact_phone || "",
    adminContactPhone: row.contact_phone || row.contactPhone || meta.contactPhone || meta.contact_phone || "",
    lineId: row.line_id || row.lineId || meta.lineId || meta.line_id || "",
    lineUrl: row.line_url || row.lineUrl || meta.lineUrl || meta.line_url || "",
    docs: `ราคาเริ่มต้น: ${price} / บัญชีรับเงิน: ${bank}`,
    bio: row.short_bio || "",
    documents,
    photoUrl: row.profile_image_url || row.photoUrl || profilePhoto.url || "",
    photoName: profilePhoto.name || (row.profile_image_url ? "ภาพหมอดู" : ""),
    profileLocked: false,
    services,
    adminNote: meta.rawNote || row.admin_note || "",
    status: statusMap[row.status] || row.status || "รอตรวจสอบ",
    lineUserId: row.line_user_id || "",
    lineDisplayName: row.line_display_name || "",
    linePictureUrl: row.line_picture_url || "",
    createdAt: row.created_at || row.updated_at || "",
  };
}


// v165: ป้องกัน state update ซ้ำจาก polling ที่ข้อมูลยังเหมือนเดิม
// เพราะการ set array/object ใหม่ทุกครั้ง ทำให้หน้าหลังบ้าน re-render และรูปสลิปกระพริบบนมือถือ
function sameDataV165(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); }
  catch { return false; }
}


// เก็บใบสมัครหมอดูแบบใช้ร่วมกันระหว่างหน้าสมัครและแอดมิน
// v11 แก้ปัญหาแอดมินเปิดค้างแล้วเขียนทับใบสมัครเป็นค่าว่าง: อ่านอย่างเดียวจนกว่าจะอนุมัติ/ปฏิเสธ/ล้าง
const SEER_APPLICANTS_KEY = "dw_seer_applicants_live";
const SEER_APPLICANTS_KEYS = [
  "dw_seer_applicants_live",
  "dw_seer_applicants_v11",
  "dw_seer_applicants_v10",
  "dw_seer_applicants_v9",
  "dw_seer_applicants_v8",
  "dw_seer_applicants_v7",
  "dw_seer_applicants_v6",
  "dw_seer_applicants_v5",
  "dw_seer_applicants_v4",
  "dw_seer_applicants",
];

function readSeerApplicantsStore() {
  try {
    const merged = [];
    const seen = new Set();
    SEER_APPLICANTS_KEYS.forEach((key) => {
      try {
        const raw = localStorage.getItem(key);
        const list = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          if (!item || typeof item !== "object") return;
          const id = item.id || `${item.name || "seer"}_${item.createdAt || ""}_${item.lineUserId || ""}`;
          if (seen.has(id)) return;
          seen.add(id);
          merged.push(item);
        });
      } catch {}
    });
    return merged;
  } catch {
    return [];
  }
}

function writeSeerApplicantsStore(list) {
  const safeList = Array.isArray(list) ? list : [];
  try {
    // เขียนทั้ง key ใหม่และ key เดิม เพื่อให้หน้าแอดมินเวอร์ชันที่เปิดค้างอยู่ยังอ่านเจอ
    localStorage.setItem(SEER_APPLICANTS_KEY, JSON.stringify(safeList));
    localStorage.setItem("dw_seer_applicants_v11", JSON.stringify(safeList));
    localStorage.setItem("dw_seer_applicants_v10", JSON.stringify(safeList));
    localStorage.setItem("dw_seer_applicants_v9", JSON.stringify(safeList));
    window.dispatchEvent(new CustomEvent("duangwannee:seer-applicants-updated", { detail: safeList }));
  } catch (err) {
    console.error("save seer applicants failed", err);
  }
}

function clearOldSeerApplicantsStore() {
  // v10: ไม่ล้างอัตโนมัติแล้ว เพราะเคยทำให้ข้อมูลสมัครหาย
  // ให้ล้างจากปุ่ม “ล้างทั้งหมด” ในหน้าแอดมินเท่านั้น
}



// เก็บหมอดูที่แอดมินอนุมัติแล้ว เพื่อให้เชื่อมไปหน้า จัดการหมอดู / ทำเนียบหมอดู / สิทธิ์ผู้ใช้
const APPROVED_SEERS_KEY = "dw_approved_seers_live";
const APPROVED_SEERS_KEYS = [
  "dw_approved_seers_live",
  "dw_approved_seers_v12",
  "dw_approved_seers_v11",
];

function uniqById(list) {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).filter((item) => {
    if (!item || !item.id) return false;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function readApprovedSeersStore() {
  // v140: รีเซ็ตสิทธิ์หมอดูเก่าในเครื่องทั้งหมด
  // ต่อไปสิทธิ์หมอดูต้องมาจาก D1 /api/seers หลังแอดมินอนุมัติเท่านั้น
  try {
    APPROVED_SEERS_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem("duangwannee_line_role");
    localStorage.removeItem("DW_ACTIVE_SEER_PROFILE_ID_V52");
    localStorage.removeItem("dw_seen_seer_approved_popup");
    const user = getSavedLineUser() || {};
    if (user?.role === "seer" || user?.approvedSeer) {
      const next = {...user};
      delete next.role; delete next.seerId; delete next.seerName; delete next.approvedSeer; delete next.approvedAt;
      localStorage.setItem("duangwannee_line_user", JSON.stringify(next));
    }
  } catch {}
  return [];
}

function writeApprovedSeersStore(list) {
  const safeList = uniqById(list);
  try {
    localStorage.setItem(APPROVED_SEERS_KEY, JSON.stringify(safeList));
    localStorage.setItem("dw_approved_seers_v12", JSON.stringify(safeList));
    window.dispatchEvent(new CustomEvent("duangwannee:approved-seers-updated", { detail: safeList }));
  } catch (err) {
    console.error("save approved seers failed", err);
  }
}

function buildServicesFromApplicant(applicant) {
  const selected = Array.isArray(applicant?.services) ? applicant.services : [];
  const price = Number(String(applicant?.docs || "").match(/ราคาเริ่มต้น:\s*([^/]+)/)?.[1]?.replace(/[^0-9]/g, "")) || 199;
  const map = {
    tarot: {id:"tarot",name:"ดูไพ่ Tarot",type:"tarot",price,ico:"🃏",desc:"ดูไพ่และให้คำแนะนำเฉพาะบุคคล",input:"question"},
    phone: {id:"phone",name:"ดูเบอร์โทร",type:"phone",price,ico:"📱",desc:"วิเคราะห์เบอร์โทรศัพท์",input:"phone"},
    live: {id:"live",name:"จองคิวดูดวงสด",type:"live",price,ico:"🎯",desc:"คุยกับหมอดูแบบสด",input:"booking"},
    love: {id:"love",name:"ดูดวงความรัก",type:"love",price,ico:"💕",desc:"ดูความรักและความสัมพันธ์",input:"question"},
    career: {id:"career",name:"ดูดวงการงาน",type:"career",price,ico:"💼",desc:"ดูแนวโน้มการงาน",input:"question"},
    finance: {id:"finance",name:"ดูดวงการเงิน",type:"finance",price,ico:"💰",desc:"ดูแนวโน้มการเงิน",input:"question"},
  };
  const result = selected.map(k => map[k]).filter(Boolean);
  return result.length ? result.map((svc, i) => ({...svc, id:`${applicant.id}_${svc.id}_${i}`})) : [
    {id:`${applicant?.id || "approved"}_live`,name:"จองคิวดูดวงสด",type:"live",price,ico:"🎯",desc:"คุยกับหมอดูแบบสด",input:"booking"},
    {id:`${applicant?.id || "approved"}_general`,name:"ดูดวงทั่วไป",type:"general",price,ico:"🔮",desc:"ดูดวงและให้คำแนะนำ",input:"question"},
  ];
}

function applicantToApprovedSeer(applicant) {
  const services = buildServicesFromApplicant(applicant);
  const minPrice = Math.min(...services.map(s => Number(s.price) || 199));
  return {
    id: `approved_${applicant.lineUserId || applicant.id}`,
    applicantId: applicant.id,
    lineUserId: applicant.lineUserId || "",
    lineDisplayName: applicant.lineDisplayName || "",
    linePictureUrl: applicant.linePictureUrl || "",
    contactPhone: applicant.contactPhone || applicant.adminContactPhone || "",
    adminContactPhone: applicant.contactPhone || applicant.adminContactPhone || "",
    lineId: applicant.lineId || applicant.line_id || "",
    lineUrl: applicant.lineUrl || applicant.line_url || "",
    photoUrl: applicant.photoUrl || applicant.linePictureUrl || "",
    photoName: applicant.photoName || "",
    profileLocked: !!applicant.profileLocked,
    emoji: "🔮",
    name: applicant.name || applicant.lineDisplayName || "หมอดูใหม่",
    spec: applicant.spec || "หมอดูออนไลน์",
    bio: applicant.bio || "หมอดูที่ผ่านการอนุมัติจากแอดมินแล้ว พร้อมให้บริการบนดวงวันนี้",
    rating: 5.0,
    reviews: 0,
    status: "offline",
    lastOnline: "ยังไม่เข้าใช้งาน",
    avgReply: "เมื่อออนไลน์",
    answered: 0,
    latestViews: 0,
    chatCount: 0,
    minPrice,
    services,
    approvedAt: new Date().toLocaleString("th-TH"),
  };
}

function getApprovedSeerForCurrentLineUser() {
  try {
    const lineUser = getSavedLineUser() || {
      userId: "PENDING_LINE_" + Date.now(),
      displayName: "สมัครผ่าน LIFF / รอผูก LINE OA",
      pictureUrl: "",
    };
    if (!lineUser?.userId) return null;
    return readApprovedSeersStore().find(s => s.lineUserId && s.lineUserId === lineUser.userId) || null;
  } catch {
    return null;
  }
}

function readActiveSeerProfileId() {
  try { return localStorage.getItem("DW_ACTIVE_SEER_PROFILE_ID_V52") || ""; } catch { return ""; }
}

function writeActiveSeerProfileId(id) {
  try {
    if (id) localStorage.setItem("DW_ACTIVE_SEER_PROFILE_ID_V52", String(id));
    else localStorage.removeItem("DW_ACTIVE_SEER_PROFILE_ID_V52");
  } catch {}
}

function findSeerByAnyId(id, list) {
  const key = String(id || "");
  if (!key) return null;
  return (list || []).find(s =>
    String(s.id || "") === key ||
    String(s.seerId || "") === key ||
    String(s.lineUserId || "") === key ||
    String(s.name || "") === key
  ) || null;
}

function isCurrentLineUserApprovedSeer() {
  return !!getApprovedSeerForCurrentLineUser();
}

function markCurrentLineUserAsSeerIfApproved() {
  try {
    const approved = getApprovedSeerForCurrentLineUser();
    if (!approved) return false;
    const user = getSavedLineUser() || {};
    const next = {...user, role:"seer", seerId:approved.id, seerName:approved.name, approvedSeer:true};
    localStorage.setItem("duangwannee_line_user", JSON.stringify(next));
    localStorage.setItem("duangwannee_line_role", "seer");
    return true;
  } catch {
    return false;
  }
}

const SEER_REGISTER_DRAFT_KEY = "dw_seer_register_draft_v13";

function readSeerRegisterDraftStore() {
  try {
    const raw = localStorage.getItem(SEER_REGISTER_DRAFT_KEY);
    const data = raw ? JSON.parse(raw) : {};
    return data && typeof data === "object" ? data : {};
  } catch {
    return {};
  }
}

function writeSeerRegisterDraftStore(data) {
  try {
    localStorage.setItem(SEER_REGISTER_DRAFT_KEY, JSON.stringify(data || {}));
  } catch {}
}

function clearSeerRegisterDraftStore() {
  try {
    localStorage.removeItem(SEER_REGISTER_DRAFT_KEY);
  } catch {}
}


// LINE LIFF จริงสำหรับเว็บดวงวันนี้
// ใช้ LIFF หลักตัวเดียวสำหรับลูกค้า; ทางสมัครหมอดูเปิดผ่านลิงก์จากแอดมิน/ริชเมนูเท่านั้น
const LIFF_MAIN_ID = "2010105518-BLxLaUpj";
const LIFF_SEER_ID = "2010105518-q8TqU8J6";
const LIFF_MAIN_URL = `https://liff.line.me/${LIFF_MAIN_ID}`;
const LIFF_SEER_REGISTER_URL = `https://liff.line.me/${LIFF_SEER_ID}`;

// v3.9.4: Booking-room links sent through LINE must enter the existing LIFF
// app first. A raw Pages URL can open in a generic in-app browser and fail to
// obtain a current LINE credential for the voice-call API.
function createCustomerBookingRoomLiffUrlV394(bookingId = "") {
  const id = String(bookingId || "").trim();
  const state = new URLSearchParams({ open: "booking_room" });
  if (id) state.set("booking_id", id);
  return `${LIFF_MAIN_URL}?liff.state=${encodeURIComponent(`?${state.toString()}`)}`;
}

function getLiffStateParamsV142B() {
  try {
    const url = new URL(window.location.href);
    const stateRaw = url.searchParams.get("liff.state") || "";
    if (!stateRaw) return new URLSearchParams();
    const decoded = decodeURIComponent(stateRaw);
    const q = decoded.includes("?") ? decoded.split("?").slice(1).join("?") : decoded.replace(/^\?/, "");
    return new URLSearchParams(q);
  } catch {
    return new URLSearchParams();
  }
}

function getCurrentLiffId() {
  try {
    const url = new URL(window.location.href);
    const path = url.pathname.toLowerCase();
    const stateParams = getLiffStateParamsV142B();
    const screen = (url.searchParams.get("screen") || stateParams.get("screen") || "").toLowerCase();
    // Admin routes and invite links must always stay on the main LIFF app. A
    // generic `invite` parameter remains reserved for the existing seer flow.
    if (path.startsWith("/admin") || screen === "admin" || url.searchParams.has("admin_invite") || stateParams.has("admin_invite")) return LIFF_MAIN_ID;
    const page = (url.searchParams.get("page") || stateParams.get("page") || "").toLowerCase();
    if (path.startsWith("/seer") || screen === "seer_register" || page === "seer_register" || url.searchParams.has("invite") || stateParams.has("invite")) return LIFF_SEER_ID;
  } catch {}
  return LIFF_MAIN_ID;
}

// LINE OA ดวงวันนี้ ใช้บังคับให้หมอดูเพิ่มเพื่อนก่อนสมัคร/ก่อนส่งข้อความผ่าน Messaging API
const LINE_OA_BASIC_ID = "@12Odclge";
const LINE_OA_ADD_FRIEND_URL = "https://line.me/R/ti/p/@12Odclge";

function openLineOaAddFriend() {
  try { localStorage.setItem("DW_SEER_OA_FRIEND_CLICKED", "yes"); localStorage.setItem("DW_LINE_OA_FRIEND_STATUS", "pending_check"); } catch {}
  try {
    window.open(LINE_OA_ADD_FRIEND_URL, "_blank", "noopener,noreferrer");
  } catch {
    window.location.href = LINE_OA_ADD_FRIEND_URL;
  }
}

async function acceptLineAppForSeerRegister(saveDraftFn) {
  try { saveDraftFn?.(); } catch {}
  try {
    const user = await ensureLineLogin();
    if (user?.userId) {
      alert(`เชื่อม LINE แล้ว: ${user.displayName || user.userId}`);
      return user;
    }
  } catch (err) {
    alert("เปิด LINE Login ไม่สำเร็จ: " + (err.message || err));
  }
  return null;
}

function hasAcceptedLineApp() {
  return !!getSavedLineUser()?.userId;
}

function hasClickedOaAddFriend() {
  try { return localStorage.getItem("DW_SEER_OA_FRIEND_CLICKED") === "yes"; } catch { return false; }
}

function getCachedOaFriendStatus() {
  try { return localStorage.getItem("DW_LINE_OA_FRIEND_STATUS") || ""; } catch { return ""; }
}

function setCachedOaFriendStatus(status) {
  try { localStorage.setItem("DW_LINE_OA_FRIEND_STATUS", String(status || "")); } catch {}
}

function isOaFriendConfirmed() {
  return getCachedOaFriendStatus() === "friend";
}

async function checkLineOaFriendshipNow(saveDraftFn) {
  try { saveDraftFn?.(); } catch {}
  try {
    const liff = await loadLiffSdk();
    if (!liff) {
      alert("โหลด LINE LIFF ไม่สำเร็จ");
      return false;
    }
    const currentLiffId = getCurrentLiffId();
  await liff.init({ liffId: currentLiffId });
    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri: window.location.href });
      return false;
    }
    const profile = await liff.getProfile();
    const lineUser = {
      role: "customer",
      liffId: currentLiffId,
      userId: profile.userId,
      displayName: profile.displayName || "",
      pictureUrl: profile.pictureUrl || "",
      statusMessage: profile.statusMessage || "",
      isInClient: liff.isInClient(),
      os: liff.getOS(),
      language: liff.getLanguage(),
      loggedInAt: new Date().toISOString(),
    };
    localStorage.setItem("duangwannee_line_user", JSON.stringify(lineUser));
    localStorage.setItem("duangwannee_line_user_id", lineUser.userId);
    localStorage.setItem("duangwannee_line_display_name", lineUser.displayName);
    localStorage.setItem("duangwannee_line_picture_url", lineUser.pictureUrl);

    if (typeof liff.getFriendship === "function") {
      const friendship = await liff.getFriendship();
      const isFriend = !!friendship?.friendFlag;
      setCachedOaFriendStatus(isFriend ? "friend" : "not_friend");
      alert(isFriend ? "ตรวจสอบแล้ว: คุณเป็นเพื่อน LINE OA ดวงวันนี้แล้ว" : "ตรวจสอบแล้ว: ยังไม่ได้เป็นเพื่อน LINE OA ดวงวันนี้");
      return isFriend;
    }

    setCachedOaFriendStatus("unknown");
    alert("LIFF app นี้ยังตรวจสอบสถานะ OA อัตโนมัติไม่ได้ ให้กดเพิ่มเพื่อน OA แล้วกลับมาตรวจอีกครั้ง");
    return false;
  } catch (err) {
    setCachedOaFriendStatus("unknown");
    alert("ตรวจสอบสถานะ LINE OA ไม่สำเร็จ: " + (err.message || err));
    return false;
  }
}

function clearThisDeviceLoginAndReload() {
  if (!confirm("ล้างข้อมูลล็อกอิน/ชื่อ LINE ที่จำไว้ในเครื่องนี้ใช่ไหม?\nข้อมูลใน D1 ไม่ถูกลบ")) return;
  try {
    const keepKeys = [];
    Object.keys(localStorage || {}).forEach((k) => {
      const key = String(k || "");
      if (
        key.includes("duangwannee") ||
        key.includes("DW_") ||
        key.includes("dw_") ||
        key.includes("LIFF") ||
        key.includes("liff") ||
        key.includes("line")
      ) {
        localStorage.removeItem(key);
      }
    });
    sessionStorage.clear();
  } catch {}
  window.location.href = "/?clean=" + Date.now();
}

function loadLiffSdk() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.liff) return resolve(window.liff);

    const old = document.getElementById("line-liff-sdk");
    if (old) {
      old.addEventListener("load", () => resolve(window.liff));
      old.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = "line-liff-sdk";
    script.src = "https://static.line-scdn.net/liff/edge/2/sdk.js";
    script.async = true;
    script.onload = () => resolve(window.liff);
    script.onerror = () => reject(new Error("โหลด LINE LIFF SDK ไม่สำเร็จ"));
    document.head.appendChild(script);
  });
}

async function ensureLineLogin() {
  if (typeof window === "undefined") return null;

  const path = window.location.pathname.toLowerCase();
  if (path.startsWith("/admin")) return null;

  const cached = localStorage.getItem("duangwannee_line_user");
  if (cached) {
    try {
      const user = JSON.parse(cached);
      if (user && user.userId) return user;
    } catch {}
  }

  const liff = await loadLiffSdk();
  if (!liff) return null;

  const currentLiffId = getCurrentLiffId();
  await liff.init({ liffId: currentLiffId });

  if (!liff.isLoggedIn()) {
    liff.login({ redirectUri: window.location.href });
    return null;
  }

  const profile = await liff.getProfile();
  const lineUser = {
    role: currentLiffId === LIFF_SEER_ID ? "seer_applicant" : "customer",
    liffId: currentLiffId,
    userId: profile.userId,
    displayName: profile.displayName || "",
    pictureUrl: profile.pictureUrl || "",
    statusMessage: profile.statusMessage || "",
    isInClient: liff.isInClient(),
    os: liff.getOS(),
    language: liff.getLanguage(),
    loggedInAt: new Date().toISOString(),
  };

  localStorage.setItem("duangwannee_line_user", JSON.stringify(lineUser));
  localStorage.setItem("duangwannee_line_user_id", lineUser.userId);
  localStorage.setItem("duangwannee_line_display_name", lineUser.displayName);
  localStorage.setItem("duangwannee_line_picture_url", lineUser.pictureUrl);

  window.duangwanneeLineUser = lineUser;
  window.dispatchEvent(new CustomEvent("duangwannee:line-ready", { detail: lineUser }));

  return lineUser;
}

function getSavedLineUser() {
  try {
    const raw = localStorage.getItem("duangwannee_line_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const CUSTOMER_PROFILE_KEY_V138 = "dw_customer_profile_v138";
function readCustomerProfileV138(userId = "") {
  try {
    const raw = localStorage.getItem(CUSTOMER_PROFILE_KEY_V138);
    const data = raw ? JSON.parse(raw) : null;
    if (!data || typeof data !== "object") return null;
    if (userId && String(data.line_user_id || data.userId || "") !== String(userId)) return null;
    if (!String(data.name || "").trim() || !String(data.phone || "").trim()) return null;
    return data;
  } catch {
    return null;
  }
}
function writeCustomerProfileV138(profile = {}) {
  try { localStorage.setItem(CUSTOMER_PROFILE_KEY_V138, JSON.stringify(profile || {})); } catch {}
}
function clearCustomerProfileV138() {
  try { localStorage.removeItem(CUSTOMER_PROFILE_KEY_V138); } catch {}
}

function parseBirthdateV139(value) {
  const s = String(value || "").trim();
  if (!s) return null;
  let y, m, d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    [y, m, d] = s.split("-").map(Number);
  } else {
    const parts = s.split(/[\/\-\.]/).map(Number);
    if (parts.length >= 3) {
      d = parts[0]; m = parts[1]; y = parts[2];
    }
  }
  if (!y || !m || !d) return null;
  if (y > 2400) y -= 543;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return { year: y, month: m, day: d, date: dt };
}


// v182: modal รายละเอียดคิวต้องเป็น component ระดับไฟล์ (ไม่อยู่ใน SeerDash)
// เพราะ SeerDash ถูกประกาศภายใน CustomerApp และอาจถูก remount เมื่อ state เบื้องหลังอัปเดต
// ทำให้หน้ารายละเอียดดูเหมือนรีเฟรชบน LINE WebView
function formatCustomerBirthdateV182(value) {
  const parsed = parseBirthdateV139(value);
  if (!parsed) return "ลูกค้ายังไม่ได้ระบุ";
  return `${String(parsed.day).padStart(2,"0")}/${String(parsed.month).padStart(2,"0")}/${parsed.year + 543}`;
}
function customerContactPreferenceLabelV182(booking = {}) {
  const raw = String(booking.customer_contact_preference || booking.customerContactPreference || "").toLowerCase();
  const values = raw.split(/[,|\s]+/).filter(Boolean);
  const labels = [];
  if (values.includes("phone")) labels.push("📞 เบอร์โทร");
  if (values.includes("line")) labels.push("💬 LINE");
  return labels.length ? labels.join(" / ") : "ไม่ได้ระบุ (ใช้ข้อมูลด้านล่าง)";
}
function SeerQueueDetailsModalV182({ booking, onClose, onOpenRoom }) {
  useEffect(() => {
    if (!booking?.booking_id) return undefined;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "contain";
    return () => {
      body.style.overflow = prevOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, [booking?.booking_id]);

  if (!booking?.booking_id) return null;
  const inSession = String(booking.status || "") === "in_session";
  const roomOpen = isBookingRoomOpenV112(booking);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="รายละเอียดคิวดูดวง"
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:1000002, background:"rgba(0,0,0,.76)",
        display:"flex", alignItems:"flex-end", justifyContent:"center", padding:0,
        overscrollBehavior:"contain", WebkitTapHighlightColor:"transparent"
      }}
    >
      <div
        onClick={e=>e.stopPropagation()}
        style={{
          width:"100%", maxWidth:560, maxHeight:"90dvh", overflowY:"auto",
          WebkitOverflowScrolling:"touch", overscrollBehavior:"contain",
          contain:"layout paint", transform:"translateZ(0)",
          background:"linear-gradient(180deg, #211541, #120b2a)",
          border:`1px solid ${C.green}88`, borderRadius:"24px 24px 0 0",
          padding:"18px 16px calc(22px + env(safe-area-inset-bottom, 0px))",
          boxShadow:"0 -16px 46px rgba(0,0,0,.52)"
        }}
      >
        <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:19,fontWeight:950,color:C.goldLight}}>🔮 รายละเอียดคิวดูดวง</div>
            <div style={{fontSize:12,color:C.green,marginTop:4}}>ยืนยันคิวแล้ว • รอถึงเวลานัดเพื่อเปิดห้องดูดวง</div>
          </div>
          <button className="btn btn-outline btn-sm" type="button" onClick={onClose}>ปิด</button>
        </div>

        <div className="brow" style={{margin:"16px 0 0",borderColor:`${C.green}66`,background:"rgba(68,201,135,.07)"}}>
          <div className="brow-hd">
            <div>
              <div className="brow-name">{booking.customer_name || "ลูกค้า"}</div>
              <div style={{fontSize:13,color:C.purpleLight,marginTop:3}}>{booking.service_name || "จองคิวดูดวงสด"}</div>
            </div>
            <span className="badge b-green">{inSession ? "กำลังดูดวง" : "ยืนยันคิวแล้ว"}</span>
          </div>
          <div className="brow-detail" style={{fontSize:14,lineHeight:1.85}}>
            📅 วันและเวลานัด: {dwBookingScheduledText(booking)}<br/>
            🎂 วัน/เดือน/ปีเกิด: {formatCustomerBirthdateV182(booking.customer_birthdate_resolved || booking.customer_birthdate)}<br/>
            📌 สะดวกติดต่อกลับ: {customerContactPreferenceLabelV182(booking)}<br/>
            📞 เบอร์โทร: {booking.customer_phone || "-"}<br/>
            💬 LINE ID: {booking.customer_line_id || "-"}<br/>
            💰 ยอดบริการ: ฿{Number(booking.price || 0).toLocaleString()}
          </div>
        </div>

        <div className="card" style={{padding:14,marginTop:12,borderColor:`${C.purple}66`,background:`${C.purple}10`}}>
          <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:7}}>📝 เรื่องที่ลูกค้าต้องการดู</div>
          <div style={{fontSize:14,color:C.text,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{booking.customer_note || "ลูกค้าไม่ได้ระบุรายละเอียดเพิ่มเติม"}</div>
        </div>

        <div className="card" style={{padding:14,marginTop:12,borderColor:`${C.orange}66`,background:`${C.orange}10`}}>
          <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:7}}>✅ ขั้นตอนปิดงานและรับรายได้</div>
          <div style={{fontSize:12,color:C.text,lineHeight:1.8}}>
            1. แนะนำให้หมอดูแอด LINE หรือโทรสอบถามลูกค้า เพื่อแอดคอนแทคกันก่อนถึงเวลานัด<br/>
            2. เมื่อดูดวงเสร็จ ให้กด <b>ปิดงาน / ส่งสรุป</b> ในห้องดูดวง<br/>
            3. ระบบจะบันทึกว่างานเสร็จสิ้น และลงรายได้ของงานนี้ในเมนู <b>รายได้</b> ทันที
          </div>
        </div>

        <button
          className={`btn ${roomOpen ? "btn-gold" : "btn-outline"}`}
          type="button"
          style={{marginTop:14,minHeight:52,fontSize:16,fontWeight:950}}
          onClick={()=>{
            if (!roomOpen) { alert(bookingRoomWaitTextV112(booking)); return; }
            onOpenRoom?.(booking);
          }}
        >{roomOpen ? "เปิดห้องดูดวง" : "รอถึงเวลานัดเพื่อเปิดห้องดูดวง"}</button>
      </div>
    </div>
  );
}

function getThaiZodiacV139(month, day) {
  const md = month * 100 + day;
  // v139E: ปรับเป็นช่วงราศีแบบไทยตามที่กำหนด
  // ตัวอย่าง: 16 มีนาคม - 15 เมษายน = ราศีมีน
  const list = [
    {name:"มังกร", from:116, to:215, element:"ดิน", boost:"เสริมด้วยความมั่นคง วินัย การเคารพผู้ใหญ่ และการจัดบ้าน/โต๊ะทำงานให้เป็นระเบียบ"},
    {name:"กุมภ์", from:216, to:315, element:"ลม", boost:"เสริมด้วยการวางแผน ตรวจรายละเอียด ทำบุญยา/สุขภาพ และใช้สีเขียว"},
    {name:"มีน", from:316, to:415, element:"น้ำ", boost:"เสริมด้วยการทำบุญเกี่ยวกับน้ำ ไหว้พระขอพรเรื่องจิตใจ และใช้สีเขียว/ฟ้า"},
    {name:"เมษ", from:416, to:515, element:"ไฟ", boost:"เสริมด้วยการเริ่มสิ่งใหม่อย่างมั่นใจ ใช้สีแดง/ทอง และตั้งเป้าหมายชัดเจน"},
    {name:"พฤษภ", from:516, to:615, element:"ดิน", boost:"เสริมด้วยการเก็บออม ดูแลทรัพย์สิน และใช้สีเขียว/ครีม"},
    {name:"เมถุน", from:616, to:715, element:"ลม", boost:"เสริมด้วยการพูดคุย เจรจา เรียนรู้สิ่งใหม่ และใช้สีเหลือง/ฟ้า"},
    {name:"กรกฎ", from:716, to:815, element:"น้ำ", boost:"เสริมด้วยการดูแลบ้าน ครอบครัว ทำบุญอาหาร และใช้สีขาว/เงิน"},
    {name:"สิงห์", from:816, to:915, element:"ไฟ", boost:"เสริมด้วยความกล้า ภาวะผู้นำ การแต่งกายให้มั่นใจ และใช้สีทอง"},
    {name:"กันย์", from:916, to:1015, element:"ดิน", boost:"เสริมด้วยความละเอียดรอบคอบ การวางแผน และการทำบุญด้านสุขภาพ"},
    {name:"ตุลย์", from:1016, to:1115, element:"ลม", boost:"เสริมด้วยความสมดุล ความสัมพันธ์ดี ๆ และใช้สีชมพู/ฟ้า"},
    {name:"พิจิก", from:1116, to:1215, element:"น้ำ", boost:"เสริมด้วยการปล่อยวาง ทำบุญเกี่ยวกับแสงสว่าง และใช้สีม่วง/ดำอย่างพอดี"},
    {name:"ธนู", from:1216, to:115, element:"ไฟ", boost:"เสริมด้วยการเดินทาง เรียนรู้ อธิษฐานเป้าหมาย และใช้สีส้ม/ทอง"},
  ];
  return list.find(z => z.from <= z.to ? (md >= z.from && md <= z.to) : (md >= z.from || md <= z.to)) || list[0];
}

function getThaiAnimalYearV139(year) {
  const animals = ["ชวด","ฉลู","ขาล","เถาะ","มะโรง","มะเส็ง","มะเมีย","มะแม","วอก","ระกา","จอ","กุน"];
  const idx = ((year - 2020) % 12 + 12) % 12;
  return animals[idx];
}

function getChineseElementV139(year) {
  const elements = ["ทอง","ทอง","น้ำ","น้ำ","ไม้","ไม้","ไฟ","ไฟ","ดิน","ดิน"];
  return elements[((year - 2020) % 10 + 10) % 10];
}

function getCustomerAstroV139(birthdate) {
  const b = parseBirthdateV139(birthdate);
  if (!b) return null;
  const zodiac = getThaiZodiacV139(b.month, b.day);
  const animal = getThaiAnimalYearV139(b.year);
  const yearElement = getChineseElementV139(b.year);
  const luckyByElement = {
    "ดิน":"เสริมดวงด้วยการทำบุญเกี่ยวกับที่ดิน บ้าน ผู้ใหญ่ และจัดพื้นที่ทำงานให้มั่นคง",
    "น้ำ":"เสริมดวงด้วยการทำบุญน้ำดื่ม ปล่อยปลา และดูแลอารมณ์ให้เย็น",
    "ลม":"เสริมดวงด้วยการสวดมนต์ ฝึกสมาธิสั้น ๆ และใช้คำพูดสร้างโอกาส",
    "ไฟ":"เสริมดวงด้วยแสงสว่าง เทียน ไฟฟ้า การเริ่มต้นสิ่งใหม่ และสีทอง",
    "ทอง":"เสริมดวงด้วยการทำบุญโลหะ ระฆัง พระพุทธรูป หรือการออมเงินอย่างมีวินัย",
    "ไม้":"เสริมดวงด้วยต้นไม้ การปลูกสิ่งดี ๆ และการเรียนรู้ต่อเนื่อง",
  };
  return {
    zodiac: zodiac.name,
    zodiacElement: zodiac.element,
    animal,
    yearElement,
    text: `คุณเป็นคนราศี${zodiac.name} ธาตุ${zodiac.element} ปีนักษัตร${animal} ธาตุ${yearElement}`,
    advice: zodiac.boost || luckyByElement[zodiac.element] || "เสริมดวงด้วยการทำบุญและตั้งใจทำสิ่งดีอย่างสม่ำเสมอ",
    yearAdvice: luckyByElement[yearElement] || "",
  };
}

async function captureLiffProfileForRegister({ allowLogin = false } = {}) {
  try {
    const cached = getSavedLineUser();
    if (cached?.userId && String(cached.liffId || "") === getCurrentLiffId()) return cached;

    const liff = await loadLiffSdk();
    if (!liff) return cached || null;

    const currentLiffId = getCurrentLiffId();
    await liff.init({ liffId: currentLiffId });

    if (!liff.isLoggedIn()) {
      if (allowLogin) {
        liff.login({ redirectUri: window.location.href });
      }
      return cached || null;
    }

    const profile = await liff.getProfile();
    const lineUser = {
      role: currentLiffId === LIFF_SEER_ID ? "seer_applicant" : "customer",
      liffId: currentLiffId,
      userId: profile.userId,
      displayName: profile.displayName || "",
      pictureUrl: profile.pictureUrl || "",
      statusMessage: profile.statusMessage || "",
      isInClient: typeof liff.isInClient === "function" ? liff.isInClient() : false,
      os: typeof liff.getOS === "function" ? liff.getOS() : "",
      language: typeof liff.getLanguage === "function" ? liff.getLanguage() : "",
      loggedInAt: new Date().toISOString(),
    };

    localStorage.setItem("duangwannee_line_user", JSON.stringify(lineUser));
    localStorage.setItem("duangwannee_line_user_id", lineUser.userId);
    localStorage.setItem("duangwannee_line_display_name", lineUser.displayName);
    localStorage.setItem("duangwannee_line_picture_url", lineUser.pictureUrl);
    window.duangwanneeLineUser = lineUser;
    window.dispatchEvent(new CustomEvent("duangwannee:line-ready", { detail: lineUser }));
    return lineUser;
  } catch (err) {
    console.warn("capture LIFF profile for register failed", err);
    return getSavedLineUser() || null;
  }
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Sarabun:wght@300;400;500;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
html,body{background:#05021a;color:${C.text};font-family:'Sarabun',sans-serif;-webkit-tap-highlight-color:transparent;overflow-x:hidden}
.root{max-width:430px;margin:0 auto;min-height:100vh;background:transparent;position:relative}
.app-frame{display:flex;flex-direction:column;min-height:100vh}
h1,h2,.cinzel{font-family:'Cinzel',serif}
.scr{display:none;flex-direction:column;min-height:100vh}
.scr.on{display:flex}
/* v3: light, large-print workspace only for the seer back office. */
/* v3.7: easy-to-read white workspace for the seer back office only. */
.seer-light{background:linear-gradient(180deg,#ffffff 0%,#f8f7ff 100%)!important;color:#1f1934!important;min-height:100vh}
.seer-light .topbar{background:rgba(255,255,255,.96)!important;border-bottom:1px solid #e5e1f2!important;color:#2a174f!important;box-shadow:0 2px 10px rgba(66,35,126,.05)!important}
.seer-light .topbar-title{color:#2f176c!important;font-family:'Sarabun',sans-serif!important;font-size:23px!important;font-weight:950!important;letter-spacing:-.02em}
.seer-light .content{background:transparent!important;color:#211a37!important}
.seer-light .card,.seer-light .brow,.seer-light .stat-card,.seer-light .notif{background:#ffffff!important;color:#211a37!important;border:1px solid #ded8ec!important;box-shadow:0 4px 14px rgba(44,22,94,.09)!important}
.seer-light .brow-name,.seer-light .sec-hd,.seer-light .stat-num{color:#1e1734!important;font-size:22px!important;font-weight:950!important}
.seer-light .brow-detail,.seer-light .stat-lbl,.seer-light .flabel{color:#45405a!important;font-size:17px!important;line-height:1.75!important}
.seer-light .tab-bar{background:transparent!important;border-color:transparent!important;gap:10px!important;padding:0 16px 10px!important}
.seer-light .tab{display:inline-flex!important;align-items:center!important;justify-content:center!important;gap:8px!important;color:#3f237b!important;font-size:17px!important;font-weight:850!important;min-height:48px!important;padding:9px 15px!important;border:2px solid #8d66e7!important;border-radius:999px!important;background:#ffffff!important;box-shadow:0 2px 7px rgba(63,35,123,.09)!important}
.seer-light .tab.on{background:linear-gradient(135deg,#6f32c4,#48209e)!important;border-color:#4a219c!important;color:#ffffff!important;box-shadow:0 5px 13px rgba(83,38,172,.28)!important}
.seer-light .btn{min-height:54px!important;font-size:18px!important;font-weight:950!important;border-radius:14px!important}
.seer-light .btn-gold{background:linear-gradient(135deg,#ffe184,#e2a70e)!important;color:#2c1900!important;border:1px solid #d49700!important;box-shadow:0 5px 12px rgba(216,155,0,.18)!important}
.seer-light .btn-purple{background:linear-gradient(135deg,#8849e6,#4c1fa5)!important;color:#ffffff!important;border:1px solid #5b25ba!important;box-shadow:0 5px 12px rgba(89,37,185,.22)!important}
.seer-light .btn-outline{background:#ffffff!important;color:#5722bd!important;border:2px solid #7b41dc!important;box-shadow:0 2px 8px rgba(94,42,188,.10)!important}
.seer-light .btn-success{background:linear-gradient(135deg,#229c76,#0f6f56)!important;color:#ffffff!important;border:1px solid #0d7657!important}
.seer-light .finput,.seer-light select,.seer-light textarea{font-size:18px!important;min-height:54px!important;background:#fff!important;color:#211a37!important;border:2px solid #c7bddf!important;border-radius:14px!important}
.seer-light .badge{font-size:16px!important;padding:8px 12px!important;font-weight:950!important}
.seer-light .seer-dashboard-stats{margin:18px 16px 12px!important;gap:12px!important;padding:0!important}
.seer-light .seer-stat-card{display:flex!important;align-items:center!important;gap:12px!important;min-height:110px!important;padding:16px!important;border-radius:20px!important;cursor:pointer!important}
/* v3.9.9: use native buttons so each entire dashboard card is clickable. */
.seer-light button.seer-stat-card-button{appearance:none!important;-webkit-appearance:none!important;width:100%!important;min-width:0!important;margin:0!important;background:#ffffff!important;color:#211a37!important;font:inherit!important;text-align:left!important;border-style:solid!important}
.seer-light button.seer-stat-card-button:focus-visible{outline:3px solid #7C5CBF!important;outline-offset:3px!important}
.seer-light button.seer-stat-card-button:active{transform:translateY(1px) scale(.995)}
.seer-light .seer-stat-icon{width:48px;height:48px;flex:0 0 48px;display:grid;place-items:center;border-radius:16px;background:linear-gradient(135deg,#f1eaff,#e6d9ff);color:#5420b3;font-size:25px;box-shadow:inset 0 0 0 1px rgba(93,39,190,.10)}
.seer-light .seer-stat-copy{min-width:0;flex:1}
.seer-light .seer-stat-card .stat-num{font-size:31px!important;line-height:1!important;letter-spacing:-.04em}
.seer-light .seer-stat-card .stat-lbl{font-size:18px!important;font-weight:850!important;line-height:1.3!important;margin-top:6px}
.seer-light .seer-share-card{margin:14px 16px 16px!important;padding:20px!important;border-radius:24px!important;border:1px solid #ded4ee!important;background:linear-gradient(145deg,#ffffff 0%,#fcfbff 100%)!important;box-shadow:0 8px 20px rgba(66,29,123,.11)!important}
.seer-light .seer-share-head{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}
.seer-light .seer-share-copy{flex:1;min-width:0}
.seer-light .seer-share-title{display:flex;align-items:center;gap:9px;color:#7a34d6!important;font-size:28px!important;font-weight:950!important;line-height:1.18!important;letter-spacing:-.035em;-webkit-text-stroke:.65px #211233;text-shadow:0 1px 0 rgba(255,255,255,.98),0 2px 3px rgba(74,31,142,.22)}
.seer-light .seer-share-title .seer-share-icon{font-size:34px;line-height:1;filter:drop-shadow(0 2px 1px rgba(45,19,98,.23))}
.seer-light .seer-share-desc{font-size:17px!important;color:#392c61!important;line-height:1.62!important;margin-top:10px!important;font-weight:700!important}
.seer-light .seer-level-badge{display:inline-flex!important;align-items:center!important;white-space:nowrap!important;gap:5px!important;background:linear-gradient(135deg,#fff3bf,#efb719)!important;color:#2b1900!important;border:2px solid #ca8b00!important;border-radius:999px!important;font-size:18px!important;font-weight:950!important;padding:9px 13px!important;-webkit-text-stroke:.52px #000000;text-shadow:0 1px 0 rgba(255,255,255,.94),0 2px 2px rgba(0,0,0,.22);box-shadow:0 4px 10px rgba(211,145,0,.19)!important}
.seer-light .seer-share-actions{display:grid!important;grid-template-columns:1.25fr .8fr 1.25fr!important;gap:10px!important;margin-top:16px!important}
.seer-light .seer-share-actions .btn{width:100%!important;min-height:58px!important;padding:10px 12px!important}
.seer-light .seer-share-actions .seer-invite-button{grid-column:1/2!important}
.seer-light .seer-tab-icon{font-size:20px!important;line-height:1!important}
.seer-light .sec-hd{color:#3d187e!important;text-transform:none!important;letter-spacing:0!important;font-size:24px!important;font-weight:950!important;padding:16px 16px 10px!important}
@media (max-width:380px){
  .seer-light .seer-share-title{font-size:24px!important}
  .seer-light .seer-level-badge{font-size:15px!important;padding:8px 10px!important}
  .seer-light .seer-share-actions{grid-template-columns:1fr 1fr!important}
  .seer-light .seer-share-actions .btn-purple{grid-column:1/-1!important}
}

.content{flex:1;overflow-y:auto;padding-bottom:84px}
/* v34 Cosmic Animated Background */
.stars{position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:0;overflow:hidden;background:
  radial-gradient(circle at 18% 14%, rgba(108,79,255,.16), transparent 24%),
  radial-gradient(circle at 78% 25%, rgba(85,185,255,.10), transparent 20%),
  radial-gradient(circle at 50% 86%, rgba(201,168,76,.07), transparent 26%),
  linear-gradient(180deg,#05021a 0%,#090327 48%,#06021c 100%)}
.stars::before,.stars::after{content:'';position:absolute;left:-24%;width:150%;height:240px;border-radius:999px;filter:blur(34px);opacity:.16;mix-blend-mode:screen;animation:mwFloat 22s ease-in-out infinite alternate}
.stars::before{top:10%;transform:rotate(-13deg);background:linear-gradient(90deg,transparent,rgba(153,190,255,.20),rgba(210,170,255,.25),rgba(255,225,150,.13),transparent)}
.stars::after{bottom:9%;transform:rotate(9deg);background:linear-gradient(90deg,transparent,rgba(86,149,255,.14),rgba(190,135,255,.22),transparent);animation-duration:28s}
.star{position:absolute;background:rgba(255,255,255,.88);border-radius:50%;box-shadow:0 0 7px rgba(255,255,255,.72),0 0 16px rgba(150,177,255,.28);animation:tw 4s ease-in-out infinite}
@keyframes tw{0%,100%{opacity:.18;transform:scale(.75)}45%{opacity:.95;transform:scale(1.15)}70%{opacity:.38;transform:scale(.9)}}
.zodiac-bg{position:absolute;color:rgba(207,191,255,.72);text-shadow:0 0 14px rgba(145,101,255,.42);font-family:'Cinzel',serif;font-weight:400;user-select:none;animation:zodiacFloat 18s ease-in-out infinite}
@keyframes zodiacFloat{0%,100%{transform:translate3d(0,0,0) rotate(0deg);opacity:.07}50%{transform:translate3d(8px,-18px,0) rotate(4deg);opacity:.22}}
.constellation{position:absolute;width:94px;height:60px;opacity:.14;animation:constGlow 8s ease-in-out infinite}
.constellation span{position:absolute;width:4px;height:4px;border-radius:999px;background:#fff;box-shadow:0 0 10px rgba(255,255,255,.72)}
.constellation i{position:absolute;left:8px;top:28px;width:78px;height:1px;background:linear-gradient(90deg,rgba(255,255,255,.05),rgba(255,255,255,.38),rgba(255,255,255,.05));transform:rotate(-18deg);transform-origin:left center}
@keyframes constGlow{0%,100%{opacity:.08}50%{opacity:.22}}
.shooting-star{position:absolute;width:5px;height:5px;border-radius:999px;background:radial-gradient(circle at 35% 35%, rgba(255,255,255,1) 0%, rgba(255,255,255,.98) 38%, rgba(196,226,255,.98) 68%, rgba(196,226,255,.35) 100%);box-shadow:0 0 12px rgba(255,255,255,.98),0 0 22px rgba(172,210,255,.9),0 0 32px rgba(180,220,255,.45);opacity:0}
.shooting-star::after{display:none}
.shooting-star.up-right{animation:meteorOrbUpRight 1.55s ease-out forwards}
.shooting-star.up-left{animation:meteorOrbUpLeft 1.55s ease-out forwards}
.shooting-star.left-right{animation:meteorOrbLeftRight 1.55s ease-out forwards}
.shooting-star.right-left{animation:meteorOrbRightLeft 1.55s ease-out forwards}
@keyframes meteorOrbUpRight{0%{opacity:0;transform:translate3d(0,0,0) scale(.82)}6%{opacity:1}84%{opacity:.96}100%{opacity:0;transform:translate3d(112vw,-54vh,0) scale(1)}}
@keyframes meteorOrbUpLeft{0%{opacity:0;transform:translate3d(0,0,0) scale(.82)}6%{opacity:1}84%{opacity:.96}100%{opacity:0;transform:translate3d(-112vw,-54vh,0) scale(1)}}
@keyframes meteorOrbLeftRight{0%{opacity:0;transform:translate3d(0,0,0) scale(.82)}6%{opacity:1}84%{opacity:.96}100%{opacity:0;transform:translate3d(112vw,-10vh,0) scale(1)}}
@keyframes meteorOrbRightLeft{0%{opacity:0;transform:translate3d(0,0,0) scale(.82)}6%{opacity:1}84%{opacity:.96}100%{opacity:0;transform:translate3d(-112vw,-10vh,0) scale(1)}}
@keyframes mwFloat{0%{transform:translateX(-24px) rotate(-12deg) scale(1);opacity:.09}50%{opacity:.17}100%{transform:translateX(34px) rotate(-8deg) scale(1.04);opacity:.12}}
.z1{position:relative;z-index:1}
/* Topbar */
.topbar{position:sticky;top:0;z-index:50;background:rgba(9,7,26,.92);backdrop-filter:blur(14px);border-bottom:1px solid ${C.border};padding:13px 16px;display:flex;align-items:center;gap:10px}
.topbar-title{font-family:'Cinzel',serif;font-size:16px;color:${C.goldLight};flex:1}
.back-btn{background:none;border:none;color:${C.muted};cursor:pointer;font-size:20px;padding:4px 8px 4px 0;line-height:1}
/* Bottom Nav */
.bnav-wrap{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:rgba(17,13,38,.97);border-top:1px solid ${C.border};display:flex;z-index:100;padding-bottom:env(safe-area-inset-bottom,0)}
.bnav{flex:1;display:flex;flex-direction:column;align-items:center;padding:9px 4px 7px;cursor:pointer;gap:3px;border:none;background:none;color:${C.muted};font-family:'Sarabun',sans-serif;font-size:10px;transition:color .15s}
.bnav.on{color:${C.goldLight}}
.bnav .ico{font-size:21px}
/* Cards */
.card{background:${C.card};border:1px solid ${C.border};border-radius:16px}
.card-gold{border-color:${C.gold}44}
.gold-bar{height:2px;background:linear-gradient(90deg,transparent,${C.gold},transparent);margin:0 16px}
/* Sections */
.sec-hd{padding:14px 16px 8px;font-size:11px;color:${C.muted};text-transform:uppercase;letter-spacing:.1em}
/* Seer Card */
.seer-card{margin:0 16px 12px;cursor:pointer;transition:transform .18s,border-color .18s}
.seer-card:active{transform:scale(.98)}
.seer-inner{padding:14px;display:flex;gap:12px}
.seer-ava{width:68px;height:68px;border-radius:50%;border:2px solid ${C.gold}55;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:26px;background:${C.dim}}
.seer-info{flex:1;min-width:0}
.seer-name{font-family:'Cinzel',serif;font-size:14px;color:${C.goldLight};margin-bottom:3px}
.seer-spec{font-size:11px;color:${C.purpleLight};margin-bottom:5px}
.seer-bio{font-size:12px;color:${C.muted};line-height:1.5;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:7px}
.seer-row{display:flex;justify-content:space-between;align-items:center}
.rating{font-size:12px;color:${C.gold}}
.price-tag{font-size:12px;color:${C.goldLight};font-weight:600}
.tag{display:inline-block;background:${C.purple}20;color:${C.purpleLight};border:1px solid ${C.purple}40;border-radius:20px;font-size:10px;padding:2px 8px;margin:2px 2px 0 0}
/* Profile Hero */
.profile-hero{padding:24px 20px 20px;text-align:center;border-bottom:1px solid ${C.border};background:linear-gradient(180deg,${C.purple}0D 0%,transparent 100%)}
.ava-lg{width:84px;height:84px;border-radius:50%;border:3px solid ${C.gold}55;margin:0 auto 12px;display:flex;align-items:center;justify-content:center;font-size:34px;background:${C.dim}}
.profile-name{font-family:'Cinzel',serif;font-size:20px;color:${C.goldLight};margin-bottom:5px}
/* Forms */
.fgroup{margin-bottom:13px}
.flabel{font-size:12px;color:${C.muted};margin-bottom:5px;display:block}
.finput{width:100%;background:${C.surface};border:1px solid ${C.border};border-radius:10px;padding:11px 13px;color:${C.text};font-family:'Sarabun',sans-serif;font-size:14px;outline:none;transition:border-color .15s}
.finput:focus{border-color:${C.purple}}
textarea.finput{resize:none;height:80px;line-height:1.5}
.fpad{padding:16px}
/* Buttons */
.btn{width:100%;padding:13px;border-radius:12px;border:none;font-family:'Sarabun',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .1s}
.btn:active{transform:scale(.98)}
.btn-gold{background:linear-gradient(135deg,${C.gold},#9A7018);color:#1a1000}
.btn-outline{background:transparent;border:1px solid ${C.border};color:${C.muted}}
.btn-purple{background:${C.purple};color:white}
.btn-danger{background:${C.red};color:white}
.btn-success{background:${C.green};color:white}
.btn-sm{padding:7px 14px;font-size:12px;width:auto;border-radius:8px}
.btn-line{background:#06C755;color:white}
/* Time slots */
.slot-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;padding:0 16px 16px}
.slot{background:${C.surface};border:1px solid ${C.border};border-radius:10px;padding:10px 4px;text-align:center;cursor:pointer;font-size:12px;color:${C.text};transition:all .15s;line-height:1.3}
.slot.busy{opacity:.4;cursor:not-allowed;text-decoration:line-through}
.slot.sel{background:${C.purple}33;border-color:${C.purple};color:${C.purpleLight};font-weight:600}
.slot:not(.busy):not(.sel):hover{border-color:${C.gold}55}
.day-strip{display:flex;gap:8px;padding:0 16px 12px;overflow-x:auto;scrollbar-width:none}
.day-strip::-webkit-scrollbar{display:none}
.day-btn{min-width:50px;background:${C.surface};border:1px solid ${C.border};border-radius:10px;padding:8px 5px;text-align:center;cursor:pointer;flex-shrink:0;transition:all .15s}
.day-btn.sel{background:${C.purple}33;border-color:${C.purple}}
.dname{font-size:10px;color:${C.muted}}
.dnum{font-size:17px;font-weight:600;color:${C.text}}
.day-btn.sel .dname,.day-btn.sel .dnum{color:${C.purpleLight}}
/* Badges */
.badge{display:inline-flex;align-items:center;border-radius:20px;font-size:11px;padding:3px 10px;font-weight:500}
.b-orange{background:${C.orange}22;color:${C.orange};border:1px solid ${C.orange}44}
.b-green{background:${C.green}22;color:${C.green};border:1px solid ${C.green}44}
.b-red{background:${C.red}22;color:${C.red};border:1px solid ${C.red}44}
.b-purple{background:${C.purple}22;color:${C.purpleLight};border:1px solid ${C.purple}44}
.b-gold{background:${C.gold}22;color:${C.goldLight};border:1px solid ${C.gold}44}
/* Booking rows */
.brow{margin:0 16px 10px;padding:14px;background:${C.card};border:1px solid ${C.border};border-radius:14px;cursor:pointer;transition:border-color .15s}
.brow:active{border-color:${C.purple}}
.brow-hd{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:9px}
.brow-name{font-size:14px;font-weight:600;color:${C.text}}
.brow-detail{font-size:12px;color:${C.muted};line-height:1.7}
/* Stats */
.stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:0 16px 14px}
.stat-card{background:${C.card};border:1px solid ${C.border};border-radius:12px;padding:14px}
.stat-num{font-size:24px;font-weight:700;color:${C.goldLight};font-family:'Cinzel',serif}
.stat-lbl{font-size:11px;color:${C.muted};margin-top:3px}
/* Tabs */
.tab-bar{display:flex;gap:6px;padding:0 16px 6px;overflow-x:auto;scrollbar-width:none}
.tab-bar::-webkit-scrollbar{display:none}
.tab{padding:7px 13px;border-radius:20px;font-size:12px;cursor:pointer;white-space:nowrap;border:1px solid ${C.border};color:${C.muted};background:transparent;font-family:'Sarabun',sans-serif;transition:all .15s}
.tab.on{background:${C.purple}33;border-color:${C.purple};color:${C.purpleLight};font-weight:600}
/* Slip */
.slip-box{border:2px dashed ${C.border};border-radius:12px;padding:22px;text-align:center;cursor:pointer;transition:border-color .2s}
.slip-box:hover{border-color:${C.gold}55}
.slip-done{background:${C.surface};border:1px solid ${C.green}44;border-radius:12px;padding:16px;text-align:center}
/* Info rows */
.irow{display:flex;justify-content:space-between;padding:9px 0;border-bottom:1px solid ${C.border}22;font-size:13px}
.irow .lbl{color:${C.muted}}.irow .val{color:${C.text};font-weight:500}
/* Toggle */
.tog-wrap{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid ${C.border}}
.tog{width:44px;height:24px;background:${C.dim};border-radius:12px;position:relative;cursor:pointer;transition:background .2s;flex-shrink:0}
.tog.on{background:${C.green}}
.tog::after{content:'';position:absolute;width:18px;height:18px;background:white;border-radius:50%;top:3px;left:3px;transition:transform .2s}
.tog.on::after{transform:translateX(20px)}
/* Service cards */
.svc-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:0 16px 16px}
.svc-card{background:${C.card};border:1px solid ${C.border};border-radius:14px;padding:16px 12px;cursor:pointer;transition:all .15s;text-align:center}
.svc-card:active{transform:scale(.97)}
.svc-card:hover{border-color:${C.gold}55}
.svc-ico{font-size:26px;margin-bottom:8px}
.svc-name{font-size:13px;font-weight:600;color:${C.text};margin-bottom:3px}
.svc-price{font-size:12px;color:${C.goldLight}}
.svc-desc{font-size:11px;color:${C.muted};margin-top:4px;line-height:1.4}
/* Hero */
.hero{padding:28px 20px 20px;text-align:center}
.hero-icon{font-size:48px;margin-bottom:12px}
.hero h1{font-size:28px;color:${C.goldLight};letter-spacing:.04em;margin-bottom:5px}
.hero p{font-size:13px;color:${C.muted}}
/* Login */
.login-wrap{display:flex;flex-direction:column;justify-content:center;align-items:center;min-height:100vh;padding:32px 24px;text-align:center}
/* Points card */
.pts-card{margin:0 16px 14px;padding:16px;background:linear-gradient(135deg,${C.goldDim},${C.card});border:1px solid ${C.gold}44;border-radius:14px}
/* Empty state */
.empty{text-align:center;padding:48px 24px;color:${C.muted};font-size:14px}
.empty .e-ico{font-size:40px;margin-bottom:12px}
/* Admin nav */
.admin-sidebar{background:${C.surface};border-right:1px solid ${C.border};min-height:100vh;width:100%;padding:16px 0}
.admin-menu-item{display:flex;align-items:center;gap:10px;padding:12px 20px;cursor:pointer;font-size:14px;color:${C.muted};transition:all .15s;border:none;background:none;width:100%;text-align:left;font-family:'Sarabun',sans-serif}
.admin-menu-item.on{background:${C.purple}22;color:${C.purpleLight};border-left:3px solid ${C.purple}}
.admin-menu-item:hover{background:${C.card};color:${C.text}}
.admin-content{flex:1;overflow-y:auto;padding-bottom:20px}
/* Notification */
.notif{background:${C.green}11;border:1px solid ${C.green}33;border-radius:10px;padding:12px 14px;margin:0 16px 12px;font-size:13px;color:${C.green}}
/* App mode pills - hidden for end users, used for demo */
.demo-bar{background:rgba(17,13,38,.98);border-bottom:1px solid ${C.border};padding:6px 12px;display:flex;gap:6px;align-items:center;font-size:11px;color:${C.muted}}
.demo-pill{padding:4px 12px;border-radius:20px;border:1px solid ${C.border};background:transparent;color:${C.muted};cursor:pointer;font-family:'Sarabun',sans-serif;font-size:11px;transition:all .15s}
.demo-pill.on{background:${C.purple}33;border-color:${C.purple};color:${C.purpleLight}}
.ans-box{background:${C.surface};border:1px solid ${C.border};border-radius:12px;padding:14px;margin-bottom:14px}
.q-box{font-size:13px;color:${C.purpleLight};background:${C.purple}11;border-radius:8px;padding:10px;margin-bottom:10px;line-height:1.6}

/* Live booking / seer status */
.status-line{display:flex;align-items:center;gap:6px;font-size:12px;font-weight:600}
.status-dot{width:10px;height:10px;border-radius:999px;display:inline-block}
.status-online{color:#58d68d}.status-online .status-dot{background:#58d68d;box-shadow:0 0 10px #58d68d}
.status-offline{color:#8d98aa}.status-offline .status-dot{background:#8d98aa}
.status-busy{color:#f7b731}.status-busy .status-dot{background:#f7b731;box-shadow:0 0 10px #f7b731}
.meta-row{display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-top:1px solid rgba(255,255,255,.08);font-size:12px;color:${C.muted}}
.rate-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:500;display:flex;align-items:center;justify-content:center;padding:18px}
.rate-modal{width:100%;max-width:390px;background:#f6f7fb;color:#606b7b;border-radius:22px;overflow:hidden;box-shadow:0 20px 80px rgba(0,0,0,.45)}
.rate-head{background:#7fe01b;color:white;text-align:center;padding:18px;font-size:25px;font-weight:800;position:relative}
.rate-close{position:absolute;right:16px;top:11px;border:none;background:transparent;color:white;font-size:34px;cursor:pointer}
.rate-body{padding:18px 20px 22px}
.rate-price{display:flex;align-items:center;justify-content:space-between;background:white;border-radius:16px;padding:18px;margin-bottom:14px;border:1px solid #e8e8f0}
.rate-coin{font-size:34px}.rate-main{font-size:26px;font-weight:800;color:#5b6575}.rate-sub{font-size:13px;color:#a5aebd;margin-top:2px}
.chat-box{background:${C.surface};border:1px solid ${C.border};border-radius:14px;padding:14px;margin:0 16px 14px}
.chat-bubble{max-width:86%;padding:10px 12px;border-radius:14px;margin-bottom:10px;font-size:13px;line-height:1.6}
.chat-left{background:${C.card};color:${C.text};border-bottom-left-radius:4px}.chat-right{background:${C.purple};color:white;margin-left:auto;border-bottom-right-radius:4px}
.warn-box{background:${C.gold}18;border:1px solid ${C.gold}44;border-radius:12px;padding:12px;color:${C.goldLight};font-size:12px;line-height:1.7}
`;

// ─── Mock Data ────────────────────────────────────────────────────────────────
const SEERS = [
  {id:1,emoji:"🔮",name:"อ.มณีรัตน์",spec:"ไพ่ทาโรต์ • โหราศาสตร์",bio:"ดูไพ่มากกว่า 3,000 ครั้ง เน้นความตรงและนำไปปฏิบัติได้จริง",rating:4.9,reviews:312,status:"online",lastOnline:"กำลังออนไลน์",avgReply:"ภายใน 3 นาที",answered:2960,latestViews:4132,chatCount:138},
  {id:2,emoji:"⭐",name:"อ.วรวิทย์",spec:"โหราศาสตร์ไทย • เลขศาสตร์",bio:"ผู้เชี่ยวชาญโหราศาสตร์ไทยโบราณกว่า 18 ปี ดวงชะตาระยะยาว",rating:4.8,reviews:201,status:"busy",lastOnline:"กำลังติดลูกค้า",avgReply:"ภายใน 10 นาที",answered:850,latestViews:1204,chatCount:74},
  {id:3,emoji:"✨",name:"อ.นลินี",spec:"ไพ่ออราเคิล • เลขฐานดวง",bio:"ผสมจิตวิทยากับจิตวิญญาณ เชี่ยวชาญความสัมพันธ์และการค้นพบตัวตน",rating:4.6,reviews:145,status:"offline",lastOnline:"3 นาทีที่แล้ว",avgReply:"ภายใน 5 นาที",answered:0,latestViews:5,chatCount:5},
  {id:4,emoji:"🌙",name:"อ.ประเสริฐ",spec:"ดูดวงจากชื่อ • หมอดูไทย",bio:"เลขศาสตร์ผสมโหราศาสตร์ดั้งเดิม วิเคราะห์ชื่อ-วันเกิดแม่นยำ",rating:4.7,reviews:98,status:"online",lastOnline:"กำลังออนไลน์",avgReply:"ภายใน 8 นาที",answered:393,latestViews:560,chatCount:24},
];

const SEER_SVCS = {
  1:[
    {id:"s1a",name:"ดูไพ่ 3 ใบ",type:"tarot",price:99,ico:"🃏",desc:"ถามได้ 1 คำถาม ตอบภายใน 24 ชม.",input:"question"},
    {id:"s1b",name:"ดูเบอร์โทร",type:"phone",price:149,ico:"📱",desc:"วิเคราะห์พลังตัวเลขจากเบอร์โทร",input:"phone"},
    {id:"s1c",name:"จองคิวดูดวงสด",type:"live",price:399,ico:"🎯",desc:"วิดีโอคอลหรือโทรหาหมอดูแบบสด",input:"booking"},
    {id:"s1d",name:"ดูดวงความรัก",type:"love",price:199,ico:"💕",desc:"ดวงความรักและความสัมพันธ์เชิงลึก",input:"question"},
  ],
  2:[
    {id:"s2a",name:"ดูเบอร์โทร",type:"phone",price:149,ico:"📱",desc:"วิเคราะห์พลังตัวเลข",input:"phone"},
    {id:"s2b",name:"จองคิวดูดวงสด",type:"live",price:499,ico:"🎯",desc:"โหราศาสตร์ไทย 1:1 ทางโทรศัพท์",input:"booking"},
    {id:"s2c",name:"ดูดวงการงาน",type:"career",price:249,ico:"💼",desc:"ดวงการงานและโอกาสเลื่อนตำแหน่ง",input:"question"},
    {id:"s2d",name:"ดูดวงการเงิน",type:"finance",price:249,ico:"💰",desc:"ทำนายดวงการเงินและการลงทุน",input:"question"},
  ],
  3:[
    {id:"s3a",name:"ดูไพ่ 3 ใบ",type:"tarot",price:89,ico:"🃏",desc:"ถามได้ 1 คำถาม ตอบภายใน 12 ชม.",input:"question"},
    {id:"s3b",name:"ดูดวงความรัก",type:"love",price:189,ico:"💕",desc:"ดูความรักผ่านไพ่ออราเคิล",input:"question"},
    {id:"s3c",name:"จองคิวดูดวงสด",type:"live",price:349,ico:"🎯",desc:"ปรึกษาสดผ่านไพ่และเลขฐานดวง",input:"booking"},
  ],
  4:[
    {id:"s4a",name:"ดูดวงจากชื่อ",type:"name",price:199,ico:"📛",desc:"วิเคราะห์ดวงชะตาจากชื่อ-นามสกุล",input:"question"},
    {id:"s4b",name:"ดูเบอร์โทร",type:"phone",price:149,ico:"📱",desc:"วิเคราะห์พลังตัวเลข",input:"phone"},
    {id:"s4c",name:"จองคิวดูดวงสด",type:"live",price:450,ico:"🎯",desc:"ดูดวงแบบ 1:1 ทางโทรศัพท์",input:"booking"},
  ],
};

const SLOTS_ALL = BOOKING_TIME_SLOTS_V92;
const BOOKED_SLOTS = {1:{"พฤ 22":["09:00-10:00","11:00-12:00"]},2:{"ศ 23":["14:00-15:00"]}};

const INIT_ORDERS = [
  {id:"O001",cname:"สมใจ วงศ์ดี",cphone:"081-234-5678",seer_id:1,seer_name:"อ.มณีรัตน์",svc_name:"ดูไพ่ 3 ใบ",svc_type:"tarot",date:"พฤ 22",time:"",price:99,status:"pending_slip",pay:"pending",slip:true,question:"ช่วงนี้ดวงการงานเป็นอย่างไร",phone_input:"",contact:"LINE",answer:"",created:"22 พ.ค. 10:30"},
  {id:"O002",cname:"ธนา รุ่งเรือง",cphone:"089-765-4321",seer_id:2,seer_name:"อ.วรวิทย์",svc_name:"จองคิวดูดวงสด",svc_type:"live",date:"ศ 23",time:"14:00-15:00",price:499,status:"approved",pay:"paid",slip:true,question:"",phone_input:"",contact:"โทรศัพท์",answer:"",created:"21 พ.ค. 14:00"},
  {id:"O003",cname:"มาลี สวัสดิ์",cphone:"062-111-2222",seer_id:1,seer_name:"อ.มณีรัตน์",svc_name:"ดูเบอร์โทร",svc_type:"phone",date:"พฤ 22",time:"",price:149,status:"approved_pending",pay:"paid",slip:true,question:"",phone_input:"081-999-8888",contact:"LINE",answer:"",created:"22 พ.ค. 09:00"},
  {id:"O004",cname:"วิชัย ประดับ",cphone:"095-333-4444",seer_id:3,seer_name:"อ.นลินี",svc_name:"ดูไพ่ 3 ใบ",svc_type:"tarot",date:"จ 21",time:"",price:89,status:"answered",pay:"paid",slip:true,question:"ความรักจะดีขึ้นไหมปลายปี",phone_input:"",contact:"LINE",answer:"ดวงความรักของคุณมีแนวโน้มดีมาก ไพ่ใบที่ 1 The Star บอกว่าความหวังที่มีไม่ไร้ผล โดยเฉพาะช่วงปลายปีจะมีโอกาสพบเจอคนพิเศษ...",created:"20 พ.ค. 16:00"},
];

const DAYS_TH = ["อา","จ","อ","พ","พฤ","ศ","ส"];
const MONTHS_TH = ["ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."];

function getDays() {
  const today = new Date();
  return Array.from({length:7},(_,i)=>{
    const d = new Date(today); d.setDate(today.getDate()+i);
    return {day:DAYS_TH[d.getDay()],date:d.getDate(),label:`${DAYS_TH[d.getDay()]} ${d.getDate()}`};
  });
}

function getSeerStatusLabel(status) {
  if (status === "online") return "ออนไลน์";
  if (status === "busy") return "กำลังดูดวง";
  return "ออฟไลน์";
}
function getSeerStatusClass(status) {
  if (status === "online") return "status-online";
  if (status === "busy") return "status-busy";
  return "status-offline";
}

function formatSeerLastSeenV136(value) {
  if (!value) return "ยังไม่เข้าใช้งาน";
  const t = Date.parse(value);
  if (!Number.isFinite(t)) return String(value || "ยังไม่เข้าใช้งาน");
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "เมื่อสักครู่";
  if (mins < 60) return `${mins} นาทีที่แล้ว`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ชั่วโมงที่แล้ว`;
  const days = Math.floor(hrs / 24);
  return `${days} วันที่แล้ว`;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function Badge({status}) {
  const m = {
    pending_slip:["b-orange","รอตรวจสลิป"],
    approved:["b-green","อนุมัติแล้ว"],
    approved_pending:["b-gold","รอหมอดูตอบ"],
    answering:["b-purple","หมอดูกำลังตอบ"],
    answered:["b-green","ตอบแล้ว"],
    rejected:["b-red","สลิปไม่ผ่าน"],
    cancelled:["b-red","ยกเลิก"],
  };
  const [cls,lbl] = m[status]||["b-purple",status];
  return <span className={`badge ${cls}`}>{lbl}</span>;
}

// ─── v34 Cosmic Background ───────────────────────────────────────────────────
function Stars() {
  const zodiac = ["♈","♉","♊","♋","♌","♍","♎","♏","♐","♑","♒","♓"];
  const stars = useMemo(() => Array.from({ length: 96 }, (_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    delay: Math.random() * 6,
    duration: 2.4 + Math.random() * 5.2,
    size: Math.random() > 0.82 ? 3.2 : (1.1 + Math.random() * 1.8),
    opacity: 0.28 + Math.random() * 0.68,
  })), []);

  const signs = useMemo(() => {
    const shuffled = [...zodiac].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 7).map((symbol, i) => ({
      id: i,
      symbol,
      top: 6 + Math.random() * 84,
      left: 3 + Math.random() * 92,
      size: 22 + Math.random() * 30,
      delay: Math.random() * 10,
      duration: 14 + Math.random() * 14,
      opacity: 0.08 + Math.random() * 0.12,
    }));
  }, []);

  const [meteors, setMeteors] = useState([]);

  useEffect(() => {
    let removeTimer;
    const spawnMeteor = () => {
      const directions = ["up-right", "up-left", "left-right", "right-left"];
      const dir = directions[Math.floor(Math.random() * directions.length)];
      const meteor = {
        id: Date.now() + Math.random(),
        className: dir,
        bottom: dir === "up-right" || dir === "up-left" ? 14 + Math.random() * 16 : undefined,
        top: dir === "left-right" || dir === "right-left" ? 22 + Math.random() * 34 : undefined,
        left: dir === "up-right" || dir === "left-right" ? -5 : undefined,
        right: dir === "up-left" || dir === "right-left" ? -5 : undefined,
      };
      setMeteors([meteor]);
      clearTimeout(removeTimer);
      removeTimer = setTimeout(() => setMeteors([]), 1900);
    };

    const firstDelay = 7000;
    const firstTimer = setTimeout(spawnMeteor, firstDelay);
    const interval = setInterval(spawnMeteor, 35000);

    return () => {
      clearTimeout(firstTimer);
      clearInterval(interval);
      clearTimeout(removeTimer);
    };
  }, []);

  const constellations = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    id: i,
    top: 7 + Math.random() * 86,
    left: 2 + Math.random() * 86,
    delay: Math.random() * 8,
    rotate: -20 + Math.random() * 40,
  })), []);

  return (
    <div className="stars" aria-hidden="true">
      {stars.map(x => (
        <div key={x.id} className="star" style={{
          top: `${x.top}%`,
          left: `${x.left}%`,
          width: x.size,
          height: x.size,
          opacity: x.opacity,
          animationDelay: `${x.delay}s`,
          animationDuration: `${x.duration}s`,
        }}/>
      ))}

      {signs.map(x => (
        <div key={x.id} className="zodiac-bg" style={{
          top: `${x.top}%`,
          left: `${x.left}%`,
          fontSize: x.size,
          opacity: x.opacity,
          animationDelay: `${x.delay}s`,
          animationDuration: `${x.duration}s`,
        }}>{x.symbol}</div>
      ))}

      {constellations.map(x => (
        <div key={x.id} className="constellation" style={{
          top: `${x.top}%`, left: `${x.left}%`, transform: `rotate(${x.rotate}deg)`, animationDelay: `${x.delay}s`
        }}>
          <span style={{left:4,top:34}}/><span style={{left:28,top:18}}/><span style={{left:54,top:26}}/><span style={{left:84,top:8}}/><i/>
        </div>
      ))}

      {meteors.map(x => (
        <div key={x.id} className={`shooting-star ${x.className}`} style={{
          left: x.left !== undefined ? `${x.left}%` : undefined,
          right: x.right !== undefined ? `${x.right}%` : undefined,
          bottom: x.bottom !== undefined ? `${x.bottom}%` : undefined,
          top: x.top !== undefined ? `${x.top}%` : undefined,
        }}/>
      ))}
    </div>
  );
}



function SeerAvatar({ seer, className = "seer-ava", style = {} }) {
  const url = seer?.photoUrl || seer?.linePictureUrl || seer?.pictureUrl || "";
  if (url) {
    return (
      <div className={className} style={{ overflow:"hidden", padding:0, ...style }}>
        <img src={url} alt={seer?.name || "หมอดู"} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
      </div>
    );
  }
  return <div className={className} style={style}>{seer?.emoji || "🔮"}</div>;
}



function imageFileToCompressedDataUrl(file, cb) {
  if (!file) return;
  if (!String(file.type || "").startsWith("image/")) {
    alert("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
    return;
  }
  const img = new Image();
  const reader = new FileReader();
  reader.onload = () => {
    img.onload = () => {
      const maxW = 1200;
      const maxH = 800;
      let { width, height } = img;
      const ratio = Math.min(maxW / width, maxH / height, 1);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      let dataUrl = canvas.toDataURL("image/jpeg", 0.72);
      // D1 ไม่เหมาะกับไฟล์ใหญ่: ลดคุณภาพอีกรอบถ้ายังใหญ่เกินประมาณ 650KB
      if (dataUrl.length > 650000) dataUrl = canvas.toDataURL("image/jpeg", 0.52);
      cb(dataUrl, file.name || "article-image.jpg");
    };
    img.onerror = () => alert("อ่านรูปภาพไม่สำเร็จ กรุณาเลือกรูปใหม่");
    img.src = String(reader.result || "");
  };
  reader.readAsDataURL(file);
}

function fileToDataUrl(file, cb) {
  if (!file) return;
  if (String(file.type || "").startsWith("image/")) {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const max = 720;
          let w = img.width || max;
          let h = img.height || max;
          const ratio = Math.min(max / w, max / h, 1);
          w = Math.max(1, Math.round(w * ratio));
          h = Math.max(1, Math.round(h * ratio));
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          cb(canvas.toDataURL("image/jpeg", 0.82));
        } catch {
          cb(String(reader.result || ""));
        }
      };
      img.onerror = () => cb(String(reader.result || ""));
      img.src = String(reader.result || "");
    };
    reader.readAsDataURL(file);
    return;
  }
  const reader = new FileReader();
  reader.onload = () => cb(reader.result || "");
  reader.readAsDataURL(file);
}

function filesToDataRecords(files, cb) {
  const list = Array.from(files || []);
  if (!list.length) return cb([]);
  Promise.all(list.map(file => new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type || "file", size: file.size || 0, url: String(reader.result || "") });
    reader.readAsDataURL(file);
  }))).then(cb).catch(() => cb([]));
}

function formatBytesV154(bytes = 0) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n >= 100 * 1024 ? 0 : 1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fileToDataUrlPromiseV154(file) {
  return new Promise((resolve) => fileToDataUrl(file, (dataUrl) => resolve(String(dataUrl || ""))));
}

function filesToDataRecordsPromiseV154(files) {
  return new Promise((resolve) => filesToDataRecords(files, (records) => resolve(Array.isArray(records) ? records : [])));
}

function makeJpegNameV154(name = "image.jpg") {
  const base = String(name || "image").replace(/\.[^.]+$/, "").trim() || "image";
  return `${base}.jpg`;
}

function dataUrlToFileV154(dataUrl, filename = "image.jpg", typeHint = "") {
  const src = String(dataUrl || "");
  if (!src.startsWith("data:")) return null;
  const parts = src.split(",");
  if (parts.length < 2) return null;
  const header = parts[0] || "";
  const mime = String(typeHint || ((header.match(/data:([^;]+)/) || [])[1]) || "application/octet-stream");
  const binary = atob(parts[1] || "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename || "file", { type: mime, lastModified: Date.now() });
}

function dataRecordToFileV154(record, fallbackName = "file") {
  if (!record || typeof record !== "object") return null;
  return dataUrlToFileV154(record.url || "", record.name || fallbackName, record.type || "");
}

function compressImageFileV154(file, { maxWidth = 1600, maxHeight = 1600, initialQuality = 0.82, minQuality = 0.52, targetBytes = 850 * 1024 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("missing file"));
    if (!String(file.type || "").startsWith("image/")) return resolve(file);
    const reader = new FileReader();
    const img = new Image();
    reader.onerror = () => reject(reader.error || new Error("อ่านรูปไม่สำเร็จ"));
    img.onerror = () => reject(new Error("โหลดรูปไม่สำเร็จ"));
    img.onload = () => {
      try {
        let width = Number(img.width || 0) || maxWidth;
        let height = Number(img.height || 0) || maxHeight;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.max(1, Math.round(width * ratio));
        height = Math.max(1, Math.round(height * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const tryBlob = (quality) => {
          canvas.toBlob((blob) => {
            if (!blob) return resolve(file);
            if ((blob.size <= targetBytes || quality <= minQuality) && blob.size > 0) {
              const out = new File([blob], makeJpegNameV154(file.name || "image"), { type: "image/jpeg", lastModified: Date.now() });
              return resolve(out);
            }
            tryBlob(Math.max(minQuality, Number((quality - 0.08).toFixed(2))));
          }, "image/jpeg", quality);
        };
        tryBlob(initialQuality);
      } catch (err) {
        reject(err);
      }
    };
    reader.onload = () => { img.src = String(reader.result || ""); };
    reader.readAsDataURL(file);
  });
}

const SEER_REGISTER_UPLOAD_CACHE_KEY_V154 = "__DW_SEER_REGISTER_UPLOAD_CACHE_V154";
function getSeerRegisterUploadCacheV154() {
  try {
    if (!window[SEER_REGISTER_UPLOAD_CACHE_KEY_V154]) window[SEER_REGISTER_UPLOAD_CACHE_KEY_V154] = { photoFile: null, docFiles: [] };
    return window[SEER_REGISTER_UPLOAD_CACHE_KEY_V154];
  } catch {
    return { photoFile: null, docFiles: [] };
  }
}
function clearSeerRegisterUploadCacheV154() {
  try { window[SEER_REGISTER_UPLOAD_CACHE_KEY_V154] = { photoFile: null, docFiles: [] }; } catch {}
}

// ─── Slip Upload ──────────────────────────────────────────────────────────────
function SlipUpload({val,onChange}) {
  if(val) return (
    <div className="slip-done">
      <div style={{fontSize:36,marginBottom:6}}>🧾</div>
      <div style={{fontSize:13,color:C.green,marginBottom:8}}>แนบสลิปแล้ว ✓</div>
      <button className="btn btn-outline btn-sm" style={{margin:"0 auto",display:"block"}} onClick={()=>onChange(null)}>เปลี่ยนสลิป</button>
    </div>
  );
  return (
    <div className="slip-box" onClick={()=>onChange("mock_slip")}>
      <div style={{fontSize:34,marginBottom:6}}>📎</div>
      <div style={{fontSize:13,color:C.muted,marginBottom:3}}>แตะเพื่อแนบสลิปโอนเงิน</div>
      <div style={{fontSize:11,color:C.dim}}>JPG, PNG</div>
    </div>
  );
}



const SEER_TERMS_CONFIG_KEY_V150 = "dw_seer_terms_config_v153";
const DEFAULT_SEER_TERMS_CONFIG_V150 = {
  title: "ข้อกำหนดสำหรับหมอดู",
  intro: "กรุณาอ่านข้อกำหนดต่อไปนี้ให้ครบถ้วน ก่อนกดยอมรับและกรอกใบสมัคร",
  body: `1. หมอดูต้องกรอกข้อมูลจริง และแนบเอกสารที่แอดมินใช้ตรวจสอบได้
2. หมอดูต้องแนบหน้าสมุดบัญชีหรือหลักฐานบัญชีรับเงิน เพื่อใช้รับค่าดูดวง
3. รายได้จากงานดูดวงในระบบ จะถูกหักค่าบริการแพลตฟอร์ม 35% ก่อนจ่ายเป็นรายได้ของหมอดู
4. หมอดูต้องให้บริการตามแพ็กเกจ ราคา และรายละเอียดที่แจ้งไว้กับลูกค้า
5. ห้ามชักชวนลูกค้าออกไปนัด/ชำระเงินนอกระบบ หากพบอาจถูกระงับบัญชี
6. แอดมินมีสิทธิ์ตรวจสอบเอกสาร โปรไฟล์ การให้บริการ และประวัติการสนทนาเพื่อป้องกันการทุจริต
7. ระบบจะแสดงหมอดูให้ลูกค้าเห็นเฉพาะหลังแอดมินอนุมัติแล้วเท่านั้น`,
  warning: "หมายเหตุ: ระบบจะหักรายได้ 35% ก่อนเป็นรายได้ของหมอดู และต้องแนบหน้าสมุดบัญชีเพื่อรับค่าดูดวง",
  priceWarning: "ระบบจะหักรายได้ 35% ก่อนเป็นรายได้ของหมอดู",
  documentWarning: "กรุณาแนบหน้าสมุดบัญชีเพื่อรับค่าดูดวง",
  commissionPercent: 35,
  buttonText: "ยอมรับข้อกำหนดและไปสมัคร",
  version: "v1.0",
};
function readSeerTermsConfigV150() {
  try {
    const raw = localStorage.getItem(SEER_TERMS_CONFIG_KEY_V150);
    const obj = raw ? JSON.parse(raw) : {};
    return {...DEFAULT_SEER_TERMS_CONFIG_V150, ...(obj && typeof obj === "object" ? obj : {})};
  } catch {
    return DEFAULT_SEER_TERMS_CONFIG_V150;
  }
}
function writeSeerTermsConfigV150(cfg) {
  const next = {...DEFAULT_SEER_TERMS_CONFIG_V150, ...(cfg || {})};
  try {
    localStorage.setItem(SEER_TERMS_CONFIG_KEY_V150, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("duangwannee:seer-terms-updated", {detail:next}));
  } catch {}
  return next;
}


// ─── Standalone Seer Register Page v9 ───────────────────────────────────────
// ใช้หน้านี้สำหรับ /seer-register โดยตรง เพื่อไม่ให้ state ของหน้าลูกค้า/แดชบอร์ดทำให้ฟอร์มเด้งหรือข้อมูลหาย
function SeerRegisterStable({go}) {
  const [submitPopup, setSubmitPopup] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("ระบบได้รับใบสมัครของคุณแล้ว โปรดรอเจ้าหน้าที่ตรวจสอบ");

  const draft = readSeerRegisterDraftStore();
  const [packageCountV143, setPackageCountV143] = useState(() => Math.min(5, Math.max(1, Array.isArray(draft.packages) ? draft.packages.length : 1)));
  const [seerTermsV150, setSeerTermsV150] = useState(() => readSeerTermsConfigV150());
  const termsKeyV152 = (cfg = seerTermsV150) => {
    const token = String(cfg.updatedAt || cfg.version || JSON.stringify({body:cfg.body,warning:cfg.warning,priceWarning:cfg.priceWarning,documentWarning:cfg.documentWarning})).slice(0,120);
    return "DW_SEER_TERMS_ACCEPTED_V152::" + token;
  };
  const [termsAcceptedV149, setTermsAcceptedV149] = useState(() => false);
  const [termsLoadedV152, setTermsLoadedV152] = useState(false);
  const [inviteCheckV152, setInviteCheckV152] = useState({ loading:true, ok:false, message:"กำลังตรวจสอบลิงก์สมัครหมอดู..." });
  const [uploadBusyV154, setUploadBusyV154] = useState(false);
  const [uploadStatusV154, setUploadStatusV154] = useState("");

  useEffect(() => {
    captureLiffProfileForRegister({ allowLogin: false });
    let alive = true;
    apiGet("/api/seer-terms").then(data => {
      if (!alive) return;
      const cfg = writeSeerTermsConfigV150(data?.terms || data || {});
      setSeerTermsV150(cfg);
      try { setTermsAcceptedV149(sessionStorage.getItem(termsKeyV152(cfg)) === "yes"); } catch { setTermsAcceptedV149(false); }
      setTermsLoadedV152(true);
    }).catch(() => {
      const cfg = readSeerTermsConfigV150();
      setSeerTermsV150(cfg);
      try { setTermsAcceptedV149(sessionStorage.getItem(termsKeyV152(cfg)) === "yes"); } catch { setTermsAcceptedV149(false); }
      setTermsLoadedV152(true);
    });
    const sync = () => {
      const cfg = readSeerTermsConfigV150();
      setSeerTermsV150(cfg);
      try { setTermsAcceptedV149(sessionStorage.getItem(termsKeyV152(cfg)) === "yes"); } catch { setTermsAcceptedV149(false); }
    };
    window.addEventListener("duangwannee:seer-terms-updated", sync);
    return () => { alive = false; window.removeEventListener("duangwannee:seer-terms-updated", sync); };
  }, []);

  useEffect(() => {
    let alive = true;
    const code = getInviteCodeV152();
    if (!code) {
      setInviteCheckV152({ loading:false, ok:false, message:"ลิงก์สมัครหมอดูไม่ถูกต้องหรือไม่มี invite code กรุณาขอลิงก์ใหม่จากแอดมิน" });
      return () => { alive = false; };
    }
    apiGet(`/api/seer-invites?code=${encodeURIComponent(code)}&validate=1`).then(data => {
      if (!alive) return;
      const invite = data?.invite || {};
      const status = String(invite.status || "").toLowerCase();
      const ok = data?.ok === true && (data?.valid === true || status === "unused");
      setInviteCheckV152({
        loading:false,
        ok,
        message: ok ? "" : (data?.message || "ลิงก์สมัครหมอดูนี้ใช้ไม่ได้หรือถูกใช้ไปแล้ว กรุณาขอลิงก์ใหม่จากแอดมิน")
      });
    }).catch(err => {
      if (!alive) return;
      setInviteCheckV152({ loading:false, ok:false, message:"ตรวจสอบลิงก์สมัครไม่สำเร็จ กรุณาขอลิงก์ใหม่จากแอดมิน หรือแจ้งแอดมินตรวจสอบ: " + (err.message || err) });
    });
    return () => { alive = false; };
  }, []);

  const getInviteCodeV152 = () => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("invite")) return p.get("invite") || "";
      const sp = getLiffStateParamsV142B?.();
      return sp?.get("invite") || "";
    } catch { return ""; }
  };

  const getValue = (id) => {
    const el = document.getElementById(id);
    return (el?.value || "").trim();
  };

  const getServices = () => Array.from(document.querySelectorAll("[data-seer-service-v10][data-selected='yes']"))
    .map((el) => el.getAttribute("data-seer-service-v10"))
    .filter(Boolean);

  const getPackagesV143 = () => {
    const items = [];
    for (let i = 0; i < 5; i++) {
      const name = getValue(`seerRegV143PkgName${i}`);
      const price = getValue(`seerRegV143PkgPrice${i}`);
      const detail = getValue(`seerRegV143PkgDetail${i}`);
      if (name || price || detail) {
        items.push({
          name: name || `แพ็กเกจที่ ${i + 1}`,
          price,
          detail,
        });
      }
    }
    return items.slice(0, 5);
  };

  const getPackageTextV143 = (packages = []) => {
    const lines = (Array.isArray(packages) ? packages : [])
      .filter(p => p && (p.name || p.price || p.detail))
      .slice(0, 5)
      .map((p, i) => `${i + 1}. ${p.name || "แพ็กเกจ"} - ราคา ${p.price || "-"} บาท${p.detail ? `\\n   รายละเอียด: ${p.detail}` : ""}`);
    return lines.join("\\n");
  };

  const uploadSeerRegisterFilesV148 = async (applicationId) => {
    const cache = getSeerRegisterUploadCacheV154();
    const fallbackPhotoUrl = document.getElementById("seerRegV10PhotoUrl")?.value || "";
    const fallbackPhotoName = document.getElementById("seerRegV10PhotoName")?.value || "";
    const hiddenDocs = getDocs();
    const inputPhotoFile = document.getElementById("seerRegV10Photo")?.files?.[0] || null;
    const inputDocFiles = Array.from(document.getElementById("seerRegV10Docs")?.files || []);
    const photoFile = cache.photoFile || inputPhotoFile || dataUrlToFileV154(fallbackPhotoUrl, fallbackPhotoName || "seer-photo.jpg", "image/jpeg") || null;
    const docFiles = (cache.docFiles && cache.docFiles.length ? cache.docFiles : inputDocFiles.length ? inputDocFiles : hiddenDocs.map((doc, i) => dataRecordToFileV154(doc, doc?.name || `document-${i + 1}`)).filter(Boolean));

    let photo = {
      url: fallbackPhotoUrl,
      name: fallbackPhotoName,
      uploaded: false,
    };

    const documents = [];

    if (photoFile) {
      setUploadStatusV154(`กำลังอัปโหลดรูปโปรไฟล์ (${formatBytesV154(photoFile.size)}) ...`);
      const up = await uploadFileToR2(photoFile, { ownerType:"seer_application", ownerId:applicationId, fileRole:"profile_photo" });
      photo = {
        url: up.public_url || up.url || "",
        name: up.file_name || photoFile.name || "ภาพถ่ายหมอดู",
        uploaded: true,
      };
    }

    for (let i = 0; i < docFiles.length; i++) {
      const file = docFiles[i];
      setUploadStatusV154(`กำลังอัปโหลดเอกสาร ${i + 1}/${docFiles.length} : ${file.name || `เอกสาร ${i + 1}`}`);
      const up = await uploadFileToR2(file, { ownerType:"seer_application", ownerId:applicationId, fileRole:`document_${i + 1}` });
      documents.push({
        name: up.file_name || file.name || `เอกสาร ${i + 1}`,
        url: up.public_url || up.url || "",
        content_type: up.content_type || file.type || "",
        uploaded: true,
      });
    }

    return { photo, documents };
  };

  const getDocs = () => {
    const hiddenJson = document.getElementById("seerRegV10DocsJson");
    const parsed = parseJsonMaybe(hiddenJson?.value || "", []);
    if (Array.isArray(parsed) && parsed.length) return parsed;
    const hidden = document.getElementById("seerRegV10DocsText");
    return (hidden?.value || "").split("|").map(x=>x.trim()).filter(Boolean).map(name=>({name,url:""}));
  };

  const collectDraft = () => ({
    name: getValue("seerRegV10Name"),
    spec: getValue("seerRegV10Spec"),
    price: getValue("seerRegV10Price"),
    bank: getValue("seerRegV10Bank"),
    contactPhone: getValue("seerRegV10ContactPhone"),
    lineId: getValue("seerRegV10LineId"),
    lineUrl: getValue("seerRegV10LineUrl"),
    bio: getValue("seerRegV10Bio"),
    packages: getPackagesV143(),
    documents: getDocs(),
    photoUrl: document.getElementById("seerRegV10PhotoUrl")?.value || "",
    photoName: document.getElementById("seerRegV10PhotoName")?.value || "",
    services: getServices(),
  });

  const saveDraft = () => writeSeerRegisterDraftStore(collectDraft());

  const clearForm = () => {
    ["seerRegV10Name", "seerRegV10Spec", "seerRegV10Price", "seerRegV10Bank", "seerRegV10ContactPhone", "seerRegV10LineId", "seerRegV10LineUrl", "seerRegV10Bio", "seerRegV10Docs", "seerRegV10DocsText", "seerRegV10Photo", "seerRegV10PhotoUrl", "seerRegV10PhotoName", "seerRegV10DocsJson"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.value = "";
    });
    const photoPreview = document.getElementById("seerRegV10PhotoPreview");
    if (photoPreview) { photoPreview.src = ""; photoPreview.style.display = "none"; }
    const photoLabel = document.getElementById("seerRegV10PhotoLabel");
    if (photoLabel) photoLabel.textContent = "ยังไม่ได้แนบภาพหมอดู";
    const docsLabel = document.getElementById("seerRegV10DocsLabel");
    if (docsLabel) docsLabel.textContent = `${seerTermsV150.documentWarning || DEFAULT_SEER_TERMS_CONFIG_V150.documentWarning} หรือเอกสารยืนยันตัวตน`;
    document.querySelectorAll("[data-seer-service-v10]").forEach((el) => {
      el.setAttribute("data-selected", "no");
      el.classList.remove("btn-purple");
      el.classList.add("btn-outline");
    });
    for (let i = 0; i < 5; i++) {
      ["Name","Price","Detail"].forEach(s => {
        const el = document.getElementById(`seerRegV143Pkg${s}${i}`);
        if (el) el.value = "";
      });
    }
    clearSeerRegisterUploadCacheV154();
    setUploadBusyV154(false);
    setUploadStatusV154("");
    setPackageCountV143(1);
    clearSeerRegisterDraftStore();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (uploadBusyV154) {
      alert("ระบบกำลังเตรียมไฟล์/อัปโหลดรูปอยู่ กรุณารอสักครู่แล้วกดส่งอีกครั้ง");
      return;
    }

    if (!termsAcceptedV149) {
      alert("กรุณาอ่านและกดยอมรับข้อกำหนดสำหรับหมอดูก่อนสมัคร");
      return;
    }

    saveDraft();
    const f = collectDraft();

    if (!f.name) { alert("กรุณากรอกชื่อหมอดู"); return; }
    if (!f.spec) { alert("กรุณากรอกศาสตร์ที่ถนัด"); return; }
    if (!f.price) { alert("กรุณากรอกราคาเริ่มต้น"); return; }
    if (!f.bank) { alert("กรุณากรอกบัญชีรับเงิน"); return; }
    if (!f.contactPhone) { alert("กรุณากรอกเบอร์ติดต่อสำหรับแอดมิน"); return; }
    if (!f.lineId && !f.lineUrl) { alert("กรุณากรอก LINE ID หรือ LINE URL สำหรับใช้โทรหลังลูกค้าชำระเงิน"); return; }

    if (!f.photoUrl) { alert("กรุณาแนบภาพถ่ายของหมอดู 1 ภาพ"); return; }
    if (!f.documents || f.documents.length === 0) { alert("กรุณาแนบเอกสารหลักฐาน เช่น หน้าสมุดบัญชี หรือเอกสารยืนยันตัวตน"); return; }
    if (!f.services || f.services.length === 0) { alert("กรุณาเลือกประเภทบริการที่รับดูอย่างน้อย 1 รายการ"); return; }
    const validPackagesV143 = (f.packages || []).filter(p => p && String(p.name || "").trim() && String(p.price || "").trim());
    if (!validPackagesV143.length) { alert("กรุณาเพิ่มรายละเอียดบริการ/แพ็กเกจอย่างน้อย 1 รายการ พร้อมราคา"); return; }
    if (validPackagesV143.length > 5) { alert("เพิ่มรายละเอียดบริการได้สูงสุด 5 รายการ"); return; }

    const lineUser = await captureLiffProfileForRegister({ allowLogin: true });
    if (!lineUser?.userId) {
      alert("ยังไม่ได้เชื่อม LINE จาก LIFF กรุณากดลิงก์สมัครใหม่ใน LINE แล้วอนุญาตแอปก่อนส่งใบสมัคร");
      return;
    }
    const inviteCode = getInviteCodeV152();

    if (!inviteCode || !inviteCheckV152.ok) {
      alert(inviteCheckV152.message || "ลิงก์สมัครหมอดูนี้ใช้ไม่ได้หรือถูกใช้ไปแล้ว กรุณาขอลิงก์ใหม่จากแอดมิน");
      return;
    }

    const applicationIdV148 = "REQ" + Date.now();
    let uploadedFilesV148 = { photo:{url:f.photoUrl || "", name:f.photoName || "", uploaded:false}, documents:(f.documents || []).map(d => typeof d === "string" ? {name:d,url:""} : d) };
    setUploadBusyV154(true);
    setUploadStatusV154("กำลังอัปโหลดรูปและเอกสาร...");
    try {
      uploadedFilesV148 = await uploadSeerRegisterFilesV148(applicationIdV148);
    } catch (err) {
      console.warn("อัปโหลดรูป/เอกสารเข้า R2 ไม่สำเร็จ", err);
      setUploadBusyV154(false);
      setUploadStatusV154("อัปโหลดรูป/เอกสารไม่สำเร็จ");
      alert("อัปโหลดรูป/เอกสารไม่สำเร็จ: " + (err.message || err) + "\nกรุณาลองใหม่ หรือส่งรูป error ให้แอดมินตรวจสอบ");
      return;
    }

    const applicant = {
      id: applicationIdV148,
      inviteCode,
      sourceInviteCode: inviteCode,
      name: f.name,
      spec: f.spec,
      phone: lineUser?.displayName || "LINE: รอผูกบัญชี",
      contactPhone: f.contactPhone || "",
      adminContactPhone: f.contactPhone || "",
      lineId: f.lineId || "",
      lineUrl: f.lineUrl || "",
      docs: `ราคาเริ่มต้น: ${f.price || "-"} / บัญชีรับเงิน: ${f.bank || "-"}`,
      bio: f.bio || "",
      packages: validPackagesV143,
      documents: uploadedFilesV148.documents && uploadedFilesV148.documents.length ? uploadedFilesV148.documents : (f.documents || []).map(d => typeof d === "string" ? {name:d,url:""} : d),
      photoUrl: uploadedFilesV148.photo?.url || f.photoUrl || "",
      photoName: uploadedFilesV148.photo?.name || f.photoName || "",
      profileLocked: false,
      services: f.services || [],
      status: "รอตรวจสอบ",
      lineUserId: lineUser?.userId || "",
      lineDisplayName: lineUser?.displayName || "",
      linePictureUrl: lineUser?.pictureUrl || "",
      createdAt: new Date().toLocaleString("th-TH"),
    };

    try {
      setUploadStatusV154("กำลังบันทึกใบสมัครและแจ้งแอดมิน...");
      const res = await apiPost("/api/seer-applications", {
        action: "submit",
        application_id: applicant.id,
        invite_code: inviteCode,
        line_user_id: applicant.lineUserId,
        line_display_name: applicant.lineDisplayName,
        line_picture_url: applicant.linePictureUrl,
        display_name: applicant.name,
        profile_image_url: applicant.photoUrl || safePhotoForD1(applicant.photoUrl),
        specialties: applicant.spec,
        short_bio: applicant.bio,
        start_price: Math.min(...validPackagesV143.map(p => parseMoneyNumber(p.price, 0)).filter(n => n > 0), parseMoneyNumber(f.price, 0) || 999),
        price_text: getPackageTextV143(validPackagesV143) || `เริ่มต้น ${f.price || "-"} บาท`,
        bank_account_number: f.bank,
        phone: applicant.phone,
        contact_phone: applicant.contactPhone,
        line_id: applicant.lineId,
        line_url: applicant.lineUrl,
        terms_version: "v1.0",
        terms_accepted_at: new Date().toISOString(),
        admin_note: JSON.stringify({
          services: f.services || [],
          packages: validPackagesV143,
          packageText: getPackageTextV143(validPackagesV143),
          contactPhone: f.contactPhone || "",
          contact_phone: f.contactPhone || "",
          lineId: f.lineId || "",
          lineUrl: f.lineUrl || "",
          seerTermsAccepted: true,
          platformCommissionPercent: 35,
          photo: { name: applicant.photoName || "ภาพถ่ายหมอดู", url: applicant.photoUrl || "", uploaded: !!applicant.photoUrl },
          documents: applicant.documents || [],
          rawNote: `เบอร์ติดต่อแอดมิน: ${f.contactPhone || "-"} / บริการ: ${(f.services || []).join(", ")} / รายละเอียดบริการ: ${getPackageTextV143(validPackagesV143).replace(/\\n/g, " | ")} / เอกสาร: ${(f.documents || []).map(d=>d.name||d).join(" / ")} / ภาพหมอ: ${f.photoName || "แนบแล้ว"}`
        }),
      });
      applicant.id = res.application_id || applicant.id;
      setSubmitMessage("ระบบได้รับใบสมัครของคุณแล้ว และแจ้งแอดมินให้ตรวจสอบแล้ว โปรดรอเจ้าหน้าที่อนุมัติ");
    } catch (err) {
      console.warn("บันทึกใบสมัครเข้า D1 ไม่สำเร็จ จะเก็บสำรองในเครื่อง", err);
      setUploadBusyV154(false);
      setUploadStatusV154("บันทึกใบสมัครเข้า D1 ไม่สำเร็จ");
      setSubmitMessage("ส่งใบสมัครเข้า D1 ไม่สำเร็จ กรุณาส่งรูป error ให้แอดมิน/ChatGPT ตรวจสอบ: " + (err.message || err));
      setSubmitPopup(true);
      return;
    }

    try {
      localStorage.setItem("duangwannee_logged_in", "yes");
      localStorage.setItem("DW_SEER_REGISTER_LAST_APPLICATION_ID", applicant.id);
      localStorage.setItem("DW_SEER_REGISTER_LAST_LINE_USER_ID", applicant.lineUserId || "");
    } catch {}
    const nextApplicants = [applicant, ...readSeerApplicantsStore()];
    writeSeerApplicantsStore(nextApplicants);
    setUploadBusyV154(false);
    setUploadStatusV154("ส่งใบสมัครสำเร็จ ✓");
    setSubmitPopup(true);
    clearForm();
  };

  const serviceRows = [["tarot","ไพ่ทาโรต์"],["phone","ดูเบอร์โทร"],["live","จองคิวสด"],["love","ดูดวงความรัก"],["career","ดูดวงการงาน"],["finance","ดูดวงการเงิน"]];

  if (inviteCheckV152.loading) {
    return (
      <div className="root z1">
        <style>{css}</style><Stars/>
        <div className="scr on">
          <div className="topbar"><span className="topbar-title">ตรวจสอบลิงก์สมัคร</span></div>
          <div className="content z1" style={{padding:"24px 16px"}}><div className="card" style={{padding:18,textAlign:"center"}}>{inviteCheckV152.message}</div></div>
        </div>
      </div>
    );
  }

  if (!inviteCheckV152.ok) {
    return (
      <div className="root z1">
        <style>{css}</style><Stars/>
        <div className="scr on">
          <div className="topbar"><button type="button" className="back-btn" onClick={()=>window.location.href="/"}>←</button><span className="topbar-title">ลิงก์ใช้ไม่ได้</span></div>
          <div className="content z1" style={{padding:"24px 16px"}}>
            <div className="card card-gold" style={{padding:18,textAlign:"center"}}>
              <div style={{fontSize:42,marginBottom:8}}>⛔</div>
              <div style={{fontSize:18,fontWeight:900,color:C.goldLight,marginBottom:8}}>ลิงก์สมัครหมอดูนี้ใช้ไม่ได้</div>
              <div style={{fontSize:13,color:C.text,lineHeight:1.8}}>{inviteCheckV152.message}</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:12}}>กรุณาขอลิงก์สมัครใหม่จากแอดมิน</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!termsLoadedV152) {
    return (
      <div className="root z1">
        <style>{css}</style><Stars/>
        <div className="scr on">
          <div className="topbar"><span className="topbar-title">กำลังโหลดข้อกำหนด</span></div>
          <div className="content z1" style={{padding:"24px 16px"}}><div className="card" style={{padding:18,textAlign:"center"}}>กำลังโหลดข้อกำหนดล่าสุด...</div></div>
        </div>
      </div>
    );
  }

  if (!termsAcceptedV149) {
    return (
      <div className="root z1">
        <style>{css}</style>
        <Stars/>
        <div className="scr on">
          <div className="topbar">
            <button type="button" className="back-btn" onClick={()=>go ? go("home") : (window.location.href = "/")}>←</button>
            <span className="topbar-title">{seerTermsV150.title || "ข้อกำหนดสำหรับหมอดู"}</span>
          </div>
          <div className="content z1" style={{padding:"16px",paddingBottom:90}}>
            <div className="card card-gold" style={{padding:16,marginBottom:14}}>
              <div style={{fontSize:20,fontWeight:900,color:C.goldLight,marginBottom:8}}>ก่อนสมัครเป็นหมอดู</div>
              <div style={{fontSize:13,color:C.text,lineHeight:1.9}}>
                {seerTermsV150.intro || DEFAULT_SEER_TERMS_CONFIG_V150.intro}
              </div>
            </div>

            <div className="card" style={{padding:16}}>
              <div style={{fontSize:15,fontWeight:900,color:C.goldLight,marginBottom:10}}>ข้อกำหนดการเป็นหมอดูในระบบดวงวันนี้</div>
              <div style={{fontSize:13,color:C.text,lineHeight:1.9,whiteSpace:"pre-line"}}>
                {seerTermsV150.body || DEFAULT_SEER_TERMS_CONFIG_V150.body}
              </div>
              <div style={{fontSize:13,color:"#ff5b5b",fontWeight:900,lineHeight:1.8,marginTop:14,whiteSpace:"pre-line"}}>
                {seerTermsV150.warning || DEFAULT_SEER_TERMS_CONFIG_V150.warning}
              </div>
              <button
                type="button"
                className="btn btn-gold"
                style={{marginTop:16}}
                onClick={()=>{
                  try { sessionStorage.setItem(termsKeyV152(seerTermsV150), "yes"); } catch {}
                  setTermsAcceptedV149(true);
                }}
              >
                {seerTermsV150.buttonText || DEFAULT_SEER_TERMS_CONFIG_V150.buttonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="root z1">
      <style>{css}</style>
      <Stars/>
      <div className="scr on">
        <div className="topbar">
          <button type="button" className="back-btn" onClick={()=>go ? go("home") : (window.location.href = "/")}>←</button>
          <span className="topbar-title">สมัครเป็นหมอดู</span>
        </div>
        <div className="content z1">
          <div style={{padding:"16px 16px 0",fontSize:13,color:C.muted,lineHeight:1.7}}>
            กรอกข้อมูลเพื่อสมัครเป็นหมอดูในระบบ<br/>ทีมงานจะตรวจสอบและแจ้งผลภายใน 1-3 วัน
          </div>
          <div className="fpad">
            <div className="fgroup">
              <label className="flabel">ชื่อหมอดู / ชื่อที่ใช้แสดง *</label>
              <input id="seerRegV10Name" className="finput" type="text" placeholder="เช่น อ.มณีรัตน์" defaultValue={draft.name || ""} onBlur={saveDraft} autoComplete="off" />
            </div>
            <div className="fgroup">
              <label className="flabel">ศาสตร์ที่ถนัด *</label>
              <input id="seerRegV10Spec" className="finput" type="text" placeholder="เช่น ไพ่ทาโรต์, โหราศาสตร์" defaultValue={draft.spec || ""} onBlur={saveDraft} autoComplete="off" />
            </div>
            <div className="fgroup">
              <label className="flabel">ราคาเริ่มต้น *</label>
              <input id="seerRegV10Price" className="finput" type="text" placeholder="เช่น 99 บาท" defaultValue={draft.price || ""} onBlur={saveDraft} autoComplete="off" />
              <div style={{fontSize:12,color:"#ff5b5b",fontWeight:900,lineHeight:1.6,marginTop:6}}>
                {seerTermsV150.priceWarning || DEFAULT_SEER_TERMS_CONFIG_V150.priceWarning}
              </div>
            </div>
            <div className="fgroup">
              <label className="flabel">บัญชีรับเงิน *</label>
              <input id="seerRegV10Bank" className="finput" type="text" placeholder="เลขบัญชี / PromptPay" defaultValue={draft.bank || ""} onBlur={saveDraft} autoComplete="off" />
            </div>
            <div className="fgroup">
              <label className="flabel">เบอร์ติดต่อสำหรับแอดมิน *</label>
              <input id="seerRegV10ContactPhone" className="finput" type="tel" placeholder="เช่น 0812345678" defaultValue={draft.contactPhone || ""} onBlur={saveDraft} autoComplete="tel" />
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:6}}>เบอร์นี้ใช้เฉพาะแอดมินติดต่อหมอดู ลูกค้าจะไม่เห็น</div>
            </div>
            <div className="fgroup">
              <label className="flabel">LINE ID สำหรับโทรหลังลูกค้าชำระเงิน *</label>
              <input id="seerRegV10LineId" className="finput" type="text" placeholder="เช่น lineid หรือ @lineid" defaultValue={draft.lineId || ""} onBlur={saveDraft} autoComplete="off" />
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:6}}>จะไม่โชว์ให้ลูกค้าเห็นจนกว่าลูกค้าจะชำระเงินและเข้าห้องดูดวง</div>
            </div>
            <div className="fgroup">
              <label className="flabel">LINE URL ถ้ามี</label>
              <input id="seerRegV10LineUrl" className="finput" type="url" placeholder="https://line.me/ti/p/..." defaultValue={draft.lineUrl || ""} onBlur={saveDraft} autoComplete="off" />
            </div>
            <div className="fgroup">
              <label className="flabel">ภาพถ่ายหมอดู 1 ภาพ *</label>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,margin:"4px 0 8px"}}>ระบบจะบีบอัดรูปก่อนอัปโหลด เพื่อลดเวลารอและใช้เน็ตน้อยลง</div>
              <input id="seerRegV10Photo" className="finput" type="file" accept="image/*" onChange={async (e)=>{
                const file = e.currentTarget.files?.[0];
                const cache = getSeerRegisterUploadCacheV154();
                if (!file) {
                  cache.photoFile = null;
                  const hidden = document.getElementById("seerRegV10PhotoUrl");
                  const nameHidden = document.getElementById("seerRegV10PhotoName");
                  const img = document.getElementById("seerRegV10PhotoPreview");
                  const label = document.getElementById("seerRegV10PhotoLabel");
                  if (hidden) hidden.value = "";
                  if (nameHidden) nameHidden.value = "";
                  if (img) { img.src = ""; img.style.display = "none"; }
                  if (label) label.textContent = "ยังไม่ได้แนบภาพหมอดู";
                  saveDraft();
                  return;
                }
                setUploadBusyV154(true);
                setUploadStatusV154(`กำลังบีบอัดรูปโปรไฟล์: ${file.name}`);
                try {
                  const compressed = await compressImageFileV154(file, { maxWidth: 1280, maxHeight: 1280, initialQuality: 0.82, minQuality: 0.5, targetBytes: 650 * 1024 });
                  cache.photoFile = compressed;
                  const dataUrl = await fileToDataUrlPromiseV154(compressed);
                  const hidden = document.getElementById("seerRegV10PhotoUrl");
                  const nameHidden = document.getElementById("seerRegV10PhotoName");
                  const img = document.getElementById("seerRegV10PhotoPreview");
                  const label = document.getElementById("seerRegV10PhotoLabel");
                  if (hidden) hidden.value = String(dataUrl);
                  if (nameHidden) nameHidden.value = compressed.name || file.name;
                  if (img) { img.src = String(dataUrl); img.style.display = "block"; }
                  if (label) label.textContent = `${compressed.name || file.name} (${formatBytesV154(file.size)} → ${formatBytesV154(compressed.size)})`;
                  setUploadStatusV154(`เตรียมรูปโปรไฟล์แล้ว ${formatBytesV154(file.size)} → ${formatBytesV154(compressed.size)}`);
                  saveDraft();
                } catch (err) {
                  console.warn("compress seer photo failed", err);
                  setUploadStatusV154("เตรียมรูปโปรไฟล์ไม่สำเร็จ");
                  alert("เตรียมรูปโปรไฟล์ไม่สำเร็จ: " + (err.message || err));
                } finally {
                  setUploadBusyV154(false);
                }
              }} />
              <input id="seerRegV10PhotoUrl" type="hidden" defaultValue={draft.photoUrl || ""} />
              <input id="seerRegV10PhotoName" type="hidden" defaultValue={draft.photoName || ""} />
              <div style={{display:"flex",gap:12,alignItems:"center",marginTop:8}}>
                <img id="seerRegV10PhotoPreview" src={draft.photoUrl || ""} alt="ภาพหมอดู" style={{display:draft.photoUrl?"block":"none",width:72,height:72,borderRadius:"50%",objectFit:"cover",border:`2px solid ${C.gold}66`}} />
                <div id="seerRegV10PhotoLabel" style={{fontSize:11,color:C.goldLight,lineHeight:1.6}}>{draft.photoName || "ยังไม่ได้แนบภาพหมอดู"}</div>
              </div>
            </div>
            <div className="fgroup">
              <label className="flabel">แนบเอกสารหลักฐาน *</label>
              <div style={{fontSize:12,color:"#ff5b5b",fontWeight:900,lineHeight:1.6,margin:"4px 0 8px"}}>
                {seerTermsV150.documentWarning || DEFAULT_SEER_TERMS_CONFIG_V150.documentWarning}
              </div>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,margin:"0 0 8px"}}>ไฟล์รูปจะถูกบีบอัดอัตโนมัติก่อนอัปโหลด ส่วน PDF จะส่งตามไฟล์เดิม</div>
              <input id="seerRegV10Docs" className="finput" type="file" multiple accept="image/*,.pdf" onChange={async (e)=>{
                const files = Array.from(e.currentTarget.files || []);
                const cache = getSeerRegisterUploadCacheV154();
                if (!files.length) {
                  cache.docFiles = [];
                  const hiddenJson = document.getElementById("seerRegV10DocsJson");
                  const hidden = document.getElementById("seerRegV10DocsText");
                  const label = document.getElementById("seerRegV10DocsLabel");
                  if (hiddenJson) hiddenJson.value = "[]";
                  if (hidden) hidden.value = "";
                  if (label) label.textContent = "ยังไม่ได้แนบเอกสาร";
                  saveDraft();
                  return;
                }
                setUploadBusyV154(true);
                setUploadStatusV154(`กำลังเตรียมเอกสาร ${files.length} ไฟล์...`);
                try {
                  const prepared = [];
                  for (let i = 0; i < files.length; i++) {
                    const f = files[i];
                    setUploadStatusV154(`กำลังเตรียมเอกสาร ${i + 1}/${files.length}: ${f.name}`);
                    if (String(f.type || "").startsWith("image/")) {
                      prepared.push(await compressImageFileV154(f, { maxWidth: 1600, maxHeight: 1600, initialQuality: 0.82, minQuality: 0.5, targetBytes: 900 * 1024 }));
                    } else {
                      prepared.push(f);
                    }
                  }
                  cache.docFiles = prepared;
                  const records = await filesToDataRecordsPromiseV154(prepared);
                  const names = prepared.map(f=>f.name || "file");
                  const hiddenJson = document.getElementById("seerRegV10DocsJson");
                  const hidden = document.getElementById("seerRegV10DocsText");
                  if (hiddenJson) hiddenJson.value = JSON.stringify(records);
                  if (hidden) hidden.value = names.join("|");
                  const label = document.getElementById("seerRegV10DocsLabel");
                  const before = files.reduce((sum, f) => sum + Number(f.size || 0), 0);
                  const after = prepared.reduce((sum, f) => sum + Number(f.size || 0), 0);
                  if (label) label.textContent = `${names.join(" / ")} (${formatBytesV154(before)} → ${formatBytesV154(after)})`;
                  setUploadStatusV154(`เตรียมเอกสารแล้ว ${prepared.length} ไฟล์ (${formatBytesV154(before)} → ${formatBytesV154(after)})`);
                  saveDraft();
                } catch (err) {
                  console.warn("prepare seer docs failed", err);
                  setUploadStatusV154("เตรียมเอกสารไม่สำเร็จ");
                  alert("เตรียมเอกสารไม่สำเร็จ: " + (err.message || err));
                } finally {
                  setUploadBusyV154(false);
                }
              }} />
              <input id="seerRegV10DocsText" type="hidden" defaultValue={(draft.documents || []).map(d=>d.name||d).join("|")} />
              <input id="seerRegV10DocsJson" type="hidden" defaultValue={JSON.stringify(draft.documents || [])} />
              <div id="seerRegV10DocsLabel" style={{fontSize:11,color:C.goldLight,lineHeight:1.6,marginTop:6}}>{(draft.documents || []).length ? (draft.documents || []).map(d=>d.name||d).join(" / ") : `${seerTermsV150.documentWarning || DEFAULT_SEER_TERMS_CONFIG_V150.documentWarning} หรือเอกสารยืนยันตัวตน`}</div>
            </div>
            <div className="fgroup">
              <label className="flabel">คำแนะนำตัวสั้น ๆ</label>
              <textarea id="seerRegV10Bio" className="finput" placeholder="บอกเล่าประสบการณ์และความเชี่ยวชาญ..." defaultValue={draft.bio || ""} onBlur={saveDraft} style={{height:120}} />
            </div>
            <div className="fgroup">
              <label className="flabel">ประเภทบริการที่รับดู *</label>
              <div style={{fontSize:12,color:"#ff5b5b",fontWeight:900,lineHeight:1.6,margin:"4px 0 10px"}}>
                (ควรเลือกเฉพาะทางหรือทั้งหมด เพราะลูกค้าจะเห็นคุณในทุกเมนู)
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {serviceRows.map(([k,l])=>{
                  const selected = Array.isArray(draft.services) && draft.services.includes(k);
                  return <button
                    key={k}
                    type="button"
                    data-seer-service-v10={k}
                    data-selected={selected ? "yes" : "no"}
                    className={`btn btn-sm ${selected ? "btn-purple" : "btn-outline"}`}
                    onMouseDown={(e)=>e.preventDefault()}
                    onClick={(e)=>{
                      e.preventDefault();
                      e.stopPropagation();
                      const btn = e.currentTarget;
                      const isSelected = btn.getAttribute("data-selected") === "yes";
                      btn.setAttribute("data-selected", isSelected ? "no" : "yes");
                      btn.classList.toggle("btn-purple", !isSelected);
                      btn.classList.toggle("btn-outline", isSelected);
                      saveDraft();
                    }}
                  >{l}</button>
                })}
              </div>
            </div>

            <div className="fgroup">
              <label className="flabel">รายละเอียดบริการ / ราคา *</label>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:10}}>
                เพิ่มได้สูงสุด 5 รายการ รายการนี้จะใช้แสดงให้ลูกค้าเลือกตอนจองคิว
              </div>
              {Array.from({length:packageCountV143}).map((_, i) => {
                const pkg = Array.isArray(draft.packages) ? (draft.packages[i] || {}) : {};
                return (
                  <div key={i} style={{border:`1px solid ${C.line}`,borderRadius:14,padding:12,marginBottom:10,background:"rgba(255,255,255,.03)"}}>
                    <div style={{fontSize:13,color:C.goldLight,fontWeight:800,marginBottom:8}}>รายการที่ {i + 1}</div>
                    <input id={`seerRegV143PkgName${i}`} className="finput" type="text" placeholder="ชื่อบริการ เช่น ดูดวงความรัก 30 นาที" defaultValue={pkg.name || ""} onBlur={saveDraft} autoComplete="off" style={{marginBottom:8}} />
                    <input id={`seerRegV143PkgPrice${i}`} className="finput" type="tel" placeholder="ราคา เช่น 399" defaultValue={pkg.price || ""} onBlur={saveDraft} inputMode="numeric" autoComplete="off" style={{marginBottom:8}} />
                    <textarea id={`seerRegV143PkgDetail${i}`} className="finput" placeholder="รายละเอียด เช่น ถามได้ 3 คำถาม ดูผ่านแชท/โทร LINE ใช้เวลา 30 นาที" defaultValue={pkg.detail || ""} onBlur={saveDraft} style={{height:90}} />
                  </div>
                );
              })}
              {packageCountV143 < 5 && (
                <button type="button" className="btn btn-outline btn-sm" onClick={()=>{
                  saveDraft();
                  setPackageCountV143(c => Math.min(5, c + 1));
                }}>
                  + เพิ่มรายละเอียด
                </button>
              )}
              {packageCountV143 >= 5 && <div style={{fontSize:12,color:C.muted}}>เพิ่มครบ 5 รายการแล้ว</div>}
            </div>
            <div style={{fontSize:12,lineHeight:1.7,marginTop:2,marginBottom:10,textAlign:"center",color:uploadBusyV154 ? C.goldLight : (uploadStatusV154 ? C.green : C.muted),minHeight:22}}>
              {uploadBusyV154 ? (uploadStatusV154 || "กำลังเตรียมไฟล์...") : (uploadStatusV154 || "")}
            </div>
            <button type="button" className="btn btn-gold" onClick={submit} disabled={uploadBusyV154} style={{opacity:uploadBusyV154?0.72:1,cursor:uploadBusyV154?"wait":"pointer"}}>{uploadBusyV154 ? "กำลังประมวลผลไฟล์..." : "ส่งใบสมัคร ✨"}</button>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.7,marginTop:10,textAlign:"center"}}>
              หลังส่งสำเร็จ ใบสมัครจะถูกบันทึกเข้า D1 กลาง แอดมินเปิดจากคอมหรือมือถือก็ตรวจได้ ส่วนรูป/เอกสารจริงจะเปิดดูได้หลังต่อ R2
            </div>
          </div>
        </div>
        {submitPopup && (
          <div data-no-input-guard="1" style={{position:"fixed",inset:0,zIndex:999999,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
            <div style={{width:"100%",maxWidth:360,background:"linear-gradient(180deg,#20164a,#110D26)",border:`1px solid ${C.gold}66`,borderRadius:22,boxShadow:"0 22px 80px rgba(0,0,0,.55)",padding:22,textAlign:"center"}}>
              <div style={{width:70,height:70,borderRadius:"50%",margin:"0 auto 14px",display:"flex",alignItems:"center",justifyContent:"center",background:`${C.green}22`,border:`1px solid ${C.green}66`,fontSize:38}}>✓</div>
              <div style={{fontSize:21,fontWeight:800,color:C.goldLight,marginBottom:8}}>ระบบได้รับใบสมัครของคุณแล้ว</div>
              <div style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:16}}>โปรดรอเจ้าหน้าที่ตรวจสอบ</div>
              <button type="button" className="btn btn-gold" onClick={()=>{setSubmitPopup(false); go ? go("home") : (window.location.href = "/");}}>กลับหน้าหลัก</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function getCustomerGreetingName(fallbackName = "") {
  try {
    const lineUser = getSavedLineUser?.();
    const name =
      lineUser?.displayName ||
      localStorage.getItem("duangwannee_line_display_name") ||
      fallbackName ||
      "";
    return String(name || "ผู้ใช้").trim();
  } catch {
    return String(fallbackName || "ผู้ใช้").trim();
  }
}

function getThaiTodayInfo(date = new Date()) {
  const dayNames = ["วันอาทิตย์","วันจันทร์","วันอังคาร","วันพุธ","วันพฤหัสบดี","วันศุกร์","วันเสาร์"];
  const monthNames = [
    "มกราคม","กุมภาพันธ์","มีนาคม","เมษายน","พฤษภาคม","มิถุนายน",
    "กรกฎาคม","สิงหาคม","กันยายน","ตุลาคม","พฤศจิกายน","ธันวาคม"
  ];
  const thaiDate = `${dayNames[date.getDay()]}ที่ ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear() + 543}`;

  // คำนวณข้างขึ้น/ข้างแรมแบบประมาณการจากรอบจันทร์ เพื่อใช้แสดงผลหน้าแอป
  const synodicMonth = 29.530588853;
  const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14); // known new moon reference
  const todayNoon = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0);
  const days = (todayNoon - knownNewMoon) / 86400000;
  const age = ((days % synodicMonth) + synodicMonth) % synodicMonth;

  let lunarText = "";
  let lunarDay = 1;
  let lunarPhase = "ขึ้น";
  let isWanPhra = false;

  if (age < synodicMonth / 2) {
    lunarDay = Math.max(1, Math.min(15, Math.floor(age) + 1));
    lunarPhase = "ขึ้น";
    lunarText = `ขึ้น ${lunarDay} ค่ำ`;
  } else {
    lunarDay = Math.max(1, Math.min(15, Math.floor(age - synodicMonth / 2) + 1));
    lunarPhase = "แรม";
    lunarText = `แรม ${lunarDay} ค่ำ`;
  }

  // วันพระโดยประมาณ: ขึ้น/แรม 8 ค่ำ และ 15 ค่ำ
  const holyTargets = [7.4, 14.8, 22.1, 29.0, 0.2];
  isWanPhra = holyTargets.some(t => Math.abs(age - t) < 0.8);

  const buddhistSpecialDays = [];
  if (date.getMonth() === 0 && date.getDate() === 1) buddhistSpecialDays.push("วันขึ้นปีใหม่");
  if (date.getMonth() === 3 && date.getDate() === 13) buddhistSpecialDays.push("วันสงกรานต์");
  if (date.getMonth() === 11 && date.getDate() === 31) buddhistSpecialDays.push("วันสิ้นปี");
  if (isWanPhra) buddhistSpecialDays.push("วันพระ");

  return { thaiDate, lunarText, lunarDay, lunarPhase, isWanPhra, buddhistSpecialDays };
}

function getChineseAstroTodayInfo(date = new Date()) {
  const animals = ["ชวด","ฉลู","ขาล","เถาะ","มะโรง","มะเส็ง","มะเมีย","มะแม","วอก","ระกา","จอ","กุน"];
  const stems = ["ไม้หยาง","ไม้หยิน","ไฟหยาง","ไฟหยิน","ดินหยาง","ดินหยิน","ทองหยาง","ทองหยิน","น้ำหยาง","น้ำหยิน"];
  const branches = ["ชวด","ฉลู","ขาล","เถาะ","มะโรง","มะเส็ง","มะเมีย","มะแม","วอก","ระกา","จอ","กุน"];
  const chineseMonths = ["เจีย","ยี่","ซา","สี่","โหงว","ลัก","ชิก","โป๊ย","เก้า","จับ","จับอิก","จับยี่"];

  const y = date.getFullYear();
  const animal = animals[(y - 4) % 12];
  const dayIndex = Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000);
  const stem = stems[((dayIndex + 4) % 10 + 10) % 10];
  const branch = branches[((dayIndex + 8) % 12 + 12) % 12];
  const monthName = chineseMonths[date.getMonth()];
  const chineseDay = (date.getDate() % 30) || 30;
  const chineseDayText = chineseDay === 1 ? "ชิวอิก" : chineseDay === 15 ? "จับโหงว" : `วันจีน ${chineseDay} ค่ำ`;

  const sets = [
    {
      label: "วันเปิดทาง",
      good: ["เริ่มงานใหม่", "วางแผนงาน", "ติดต่อผู้ใหญ่", "ขอคำปรึกษา"],
      avoid: ["ตัดสินใจด้วยอารมณ์", "ใช้เงินก้อนใหญ่แบบเร่งด่วน"],
      tip: "วันนี้เหมาะกับการเริ่มต้นแบบค่อยเป็นค่อยไป ตั้งเจตนาดีแล้วลงมือทีละขั้น"
    },
    {
      label: "วันเก็บทรัพย์",
      good: ["จัดการเงิน", "เคลียร์บัญชี", "เก็บออม", "วางแผนรายรับรายจ่าย"],
      avoid: ["เสี่ยงโชคหนัก", "ค้ำประกัน", "รับปากเรื่องเงินแทนผู้อื่น"],
      tip: "เหมาะกับการจัดระบบเงินและตัดรายจ่ายที่ไม่จำเป็น"
    },
    {
      label: "วันเจรจา",
      good: ["พูดคุยตกลง", "ขายของ", "นัดหมายลูกค้า", "ปรับความเข้าใจ"],
      avoid: ["พูดแรง", "ตอบแชทตอนโมโห", "เซ็นสัญญาโดยไม่อ่านละเอียด"],
      tip: "คำพูดคือพลังหลักของวันนี้ ใช้ถ้อยคำดีจะเปิดโอกาสใหม่ได้"
    },
    {
      label: "วันเสริมชื่อเสียง",
      good: ["โพสต์งาน", "โปรโมตตัวเอง", "สมัครงาน", "นำเสนอผลงาน"],
      avoid: ["โต้เถียงสาธารณะ", "เผยแพร่ข้อมูลที่ยังไม่ตรวจ"],
      tip: "เหมาะกับการทำให้คนเห็นคุณค่า แต่ควรตรวจรายละเอียดก่อนเผยแพร่"
    },
    {
      label: "วันพักใจ",
      good: ["ทำบุญ", "ดูแลสุขภาพ", "จัดบ้าน", "ทบทวนแผนชีวิต"],
      avoid: ["ฝืนร่างกาย", "รับงานเกินกำลัง", "เร่งสรุปเรื่องใหญ่"],
      tip: "วันนี้ควรลดความวุ่นวาย เติมพลังใจ และทำสิ่งที่ช่วยให้บ้าน/ใจสงบ"
    },
    {
      label: "วันธงชัยโดยประมาณ",
      good: ["เริ่มเรื่องมงคล", "นัดหมายสำคัญ", "เปิดตัวสิ่งใหม่", "ขอความร่วมมือ"],
      avoid: ["ทำแบบลวก ๆ", "เริ่มงานโดยไม่เตรียมเอกสาร"],
      tip: "เหมาะกับการเริ่มสิ่งที่เตรียมพร้อมแล้ว ใช้ฤกษ์ดีคู่กับความรอบคอบ"
    }
  ];
  const pick = sets[((dayIndex % sets.length) + sets.length) % sets.length];

  const festivals = [];
  if (date.getMonth() === 0 && date.getDate() >= 20 && date.getDate() <= 31) festivals.push("ช่วงเตรียมตรุษจีน");
  if (date.getMonth() === 8 && date.getDate() >= 10 && date.getDate() <= 25) festivals.push("ช่วงเทศกาลไหว้พระจันทร์โดยประมาณ");

  return {
    chineseDayText,
    chineseMonthText: `เดือนจีน ${monthName}`,
    zodiacYear: `ปี${animal}`,
    dayPillar: `${stem}${branch}`,
    label: pick.label,
    good: pick.good,
    avoid: pick.avoid,
    tip: pick.tip,
    festivals
  };
}

function readSavedSystemReading(key) {
  try {
    const raw = localStorage.getItem(`duangwannee_system_result_${key || "general"}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSystemReading(key, value) {
  try {
    if (value) localStorage.setItem(`duangwannee_system_result_${key || "general"}`, JSON.stringify(value));
  } catch {}
}



const SEER_LEVELS_KEY = "dw_seer_platform_levels_v44";
const SEER_INVITES_KEY = "dw_seer_invites_v44";
const SEER_REFERRALS_KEY = "dw_seer_referrals_v44";
const SEER_ADMIN_MESSAGES_KEY = "dw_seer_admin_messages_v45";

function readJsonStore(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJsonStore(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent("duangwannee:store-updated", { detail: { key, value } }));
  } catch {}
}

function readSeerLevelsStore() {
  return readJsonStore(SEER_LEVELS_KEY, {});
}

function writeSeerLevelsStore(value) {
  writeJsonStore(SEER_LEVELS_KEY, value || {});
}

function normalizeSeerPlatformLevel(value) {
  const level = String(value || "standard").trim().toLowerCase();
  // รองรับข้อมูล Diamond เก่าที่อาจค้างอยู่ โดยย้ายสิทธิ์เดิมไปเป็น Platinum
  // แต่หน้าระบบและ API ใหม่มีเพียง 2 ระดับ: ทั่วไป / แพลตินั่ม
  return level === "platinum" || level === "diamond" ? "platinum" : "standard";
}

function getSeerPlatformLevel(seer, levelMap = readSeerLevelsStore()) {
  const nameKey = String(seer?.name || seer?.displayName || seer?.display_name || "").trim();
  const lineNameKey = String(seer?.lineName || seer?.lineDisplayName || "").trim();

  const ids = [
    seer?.id,
    seer?.seerId,
    seer?.lineUserId,
    seer?.line_user_id,
    seer?.line_userId,
    nameKey ? `NAME:${nameKey}` : "",
    lineNameKey ? `NAME:${lineNameKey}` : "",
  ].map(x => String(x || "").trim()).filter(Boolean);

  for (const id of ids) {
    if (levelMap[id]) return normalizeSeerPlatformLevel(levelMap[id]);
  }

  return normalizeSeerPlatformLevel(seer?.platformLevel || seer?.seerLevel || "standard");
}

function getSeerLevelLabel(level) {
  return normalizeSeerPlatformLevel(level) === "platinum" ? "แพลตินั่ม" : "ทั่วไป";
}

function readSeerInvitesStore() {
  return readJsonStore(SEER_INVITES_KEY, []);
}

function writeSeerInvitesStore(value) {
  writeJsonStore(SEER_INVITES_KEY, Array.isArray(value) ? value : []);
}

function readSeerReferralsStore() {
  return readJsonStore(SEER_REFERRALS_KEY, []);
}

function writeSeerReferralsStore(value) {
  writeJsonStore(SEER_REFERRALS_KEY, Array.isArray(value) ? value : []);
}

function readSeerAdminMessagesStore() {
  return readJsonStore(SEER_ADMIN_MESSAGES_KEY, []);
}

function writeSeerAdminMessagesStore(value) {
  writeJsonStore(SEER_ADMIN_MESSAGES_KEY, Array.isArray(value) ? value : []);
}

function unreadAdminMessagesForSeer(seer) {
  const id = String(seer?.id || seer?.seerId || "");
  const lineId = String(seer?.lineUserId || "");
  return readSeerAdminMessagesStore().filter(m =>
    !m.read &&
    (String(m.seerId || "") === id || (lineId && String(m.lineUserId || "") === lineId))
  );
}

function markAdminMessagesReadForSeer(seer) {
  const id = String(seer?.id || seer?.seerId || "");
  const lineId = String(seer?.lineUserId || "");
  const next = readSeerAdminMessagesStore().map(m =>
    (String(m.seerId || "") === id || (lineId && String(m.lineUserId || "") === lineId))
      ? {...m, read:true, readAt:new Date().toLocaleString("th-TH")}
      : m
  );
  writeSeerAdminMessagesStore(next);
  return next;
}

function makeOneTimeInviteCode(seerId) {
  return `DW-${String(seerId || "SEER").replace(/[^a-zA-Z0-9]/g,"").slice(0,8).toUpperCase()}-${Math.random().toString(36).slice(2,8).toUpperCase()}${Date.now().toString(36).slice(-4).toUpperCase()}`;
}

function buildSeerProfileShareUrl(seer) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://duangwannee-web.pages.dev";
  return `${origin}/seer-profile/${encodeURIComponent(seer?.id || seer?.seerId || "")}`;
}

function buildSeerInviteUrl(code) {
  const invite = encodeURIComponent(code || "");
  const v = Date.now();
  // v142B: LIFF บางครั้งส่ง query กลับมาใน liff.state แทนที่จะเป็น screen/invite ตรง ๆ
  // ใช้ liff.state เพื่อให้เปิดจาก LINE แล้วเข้าหน้าสมัครหมอดูแน่นอน
  const state = encodeURIComponent(`/?screen=seer_register&invite=${invite}&v=${v}`);
  return `${LIFF_SEER_REGISTER_URL}?liff.state=${state}`;
}

function registerSeerReferralFromInvite(applicant, newSeer) {
  const code = applicant?.inviteCode || applicant?.sourceInviteCode || "";
  if (!code) return null;

  const invites = readSeerInvitesStore();
  const invite = invites.find(x => x.code === code && x.status !== "used");
  if (!invite) return null;

  const usedInvite = {
    ...invite,
    status: "used",
    usedBySeerId: newSeer?.id || "",
    usedByName: newSeer?.name || applicant?.name || "",
    usedAt: new Date().toLocaleString("th-TH"),
  };

  writeSeerInvitesStore(invites.map(x => x.code === code ? usedInvite : x));

  const referral = {
    id: `REF-${Date.now()}`,
    inviteCode: code,
    inviterSeerId: invite.inviterSeerId,
    inviterName: invite.inviterName,
    invitedSeerId: newSeer?.id || "",
    invitedName: newSeer?.name || applicant?.name || "",
    commissionPercent: 3,
    lifetime: true,
    status: "active",
    createdAt: new Date().toLocaleString("th-TH"),
  };

  writeSeerReferralsStore([referral, ...readSeerReferralsStore()]);
  return referral;
}


async function copyTextSafe(text, okText = "คัดลอกแล้ว") {
  try {
    await navigator.clipboard.writeText(text);
    alert(okText);
  } catch {
    prompt("คัดลอกลิงก์นี้", text);
  }
}

function openLineOaAdminChatTarget(lineUserId = "") {
  const id = String(lineUserId || "").trim();
  if (id) {
    copyTextSafe(id, "คัดลอก LINE userId แล้ว จากนั้นเปิด LINE OA Manager เพื่อค้นหา/ทักหมอดู");
  } else {
    alert("ยังไม่มี LINE userId ของหมอดูคนนี้");
  }
  try {
    window.open("https://manager.line.biz/", "_blank", "noopener,noreferrer");
  } catch {}
}


// ═══════════════════════════════════════════════════════════════════════════════
//  CUSTOMER / SEER APP
// ═══════════════════════════════════════════════════════════════════════════════
function CustomerApp({orders, setOrders, isSeer, initialScreen=null, initialArticleId=null, initialSeerId=null, initialOpenAction=null, initialBookingId=null, chatAlerts=[], setChatAlerts=()=>{}}) {
  const hasLineLogin = localStorage.getItem("duangwannee_logged_in") === "yes";
  const isApprovedSeerAtStart = hasLineLogin && isCurrentLineUserApprovedSeer();
  const isDirectSeerRegister = initialScreen === "seer_register";
  const isDirectNewsList = initialScreen === "movement";
  const isDirectNewsDetail = initialScreen === "movement_detail";
  const [approvedSeers, setApprovedSeers] = useState(() => readApprovedSeersStore());
  const [seerLevels, setSeerLevels] = useState(() => readSeerLevelsStore());
  useEffect(() => {
    let alive = true;
    const saveLevelsCacheOnly = (value) => {
      try { localStorage.setItem(SEER_LEVELS_KEY, JSON.stringify(value || {})); } catch {}
    };
    const loadLevelsFromD1 = async () => {
      if (isSeerProfileEditingNowV133()) return;
      try {
        const data = await apiGet("/api/seer-levels");
        const map = data.levels || {};
        if (!alive) return;
        const merged = map;
        setSeerLevels(merged);
        saveLevelsCacheOnly(merged); // ไม่ dispatch event กัน loop / เด้งหน้า
      } catch (err) {
        console.warn("โหลดระดับหมอดูจาก D1 ไม่สำเร็จ", err);
        if (alive) setSeerLevels(readSeerLevelsStore());
      }
    };
    const sync = () => {
      if (isSeerProfileEditingNowV133()) return;
      setSeerLevels(readSeerLevelsStore());
    };
    loadLevelsFromD1();
    const timer = setInterval(loadLevelsFromD1, 60000);
    window.addEventListener("storage", sync);
    window.addEventListener("duangwannee:approved-seers-updated", sync);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener("storage", sync);
      window.removeEventListener("duangwannee:approved-seers-updated", sync);
    };
  }, []);
  const [dbSeers, setDbSeers] = useState([]);
  useEffect(() => {
    let alive = true;
    const loadSeersV136 = () => {
      apiGet("/api/seers").then(data => {
        if (!alive) return;
        setDbSeers((data.seers || []).map(mapApiSeer));
      }).catch(err => console.warn("โหลดทำเนียบหมอดูจาก D1 ไม่สำเร็จ", err));
    };
    loadSeersV136();
    const timer = setInterval(loadSeersV136, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);
  const allSeers = uniqById([...dbSeers, ...SEERS]).sort((a,b)=>(Number(a.sortOrder||999)-Number(b.sortOrder||999)));

  // v54: ล็อกโปรไฟล์หมอดูด้วย LINE userId + D1 เป็นหลัก
  const [lockedSeerProfile, setLockedSeerProfile] = useState(null);
  useEffect(() => {
    let alive = true;
    const loadLockedSeerProfile = async () => {
      if (isSeerProfileEditingNowV133()) return;
      try {
        const lineUser = getSavedLineUser() || {
        userId: "PENDING_LINE_" + Date.now(),
        displayName: "สมัครผ่าน LIFF / รอผูก LINE OA",
        pictureUrl: "",
      };
        if (!lineUser?.userId) {
          if (alive) setLockedSeerProfile(null);
          return;
        }
        const data = await apiGet(`/api/seer-profile?line_user_id=${encodeURIComponent(lineUser.userId)}`);
        if (!alive) return;
        if (data?.seer) setLockedSeerProfile(mapApiSeer(data.seer));
        else setLockedSeerProfile(null);
      } catch (err) {
        if (alive) setLockedSeerProfile(null);
        console.warn("โหลดโปรไฟล์หมอดูตาม LINE userId จาก D1 ไม่สำเร็จ", err);
      }
    };
    loadLockedSeerProfile();
    const timer = setInterval(loadLockedSeerProfile, 45000);
    window.addEventListener("duangwannee:line-ready", loadLockedSeerProfile);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener("duangwannee:line-ready", loadLockedSeerProfile);
    };
  }, []);
  const [userNotifications, setUserNotifications] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (isSeerProfileEditingNowV133()) return;
      try {
        const lineUser = getSavedLineUser();
        if (!lineUser?.userId) return;
        const data = await apiGet(`/api/notifications?line_user_id=${encodeURIComponent(lineUser.userId)}`);
        if (alive) setUserNotifications(data.notifications || []);
      } catch (err) {
        console.warn("โหลดแจ้งเตือนผู้ใช้ไม่สำเร็จ", err);
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);
  const [movementArticles, setMovementArticles] = useState([]);
  const [selectedMovementArticle, setSelectedMovementArticle] = useState(null);
  useEffect(() => {
    let alive = true;
    apiGet("/api/articles?status=published&limit=50").then(data => {
      if (!alive) return;
      setMovementArticles((data.articles || []).map(a => ({
        ...a,
        id: a.article_id || a.id,
        title: a.title || "ความเคลื่อนไหวประจำวัน",
        body: a.body || "",
        image_url: a.image_url || a.imageUrl || "",
        external_url: a.external_url || a.externalUrl || "",
        link_text: a.link_text || a.linkText || "อ่านต่อ",
        createdAt: a.created_at || a.createdAt || "",
      })));
    }).catch(err => console.warn("โหลดความเคลื่อนไหวจาก D1 ไม่สำเร็จ", err));
    return () => { alive = false; };
  }, []);
  const openMovementArticle = async (articleId) => {
    let article = articleId ? movementArticles.find(a => String(a.id) === String(articleId) || String(a.article_id) === String(articleId)) : null;
    if (!article && articleId) {
      try {
        const data = await apiGet(`/api/articles?article_id=${encodeURIComponent(articleId)}`);
        article = (data.article || (data.articles || [])[0]);
        if (article) article = {...article, id: article.article_id || article.id};
      } catch (err) { console.warn("โหลดบทความเฉพาะไม่สำเร็จ", err); }
    }
    if (!article && !articleId) article = movementArticles[0] || null;
    setSelectedMovementArticle(article || null);
    go("movement_detail", article?.id || articleId ? {article_id: article?.id || articleId} : {});
  };
  useEffect(() => {
    if (isDirectNewsDetail && initialArticleId) openMovementArticle(initialArticleId);
  }, [isDirectNewsDetail, initialArticleId, movementArticles.length]);
  const parseBookingPackageTextV109 = (text, seerId = "seer") => {
    const s = String(text || "");
    const out = [];
    const lines = s.split(/\n+/).map(x => x.trim()).filter(Boolean).slice(0, 5);

    const tidy = (v) => String(v || "")
      .replace(/\s{2,}/g, " ")
      .replace(/[|]+/g, " ")
      .replace(/^[\-–—:|\s]+|[\-–—:|\s]+$/g, "")
      .trim();

    const cleanNameV134 = (raw, minutes, price, desc = "") => {
      let name = String(raw || "");
      // ถ้าเป็น format ใหม่: ชื่อ | 12 นาที | 99 บาท | รายละเอียด
      if (name.includes("|")) name = name.split("|")[0] || name;
      // ตัดรายละเอียดหลังขีดออกจากชื่อ
      name = name.split(/\s[-–—]\s/)[0] || name;
      name = name
        .replace(/(?:ราคา\s*)?\d{2,6}\s*(?:บาท|บ\.?|฿)/gi, "")
        .replace(/฿\s*\d{2,6}/gi, "")
        .replace(/\d{1,3}\s*(?:นาที|min)/gi, "")
        .replace(/\bราคา\b/gi, "")
        .trim();
      name = tidy(name);
      // แก้ข้อมูลเก่าที่เคยบันทึกซ้ำ เช่น "ถามคำตอบคำ 12นาที 99บาท 12 นาที ราคา 99 บาท - ถาม..."
      if (desc && name.length > 42 && desc.length > 0) {
        const firstPart = name.split(/\s{1,}/).slice(0, 6).join(" ");
        name = tidy(firstPart);
      }
      if (!name) name = minutes ? `ดูดวง ${minutes} นาที` : "จองคิวดูดวงสด";
      return name.slice(0, 60);
    };

    const cleanDescV134 = (line, name, minutes, price) => {
      let desc = "";
      const parts = String(line || "").split(/\s[-–—]\s/);
      if (parts.length > 1) desc = parts.slice(1).join(" - ");
      if (!desc && String(line || "").includes("|")) {
        const p = String(line || "").split("|").map(tidy);
        desc = p[3] || "";
      }
      if (!desc) desc = String(line || "");
      desc = desc
        .replace(new RegExp(String(name || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), "")
        .replace(/(?:ราคา\s*)?\d{2,6}\s*(?:บาท|บ\.?|฿)/gi, "")
        .replace(/฿\s*\d{2,6}/gi, "")
        .replace(/\d{1,3}\s*(?:นาที|min)/gi, "")
        .replace(/\bราคา\b/gi, "");
      desc = tidy(desc);
      if (!desc) desc = minutes ? `บริการดูดวง ${minutes} นาที` : "บริการดูดวงสด";
      return desc.slice(0, 140);
    };

    const addPkg = (line, price, minutes, i) => {
      const p = Number(price || 0);
      const m = Number(minutes || 0);
      if (!p) return;
      let descPart = "";
      if (String(line || "").includes("|")) {
        const parts = String(line || "").split("|").map(tidy);
        descPart = parts[3] || "";
      } else {
        const parts = String(line || "").split(/\s[-–—]\s/);
        descPart = parts.length > 1 ? parts.slice(1).join(" - ") : "";
      }
      const title = cleanNameV134(line, m, p, descPart);
      const desc = cleanDescV134(line, title, m, p);
      out.push({
        id: `${seerId}_live_pkg_${i}_${m || "x"}_${p}`,
        name: title,
        type: "live",
        price: p,
        minutes: m,
        ico: "🎯",
        desc,
        input: "booking",
        fromProfilePackage: true,
      });
    };

    lines.forEach((line, i) => {
      const parts = String(line).split("|").map(tidy);
      const price = (parts.find(x => /\d{2,6}\s*(บาท|บ\.?|฿)/i.test(x)) || line).match(/(?:ราคา\s*)?(\d{2,6})\s*(?:บาท|บ\.?|฿)/i)?.[1] || String(line).match(/฿\s*(\d{2,6})/i)?.[1] || "";
      const minutes = (parts.find(x => /\d{1,3}\s*(นาที|min)/i.test(x)) || line).match(/(\d{1,3})\s*(?:นาที|min)/i)?.[1] || "";
      addPkg(line, price, minutes, i);
    });

    if (!out.length) {
      let m;
      const rePriceFirst = /(?:฿|ราคา)?\s*(\d{2,6})\s*(?:บาท|บ\.?|฿)?[\s,\/\-:]*?(\d{1,3})\s*(?:นาที|min)/gi;
      while ((m = rePriceFirst.exec(s))) addPkg(`ดูดวง ${m[2]} นาที ราคา ${m[1]} บาท`, m[1], m[2], out.length);
      const reMinuteFirst = /(\d{1,3})\s*(?:นาที|min).*?(?:฿|ราคา)?\s*(\d{2,6})/gi;
      while ((m = reMinuteFirst.exec(s))) addPkg(`ดูดวง ${m[1]} นาที ราคา ${m[2]} บาท`, m[2], m[1], out.length);
    }
    return out.slice(0, 5);
  };

  const normalizeBookingServiceV109 = (svc, seer, index = 0) => {
    const price = Number(svc?.price ?? svc?.amount ?? svc?.fee ?? svc?.service_price ?? 0) || Number(seer?.minPrice || 0) || 199;
    const minutes = Number(svc?.minutes || svc?.duration || String(svc?.name || svc?.desc || "").match(/(\d{1,3})\s*(?:นาที|min)/i)?.[1] || 0);
    return {
      id: svc?.id || `${seer?.id || seer?.seerId || "seer"}_booking_${index}_${price}_${minutes || ""}`,
      name: svc?.name || (minutes ? `ดูดวงสด ${minutes} นาที` : "จองคิวดูดวงสด"),
      type: svc?.type || "live",
      price,
      minutes,
      ico: svc?.ico || "🎯",
      desc: svc?.desc || (minutes ? `คุยกับหมอดูสด ${minutes} นาที` : "คุยกับหมอดูแบบสด"),
      input: "booking",
    };
  };

  const getBookingServicesForSeerV109 = (seer) => {
    if (!seer) return [];

    const textPackages = [
      seer?.bookingPackageText,
      seer?.booking_packages_text,
      seer?.price_text,
      seer?.priceText,
      // adminNote/docs/bio เคยถูกเอามา parse แล้วทำให้ข้อมูลยาว/ซ้ำ จึงไม่เอามาเป็น package หลักแล้ว
    ].flatMap((x) => parseBookingPackageTextV109(x, seer?.id || seer?.seerId || "seer"));

    const hasProfilePackages = textPackages.some(x => Number(x.price || 0) > 0);

    const directSources = [
      ...((SEER_SVCS[seer?.id] || []).filter(Boolean)),
      // ถ้ามี package จากโปรไฟล์แล้ว ห้ามเอา seer.services default live มาปน เพราะจะเกิดรายการซ้ำ/ราคาเพี้ยน
      ...(!hasProfilePackages ? ((Array.isArray(seer?.services) ? seer.services : []).filter(Boolean)) : []),
      ...((Array.isArray(seer?.bookingServices) ? seer.bookingServices : []).filter(Boolean)),
      ...((Array.isArray(seer?.booking_packages) ? seer.booking_packages : []).filter(Boolean)),
      ...((Array.isArray(seer?.bookingPackages) ? seer.bookingPackages : []).filter(Boolean)),
    ];

    const booking = directSources
      .filter(x => String(x?.input || "").toLowerCase() === "booking" || String(x?.type || "").toLowerCase() === "live")
      .map((x,i) => normalizeBookingServiceV109(x, seer, i));

    const merged = hasProfilePackages ? textPackages : [...booking, ...textPackages];
    const seen = new Set();
    const clean = merged.filter(x => {
      const key = `${Number(x.price || 0)}_${Number(x.minutes || 0)}_${String(x.name || "")}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return Number(x.price || 0) > 0;
    }).sort((a,b) => (Number(a.minutes || 999) - Number(b.minutes || 999)) || (Number(a.price || 0) - Number(b.price || 0)));

    if (clean.length) return clean.slice(0,5);

    const basePrice =
      Number(seer?.minPrice || 0) ||
      Number(String(seer?.price_text || seer?.priceText || "").match(/(\d{2,6})/)?.[1] || 0) ||
      199;
    return [{
      id: `${seer?.id || seer?.seerId || "seer"}_live_default_${basePrice}`,
      name: "จองคิวดูดวงสด",
      type: "live",
      price: basePrice,
      minutes: 0,
      ico: "🎯",
      desc: "คุยกับหมอดูแบบสด",
      input: "booking",
    }];
  };

  const getServicesForSeer = (seer) => {
    const raw = [
      ...((SEER_SVCS[seer?.id] || []).filter(Boolean)),
      ...((Array.isArray(seer?.services) ? seer.services : []).filter(Boolean)),
    ];
    const nonBooking = raw.filter(x => !(String(x?.input || "").toLowerCase() === "booking" || String(x?.type || "").toLowerCase() === "live"));
    return [...nonBooking, ...getBookingServicesForSeerV109(seer)];
  };
  const getMinPriceForSeer = (seer) => {
    const services = getServicesForSeer(seer);
    return services.length ? Math.min(...services.map(x => Number(x.price) || 0).filter(Boolean)) : (seer?.minPrice || 199);
  };
  const [scr, setScr] = useState(() => {
    if (isDirectSeerRegister) {
      try { sessionStorage.setItem("DW_FORCE_SEER_REGISTER", "yes"); } catch {}
      return "seer_register";
    }
    if (isDirectNewsDetail) return "movement_detail";
    if (isDirectNewsList) return "movement";
    if (initialScreen === "seer_detail") return "seer_detail";

    // v137C: รองรับ ?page=seers / ?page=history / ?page=my_bookings ตั้งแต่โหลดหน้าแรก
    // เดิม query page ใช้เปลี่ยน URL ได้ แต่ตอนเปิด URL ตรง ๆ ยังไม่เอา initialScreen ไปตั้ง scr
    const allowedQueryScreensV137C = new Set([
      "home","services","seers","my_bookings","my_info","history","movement","points_detail","member_level_detail",
      "seer_detail","booking_form","booking_rules","fortune_room","customer_register","seer_dash",
      "seer_new_jobs","seer_today_jobs","seer_job_history","seer_income"
    ]);
    if (initialScreen && allowedQueryScreensV137C.has(String(initialScreen))) {
      if (!hasLineLogin && !["home","seers","seer_detail","movement","movement_detail"].includes(String(initialScreen))) return "splash";
      return initialScreen;
    }

    if (!hasLineLogin) return "splash";
    if (!isSeer && !readCustomerProfileV138(getSavedLineUser()?.userId || "")) return "customer_register";
    return "home";
  });
  const [loggedIn, setLoggedIn] = useState(isDirectSeerRegister ? true : hasLineLogin);
  const [seerMode, setSeerMode] = useState(() => false); // v140: เริ่มเป็นลูกค้าก่อนเสมอ รอ D1 ยืนยันว่าเป็นหมอดูที่อนุมัติแล้ว
  const [activeNav, setActiveNav] = useState(() => {
    const p = String(initialScreen || "");
    return ["home","services","seers","my_bookings","my_info","history","seer_dash"].includes(p) ? p : "home";
  });
  const [selSeer, setSelSeer] = useState(() => initialSeerId ? (allSeers.find(s => String(s.id) === String(initialSeerId) || String(s.seerId) === String(initialSeerId)) || null) : null);
  useEffect(() => {
    if (!initialSeerId || selSeer) return;
    const found = allSeers.find(s => String(s.id) === String(initialSeerId) || String(s.seerId) === String(initialSeerId));
    if (found) setSelSeer(found);
  }, [initialSeerId, allSeers.length, !!selSeer]);
  const [selSvc, setSelSvc] = useState(null);
  const [selDay, setSelDay] = useState(0);
  const [selSlot, setSelSlot] = useState(null);
  const [form, setForm] = useState({name:"",phone:"",contact:"LINE",question:"",phoneNum:"",slip:null});
  const [rateModal, setRateModal] = useState(null);
  const [bookingStep, setBookingStep] = useState("rules");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {from:"seer", text:"สวัสดีค่ะ ต้องการนัดดูดวงสด 40 นาที ใช่ไหมคะ", time:"ตอนนี้"},
    {from:"customer", text:"ใช่ค่ะ อยากนัดช่วงเย็นวันนี้ได้ไหมคะ", time:"ตอนนี้"},
    {from:"seer", text:"ได้ค่ะ หมอดูสะดวก 19:00 น. หากตกลงกดชำระเงินเพื่อยืนยันคิวได้เลยค่ะ", time:"ตอนนี้"},
  ]);
  const [seerTabRaw, setSeerTabRaw] = useState("booking_requests");
  const seerTab = seerTabRaw;
  const setSeerTab = (next) => {
    const value = typeof next === "function" ? next(seerTabRaw) : next;
    try {
      if (value === "profile") localStorage.setItem("dw_seer_profile_tab_v133", "profile");
      else {
        localStorage.removeItem("dw_seer_profile_tab_v133");
        localStorage.removeItem("dw_seer_profile_editing_v133");
      }
    } catch {}
    if (value !== "profile") seerProfileEditingRefV132B.current = false;
    setSeerTabRaw(value);
  };
  const [viewOrder, setViewOrder] = useState(null);
  const [viewChatAlert, setViewChatAlert] = useState(null);
  const [seerReplyInput, setSeerReplyInput] = useState("");
  const [reply, setReply] = useState("");
  const [seerSlotState, setSeerSlotState] = useState({
    "พฤ 22": Object.fromEntries(BOOKING_TIME_SLOTS_V92.map(slot => [slot, "open"]))
  });
  const [registerForm, setRegForm] = useState({name:"",spec:"",bio:"",price:"",bank:"",contactPhone:"",lineId:"",lineUrl:"",services:[]});
  const [favoriteSeers, setFavoriteSeers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dw_favorite_seers") || "[]"); } catch { return []; }
  });
  const [seerListFilter, setSeerListFilter] = useState("ทั้งหมด");
  const [notifySeers, setNotifySeers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dw_notify_seers") || "[]"); } catch { return []; }
  });
  const [seerReviews, setSeerReviews] = useState([]);
  useEffect(() => {
    let alive = true;
    apiGet("/api/seer-reviews").then(data => {
      if (!alive) return;
      setSeerReviews((data.reviews || []).map(mapApiReview));
    }).catch(err => console.warn("โหลดรีวิวจาก D1 ไม่สำเร็จ", err));
    return () => { alive = false; };
  }, []);
  const [reviewForm, setReviewForm] = useState({rating:5,text:""});
  const [bookings, setBookings] = useState([]);
  const [bookingHistoryTickV103, setBookingHistoryTickV103] = useState(0);
  const [customerBookingListV104, setCustomerBookingListV104] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  // v3 clean booking reset: never render stale booking/seer records cached by an older prototype.
  useEffect(() => {
    try {
      const marker = "dw_booking_core_clean_v3";
      if (localStorage.getItem(marker) === "done") return;
      Object.keys(localStorage).forEach((key) => {
        if (/^(dw_(my_booking|booking_history|orders|customer_booking|approved_seers|seer_invites|seer_admin_messages)|DW_ACTIVE_SEER_PROFILE_ID_V52)/.test(key)) localStorage.removeItem(key);
      });
      localStorage.setItem(marker, "done");
    } catch {}
  }, []);
  const [bookingNowTickV113, setBookingNowTickV113] = useState(Date.now());
  const [selectedBooking, setSelectedBooking] = useState(null);
  // v175: เก็บงานที่เปิดดูรายละเอียดไว้ใน App root เพื่อไม่ให้ popup ปิดเอง
  // เมื่อแดชหมอดู re-render จากการโหลดรายการ/แจ้งเตือน
  const [newJobDetailsV175, setNewJobDetailsV175] = useState(null);
  // v178: รายละเอียดคิวที่ยืนยันแล้วของหมอดู เก็บใน App root เช่นกัน
  // เพื่อให้เปิดดูข้อมูลลูกค้าได้อย่างนิ่ง แม้รายการคิวอัปเดตระหว่างอยู่หน้าแดชบอร์ด
  const [seerQueueDetailsV178, setSeerQueueDetailsV178] = useState(null);
  // v3.9.42: อัปเดตหน้าจอเมื่อถึงเวลาเปิดห้องและเมื่อครบ 15 นาทีหลังเวลานัด
  // ไม่ใช้ polling ทุกวินาที เพื่อไม่ให้แดชหมอดูกระพริบหรือรีเซ็ต
  useEffect(() => {
    const target = seerQueueDetailsV178 || (scr === "booking_room" ? selectedBooking : null);
    if (!target?.booking_id) return;
    const moments = [parseBookingScheduleDateV112(target), bookingCompletionAvailableAtV3942(target)]
      .filter(Boolean)
      .map(d => d.getTime() - Date.now())
      .filter(ms => ms > 0)
      .sort((a,b) => a-b);
    if (!moments.length) return;
    const timer = setTimeout(() => setBookingNowTickV113(Date.now()), Math.min(moments[0] + 500, 2147480000));
    return () => clearTimeout(timer);
  }, [seerQueueDetailsV178?.booking_id, seerQueueDetailsV178?.scheduled_at, selectedBooking?.booking_id, selectedBooking?.scheduled_at, scr]);
  const [bookingRoomMessages, setBookingRoomMessages] = useState([]);
  const [bookingChatMessages, setBookingChatMessages] = useState([]);
  const [bookingChatInput, setBookingChatInput] = useState("");
  const bookingChatInputRefV115 = useRef(null);
  const clearBookingChatInputV115 = () => {
    if (bookingChatInputRefV115.current) bookingChatInputRefV115.current.value = "";
  };
  const setBookingChatMessagesStableV117 = (next) => {
    const safeNext = Array.isArray(next) ? next : [];
    setBookingChatMessages(prev => {
      try {
        const sig = (arr) => JSON.stringify((arr || []).map(m => ({id:m.message_id || m.id, t:m.message_text || m.text, r:m.sender_role, c:m.created_at})));
        return sig(prev) === sig(safeNext) ? prev : safeNext;
      } catch { return safeNext; }
    });
  };
  const [bookingChatPopupOpen, setBookingChatPopupOpen] = useState(false);
  const [bookingChatRoleV118, setBookingChatRoleV118] = useState("customer");
  // v117: ห้ามใช้ React state ของ visualViewport ใน popup แชท เพราะทำให้ re-render/remount ระหว่างพิมพ์
  const chatViewportHeightV116 = 0;
  const chatKeyboardModeV116 = false;
  const [paymentSettingsV106, setPaymentSettingsV106] = useState(null);
  const [paymentPopupBookingV106, setPaymentPopupBookingV106] = useState(null);
  const [slipReceivedPopupV106, setSlipReceivedPopupV106] = useState(false);
  const [slipUploadStatusV110, setSlipUploadStatusV110] = useState("");
  const [selectedSlipNameV127, setSelectedSlipNameV127] = useState("");
  const pendingSlipFileRefV127 = useRef(null);
  const paymentPopupLockRefV127 = useRef(null);
  const [slipInputKeyV121, setSlipInputKeyV121] = useState(0);
  const [customerPayContactV122, setCustomerPayContactV122] = useState({phone:"", lineId:"", preferredMethods:["phone","line"], consent:true});
  const [roomInput, setRoomInput] = useState("");
  const [roomGateMessageV112, setRoomGateMessageV112] = useState("");
  const [bookingForm, setBookingForm] = useState({ customer_name:"", requested_date:"", requested_time:"", customer_note:"", package_id:"" });
  // v168: หมอดูขอเลื่อนเวลาในหน้าต่างเฉพาะ ไม่ใช้ prompt เพื่อเลือกวัน/เวลาได้ชัดเจนบนมือถือ
  const [rescheduleBookingV168, setRescheduleBookingV168] = useState(null);
  const [rescheduleFormV168, setRescheduleFormV168] = useState({ date:"", time:"", note:"" });
  // v176: customer whose unpaid slot became unavailable can choose a new time without creating a duplicate booking.
  const [customerReselectBookingV176, setCustomerReselectBookingV176] = useState(null);
  const [customerReselectFormV176, setCustomerReselectFormV176] = useState({ date:"", time:"" });
  const [slotAvailabilityV176, setSlotAvailabilityV176] = useState({});
  // v191: เวลาปกติ 09:00-00:00 เปิด/ปิดตามวันที่จริงใน D1 ไม่ใช่ state จำลองในเครื่อง
  const [regularAvailabilityV191, setRegularAvailabilityV191] = useState({});
  const [seerRegularDateV191, setSeerRegularDateV191] = useState(() => localDateInputValue());
  const [seerRegularSavingV191, setSeerRegularSavingV191] = useState(false);
  // v180: ตารางเวลาพิเศษหลังเที่ยงคืน เปิดเฉพาะหมอดู/วันที่เลือกเท่านั้น
  const [specialNightAvailabilityV180, setSpecialNightAvailabilityV180] = useState({});
  const [seerSpecialDateV180, setSeerSpecialDateV180] = useState(() => localDateInputValue());
  const [seerSpecialSavingV180, setSeerSpecialSavingV180] = useState(false);
  const [customerProfileV138, setCustomerProfileV138] = useState(() => readCustomerProfileV138(getSavedLineUser()?.userId || "") || {});
  const [customerProfileFormV138, setCustomerProfileFormV138] = useState(() => {
    const saved = readCustomerProfileV138(getSavedLineUser()?.userId || "") || {};
    let draft = {};
    try { draft = JSON.parse(localStorage.getItem("dw_customer_register_draft_v138c") || "{}") || {}; } catch {}
    const line = getSavedLineUser() || {};
    return {
      name: draft.name ?? saved.name ?? line.displayName ?? "",
      phone: draft.phone ?? saved.phone ?? "",
      birthdate: draft.birthdate ?? saved.birthdate ?? "",
      lineId: draft.lineId ?? saved.line_id ?? saved.lineId ?? "",
      consent: draft.consent ?? (saved.consent !== false),
    };
  });
  const [myInfoEditModeV139C, setMyInfoEditModeV139C] = useState(false);
  const seerProfileEditingRefV132B = useRef(false);
  const markSeerProfileEditingV132B = () => {
    seerProfileEditingRefV132B.current = true;
    try {
      localStorage.setItem("dw_seer_profile_editing_v133", "yes");
      localStorage.setItem("dw_seer_profile_tab_v133", "profile");
    } catch {}
  };
  const clearSeerProfileEditingV133 = () => {
    seerProfileEditingRefV132B.current = false;
    try { localStorage.removeItem("dw_seer_profile_editing_v133"); } catch {}
  };
  const isSeerProfileEditingNowV133 = () => {
    try {
      return screenRef.current === "seer_dash" && (
        seerProfileEditingRefV132B.current ||
        localStorage.getItem("dw_seer_profile_tab_v133") === "profile" ||
        localStorage.getItem("dw_seer_profile_editing_v133") === "yes"
      );
    } catch {
      return screenRef.current === "seer_dash" && seerProfileEditingRefV132B.current;
    }
  };
  const [liveCallStatus, setLiveCallStatus] = useState("ready");
  const [liveSeconds, setLiveSeconds] = useState(0);
  const [seerApprovedPopup, setSeerApprovedPopup] = useState(false);
  const [seerApprovedPopupIdV155, setSeerApprovedPopupIdV155] = useState("");
  const seerApprovedPopupRefV155 = useRef(false);
  useEffect(() => { seerApprovedPopupRefV155.current = seerApprovedPopup; }, [seerApprovedPopup]);
  const screenRef = useRef(scr);
  useEffect(() => { screenRef.current = scr; }, [scr]);

  const loadSlotAvailabilityV176 = async (seer, date) => {
    const safeDate = String(date || "").slice(0,10);
    if (!seer || !safeDate) return;
    const key = bookingAvailabilityKeyV176(seer, safeDate);
    try {
      const qs = new URLSearchParams({
        availability:"1",
        date:safeDate,
        seer_id:String(seer.seerId || seer.id || seer.seer_id || ""),
        seer_line_user_id:String(seer.lineUserId || seer.line_user_id || seer.seer_line_user_id || ""),
        seer_name:String(seer.name || seer.seer_name || ""),
      });
      const data = await apiGet(`/api/bookings?${qs.toString()}`);
      const slots = Array.isArray(data.blocked_slots) ? data.blocked_slots.map(String) : [];
      setSlotAvailabilityV176(prev => JSON.stringify(prev[key] || []) === JSON.stringify(slots) ? prev : ({ ...prev, [key]:slots }));
    } catch (err) { console.warn("โหลดช่วงเวลาที่ไม่ว่างไม่สำเร็จ", err); }
  };

  const loadRegularAvailabilityV191 = async (seer, date) => {
    const safeDate = String(date || "").slice(0,10);
    if (!seer || !safeDate) return;
    const key = bookingAvailabilityKeyV176(seer, safeDate);
    try {
      const qs = new URLSearchParams({
        date:safeDate,
        seer_id:String(seer.seerId || seer.id || seer.seer_id || ""),
        seer_line_user_id:String(seer.lineUserId || seer.line_user_id || seer.seer_line_user_id || ""),
      });
      const data = await apiGet(`/api/seer-availability?${qs.toString()}`);
      const slots = Array.isArray(data.open_slots) ? data.open_slots.map(String).filter(slot => BOOKING_TIME_SLOTS_V92.includes(slot)) : BOOKING_TIME_SLOTS_V92;
      setRegularAvailabilityV191(prev => JSON.stringify(prev[key] || []) === JSON.stringify(slots) ? prev : ({ ...prev, [key]:slots }));
    } catch (err) { console.warn("โหลดเวลาเปิดรับงานของหมอดูไม่สำเร็จ", err); }
  };

  const saveSeerRegularHoursV191 = async (seer, date, action, value, slot = "") => {
    const safeDate = String(date || "").slice(0,10);
    if (!seer || !safeDate) return;
    setSeerRegularSavingV191(true);
    try {
      const data = await apiPost("/api/seer-availability", {
        action,
        available_date:safeDate,
        seer_id:String(seer.seerId || seer.id || seer.seer_id || ""),
        seer_line_user_id:String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || ""),
        ...(slot ? { time_slot:slot } : {}),
        is_open:!!value,
      });
      const key = bookingAvailabilityKeyV176(seer, safeDate);
      const slots = Array.isArray(data.open_slots) ? data.open_slots.map(String).filter(item => BOOKING_TIME_SLOTS_V92.includes(item)) : BOOKING_TIME_SLOTS_V92;
      setRegularAvailabilityV191(prev => ({ ...prev, [key]:slots }));
    } catch (err) {
      alert("บันทึกเวลาเปิด/ปิดไม่สำเร็จ: " + (err?.message || err));
    } finally {
      setSeerRegularSavingV191(false);
    }
  };

  const loadSpecialNightAvailabilityV180 = async (seer, date) => {
    const safeDate = String(date || "").slice(0,10);
    if (!seer || !safeDate) return;
    const key = bookingAvailabilityKeyV176(seer, safeDate);
    try {
      const qs = new URLSearchParams({
        date:safeDate,
        seer_id:String(seer.seerId || seer.id || seer.seer_id || ""),
        seer_line_user_id:String(seer.lineUserId || seer.line_user_id || seer.seer_line_user_id || ""),
      });
      const data = await apiGet(`/api/seer-special-hours?${qs.toString()}`);
      const slots = Array.isArray(data.open_slots) ? data.open_slots.map(String).filter(slot => SPECIAL_NIGHT_TIME_SLOTS_V180.includes(slot)) : [];
      setSpecialNightAvailabilityV180(prev => JSON.stringify(prev[key] || []) === JSON.stringify(slots) ? prev : ({ ...prev, [key]:slots }));
    } catch (err) { console.warn("โหลดช่วงเวลาพิเศษของหมอดูไม่สำเร็จ", err); }
  };

  const saveSeerSpecialNightHoursV180 = async (seer, date, action, value, slot = "") => {
    const safeDate = String(date || "").slice(0,10);
    if (!seer || !safeDate) return;
    setSeerSpecialSavingV180(true);
    try {
      const data = await apiPost("/api/seer-special-hours", {
        action,
        available_date:safeDate,
        seer_id:String(seer.seerId || seer.id || seer.seer_id || ""),
        seer_line_user_id:String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || ""),
        ...(slot ? { time_slot:slot } : {}),
        is_open:!!value,
      });
      const key = bookingAvailabilityKeyV176(seer, safeDate);
      const slots = Array.isArray(data.open_slots) ? data.open_slots.map(String).filter(item => SPECIAL_NIGHT_TIME_SLOTS_V180.includes(item)) : [];
      setSpecialNightAvailabilityV180(prev => ({ ...prev, [key]:slots }));
    } catch (err) {
      alert("บันทึกช่วงเวลาพิเศษไม่สำเร็จ: " + (err?.message || err));
    } finally {
      setSeerSpecialSavingV180(false);
    }
  };
  useEffect(() => {
    const candidates = [
      [selSeer, bookingForm.requested_date],
      [rescheduleBookingV168, rescheduleFormV168.date],
      [customerReselectBookingV176, customerReselectFormV176.date],
    ];
    candidates.forEach(([seer, date]) => {
      if (seer && date) {
        loadSlotAvailabilityV176(seer, date);
        loadRegularAvailabilityV191(seer, date);
        loadSpecialNightAvailabilityV180(seer, date);
      }
    });
  }, [selSeer?.seerId, selSeer?.id, selSeer?.lineUserId, selSeer?.name, bookingForm.requested_date, rescheduleBookingV168?.booking_id, rescheduleFormV168.date, customerReselectBookingV176?.booking_id, customerReselectFormV176.date]);

  const seerApprovedPopupKeyV155 = (id = "") => `dw_seen_seer_approved_popup_v155_${String(id || "current")}`;
  const hasSeenSeerApprovedPopupV155 = (id = "") => {
    const key = seerApprovedPopupKeyV155(id);
    try {
      if (localStorage.getItem(key) === "yes") return true;
      if (sessionStorage.getItem(key) === "yes") return true;
      if (window.__DW_SEER_APPROVED_POPUP_SEEN_V155__?.[key]) return true;
    } catch {}
    return false;
  };
  const markSeenSeerApprovedPopupV155 = (id = "") => {
    const key = seerApprovedPopupKeyV155(id);
    try {
      localStorage.setItem(key, "yes");
      sessionStorage.setItem(key, "yes");
      window.__DW_SEER_APPROVED_POPUP_SEEN_V155__ = window.__DW_SEER_APPROVED_POPUP_SEEN_V155__ || {};
      window.__DW_SEER_APPROVED_POPUP_SEEN_V155__[key] = true;
    } catch {}
  };
  const closeSeerApprovedPopupV155 = (goDash = false) => {
    const id = seerApprovedPopupIdV155 || (() => { try { return localStorage.getItem("dw_current_approved_seer_id_v155") || ""; } catch { return ""; } })();
    markSeenSeerApprovedPopupV155(id || "current");
    setSeerApprovedPopup(false);
    if (goDash) {
      setScr("seer_dash");
      setActiveNav("seer_dash");
      try { localStorage.setItem("duangwannee_last_screen", "seer_dash"); } catch {}
    }
  };

  const promoteCurrentUserToSeerIfApprovedD1 = async ({ showPopup = false, navigate = false } = {}) => {
    const lineUser = getSavedLineUser();
    if (!lineUser?.userId) return false;

    try {
      const data = await apiGet("/api/seers");
      const seers = (data.seers || []).map(mapApiSeer);
      setDbSeers(seers);

      const approved = seers.find(s => s.lineUserId && s.lineUserId === lineUser.userId);
      if (!approved) return false;

      const nextUser = {
        ...lineUser,
        role: "seer",
        seerId: approved.seerId || approved.id,
        seerName: approved.name,
        approvedSeer: true,
        approvedAt: approved.approvedAt || new Date().toISOString(),
      };

      localStorage.setItem("duangwannee_line_user", JSON.stringify(nextUser));
      localStorage.setItem("duangwannee_line_role", "seer");
      localStorage.setItem("duangwannee_logged_in", "yes");

      setLoggedIn(true);
      setSeerMode(true);

      if (navigate && isSeer) {
        setScr("seer_dash");
        setActiveNav("seer_dash");
      }

      const approvedPopupIdV155 = String(approved.seerId || approved.id || lineUser.userId || "current");
      try { localStorage.setItem("dw_current_approved_seer_id_v155", approvedPopupIdV155); } catch {}

      const currentScreenV155 = screenRef.current;
      const shouldShowApprovedPopupV155 =
        showPopup &&
        !seerApprovedPopupRefV155.current &&
        currentScreenV155 !== "seer_dash" &&
        !hasSeenSeerApprovedPopupV155(approvedPopupIdV155);

      if (shouldShowApprovedPopupV155) {
        // v155: mark ทันทีที่แสดง เพื่อกันเด้งวนจาก polling / remount / LINE webview
        markSeenSeerApprovedPopupV155(approvedPopupIdV155);
        setSeerApprovedPopupIdV155(approvedPopupIdV155);
        setSeerApprovedPopup(true);
      } else if (currentScreenV155 === "seer_dash") {
        // อยู่แดชบอร์ดหมอดูแล้ว ไม่ต้องมี popup ซ้อนหน้า
        markSeenSeerApprovedPopupV155(approvedPopupIdV155);
        setSeerApprovedPopup(false);
      }

      return true;
    } catch (err) {
      console.warn("ตรวจสิทธิ์หมอดูจาก D1 ไม่สำเร็จ", err);
      return false;
    }
  };

  useEffect(() => {
    let alive = true;

    const syncApprovedSeers = async () => {
      if (isSeerProfileEditingNowV133()) return; // v133: หยุด sync ทุกตัวตอนอยู่หน้าโปรไฟล์หมอดู ไม่ให้หลุดโฟกัส
      const latest = readApprovedSeersStore();
      if (alive) setApprovedSeers(prev => JSON.stringify(prev) === JSON.stringify(latest) ? prev : latest);

      const lineUser = getSavedLineUser();
      const hasLoggedInNow = localStorage.getItem("duangwannee_logged_in") === "yes";

      // v140: ไม่ใช้ legacy/local fallback แล้ว
      // สิทธิ์หมอดูต้องมาจาก D1 หลังแอดมินอนุมัติเท่านั้น

      // ของจริง: polling D1 เพื่ออัปเดตสิทธิ์หลังแอดมินอนุมัติจาก PC
      // ถ้าผู้ใช้กำลังอยู่หน้าอื่น เช่น ดูดวง/ประวัติ/ทำเนียบ จะไม่เด้งไปแดชหมอดู
      // จะพาไปแดชหมอดูอัตโนมัติเฉพาะตอนอยู่หน้าแดชลูกค้า/หน้าแรกเท่านั้น
      if (hasLoggedInNow && lineUser?.userId) {
        const currentScreen = screenRef.current;
        if (seerApprovedPopupRefV155.current) return; // v155: มี popup อยู่แล้ว ไม่ให้ polling เรียกซ้ำ
        await promoteCurrentUserToSeerIfApprovedD1({ showPopup: currentScreen !== "seer_dash", navigate: false });
      }
    };

    syncApprovedSeers();
    // v179: แดชหมอดูที่อนุมัติแล้วไม่ต้องตรวจสิทธิ์จาก D1 ทุก 5 วินาที
    // การเรียกซ้ำทำให้ App re-render และ SeerDash ที่เป็น component ภายในถูก remount จนดูเหมือนหน้ากระพริบ
    const onFocusApprovedSeersV179 = () => {
      if (document.visibilityState === "visible") syncApprovedSeers();
    };
    const currentStoredUserV179 = getSavedLineUser() || {};
    const stillWaitingApprovalV179 = !(
      currentStoredUserV179.approvedSeer || currentStoredUserV179.role === "seer"
    );
    window.addEventListener("duangwannee:approved-seers-updated", syncApprovedSeers);
    window.addEventListener("focus", onFocusApprovedSeersV179);
    document.addEventListener("visibilitychange", onFocusApprovedSeersV179);
    // ตรวจเฉพาะบัญชีที่ยังรออนุมัติ และลดความถี่ลงเป็น 60 วินาที
    const timer = stillWaitingApprovalV179 ? setInterval(syncApprovedSeers, 60000) : null;
    return () => {
      alive = false;
      window.removeEventListener("duangwannee:approved-seers-updated", syncApprovedSeers);
      window.removeEventListener("focus", onFocusApprovedSeersV179);
      document.removeEventListener("visibilitychange", onFocusApprovedSeersV179);
      if (timer) clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!["voice", "video"].includes(liveCallStatus)) return;
    const timer = setInterval(() => setLiveSeconds(v => v + 1), 1000);
    return () => clearInterval(timer);
  }, [liveCallStatus]);

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    try {
      const markKey = "dw_v140_reset_old_seer_local_cache";
      if (localStorage.getItem(markKey) !== "yes") {
        APPROVED_SEERS_KEYS.forEach(k => localStorage.removeItem(k));
        localStorage.removeItem("duangwannee_line_role");
        localStorage.removeItem("DW_ACTIVE_SEER_PROFILE_ID_V52");
        localStorage.removeItem("dw_seen_seer_approved_popup");
        localStorage.removeItem("dw_seer_profile_tab_v133");
        localStorage.removeItem("dw_seer_profile_editing_v133");
        const user = getSavedLineUser() || {};
        if (user?.role === "seer" || user?.approvedSeer) {
          const next = {...user};
          delete next.role; delete next.seerId; delete next.seerName; delete next.approvedSeer; delete next.approvedAt;
          localStorage.setItem("duangwannee_line_user", JSON.stringify(next));
        }
        localStorage.setItem(markKey, "yes");
      }
    } catch {}
  }, []);

  const currentLineUser = getSavedLineUser() || {};

  // v141: sync ข้อมูลสมาชิกจาก D1 ข้ามเครื่อง
  // เดิม PC สมัครผ่านแล้ว แต่มือถือยังเห็นหน้าสมัคร เพราะ frontend อ่านจาก localStorage เป็นหลัก
  useEffect(() => {
    let alive = true;
    const syncCustomerProfileV141 = async () => {
      try {
        const lineUser = getSavedLineUser() || {};
        if (!lineUser?.userId) return;
        const data = await apiGet(`/api/customer-profile?line_user_id=${encodeURIComponent(lineUser.userId)}`);
        if (!alive) return;
        const p = data?.profile;
        if (p && (p.name || p.phone || p.birthdate)) {
          const profile = {
            line_user_id: p.line_user_id || lineUser.userId || "",
            line_display_name: p.line_display_name || lineUser.displayName || "",
            line_picture_url: p.line_picture_url || lineUser.pictureUrl || "",
            name: p.name || "",
            phone: p.phone || "",
            birthdate: p.birthdate || "",
            line_id: p.line_id || "",
            consent: Number(p.consent ?? 1) !== 0,
            registered_at: p.registered_at || "",
            updated_at: p.updated_at || "",
          };
          writeCustomerProfileV138(profile);
          setCustomerProfileV138(profile);
          setCustomerProfileFormV138(prev => ({
            ...prev,
            name: profile.name || prev.name || "",
            phone: profile.phone || prev.phone || "",
            birthdate: profile.birthdate || prev.birthdate || "",
            lineId: profile.line_id || prev.lineId || "",
            consent: profile.consent !== false,
          }));
          if (screenRef.current === "customer_register") {
            setScr("home");
            setActiveNav("home");
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete("page");
              window.history.replaceState({screen:"home"}, "", `${url.pathname}${url.search ? url.search : ""}${url.hash || ""}`);
            } catch {}
          }
        }
      } catch (err) {
        console.warn("โหลดข้อมูลสมาชิกจาก D1 ไม่สำเร็จ", err);
      }
    };
    syncCustomerProfileV141();
    window.addEventListener("duangwannee:line-ready", syncCustomerProfileV141);
    return () => { alive = false; window.removeEventListener("duangwannee:line-ready", syncCustomerProfileV141); };
  }, []);

  const belongsToCurrentUserBookingV126 = (b = {}) => {
    const uid = String(currentLineUser?.userId || "").trim();
    if (!uid) return false;
    // หน้าแดชลูกค้าให้ดูเฉพาะรายการที่ user คนนี้เป็น "ลูกค้า" เท่านั้น
    // หมอดูที่เป็นลูกค้าทั่วไปด้วย จะไม่เห็นนัดของ LINE คนอื่นปนเข้ามา
    return String(b.customer_line_user_id || "") === uid || String(b.customer_id || "") === uid;
  };
  const filterCustomerBookingsV126 = (list = []) => (Array.isArray(list) ? list : []).filter(belongsToCurrentUserBookingV126);
  const isRealCustomerBookingV139D = (b = {}) => {
    if (!belongsToCurrentUserBookingV126(b)) return false;
    const id = String(b.booking_id || b.id || "").trim();
    if (!id || id.startsWith("LOCAL")) return false;
    const hasSeer = !!String(b.seer_id || b.seer_line_user_id || b.seer_name || "").trim();
    const hasTime = !!String(b.scheduled_at || b.requested_date || b.requested_time || b.time_slot || "").trim();
    const status = String(b.status || "");
    const isFakePlaceholder =
      String(b.service_name || "").includes("จองคิวดูดวงสด") &&
      (!hasTime || String(b.requested_time || b.time_slot || "").includes("ยังไม่ระบุ")) &&
      (!status || status === "pending_request");
    return hasSeer && hasTime && !isFakePlaceholder;
  };
  const getRealCustomerBookingsV139D = (...lists) => filterCustomerBookingsV126(mergeBookingListsV101(...lists)).filter(isRealCustomerBookingV139D);
  const clearOldBookingLocalCacheV126B = () => {
    try {
      // v139D: ล้าง local booking cache ใหม่ทุกเครื่อง 1 ครั้ง เพื่อรีเซ็ต “นัดของฉัน”
      const markKey = "dw_v139d_all_booking_cache_cleared";
      if (localStorage.getItem(markKey) === "yes") return;
      [
        "dw_customer_booking_history_v101",
        "dw_my_booking_ids_v100",
        "dw_v126b_old_bookings_cleared"
      ].forEach(k => localStorage.removeItem(k));
      Object.keys(localStorage).forEach(k => {
        if (String(k || "").startsWith("dw_booking_chat_local_")) localStorage.removeItem(k);
        if (String(k || "").startsWith("dw_fortune_room_local_")) localStorage.removeItem(k);
      });
      localStorage.setItem(markKey, "yes");
    } catch {}
  };
  useEffect(() => {
    // v170: อย่าล้าง/กรองรายการของหมอดูตอน LIFF เพิ่งเปิด เพราะ role จาก D1 มาช้ากว่า render แรก
    // เดิมทำให้ API โหลดรายการของหมอดูแล้วถูกกรองทิ้งเหมือนเป็นรายการลูกค้า จึงต้องรอรอบ polling ถัดไป
    if (isSeer || seerMode || screenRef.current === "seer_dash") return;
    clearOldBookingLocalCacheV126B();
    setCustomerBookingListV104([]);
    setBookings(prev => filterCustomerBookingsV126(prev));
    setBookingHistoryTickV103(t => t + 1);
  }, [currentLineUser?.userId, isSeer, seerMode]);

  useEffect(() => {
    // v182: รีเซ็ตประวัติการจอง/แชท/รายได้ทดสอบทั้งฝั่งลูกค้าและหมอดูบนแต่ละเครื่อง 1 ครั้ง
    // ใช้คู่กับ SQL v182 เพื่อไม่ให้รายการจองเก่าจาก localStorage / sessionStorage กลับมาแสดงหลังล้าง D1 แล้ว
    try {
      // v188: ผู้ใช้ขอเริ่มระบบหมอดู/สายแนะนำ/การจองใหม่ทั้งหมด
      // ล้างเฉพาะ cache ในเครื่อง เพื่อไม่ให้หมอดูหรือรายการนัดเก่ากลับมาแสดงหลัง SQL reset
      const key = "dw_v188_reset_seers_referrals_bookings_done";
      if (localStorage.getItem(key) === "yes") return;
      [
        "dw_customer_booking_history_v101",
        "dw_my_booking_ids_v100",
        "dw_orders",
        "dw_chat_alerts",
        "dw_v170_booking_test_reset_done",
        "dw_v171_all_booking_test_data_reset_done",
        "dw_v172_all_booking_test_data_reset_done",
        "dw_v173_all_booking_test_data_reset_done",
        "dw_v174_all_booking_test_data_reset_done",
        "dw_v175_all_booking_test_data_reset_done",
        "dw_v176_all_booking_test_data_reset_done",
        "dw_v177_all_booking_test_data_reset_done",
        "dw_v178_all_booking_test_data_reset_done",
        "dw_v179_all_booking_test_data_reset_done",
        "dw_v180_all_booking_test_data_reset_done",
        "dw_v181_all_booking_test_data_reset_done",
        "dw_v182_all_booking_test_data_reset_done",
        "dw_seer_referrals_v44",
        "dw_seer_invites_v159",
        "dw_seer_platform_levels_v159",
        "duangwannee_line_role",
        "DW_ACTIVE_SEER_PROFILE_ID_V52",
        "dw_v139d_all_booking_cache_cleared"
      ].forEach(k => localStorage.removeItem(k));
      // หลัง reset หมอดูทั้งหมด ฝั่ง LIFF ต้องไม่คง role หมอดูเก่าจาก localStorage
      try {
        APPROVED_SEERS_KEYS.forEach(k => localStorage.removeItem(k));
        const saved = getSavedLineUser() || {};
        if (saved?.role === "seer" || saved?.approvedSeer || saved?.seerId) {
          const next = { ...saved };
          delete next.role; delete next.seerId; delete next.seerName; delete next.approvedSeer; delete next.approvedAt;
          localStorage.setItem("duangwannee_line_user", JSON.stringify(next));
        }
      } catch {}
      Object.keys(localStorage).forEach(k => {
        const name = String(k || "");
        if (
          name.startsWith("dw_booking_chat_local_") ||
          name.startsWith("dw_fortune_room_local_") ||
          name.startsWith("dw_booking_")
        ) localStorage.removeItem(k);
      });
      Object.keys(sessionStorage).forEach(k => {
        const name = String(k || "");
        if (name.startsWith("dw_booking_") || name.startsWith("dw_fortune_room_")) sessionStorage.removeItem(k);
      });
      localStorage.setItem(key, "yes");
      setOrders([]);
      setBookings([]);
      setCustomerBookingListV104([]);
      setSelectedBooking(null);
      setNewJobDetailsV175(null);
      setSeerQueueDetailsV178(null);
      setPaymentPopupBookingV106(null);
      setBookingChatPopupOpen(false);
      setBookingHistoryTickV103(t => t + 1);
    } catch {}
  }, []);
  const DW_BOOKING_HISTORY_KEY_V101 = "dw_customer_booking_history_v101";
  const readBookingHistoryV101 = () => {
    try {
      const list = JSON.parse(localStorage.getItem(DW_BOOKING_HISTORY_KEY_V101) || "[]");
      return Array.isArray(list) ? list : [];
    } catch { return []; }
  };
  const saveBookingHistoryV101 = (booking) => {
    if (!booking?.booking_id) return;
    try {
      const oldList = readBookingHistoryV101();
      const merged = [booking, ...oldList.filter(x => String(x?.booking_id || "") !== String(booking.booking_id || ""))];
      localStorage.setItem(DW_BOOKING_HISTORY_KEY_V101, JSON.stringify(merged.slice(0, 120)));
      const oldIds = JSON.parse(localStorage.getItem("dw_my_booking_ids_v100") || "[]");
      const ids = Array.from(new Set([booking.booking_id, ...oldIds].filter(Boolean).map(String)));
      localStorage.setItem("dw_my_booking_ids_v100", JSON.stringify(ids.slice(0, 120)));
    } catch {}
  };
  const mergeBookingListsV101 = (apiList = [], localList = []) => {
    const map = new Map();
    [...localList, ...apiList].forEach(item => {
      if (!item?.booking_id) return;
      const id = String(item.booking_id);
      map.set(id, { ...(map.get(id) || {}), ...item });
    });
    return Array.from(map.values()).sort((a,b)=>String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
  };
  const forceAddBookingHistoryV102 = (bookingLike = {}) => {
    const now = new Date().toISOString();
    const fallback = {
      booking_id: bookingLike.booking_id || ("LOCAL" + Date.now()),
      customer_id: currentLineUser?.userId || "CUSTOMER",
      customer_line_user_id: currentLineUser?.userId || "",
      customer_name: bookingLike.customer_name || currentLineUser?.displayName || form?.name || "ลูกค้า",
      seer_id: bookingLike.seer_id || selSeer?.seerId || selSeer?.id || "",
      seer_line_user_id: bookingLike.seer_line_user_id || selSeer?.lineUserId || selSeer?.line_user_id || "",
      seer_name: bookingLike.seer_name || selSeer?.name || "หมอดู",
      service_name: bookingLike.service_name || selSvc?.name || "จองคิวดูดวงสด",
      price: Number(bookingLike.price || selSvc?.price || 999),
      requested_date: bookingLike.requested_date || bookingForm.requested_date || document.getElementById("dwBookingDateV91")?.value || "",
      requested_time: bookingLike.requested_time || bookingForm.requested_time || document.getElementById("dwBookingTimeV91")?.value || "",
      scheduled_at: bookingLike.scheduled_at || [bookingLike.requested_date || bookingForm.requested_date || "", bookingLike.requested_time || bookingForm.requested_time || ""].filter(Boolean).join(" "),
      customer_note: bookingLike.customer_note || bookingForm.customer_note || "",
      status: bookingLike.status || "pending_request",
      created_at: bookingLike.created_at || now,
      updated_at: bookingLike.updated_at || now,
      ...bookingLike,
    };
    saveBookingHistoryV101(fallback);
    setBookings(prev => mergeBookingListsV101([fallback], prev));
    setCustomerBookingListV104(prev => mergeBookingListsV101([fallback], prev));
    return fallback;
  };
  const showBookingHistoryNowV103 = (bookingLike = {}) => {
    const b = forceAddBookingHistoryV102(bookingLike);
    setSelectedBooking(b);
    setBookings(prev => mergeBookingListsV101([b], prev));
    setCustomerBookingListV104(prev => mergeBookingListsV101([b], prev));
    setBookingHistoryTickV103(t => t + 1);
    return b;
  };
  const refreshCustomerBookingListV104 = async () => {
    try {
      const qs = new URLSearchParams({ limit:"200", line_user_id: currentLineUser?.userId || "" });
      const data = await apiGet(`/api/bookings?${qs.toString()}`);
      // v176: D1 is authoritative. Do not merge stale local booking cache back into the schedule.
      const apiList = filterCustomerBookingsV126(Array.isArray(data.bookings) ? data.bookings : []);
      setBookings(apiList);
      setCustomerBookingListV104(apiList);
      setBookingHistoryTickV103(t => t + 1);
      return apiList;
    } catch (err) {
      console.warn("โหลดรายการนัดจาก D1 ไม่สำเร็จ", err);
      setBookings([]);
      setCustomerBookingListV104([]);
      setBookingHistoryTickV103(t => t + 1);
      return [];
    }
  };
  const loadPaymentSettingsV106 = async () => {
    try {
      const data = await apiGet("/api/payment-settings");
      setPaymentSettingsV106(data.settings || data || null);
    } catch (err) {
      console.warn("โหลดเลขบัญชีรับเงินไม่สำเร็จ", err);
    }
  };
  useEffect(() => { loadPaymentSettingsV106(); }, []);

  const bookingListSignatureV114 = (list = []) => {
    try {
      return JSON.stringify((list || []).map(b => ({
        id:b.booking_id,
        st:b.status,
        up:b.updated_at,
        room:b.room_id,
        slip:b.slip_url,
        deadline:b.payment_deadline_at,
        remind:b.room_reminder_sent_at
      })).sort((a,b)=>String(a.id).localeCompare(String(b.id))));
    } catch { return String(Date.now()); }
  };
  const loadBookingsV91 = async ({ forceSeer = false } = {}) => {
    const loadAsSeer = !!(forceSeer || isSeer || seerMode || scr === "seer_dash");
    const lineUserId = String(currentLineUser?.userId || "").trim();
    if (!lineUserId) {
      if (loadAsSeer) setBookings([]);
      return [];
    }
    try {
      if (loadAsSeer) {
        const params = new URLSearchParams({ line_user_id: lineUserId });
        if (initialBookingId) params.set("booking_id", String(initialBookingId));
        const data = await apiGet(`/api/seer-queue?${params.toString()}`);
        const apiBookings = Array.isArray(data.bookings) ? data.bookings : [];
        if (data?.seer) setLockedSeerProfile(mapApiSeer(data.seer));
        // D1 is authoritative. Do not filter the queue with local profile/cache state.
        setBookings(prev => bookingListSignatureV114(prev) === bookingListSignatureV114(apiBookings) ? prev : apiBookings);
        return apiBookings;
      }
      const qs = new URLSearchParams({ limit:"100", line_user_id: lineUserId });
      const data = await apiGet(`/api/bookings?${qs.toString()}`);
      const apiBookings = filterCustomerBookingsV126(Array.isArray(data.bookings) ? data.bookings : []);
      setBookings(prev => bookingListSignatureV114(prev) === bookingListSignatureV114(apiBookings) ? prev : apiBookings);
      setCustomerBookingListV104(apiBookings);
      return apiBookings;
    } catch (err) {
      console.warn("โหลดรายการจองจาก D1 ไม่สำเร็จ", err);
      // Never revive booking history/local storage after a failed request.
      return [];
    }
  };

  useEffect(() => {
    let alive = true;
    let retryFast = null;
    let retrySafe = null;
    let timer = null;
    const isSeerFlow = !!(isSeer || seerMode || scr === "seer_dash");
    const run = async () => {
      if (!alive) return;
      // v115: ถ้า popup แชทเปิดอยู่ ไม่โหลดข้อมูลซ้ำระหว่างพิมพ์ เพื่อไม่ให้ popup กระพริบ
      if (bookingChatPopupOpen) return;
      // v132B: ถ้าหมอดูกำลังแก้โปรไฟล์ ห้าม polling/re-render เพราะทำให้ช่องกรอกหลุดโฟกัสบนมือถือ
      if (isSeerProfileEditingNowV133()) return;
      // v182: ขณะเปิดรายละเอียดคิวงาน ให้หยุด refresh เบื้องหลังชั่วคราว
      // ป้องกัน DOM ของแดชหมอดูถูกอัปเดตจนผู้ใช้เห็นเหมือนหน้ารีเฟรช
      if (isSeerFlow && seerQueueDetailsV178?.booking_id) return;
      if (!isSeerFlow && scr !== "seer_dash") {
        try { await apiPost("/api/booking-maintenance", { line_user_id: currentLineUser?.userId || "" }); } catch {}
      }
      await loadBookingsV91({ forceSeer: isSeerFlow });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    run();
    // หน้าแดชหมอดูไม่ต้องรอ 90 วินาที: ยิงซ้ำสั้น ๆ หลังเปิด LIFF เพื่อรอ profile/LINE state พร้อม
    if (isSeerFlow) {
      retryFast = setTimeout(run, 900);
      retrySafe = setTimeout(run, 2600);
    } else {
      timer = setInterval(run, 30000);
    }
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      if (retryFast) clearTimeout(retryFast);
      if (retrySafe) clearTimeout(retrySafe);
      if (timer) clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [currentLineUser?.userId, scr, bookingChatPopupOpen, seerMode, isSeer, seerQueueDetailsV178?.booking_id]);

  // v177: ระหว่างหมอดูรอลูกค้ายอมรับเวลาใหม่ ให้ตรวจสถานะแบบเฉพาะกิจทุก 3 วินาที
  // เมื่อสถานะเปลี่ยนเป็น confirmed จะหยุดเอง จึงไม่ทำให้แดชหมอดูดึงข้อมูลวนตลอดเวลา
  useEffect(() => {
    const isSeerDashboardV177 = !!(isSeer || seerMode || scr === "seer_dash");
    if (!isSeerDashboardV177 || bookingChatPopupOpen || seerQueueDetailsV178?.booking_id) return;
    const hasPendingRescheduleV177 = (bookings || []).some(b => String(b.status || "") === "seer_proposed_new_time");
    if (!hasPendingRescheduleV177) return;
    let stopped = false;
    let loading = false;
    const syncPendingRescheduleV177 = async () => {
      if (stopped || loading || document.visibilityState !== "visible") return;
      loading = true;
      try { await loadBookingsV91({ forceSeer:true }); } finally { loading = false; }
    };
    const timer = setInterval(syncPendingRescheduleV177, 3000);
    return () => { stopped = true; clearInterval(timer); };
  }, [isSeer, seerMode, scr, bookingChatPopupOpen, currentLineUser?.userId, seerQueueDetailsV178?.booking_id, bookingListSignatureV114(bookings)]);

  useEffect(() => {
    // v170: ลิงก์จาก LINE OA/Push ที่มี booking_id ต้องเปิดแดชหมอดูและโหลดงานทันที
    // รองรับทั้ง /seer และลิงก์ LIFF เดิมที่เป็น /?page=seer_dash
    const isSeerBookingLink = !!initialBookingId && (
      isSeer || initialScreen === "seer_dash" || ["booking_request", "today_jobs", "booking_confirmed"].includes(String(initialOpenAction || ""))
    );
    if (!isSeerBookingLink) return;
    let alive = true;
    setScr("seer_dash");
    setActiveNav("seer_dash");
    setSeerTabRaw(["today_jobs", "booking_confirmed"].includes(String(initialOpenAction || "")) ? "jobs" : "booking_requests");
    const loadDirectBooking = () => { if (alive) loadBookingsV91({ forceSeer:true }); };
    loadDirectBooking();
    const retry1 = setTimeout(loadDirectBooking, 700);
    const retry2 = setTimeout(loadDirectBooking, 2200);
    return () => { alive = false; clearTimeout(retry1); clearTimeout(retry2); };
  }, [isSeer, initialScreen, initialBookingId, initialOpenAction, currentLineUser?.userId]);

  useEffect(() => {
    if (initialOpenAction !== "payment" || !initialBookingId || seerMode) return;
    let alive = true;
    const run = async () => {
      try {
        const qs = new URLSearchParams({ limit:"200", line_user_id: currentLineUser?.userId || "" });
        const data = await apiGet(`/api/bookings?${qs.toString()}`);
        if (!alive) return;
        const found = (data.bookings || []).find(x => String(x.booking_id || "") === String(initialBookingId));
        if (found) {
          setSelectedBooking(found);
          setCustomerPayContactV122(p => ({
            phone: p.phone || found.customer_phone || "",
            lineId: p.lineId || found.customer_line_id || "",
            preferredMethods: p.preferredMethods || String(found.customer_contact_preference || "phone,line").split(","),
            consent: p.consent !== false,
          }));
          setPaymentPopupBookingV106(found);
          setScr("my_bookings");
          setActiveNav("my_bookings");
        }
      } catch (err) {
        console.warn("เปิดลิงก์โอนเงินจาก LINE ไม่สำเร็จ", err);
      }
    };
    run();
    return () => { alive = false; };
  }, [initialOpenAction, initialBookingId, currentLineUser?.userId, seerMode]);

  // v3.9: LINE notification for an incoming voice call opens the booked room directly.
  // The booking is always re-read from D1; no local booking record is trusted here.
  useEffect(() => {
    if (initialOpenAction !== "booking_room" || !initialBookingId) return;
    let alive = true;
    const openVoiceRoomFromLine = async () => {
      const lineUserId = String(currentLineUser?.userId || "").trim();
      if (!lineUserId || !alive) return;
      try {
        const qs = new URLSearchParams({ limit:"200", line_user_id: lineUserId });
        const data = await apiGet(`/api/bookings?${qs.toString()}`);
        if (!alive) return;
        const found = (data.bookings || []).find(x => String(x.booking_id || "") === String(initialBookingId));
        if (!found) return;
        await openBookingRoomV91(found);
      } catch (err) {
        console.warn("เปิดห้องโทรจาก LINE ไม่สำเร็จ", err);
      }
    };
    openVoiceRoomFromLine();
    const retry = setTimeout(openVoiceRoomFromLine, 1200);
    return () => { alive = false; clearTimeout(retry); };
  }, [initialOpenAction, initialBookingId, currentLineUser?.userId]);
  useEffect(() => {
    // v117: countdown 1 วิ ห้ามทำงานตอนหมอดูกำลังตอบแชท เพราะทำให้ popup re-render และหลุดโฟกัส
    const needCountdown = !seerMode && (
      (scr === "my_bookings" && !bookingChatPopupOpen) ||
      (scr === "booking_chat" && !bookingChatPopupOpen)
    );
    if (!needCountdown) return;
    const t = setInterval(() => setBookingNowTickV113(Date.now()), 1000);
    return () => clearInterval(t);
  }, [scr, !!paymentPopupBookingV106, !!bookingChatPopupOpen, seerMode]);

  useEffect(() => {
    if (!bookingChatPopupOpen) return;
    // v117: lock body scroll เท่านั้น ห้าม setState ตาม visualViewport resize/scroll
    // เพราะทุก resize/scroll ทำให้ App re-render และช่องแชทหลุดโฟกัส
    const doc = document.documentElement;
    const body = document.body;
    const prevBodyOverflow = body.style.overflow;
    const prevDocOverflow = doc.style.overflow;
    const prevBodyHeight = body.style.height;
    const prevDocHeight = doc.style.height;
    body.style.overflow = "hidden";
    doc.style.overflow = "hidden";
    body.style.height = "100%";
    doc.style.height = "100%";
    let style = document.getElementById("dw-chat-popup-stable-style-v117");
    if (!style) {
      style = document.createElement("style");
      style.id = "dw-chat-popup-stable-style-v117";
      style.textContent = `
        html:has(.dw-chat-popup-v117), body:has(.dw-chat-popup-v117){ overflow:hidden !important; }
        body:has(.dw-chat-popup-v117) .starfield,
        body:has(.dw-chat-popup-v117) .stars,
        body:has(.dw-chat-popup-v117) .zodiac-bg,
        body:has(.dw-chat-popup-v117) .meteor,
        body:has(.dw-chat-popup-v117) .shooting-star{ animation-play-state: paused !important; }
      `;
      document.head.appendChild(style);
    }
    return () => {
      body.style.overflow = prevBodyOverflow;
      doc.style.overflow = prevDocOverflow;
      body.style.height = prevBodyHeight;
      doc.style.height = prevDocHeight;
    };
  }, [bookingChatPopupOpen]);
  const createBookingV91 = async () => {
    if (!selSeer) { alert("กรุณาเลือกหมอดูก่อน"); return; }
    const date = bookingForm.requested_date || document.getElementById("dwBookingDateV91")?.value || "";
    const time = bookingForm.requested_time || document.getElementById("dwBookingTimeV91")?.value || "";
    const customerName = bookingForm.customer_name || document.getElementById("dwBookingNameV91")?.value || currentLineUser?.displayName || form.name || "ลูกค้า";
    // v183: อ่านจาก DOM ก่อน เพื่อให้ช่องรายละเอียดเป็น uncontrolled input
    // จึงไม่ re-render ทุกตัวอักษรและไม่หลุดโฟกัสบนมือถือ
    const note = document.getElementById("dwBookingNoteV91")?.value || bookingForm.customer_note || "";
    if (!date || !time) { alert("กรุณาเลือกวันและเวลา"); return; }
    if (!String(note).trim()) { alert("กรุณาระบุเรื่องที่ต้องการดูดวง เพื่อให้หมอดูเตรียมข้อมูลเบื้องต้น"); return; }
    if (isSlotTooCloseForTodayV120(date, time)) { alert("เวลานี้ผ่านไปแล้วหรือใกล้เกินไป กรุณาเลือกเวลาใหม่"); return; }
    const bookingPackagesV130 = getBookingServicesForSeerV109(selSeer).slice(0, 5);
    const svc = bookingPackagesV130.find(x => String(x.id) === String(bookingForm.package_id)) || selSvc || bookingPackagesV130[0] || getServicesForSeer(selSeer).find(x=>x.input==="booking") || {name:"จองคิวดูดวงสด", price:Number(selSeer?.minPrice||199), minutes:30};
    const seerIdKeysV156 = [selSeer?.seerId, selSeer?.id, selSeer?.lineUserId, selSeer?.line_user_id, selSeer?.name].filter(Boolean).map(String);
    const wantedKeysV191 = new Set(bookingSlotKeysV191({requested_date:date,requested_time:time,...svc}));
    const blockedV156 = (bookings || []).some(b => {
      const sameSeer = seerIdKeysV156.includes(String(b.seer_id || "")) || seerIdKeysV156.includes(String(b.seer_line_user_id || "")) || seerIdKeysV156.includes(String(b.seer_name || ""));
      return sameSeer && isSlotBlockedStatusV113(b.status) && bookingSlotKeysV191(b).some(key=>wantedKeysV191.has(key));
    });
    if (blockedV156) { alert("ช่วงเวลานี้ไม่ว่างแล้ว กรุณาเลือกเวลาอื่น"); return; }
    setBookingLoading(true);
    try {
      const data = await apiPost("/api/bookings", {
        status: "waiting_payment",
        payment_deadline_minutes: 15,
        customer_id: currentLineUser?.userId || "CUSTOMER",
        customer_line_user_id: currentLineUser?.userId || "",
        customer_name: customerName,
        customer_note: note,
        // v178: เก็บวันเดือนปีเกิดไว้กับรายการจอง เพื่อให้หมอดูเตรียมข้อมูลก่อนถึงเวลานัด
        customer_birthdate: customerProfileV138?.birthdate || customerProfileFormV138?.birthdate || "",
        seer_id: selSeer.seerId || selSeer.id,
        seer_line_user_id: selSeer.lineUserId || selSeer.line_user_id || "",
        seer_line_id: selSeer.lineId || selSeer.line_id || "",
        seer_line_url: selSeer.lineUrl || selSeer.line_url || "",
        seer_name: selSeer.name,
        service_name: svc.name || `ดูดวงสด ${serviceDurationV191(svc)} นาที`,
        service_detail: svc.desc || "นัดดูดวงในห้องแชทของดวงวันนี้",
        service_duration_minutes: serviceDurationV191(svc),
        reserved_slot_count: reservedSlotCountV191(svc),
        price: Number(svc.price || 999),
        requested_date: date,
        requested_time: time,
        scheduled_at: bookingActualRangeV191(date, time, svc),
      });
      const createdBooking = data.booking;
      if (createdBooking?.booking_id) {
        saveBookingHistoryV101(createdBooking);
        setSelectedBooking(createdBooking);
        setBookings(prev => mergeBookingListsV101([createdBooking], prev));
        setCustomerBookingListV104(prev => mergeBookingListsV101([createdBooking], prev));
        setBookingForm({ customer_name:"", requested_date:"", requested_time:"", customer_note:"", package_id:"" });
        setCustomerPayContactV122(p => ({
          phone: p.phone || createdBooking.customer_phone || customerProfileV138?.phone || "",
          lineId: p.lineId || createdBooking.customer_line_id || customerProfileV138?.line_id || "",
          preferredMethods: p.preferredMethods || String(createdBooking.customer_contact_preference || "phone,line").split(","),
          consent: p.consent !== false,
        }));
        setPaymentPopupBookingV106(createdBooking);
        setActiveNav("my_bookings");
        go("my_bookings");
      } else {
        alert("สร้างรายการจองไม่สำเร็จ กรุณาลองใหม่");
      }
    } catch (err) {
      alert("สร้างรายการจองไม่สำเร็จ: " + (err.message || err));
    } finally { setBookingLoading(false); }
  };
  const bookingActionV91 = async (booking, action, extra = {}) => {
    if (!booking?.booking_id) return;
    setBookingLoading(true);
    try {
      const data = await apiPost("/api/booking-action", { booking_id: booking.booking_id, action, actor_line_user_id: currentLineUser?.userId || "", ...extra });
      // v177: ใช้ผลลัพธ์จาก API อัปเดตหน้าจอทันที ไม่รอรอบโหลดรายการนัดอีกครั้ง
      // สำคัญมากสำหรับจังหวะลูกค้ายอมรับเวลาใหม่ เพื่อให้สถานะคิวเปลี่ยนบนเครื่องลูกค้าทันที
      if (data.booking) {
        saveBookingHistoryV101(data.booking);
        setSelectedBooking(data.booking);
        setBookings(prev => {
          const merged = mergeBookingListsV101([data.booking], prev);
          return bookingListSignatureV114(prev) === bookingListSignatureV114(merged) ? prev : merged;
        });
        setCustomerBookingListV104(prev => mergeBookingListsV101([data.booking], prev));
      }
      if (action === "complete" && String(data?.booking?.status || "") === "completed") {
        setSeerTab("live");
        setActiveNav("seer_dash");
        go("seer_job_history");
        alert("ปิดงานสำเร็จแล้ว ระบบบันทึกรายได้และย้ายรายการไปที่ประวัติงานที่รับเรียบร้อย");
      }
      if (data.booking && bookingChatPopupOpen) {
        try {
          const qs = new URLSearchParams({ booking_id:data.booking.booking_id || "", line_user_id:currentLineUser?.userId || "" });
          const chatData = await apiGet(`/api/booking-chat?${qs.toString()}`);
          setBookingChatMessagesStableV117(chatData.messages || []);
        } catch {}
      }
      // ตรวจข้อมูลจริงซ้ำแบบ background เท่านั้น จึงไม่หน่วงการเปลี่ยนหน้าจอหลังผู้ใช้กดปุ่ม
      void loadBookingsV91();
      return data;
    } catch (err) {
      // A network/provider error can happen after the backend has safely committed
      // an idempotent completion.  Reload D1 once for this action so the seer never
      // remains on a stale legacy booking screen or retries blindly.
      if (action === "complete") {
        try { await loadBookingsV91({ forceSeer: true }); } catch {}
      }
      alert("ดำเนินการไม่สำเร็จ: " + (err.message || err));
    } finally { setBookingLoading(false); }
  };
  const seerConfirmBookingV91 = (b) => bookingActionV91(b, "confirm");
  const seerRejectBookingV91 = (b) => {
    const reject_reason = prompt("เหตุผลที่ปฏิเสธ", "เวลานี้ไม่สะดวก") || "";
    if (!reject_reason.trim()) return;
    bookingActionV91(b, "reject", { reject_reason });
  };
  const seerProposeBookingV91 = (b) => {
    if (!b?.booking_id) return;
    setRescheduleFormV168({
      date: b.requested_date || localDateInputValue(),
      time: b.requested_time || "",
      note: "เวลาที่ลูกค้าเลือกไม่สะดวก ขอเสนอเวลานี้แทนค่ะ",
    });
    setRescheduleBookingV168(b);
  };
  const submitSeerRescheduleV168 = async () => {
    const b = rescheduleBookingV168;
    const date = String(rescheduleFormV168.date || "").trim();
    const time = String(rescheduleFormV168.time || "").trim();
    const note = String(rescheduleFormV168.note || "").trim();
    if (!b?.booking_id || !date || !time) { alert("กรุณาเลือกวันและเวลาใหม่"); return; }
    if (isSlotTooCloseForTodayV120(date, time)) { alert("เวลาใหม่นี้ผ่านไปแล้วหรือใกล้เกินไป กรุณาเลือกเวลาใหม่"); return; }
    const result = await bookingActionV91(b, "propose_new_time", { proposed_date:date, proposed_time:time, proposed_note:note });
    if (result?.booking) {
      setRescheduleBookingV168(null);
      alert("ส่งคำขอเลื่อนเวลาแล้ว ระบบแจ้งลูกค้าและแอดมินเรียบร้อย");
    }
  };
  const openCustomerReselectSlotV176 = (b) => {
    if (!b?.booking_id) return;
    setCustomerReselectFormV176({ date: b.requested_date || localDateInputValue(), time: "" });
    setCustomerReselectBookingV176(b);
  };
  const submitCustomerReselectSlotV176 = async () => {
    const b = customerReselectBookingV176;
    const date = String(customerReselectFormV176.date || "").trim();
    const time = String(customerReselectFormV176.time || "").trim();
    if (!b?.booking_id || !date || !time) { alert("กรุณาเลือกวันและเวลาใหม่"); return; }
    if (isSlotTooCloseForTodayV120(date, time)) { alert("เวลานี้ผ่านไปแล้วหรือใกล้เกินไป กรุณาเลือกเวลาใหม่"); return; }
    const result = await bookingActionV91(b, "customer_reselect_slot", { requested_date:date, requested_time:time });
    if (result?.booking) {
      setCustomerReselectBookingV176(null);
      alert("บันทึกเวลาใหม่แล้ว กรุณาแนบสลิปเพื่อยืนยันการจอง");
      openPaymentStableV108(result.booking);
    }
  };

  const readCustomerPaymentContactV123 = (bookingId = "") => {
    const suffix = bookingId || "booking";
    const phoneEl = document.getElementById(`customerPhoneInputV123_${suffix}`);
    const lineEl = document.getElementById(`customerLineInputV123_${suffix}`);
    const phonePreferenceEl = document.getElementById(`customerContactPhonePreferenceV175_${suffix}`);
    const linePreferenceEl = document.getElementById(`customerContactLinePreferenceV175_${suffix}`);
    const consentEl = document.getElementById(`customerConsentInputV123_${suffix}`);
    const fallbackMethods = Array.isArray(customerPayContactV122.preferredMethods)
      ? customerPayContactV122.preferredMethods
      : String(customerPayContactV122.preferredMethods || "phone,line").split(",");
    const preferredMethods = [
      (phonePreferenceEl ? !!phonePreferenceEl.checked : fallbackMethods.includes("phone")) ? "phone" : "",
      (linePreferenceEl ? !!linePreferenceEl.checked : fallbackMethods.includes("line")) ? "line" : ""
    ].filter(Boolean);
    return {
      phone: String(phoneEl?.value || customerPayContactV122.phone || "").trim(),
      lineId: String(lineEl?.value || customerPayContactV122.lineId || "").trim(),
      preferredMethods,
      consent: consentEl ? !!consentEl.checked : customerPayContactV122.consent !== false,
    };
  };
  const customerUploadSlipV91 = async (b, file) => {
    file = file || pendingSlipFileRefV127.current;
    if (!file) { alert("กรุณาเลือกไฟล์สลิปก่อน"); return; }
    const bookingId = b?.booking_id || selectedBooking?.booking_id || "";
    if (!bookingId) { alert("ไม่พบเลขใบนัด กรุณากลับไปเปิดรายการนัดอีกครั้ง"); return; }
    const payContact = readCustomerPaymentContactV123(bookingId);
    setCustomerPayContactV122({phone: payContact.phone, lineId: payContact.lineId, preferredMethods: payContact.preferredMethods, consent: payContact.consent});
    if (!payContact.preferredMethods.length) { alert("กรุณาเลือกช่องทางที่สะดวกให้หมอดูติดต่อกลับอย่างน้อย 1 ช่องทาง"); return; }
    if (payContact.preferredMethods.includes("phone") && !payContact.phone) { alert("กรุณากรอกเบอร์โทรลูกค้า เพราะเลือกให้ติดต่อกลับทางเบอร์โทร"); return; }
    if (payContact.preferredMethods.includes("line") && !payContact.lineId) { alert("กรุณากรอก LINE ID ลูกค้า เพราะเลือกให้ติดต่อกลับทาง LINE"); return; }
    if (!payContact.consent) { alert("กรุณายินยอมให้บันทึกข้อมูลลูกค้าสำหรับติดต่อตามนัดและทำการตลาดก่อน"); return; }
    setBookingLoading(true);
    setSlipUploadStatusV110("กำลังอัปโหลดสลิป...");
    try {
      let data = null;
      try {
        setSlipUploadStatusV110("กำลังส่งสลิปเข้าระบบ...");
        data = await uploadSlipDirectV110(file, bookingId, currentLineUser?.userId || "", payContact);
      } catch (directErr) {
        // v176: a slot conflict is a real booking rule, not an upload failure. Do not fall back and upload again.
        if (Number(directErr?.status || 0) === 409 || /เวลานี้ไม่ว่าง|เลือกเวลาอื่น|เลือกวันอื่น/.test(String(directErr?.message || ""))) throw directErr;
        console.warn("upload-slip direct failed, fallback to /api/upload", directErr);
        setSlipUploadStatusV110("กำลังอัปโหลดไฟล์สำรอง...");
        try {
          const up = await uploadFileToR2(file, { ownerType:"booking", ownerId:bookingId, fileRole:"payment_slip" });
          data = await bookingActionV91(b, "upload_slip", { slip_url: up.url || up.public_url || "", slip_r2_key: up.key || up.r2_key || "", customer_phone: payContact.phone, customer_line_id: payContact.lineId, marketing_consent: payContact.consent, customer_contact_preference: payContact.preferredMethods.join(",") });
        } catch (r2Err) {
          console.warn("R2 upload failed, fallback to compressed local slip", r2Err);
          setSlipUploadStatusV110("กำลังบันทึกสลิปแบบสำรอง...");
          const slipDataUrl = await fileToCompressedDataUrlV110(file);
          data = await bookingActionV91(b, "upload_slip", { slip_url: slipDataUrl, slip_r2_key: "local-data-url", customer_phone: payContact.phone, customer_line_id: payContact.lineId, marketing_consent: payContact.consent, customer_contact_preference: payContact.preferredMethods.join(",") });
        }
      }

      const updatedBooking = data?.booking || b;
      if (updatedBooking?.booking_id) {
        saveBookingHistoryV101(updatedBooking);
        setSelectedBooking(updatedBooking);
        setBookings(prev => mergeBookingListsV101([updatedBooking], prev));
        setCustomerBookingListV104(prev => mergeBookingListsV101([updatedBooking], prev));
      }
      setPaymentPopupBookingV106(null);
      setSlipUploadStatusV110("");
      pendingSlipFileRefV127.current = null;
      setSelectedSlipNameV127("");
      setSlipInputKeyV121(k => k + 1);
      setSlipReceivedPopupV106(true);
    } catch (err) {
      console.error("slip upload failed", err);
      setSlipUploadStatusV110("");
      setSlipInputKeyV121(k => k + 1);
      if (Number(err?.status || 0) === 409 || /เวลานี้ไม่ว่าง|เลือกเวลาอื่น|เลือกวันอื่น/.test(String(err?.message || ""))) {
        setPaymentPopupBookingV106(null);
        await refreshCustomerBookingListV104();
        alert("เวลานี้ไม่ว่างแล้ว โปรดเลือกเวลาอื่น หรือวันอื่น");
      } else {
        alert("อัปโหลดสลิปไม่สำเร็จ: " + (err.message || err));
      }
    } finally { setBookingLoading(false); }
  };
  const openBookingRoomV91 = async (b) => {
    setSelectedBooking(b);
    setRoomGateMessageV112("");
    if (!isBookingRoomOpenV112(b)) {
      setRoomGateMessageV112(bookingRoomWaitTextV112(b));
      alert(bookingRoomWaitTextV112(b));
      return;
    }
    try {
      const rid = b.room_id || "";
      const qs = new URLSearchParams({ booking_id:b.booking_id || "", room_id:rid, line_user_id:currentLineUser?.userId || "" });
      const data = await apiGet(`/api/fortune-room?${qs.toString()}`);
      setBookingRoomMessages(data.messages || []);
      setSelectedBooking(data.booking || b);
      go("booking_room");
    } catch (err) {
      const msg = String(err.message || err);
      console.warn("โหลดห้องดูดวงไม่สำเร็จ", err);
      setBookingRoomMessages([]);
      setRoomGateMessageV112(msg);
      alert(msg.includes("ยังไม่ถึงเวลานัด") ? msg : "ยังเข้าห้องดูดวงไม่ได้: " + msg);
    }
  };
  const sendRoomMessageV91 = async () => {
    const text = roomInput.trim();
    const b = selectedBooking;
    if (!text || !b) return;
    if (!isBookingRoomOpenV112(b)) { alert(bookingRoomWaitTextV112(b)); return; }
    try {
      const data = await apiPost("/api/room-message", {
        booking_id: b.booking_id,
        room_id: b.room_id,
        sender_role: bookingChatRoleV118 === "seer" ? "seer" : "customer",
        sender_id: currentLineUser?.userId || "",
        sender_line_user_id: currentLineUser?.userId || "",
        message_text: text,
      });
      setRoomInput("");
      setBookingRoomMessages(data.messages || [...bookingRoomMessages, data.message].filter(Boolean));
      await loadBookingsV91();
    } catch (err) { alert("ส่งข้อความไม่สำเร็จ: " + (err.message || err)); }
  };

  const openBookingChatV99 = async (b) => {
    setSelectedBooking(b);
    clearBookingChatInputV115();
    try {
      const qs = new URLSearchParams({
        booking_id: b.booking_id || "",
        line_user_id: currentLineUser?.userId || "",
      });
      const data = await apiGet(`/api/booking-chat?${qs.toString()}`);
      if (data.booking) {
        saveBookingHistoryV101(data.booking);
        setSelectedBooking(data.booking);
        setBookings(prev => {
          const merged = mergeBookingListsV101([data.booking], prev);
          return bookingListSignatureV114(prev) === bookingListSignatureV114(merged) ? prev : merged;
        });
      }
      setBookingChatMessagesStableV117(data.messages || []);
    } catch (err) {
      console.warn("โหลดแชทนัดเวลาไม่สำเร็จ", err);
      const localMsgs = (() => {
        try { return JSON.parse(localStorage.getItem("dw_booking_chat_local_" + (b.booking_id || "")) || "[]"); } catch { return []; }
      })();
      setBookingChatMessages(localMsgs);
    }
    saveBookingHistoryV101(b);
    setBookings(prev => mergeBookingListsV101([b], prev));
    setBookingChatPopupOpen(true);
  };

  const sendBookingChatV99 = async () => {
    // v115: อ่านจาก input ด้วย ref ตอนกดส่งเท่านั้น ไม่ setState ทุกตัวอักษร
    const text = String(bookingChatInputRefV115.current?.value ?? bookingChatInput ?? "").trim();
    const b = selectedBooking;
    if (!text || !b) return;
    try {
      const data = await apiPost("/api/booking-chat", {
        booking_id: b.booking_id,
        sender_role: bookingChatRoleV118 === "seer" ? "seer" : "customer",
        sender_id: currentLineUser?.userId || "",
        sender_line_user_id: currentLineUser?.userId || "",
        message_text: text,
      });
      clearBookingChatInputV115();
      setBookingChatMessagesStableV117(data.messages || [...bookingChatMessages, data.message].filter(Boolean));
      if (data.booking) {
        saveBookingHistoryV101(data.booking);
        setSelectedBooking(data.booking);
        setBookings(prev => {
          const merged = mergeBookingListsV101([data.booking], prev);
          return bookingListSignatureV114(prev) === bookingListSignatureV114(merged) ? prev : merged;
        });
      }
      if (!bookingChatPopupOpen) await loadBookingsV91();
    } catch (err) {
      const localMsg = {
        message_id: "LMSG" + Date.now(),
        booking_id: selectedBooking?.booking_id || "",
        sender_role: bookingChatRoleV118 === "seer" ? "seer" : "customer",
        sender_id: currentLineUser?.userId || "",
        sender_line_user_id: currentLineUser?.userId || "",
        message_text: text,
        created_at: new Date().toISOString(),
      };
      const nextMsgs = [...bookingChatMessages, localMsg];
      clearBookingChatInputV115();
      setBookingChatMessages(nextMsgs);
      try { localStorage.setItem("dw_booking_chat_local_" + (selectedBooking?.booking_id || ""), JSON.stringify(nextMsgs.slice(-300))); } catch {}
      if (selectedBooking) saveBookingHistoryV101(selectedBooking);
    }
  };
  const openBookingChatStableV108 = (b, roleOverride = "") => {
    if (!b) return;
    const fixed = b.booking_id ? b : showBookingHistoryNowV103(b || {});
    const role = roleOverride || (seerMode ? "seer" : "customer");
    setBookingChatRoleV118(role);
    saveBookingHistoryV101(fixed);
    setSelectedBooking(fixed);
    setBookingChatInput("");
    clearBookingChatInputV115();
    try {
      const localMsgs = JSON.parse(localStorage.getItem("dw_booking_chat_local_" + (fixed.booking_id || "")) || "[]");
      setBookingChatMessagesStableV117(Array.isArray(localMsgs) ? localMsgs : []);
    } catch {
      setBookingChatMessagesStableV117([]);
    }
    setBookingChatPopupOpen(true);
    setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ booking_id: fixed.booking_id || "", line_user_id: currentLineUser?.userId || "" });
        const data = await apiGet(`/api/booking-chat?${qs.toString()}`);
        if (data.booking) {
          saveBookingHistoryV101(data.booking);
          setSelectedBooking(data.booking);
          setBookings(prev => {
            const merged = mergeBookingListsV101([data.booking], prev);
            return bookingListSignatureV114(prev) === bookingListSignatureV114(merged) ? prev : merged;
          });
        }
        setBookingChatMessagesStableV117(data.messages || []);
      } catch (err) {
        console.warn("โหลดแชทนัดเวลาแบบ background ไม่สำเร็จ", err);
      }
    }, 30);
  };

  const openPaymentStableV108 = (b) => {
    if (!b) return;
    const fixed = b.booking_id ? b : showBookingHistoryNowV103(b || {});
    saveBookingHistoryV101(fixed);
    setSelectedBooking(fixed);
    setCustomerPayContactV122(p => ({
      phone: p.phone || fixed.customer_phone || "",
      lineId: p.lineId || fixed.customer_line_id || "",
      preferredMethods: p.preferredMethods || String(fixed.customer_contact_preference || "phone,line").split(","),
      consent: p.consent !== false,
    }));
    setPaymentPopupBookingV106(fixed); // เปิด popup ก่อนทันที
    setTimeout(() => loadPaymentSettingsV106(), 30); // โหลดเลขบัญชีทีหลัง
  };
  const savedBookingIdsV100 = (() => {
    try { return JSON.parse(localStorage.getItem("dw_my_booking_ids_v100") || "[]").map(String); } catch { return []; }
  })();
  const localBookingHistoryV101 = readBookingHistoryV101();
  const selectedBookingFallbackV103 = selectedBooking?.booking_id ? [selectedBooking] : [];
  // v176: do not let old local records reappear after a slot changes or test data is reset.
  const allCustomerBookingCandidatesV101 = mergeBookingListsV101(bookings, selectedBookingFallbackV103);
  const myCustomerBookingsV91 = allCustomerBookingCandidatesV101.filter(b => {
    const id = String(b.booking_id || "");
    if (savedBookingIdsV100.includes(id)) return true;
    if (localBookingHistoryV101.some(x => String(x?.booking_id || "") === id)) return true;
    if (!currentLineUser?.userId) return true;
    return String(b.customer_line_user_id || b.customer_id || "") === String(currentLineUser.userId);
  });
  const customerBookingAlertsV98 = myCustomerBookingsV91.filter(b =>
    ["waiting_payment", "seer_confirmed", "seer_proposed_new_time", "slot_unavailable", "rejected", "confirmed", "in_session"].includes(String(b.status || ""))
  );
  const customerNeedPaymentCountV98 = myCustomerBookingsV91.filter(b =>
    ["waiting_payment", "seer_confirmed"].includes(String(b.status || ""))
  ).length;
  const customerReadyRoomCountV98 = myCustomerBookingsV91.filter(b =>
    ["confirmed", "in_session"].includes(String(b.status || ""))
  ).length;
  const customerBookingAlertCountV98 = customerBookingAlertsV98.length;
  const mySeerBookingsV91 = bookings.filter(b => {
    const activeSeer = lockedSeerProfile || getApprovedSeerForCurrentLineUser();
    const ids = [activeSeer?.id, activeSeer?.seerId, activeSeer?.lineUserId, currentLineUser?.userId].filter(Boolean).map(String);
    return ids.includes(String(b.seer_id || "")) || ids.includes(String(b.seer_line_user_id || ""));
  });
  const days = getDays();
  const myOrders = []; // v94: เคลียร์ข้อมูลสมมุติในแดชหมอดูก่อน ให้แสดงเฉพาะระบบจองจริงผ่าน mySeerBookingsV91
  const seerSlotDayKey = days?.[0]?.label || "พฤ 22";
  const seerDaySlots = seerSlotState[seerSlotDayKey] || Object.fromEntries(BOOKING_TIME_SLOTS_V92.map(slot => [slot, "open"]));
  const isSeerDayClosed = BOOKING_TIME_SLOTS_V92.every(slot => seerDaySlots[slot] === "closed");
  const setSeerDayAllSlots = (status) => setSeerSlotState(p => ({
    ...p,
    [seerSlotDayKey]: Object.fromEntries(BOOKING_TIME_SLOTS_V92.map(slot => [slot, status])),
  }));
  const updateQueryRouteV137 = (screen, extra = {}, replace = false) => {
    try {
      const url = new URL(window.location.href);
      const path = url.pathname.toLowerCase();
      const isCustomerPath = !path.startsWith("/admin") && !path.startsWith("/seer-register");
      if (!isCustomerPath) return;
      const keep = new URLSearchParams(url.search);
      ["page","seer_id","article_id","booking_id","open"].forEach(k => keep.delete(k));
      if (screen && screen !== "home" && screen !== "splash") keep.set("page", screen);
      Object.entries(extra || {}).forEach(([k,v]) => {
        if (v !== undefined && v !== null && String(v) !== "") keep.set(k, String(v));
      });
      const next = `${url.pathname}${keep.toString() ? "?" + keep.toString() : ""}${url.hash || ""}`;
      if (replace) window.history.replaceState({screen, ...extra}, "", next);
      else window.history.pushState({screen, ...extra}, "", next);
    } catch {}
  };
  const go = (s, extra = {}) => {
    setScr(s);
    updateQueryRouteV137(s, extra);
  };
  useEffect(() => {
    const onPopV137 = () => {
      try {
        const url = new URL(window.location.href);
        const p = url.searchParams.get("page") || (url.pathname.toLowerCase().startsWith("/news/") ? "movement_detail" : "home");
        const sid = url.searchParams.get("seer_id") || "";
        const aid = url.searchParams.get("article_id") || "";
        if (sid) {
          const found = allSeers.find(s => String(s.id) === String(sid) || String(s.seerId) === String(sid));
          if (found) setSelSeer(found);
        }
        if (p === "movement_detail" && aid) openMovementArticle(aid);
        else { setScr(p || "home"); if (["home","services","seers","my_bookings","my_info","history","seer_dash"].includes(p || "home")) setActiveNav(p || "home"); }
      } catch { setScr("home"); }
    };
    window.addEventListener("popstate", onPopV137);
    return () => window.removeEventListener("popstate", onPopV137);
  }, [allSeers.length, movementArticles.length]);
  const sendChatMessage = () => {
    const text = chatInput.trim();
    if (!text) return;
    const newMsg = {from:"customer", text, time:"ตอนนี้"};
    const nextMessages = [...chatMessages, newMsg];
    setChatMessages(nextMessages);
    if (setChatAlerts && selSeer) {
      setChatAlerts(prev => {
        const idx = prev.findIndex(a => a.seer_id === selSeer.id && ["รอหมอตอบ", "กำลังนัดหมาย", "หมอตอบแล้ว"].includes(a.status));
        if (idx >= 0) {
          return prev.map((a,i)=> i===idx ? {
            ...a,
            customer: form.name || a.customer || "ลูกค้า",
            phone: form.phone || a.phone || "",
            text,
            created:"ตอนนี้",
            read:false,
            status:"รอหมอตอบ",
            messages:[...(a.messages || []), newMsg]
          } : a);
        }
        return [{
          id: "CHAT" + Date.now(),
          seer_id: selSeer.id,
          seer_name: selSeer.name,
          customer: form.name || "ลูกค้า",
          phone: form.phone || "",
          text,
          created: "ตอนนี้",
          read: false,
          status: "รอหมอตอบ",
          messages: nextMessages,
        }, ...prev];
      });
    }
    setChatInput("");
  };

  useEffect(() => {
    if (isSeer || !selSeer || bookingStep !== "chat") return;
    const thread = (chatAlerts || []).find(a => a.seer_id === selSeer.id && a.messages && a.messages.length);
    if (thread) {
      setChatMessages(thread.messages);
    }
  }, [chatAlerts, selSeer?.id, bookingStep, isSeer]);

  const buildLiveRoomId = (orderId) => `live_${orderId}`;
  const canEnterLiveRoom = (order) => order && order.svc_type === "live" && order.status === "approved" && order.pay === "paid";
  const openLiveRoom = (order) => {
    const roomId = order.room_id || buildLiveRoomId(order.id);
    setViewOrder({...order, room_id: roomId});
    go("live_room");
  };

  const toggleFavoriteSeer = (seerId) => {
    setFavoriteSeers(prev => {
      const next = prev.includes(seerId) ? prev.filter(id=>id!==seerId) : [...prev, seerId];
      localStorage.setItem("dw_favorite_seers", JSON.stringify(next));
      return next;
    });
  };
  const toggleNotifySeer = (seerId) => {
    setNotifySeers(prev => {
      const next = prev.includes(seerId) ? prev.filter(id=>id!==seerId) : [...prev, seerId];
      localStorage.setItem("dw_notify_seers", JSON.stringify(next));
      return next;
    });
  };
  const shareSeer = async (seer) => {
    const shareText = `แนะนำหมอดู ${seer.name} ในแอพดวงวันนี้
${seer.spec}`;
    try {
      if (navigator.share) {
        await navigator.share({title:`ดวงวันนี้ - ${seer.name}`, text:shareText, url:window.location.href});
      } else {
        await navigator.clipboard.writeText(`${shareText}
${window.location.href}`);
        alert("คัดลอกลิงก์หมอดูแล้ว สามารถนำไปแชร์ต่อได้");
      }
    } catch (err) {
      console.log("share cancelled", err);
    }
  };
  const submitSeerReview = async (seer) => {
    const canReviewNow = orders.some(o=>String(o.seer_id)===String(seer.id) && ["answered","completed"].includes(o.status));
    if (!canReviewNow) { alert("รีวิวได้หลังจากดูดวงกับหมอดูท่านนี้เสร็จแล้วเท่านั้น"); return; }
    const text = reviewForm.text.trim();
    if (!text) { alert("กรุณาเขียนข้อความรีวิวก่อนส่ง"); return; }
    const hasReviewed = seerReviews.some(r=>String(r.seer_id)===String(seer.seerId || seer.id) && r.source !== "admin");
    if (hasReviewed) { alert("คุณได้รีวิวหมอดูท่านนี้ไปแล้ว"); return; }
    try {
      const lineUser = getSavedLineUser();
      const data = await apiPost("/api/seer-reviews", {
        seer_id: seer.seerId || seer.id,
        seer_name: seer.name,
        customer_line_user_id: lineUser?.userId || "CUSTOMER",
        customer_name: lineUser?.displayName || "ลูกค้า",
        rating: Number(reviewForm.rating) || 5,
        review_text: text,
        booking_id: "CUSTOMER_REVIEW",
      });
      const nextReview = mapApiReview(data.review || {
        review_id: data.review_id,
        seer_id: seer.seerId || seer.id,
        seer_name: seer.name,
        customer_name: lineUser?.displayName || "ลูกค้า",
        rating: Number(reviewForm.rating) || 5,
        review_text: text,
        status: "approved",
        created_at: new Date().toISOString(),
      });
      setSeerReviews(prev => [nextReview, ...prev]);
      setReviewForm({rating:5,text:""});
      alert("ขอบคุณสำหรับการรีวิว\nรีวิวของคุณถูกโพสต์ไปยังหน้าหมอดูแล้ว");
    } catch (err) {
      alert("ส่งรีวิวไม่สำเร็จ: " + (err.message || err));
    }
  };

  const addOrder = (o) => setOrders(prev=>[o,...prev]);
  const updOrder = (id,ch) => setOrders(prev=>prev.map(o=>o.id===id?{...o,...ch}:o));

  // ── Splash / Login ──────────────────────────────────────────────────────────
  if(scr==="splash" || !loggedIn) return (
    <div className="scr on" style={{background:`linear-gradient(180deg,${C.purple}11 0%,${C.bg} 60%)`}}>
      <Stars/>
      <div className="login-wrap z1">
        <div style={{fontSize:64,marginBottom:16}}>🔮</div>
        <h1 style={{fontFamily:"Cinzel",fontSize:30,color:C.goldLight,marginBottom:6}}>ดวงวันนี้</h1>
        <p style={{fontSize:14,color:C.muted,marginBottom:40,lineHeight:1.7}}>แพลตฟอร์มดูดวงออนไลน์<br/>จากหมอดูมืออาชีพ</p>
        <div style={{width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:12}}>
          <button
            className="btn btn-line"
            onClick={async ()=>{
              try {
                const lineUser = await ensureLineLogin();
                if (!lineUser || !lineUser.userId) return;

                localStorage.setItem("duangwannee_logged_in", "yes");
                const isApprovedLocal = markCurrentLineUserAsSeerIfApproved();
                const isApprovedD1 = await promoteCurrentUserToSeerIfApprovedD1({ showPopup: true, navigate: false });
                setLoggedIn(true);
                if (isApprovedLocal || isApprovedD1 || isSeer) {
                  setSeerMode(true);
                  if (isSeer) go("seer_dash");
                  else go("home");
                } else {
                  // ลูกค้าใหม่ต้องกรอกข้อมูลสมาชิกก่อนเข้าหน้าหลัก
                  setSeerMode(false);
                  if (!readCustomerProfileV138(lineUser.userId)) go("customer_register");
                  else go("home");
                }
              } catch (err) {
                console.error(err);
                alert("เชื่อมต่อ LINE ไม่สำเร็จ: " + (err.message || err));
              }
            }}
          >
            <span style={{fontSize:18,marginRight:8}}>L</span>เข้าสู่ระบบด้วย LINE
          </button>

          <p style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:4}}>
            ลงทะเบียนผ่าน LINE เพียงครั้งเดียว<br/>
            ครั้งต่อไปสามารถเข้าใช้งานได้ทันที
          </p>
        </div>
      </div>
    </div>
  );

  function CustomerRegisterV138() {
    const lineUser = currentLineUser || getSavedLineUser() || {};
    const setF = (k,v) => setCustomerProfileFormV138(p => {
      const next = {...p, [k]: v};
      try { localStorage.setItem("dw_customer_register_draft_v138c", JSON.stringify(next)); } catch {}
      return next;
    });
    const saveProfile = async () => {
      const f = customerProfileFormV138 || {};
      const name = String(f.name || "").trim();
      const phone = String(f.phone || "").replace(/[^0-9]/g, "");
      const birthdate = String(f.birthdate || "").trim();
      const lineId = String(f.lineId || "").trim();
      if (!name) { alert("กรุณากรอกชื่อ"); return; }
      if (!phone || phone.length < 9) { alert("กรุณากรอกเบอร์โทรให้ถูกต้อง"); return; }
      if (!birthdate) { alert("กรุณาเลือกวันเดือนปีเกิด"); return; }
      const profile = {
        line_user_id: lineUser.userId || "",
        line_display_name: lineUser.displayName || "",
        line_picture_url: lineUser.pictureUrl || "",
        name,
        phone,
        birthdate,
        line_id: lineId,
        consent: true,
        registered_at: new Date().toISOString(),
      };
      try {
        await apiPost("/api/customer-profile", profile);
      } catch (err) {
        console.warn("บันทึกข้อมูลสมาชิกลง D1 ไม่สำเร็จ แต่จะบันทึกในเครื่องไว้ก่อน", err);
      }
      writeCustomerProfileV138(profile);
      try { localStorage.removeItem("dw_customer_register_draft_v138c"); } catch {}
      setCustomerProfileV138(profile);
      localStorage.setItem("duangwannee_logged_in", "yes");
      alert("สมัครสมาชิกเรียบร้อย");
      go("home");
    };
    return (
      <div className="scr on">
        <div className="topbar"><span className="topbar-title">สมัครสมาชิกดวงวันนี้</span></div>
        <div className="content z1" style={{padding:"16px",paddingBottom:90}}>
          <div className="card card-gold" style={{padding:16,marginBottom:14}}>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              <SeerAvatar seer={{photoUrl:lineUser.pictureUrl, name:lineUser.displayName || "LINE"}} className="seer-ava"/>
              <div>
                <div style={{fontWeight:900,color:C.goldLight}}>ยินดีต้อนรับ {lineUser.displayName || "สมาชิกใหม่"}</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>กรุณากรอกข้อมูลครั้งแรกก่อนเข้าใช้งาน เพื่อใช้จองคิวและติดต่อดูดวง</div>
              </div>
            </div>
          </div>
          <div className="card" style={{padding:16}}>
            <div className="fgroup">
              <label className="flabel">ชื่อ - นามสกุล (เพื่อใช้ดูดวง ฐานดวง) *</label>
              <input className="finput" name="dw_register_fullname" autoComplete="name" value={customerProfileFormV138.name || ""} onChange={e=>setF("name",e.target.value)} placeholder="เช่น นายสมชาย ใจดี" />
            </div>
            <div className="fgroup">
              <label className="flabel">เบอร์โทร (โปรดระบุเบอร์ที่ติดต่อได้ เพื่อให้ติดต่อกับอาจารย์หมอดู) *</label>
              <input className="finput" name="dw_register_phone" autoComplete="tel" inputMode="tel" value={customerProfileFormV138.phone || ""} onChange={e=>setF("phone",e.target.value)} placeholder="โปรดระบุเบอร์ที่ติดต่อได้" />
            </div>
            <div className="fgroup">
              <label className="flabel">วันเดือนปีเกิด (โปรดระบุ วันเดือนปีเกิดจริง เพื่อ ดูตามปฎิทินมงคล) *</label>
              <input className="finput" name="dw_register_birthdate" type="date" value={customerProfileFormV138.birthdate || ""} onChange={e=>setF("birthdate",e.target.value)} />
            </div>
            <div className="fgroup">
              <label className="flabel">LINE ID ถ้ามี</label>
              <input className="finput" name="dw_register_lineid" autoComplete="username" value={customerProfileFormV138.lineId || ""} onChange={e=>setF("lineId",e.target.value)} placeholder="เช่น mylineid" />
            </div>
            <button className="btn btn-gold" onClick={saveProfile}>สมัครสมาชิกและเข้าใช้งาน</button>
          </div>
        </div>
      </div>
    );
  }

  // v138C: ห้าม render เป็น <CustomerRegisterV138/> เพราะเป็น nested component
  // เวลา setState ตอนพิมพ์ React จะมองเป็น component type ใหม่และ remount ทำให้ช่องกรอกหลุดโฟกัส
  if(scr==="customer_register") return CustomerRegisterV138();

  // ── Bottom Nav ──────────────────────────────────────────────────────────────
  function BottomNav() {
    const items = [
      {ico:"🏠",lbl:"หน้าหลัก",nav:"home"},
      {ico:"🌟",lbl:"ดูดวง",nav:"services"},
      {ico:"👥",lbl:"หมอดู",nav:"seers"},
      {ico:"📅",lbl:`นัดของฉัน${getRealCustomerBookingsV139D(myCustomerBookingsV91, customerBookingListV104, bookings).length ? ` (${getRealCustomerBookingsV139D(myCustomerBookingsV91, customerBookingListV104, bookings).length})` : ""}`,nav:"my_bookings"},
      {ico:"👤",lbl:"ข้อมูลของฉัน",nav:"my_info"},
      ...(seerMode?[{ico:"🔮",lbl:"แดชบอร์ด",nav:"seer_dash"}]:[]),
    ];
    return (
      <nav className="bnav-wrap">
        {items.map(n=>(
          <button key={n.nav} className={`bnav${activeNav===n.nav?" on":""}`} onClick={()=>{setActiveNav(n.nav);go(n.nav)}}>
            <span className="ico">{n.ico}</span><span>{n.lbl}</span>
          </button>
        ))}
      </nav>
    );
  }


  function showPointsDetail() {
    go("points_detail");
  }

  function showMemberLevelDetail() {
    go("member_level_detail");
  }

  // ── Home ────────────────────────────────────────────────────────────────────
  function Home() {
    return (
      <div className="scr on">
        <div className="topbar">
          <span className="topbar-title">ดวงวันนี้</span>
          {seerMode && <span className="badge b-purple">หมอดู</span>}
        </div>
        <div className="content z1">

          {!seerMode && (() => {
            const profile = customerProfileV138 || readCustomerProfileV138(currentLineUser?.userId || "") || {};
            const astro = getCustomerAstroV139(profile.birthdate);
            if (!astro) return null;
            return (
              <div className="card card-gold" style={{padding:14,margin:"10px 16px 12px"}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize:13,color:C.muted,marginBottom:4}}>ราศีพื้นดวงของคุณ</div>
                    <div style={{fontSize:17,fontWeight:900,color:C.goldLight}}>♈ {astro.text}</div>
                    <div style={{fontSize:12,color:C.text,lineHeight:1.7,marginTop:6}}>{astro.advice}</div>
                  </div>
                  <button className="btn btn-outline btn-sm" style={{width:"auto",whiteSpace:"nowrap"}} onClick={()=>{setActiveNav("my_info");go("my_info")}}>ข้อมูลของฉัน</button>
                </div>
              </div>
            );
          })()}

          {!seerMode && customerBookingAlertsV98.length > 0 && (() => {
            const b = customerBookingAlertsV98[0];
            const needPay = ["waiting_payment", "seer_confirmed"].includes(String(b.status || ""));
            const roomReady = ["confirmed", "in_session"].includes(String(b.status || ""));
            const proposed = String(b.status || "") === "seer_proposed_new_time";
            const rejected = String(b.status || "") === "rejected";
            const title = needPay
              ? "✅ หมอดูยืนยันเวลาแล้ว"
              : roomReady
                ? "🔮 ห้องดูดวงพร้อมใช้งาน"
                : proposed
                  ? "⏰ หมอดูเสนอเวลาใหม่"
                  : rejected
                    ? "❌ หมอดูปฏิเสธคำขอนัด"
                    : "📅 มีอัปเดตการนัดหมาย";
            const detail = needPay
              ? "กรุณาโอนเงินและอัปโหลดสลิป เพื่อยืนยันคิวและสร้างห้องดูดวง"
              : roomReady
                ? "ชำระเงินผ่านแล้ว กดเข้าห้องดูดวงเพื่อคุยกับหมอดูในแอพ"
                : proposed
                  ? "กรุณาตรวจสอบเวลาที่หมอดูเสนอ แล้วกดยอมรับหรือเลือกนัดใหม่"
                  : rejected
                    ? (b.reject_reason || "เวลานี้หมอดูไม่สะดวก กรุณาเลือกนัดใหม่")
                    : "กรุณาเปิดรายการนัดหมายเพื่อตรวจสอบ";
            return (
              <div className="card" style={{margin:"14px 16px 0",padding:13,borderColor: needPay || roomReady ? `${C.green}55` : `${C.gold}55`,background: needPay || roomReady ? `${C.green}11` : `${C.gold}10`}}>
                <div style={{fontSize:13,fontWeight:900,color: needPay || roomReady ? C.green : C.goldLight,marginBottom:6}}>{title}</div>
                <div style={{fontSize:12,color:C.text,lineHeight:1.65,marginBottom:8}}>
                  {b.seer_name || "หมอดู"} • {dwBookingScheduledText(b)}<br/>
                  {detail}
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button className="btn btn-gold btn-sm" onClick={()=>{setActiveNav("my_bookings");go("my_bookings")}}>
                    {needPay ? "ไปอัปโหลดสลิป" : roomReady ? "เข้ารายการห้องดูดวง" : "ดูนัดของฉัน"}
                  </button>
                  {customerBookingAlertsV98.length > 1 && (
                    <span style={{fontSize:11,color:C.muted,alignSelf:"center"}}>มีอัปเดต {customerBookingAlertsV98.length} รายการ</span>
                  )}
                </div>
              </div>
            );
          })()}

          {(() => {
            const replyThread = (chatAlerts || []).find(a => a.status === "หมอตอบแล้ว" && a.messages && a.messages.some(m => m.from === "seer"));
            if (!replyThread) return null;
            const seer = allSeers.find(x => x.id === replyThread.seer_id) || allSeers[0];
            const liveSvc = getServicesForSeer(seer).find(x => x.input === "booking") || {id:"live",name:"จองคิวดูดวงสด",type:"live",price:999,input:"booking"};
            const latest = replyThread.messages[replyThread.messages.length - 1];
            return (
              <div className="card" style={{margin:"14px 16px 0",padding:12,borderColor:`${C.green}55`,background:`${C.green}11`}}>
                <div style={{fontSize:13,fontWeight:800,color:C.green,marginBottom:5}}>💬 หมอดูตอบแชทนัดเวลาแล้ว</div>
                <div style={{fontSize:12,color:C.text,lineHeight:1.6,marginBottom:8}}>{seer.name}: {latest?.text}</div>
                <button className="btn btn-success btn-sm" onClick={() => {
                  setSelSeer(seer);
                  setSelSvc(liveSvc);
                  setBookingStep("chat");
                  setChatMessages(replyThread.messages || []);
                  go("book_form");
                }}>เปิดแชทนัดเวลา</button>
              </div>
            );
          })()}
          <div className="hero" style={{paddingTop:18,paddingBottom:12}}>
            <img
              src={DUANGWANNEE_LOGO}
              alt="ดวงวันนี้"
              style={{
                width:118,
                height:118,
                objectFit:"contain",
                display:"block",
                margin:"0 auto 4px",
                filter:"drop-shadow(0 0 18px rgba(232,201,106,.26))"
              }}
            />
            <h1>ดวงวันนี้</h1>
            <p>ค้นหาคำตอบจากผู้เชี่ยวชาญด้านโหราศาสตร์</p>
          </div>
          <div className="gold-bar" style={{margin:"0 0 16px"}}/>
          {/* Welcome card */}
          {(() => {
            const today = getThaiTodayInfo();
            const chinese = getChineseAstroTodayInfo();
            const customerName = getCustomerGreetingName(form?.name);
            return (
              <div className="pts-card" style={{
                position:"relative",
                overflow:"hidden",
                padding:16,
                background:`linear-gradient(145deg, ${C.goldDim}, ${C.card} 48%, ${C.purple}18)`,
                borderColor:`${C.gold}55`
              }}>
                <div style={{
                  position:"absolute",
                  right:-38,
                  top:-38,
                  width:150,
                  height:150,
                  borderRadius:"50%",
                  background:`radial-gradient(circle, ${C.gold}24, ${C.purple}14 42%, transparent 72%)`,
                  pointerEvents:"none"
                }}/>
                <div style={{
                  position:"absolute",
                  left:-40,
                  bottom:-52,
                  width:145,
                  height:145,
                  borderRadius:"50%",
                  background:`radial-gradient(circle, ${C.purple}22, transparent 70%)`,
                  pointerEvents:"none"
                }}/>

                <div style={{display:"flex",alignItems:"flex-start",gap:12,position:"relative",zIndex:1}}>
                  <div style={{
                    width:58,
                    height:58,
                    borderRadius:20,
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"center",
                    background:`linear-gradient(135deg, ${C.purple}66, ${C.gold}28)`,
                    border:`1px solid ${C.gold}55`,
                    fontSize:30,
                    flexShrink:0,
                    boxShadow:`0 0 24px ${C.gold}18`
                  }}>
                    {today.isWanPhra ? "🙏" : "🌙"}
                  </div>

                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,color:C.muted,marginBottom:3}}>สวัสดี</div>
                    <div style={{fontSize:18,fontWeight:900,color:C.goldLight,lineHeight:1.35}}>
                      คุณ {customerName}
                    </div>
                    <div style={{fontSize:13,color:C.text,lineHeight:1.65,marginTop:7}}>
                      วันนี้เป็น {today.thaiDate}
                    </div>

                    <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap",marginTop:9}}>
                      <span className="badge b-purple">🌘 {today.lunarText}</span>
                      {today.isWanPhra && <span className="badge b-gold">🙏 วันพระ</span>}
                      {today.buddhistSpecialDays.filter(x=>x!=="วันพระ").map(x => (
                        <span key={x} className="badge b-green">📿 {x}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{position:"relative",zIndex:1,marginTop:14,padding:12,borderRadius:14,background:`${C.surface}bb`,border:`1px solid ${C.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:8}}>
                    <div>
                      <div style={{fontSize:12,color:C.muted}}>ปฏิทินมงคลวันนี้</div>
                      <div style={{fontSize:15,fontWeight:900,color:C.goldLight,marginTop:2}}>
                        {chinese.label}
                      </div>
                    </div>
                    <div style={{textAlign:"right",fontSize:11,color:C.purpleLight,lineHeight:1.5}}>
                      <div>{chinese.chineseDayText}</div>
                      <div>{chinese.zodiacYear}</div>
                    </div>
                  </div>

                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
                    <div style={{padding:10,borderRadius:12,background:`${C.green}10`,border:`1px solid ${C.green}33`}}>
                      <div style={{fontSize:12,fontWeight:800,color:C.green,marginBottom:6}}>เหมาะทำ</div>
                      <div style={{fontSize:12,color:C.text,lineHeight:1.7}}>
                        {chinese.good.slice(0,3).join(" • ")}
                      </div>
                    </div>
                    <div style={{padding:10,borderRadius:12,background:`${C.orange}10`,border:`1px solid ${C.orange}33`}}>
                      <div style={{fontSize:12,fontWeight:800,color:C.orange,marginBottom:6}}>ควรเลี่ยง</div>
                      <div style={{fontSize:12,color:C.text,lineHeight:1.7}}>
                        {chinese.avoid.slice(0,2).join(" • ")}
                      </div>
                    </div>
                  </div>

                  <div style={{fontSize:12,color:C.muted,lineHeight:1.75,marginTop:10}}>
                    💡 {chinese.tip}
                  </div>

                  {(chinese.festivals.length > 0) && (
                    <div style={{display:"flex",gap:7,flexWrap:"wrap",marginTop:10}}>
                      {chinese.festivals.map(x => <span key={x} className="badge b-gold">🎐 {x}</span>)}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          {(() => {
            const latestNews = movementArticles[0] || null;
            const latestNotify = userNotifications.find(n => n.article_id) || null;
            const targetArticleId = latestNotify?.article_id || latestNews?.id;
            const title = latestNews?.title || latestNotify?.title || "ข่าววันนี้";
            const body = latestNews?.body || latestNotify?.message || "";
            return (
              <div className="card card-gold" style={{
                margin:"14px 16px 0",
                padding:14,
                borderColor:`${C.gold}55`,
                background:`linear-gradient(135deg, ${C.goldDim}, ${C.card} 58%, ${C.purple}16)`
              }}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:900,color:C.goldLight,marginBottom:6}}>📰 ข่าววันนี้</div>
                    <div style={{fontSize:15,fontWeight:900,color:C.text,lineHeight:1.5}}>
                      {title}
                    </div>
                    {body && (
                      <div style={{fontSize:12,color:C.muted,lineHeight:1.65,marginTop:5}}>
                        {String(body).slice(0,95)}{String(body).length>95?'...':''}
                      </div>
                    )}
                  </div>
                  <div style={{
                    width:42,
                    height:42,
                    borderRadius:14,
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"center",
                    background:`${C.gold}18`,
                    border:`1px solid ${C.gold}44`,
                    fontSize:22,
                    flexShrink:0
                  }}>📣</div>
                </div>
                <button
                  className="btn btn-gold btn-sm"
                  style={{marginTop:12}}
                  onClick={() => targetArticleId ? openMovementArticle(targetArticleId) : go("movement")}
                >
                  อ่านข่าวต่อ
                </button>
              </div>
            );
          })()}
          <div className="sec-hd">บริการยอดนิยม</div>
          <div className="svc-grid">
            {[
              {ico:"🃏",name:"ดูไพ่ Tarot",desc:"ดูจากระบบได้ทันที",key:"tarot"},
              {ico:"📱",name:"ดูเบอร์โทร",desc:"วิเคราะห์พลังตัวเลขจากระบบ",key:"phone"},
              {ico:"💕",name:"ดูดวงความรัก",desc:"ดูแนวโน้มความรักจากระบบ",key:"love"},
              {ico:"🏠",name:"ดูดวงบ้านเลขที่",desc:"พร้อมวิธีแก้เคล็ด",key:"home_number"},
              {ico:"🎯",name:"จองคิวสด",price:"เริ่ม ฿349",desc:"คุยกับหมอดูโดยตรง",key:"live"},
            ].map(s=>(
              <div key={s.name} className="svc-card" onClick={()=>{
                if(s.key==="live") { setActiveNav("seers"); go("seers"); }
                else { setSelSvc(s); go("system_reading"); }
              }}>
                <div className="svc-ico">{s.ico}</div>
                <div className="svc-name">{s.name}</div>
                {s.price && <div className="svc-price">{s.price}</div>}
                <div className="svc-desc">{s.desc}</div>
              </div>
            ))}
          </div>
          <div className="sec-hd">หมอดูแนะนำ</div>
          {allSeers.slice(0,2).map(s=>(
            <div key={s.id} className="seer-card card card-gold" onClick={()=>{setSelSeer(s);setActiveNav("seers");go("seer_detail")}}>
              <div className="seer-inner">
                <SeerAvatar seer={s} className="seer-ava" />
                <div className="seer-info">
                  <div className="seer-name">{s.name} {s.feedBoost&&<span style={{fontSize:10,color:C.goldLight}}>โปรโมท</span>}</div>
                  <div className="seer-spec">{s.spec}</div>
                  <div className={`status-line ${getSeerStatusClass(s.status)}`} style={{marginBottom:5}}><span className="status-dot"/> {getSeerStatusLabel(s.status)} <span style={{fontWeight:400,color:C.muted}}>• ตอบกลับเฉลี่ย {s.avgReply}</span></div>
                  {s.status==="offline" && <div style={{fontSize:11,color:C.muted,marginBottom:5}}>ออนไลน์ล่าสุด {s.lastOnline}</div>}
                  <div className="seer-bio">{s.bio}</div>
                  <div className="seer-row">
                    <span className="rating">★ {s.rating} ({s.reviews})</span>
                    <span className="price-tag">เริ่ม ฿{getMinPriceForSeer(s)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <BottomNav/>
      </div>
    );
  }


  function renderArticleBody(body) {
    const lines = String(body || "").split(/\n+/).filter(Boolean);
    return lines.map((line, idx) => {
      const urlMatch = line.match(/https?:\/\/[^\s]+/);
      if (urlMatch) {
        const url = urlMatch[0];
        const before = line.slice(0, urlMatch.index);
        const after = line.slice((urlMatch.index || 0) + url.length);
        return <p key={idx} style={{fontSize:14,lineHeight:1.9,color:C.text,margin:"0 0 10px"}}>{before}<a href={url} target="_blank" rel="noreferrer" style={{color:C.goldLight,textDecoration:"underline"}}>{url}</a>{after}</p>;
      }
      return <p key={idx} style={{fontSize:14,lineHeight:1.9,color:C.text,margin:"0 0 10px"}}>{line}</p>;
    });
  }

  function MovementList() {
    return (
      <div className="scr on">
        <div className="topbar"><button className="back-btn" onClick={()=>go("home")}>←</button><span className="topbar-title">ความเคลื่อนไหวประจำวัน</span></div>
        <div className="content z1" style={{padding:16}}>
          <div className="hero" style={{paddingTop:14,paddingBottom:12}}><div className="hero-icon">📰</div><h1 style={{fontSize:22}}>ความเคลื่อนไหวประจำวัน</h1><p>ข่าวสาร บทความ และประกาศจากดวงวันนี้</p></div>
          {movementArticles.length === 0 && <div className="card" style={{padding:18,textAlign:"center",color:C.muted}}>ยังไม่มีบทความเผยแพร่</div>}
          {movementArticles.map(a => (
            <div key={a.id} className="card card-gold" style={{padding:14,marginBottom:12}} onClick={() => openMovementArticle(a.id)}>
              {a.image_url && <img src={a.image_url} alt="" style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:14,marginBottom:10,border:`1px solid ${C.border}`}}/>}
              <div style={{fontSize:16,fontWeight:900,color:C.goldLight,lineHeight:1.5}}>{a.title}</div>
              {a.createdAt && <div style={{fontSize:11,color:C.muted,marginTop:3}}>{a.createdAt}</div>}
              {a.body && <div style={{fontSize:12,color:C.text,lineHeight:1.7,marginTop:8}}>{String(a.body).slice(0,140)}{String(a.body).length>140?'...':''}</div>}
              <button className="btn btn-outline btn-sm" style={{marginTop:10}}>อ่านต่อ</button>
            </div>
          ))}
        </div>
        <BottomNav/>
      </div>
    );
  }

  function MovementDetail() {
    const a = selectedMovementArticle;
    return (
      <div className="scr on">
        <div className="topbar"><button className="back-btn" onClick={()=>go("movement")}>←</button><span className="topbar-title">ความเคลื่อนไหวประจำวัน</span></div>
        <div className="content z1" style={{padding:16}}>
          {!a ? <div className="card" style={{padding:18,textAlign:"center",color:C.muted}}>ไม่พบบทความ</div> : (
            <article className="card card-gold" style={{padding:16}}>
              {a.image_url && <img src={a.image_url} alt={a.title} style={{width:"100%",maxHeight:260,objectFit:"cover",borderRadius:16,marginBottom:14,border:`1px solid ${C.border}`}}/>}
              <div style={{display:"inline-flex",padding:"4px 9px",borderRadius:999,background:`${C.gold}18`,border:`1px solid ${C.gold}55`,fontSize:11,color:C.goldLight,fontWeight:800,marginBottom:10}}>ความเคลื่อนไหวประจำวัน</div>
              <h1 style={{fontSize:23,lineHeight:1.45,color:C.goldLight,margin:"0 0 8px",fontWeight:900}}>{a.title}</h1>
              {a.createdAt && <div style={{fontSize:11,color:C.muted,marginBottom:14}}>เผยแพร่: {a.createdAt}</div>}
              <div>{renderArticleBody(a.body)}</div>
              {a.external_url && <a className="btn btn-gold" href={a.external_url} target="_blank" rel="noreferrer" style={{marginTop:12,textDecoration:"none"}}>{a.link_text || "เปิดลิงก์เพิ่มเติม"}</a>}
            </article>
          )}
        </div>
        <BottomNav/>
      </div>
    );
  }

  // ── Services ────────────────────────────────────────────────────────────────
  function Services() {
    const mainSvcs = [
      {ico:"🃏",name:"ดูไพ่ Tarot 3 ใบ",desc:"สุ่มไพ่จากฐานข้อมูล D1",key:"tarot"},
      {ico:"📱",name:"ดูดวงจากเบอร์โทร",desc:"กรอกเบอร์โทรศัพท์เพื่อดูผลวิเคราะห์จากระบบ",key:"phone"},
      {ico:"💕",name:"ดูดวงความรัก",desc:"สุ่มไพ่ทาโรต์และอ่านเฉพาะด้านความรัก",key:"love"},
      {ico:"🏠",name:"ดูดวงบ้านเลขที่",desc:"วิเคราะห์บ้านเลขที่ พร้อมวิธีแก้เคล็ด",key:"home_number"},
      {ico:"🎯",name:"จองคิวดูดวงสด",price:"เริ่ม ฿349",desc:"เลือกหมอดูและเวลา เพื่อปรึกษาหมอดูแบบสด",key:"live"},
      {ico:"💼",name:"ดูดวงการงาน",desc:"ดูแนวโน้มการงานจากระบบ",key:"career"},
      {ico:"💰",name:"ดูดวงการเงิน",desc:"ดูแนวโน้มการเงินจากระบบ",key:"finance"},
    ];
    return (
      <div className="scr on">
        <div className="topbar"><span className="topbar-title">ดูดวงวันนี้</span></div>
        <div className="content z1">
          <div style={{padding:"16px 16px 8px",fontSize:13,color:C.muted,lineHeight:1.7}}>
            เลือกบริการที่ต้องการ แล้วเลือกหมอดูในขั้นตอนต่อไป
          </div>
          {mainSvcs.map(s=>(
            <div key={s.key} style={{margin:"0 16px 10px",padding:16,background:C.card,border:`1px solid ${C.border}`,borderRadius:14,cursor:"pointer",transition:"border-color .15s"}}
              onClick={()=>{
                if(s.key==="live") { setActiveNav("seers"); go("seers"); }
                else { setSelSvc(s); go("system_reading"); }
              }}
              onMouseOver={e=>e.currentTarget.style.borderColor=C.gold+"55"}
              onMouseOut={e=>e.currentTarget.style.borderColor=C.border}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                <span style={{fontSize:28,flexShrink:0}}>{s.ico}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:3}}>{s.name}</div>
                  <div style={{fontSize:12,color:C.muted,lineHeight:1.5,marginBottom:6}}>{s.desc}</div>
                  {s.price ? <span className="tag">{s.price}</span> : <span className="tag">ใช้ฟรีจากระบบ</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <BottomNav/>
      </div>
    );
  }

  // ── System Reading ──────────────────────────────────────────────────────────
  function SystemReading() {
    const svc = selSvc || {ico:"🔮", name:"ดูดวงจากระบบ", key:"general"};
    const [phoneInput, setPhoneInput] = useState(() => localStorage.getItem("dw_system_phone_input_v96") || "");
    const [homeInput, setHomeInput] = useState(() => localStorage.getItem("dw_system_home_input_v96") || "");
    const restoreSystemInputFocus = () => {
      const activeId = localStorage.getItem("dw_system_active_input_v97") || "";
      if (!activeId) return;
      setTimeout(() => {
        const el = document.getElementById(activeId);
        if (!el) return;
        el.focus({ preventScroll: true });
        try {
          const len = String(el.value || "").length;
          el.setSelectionRange(len, len);
        } catch {}
      }, 0);
    };
    useEffect(() => {
      restoreSystemInputFocus();
    });
    const [loading, setLoading] = useState(false);
    const resultStorageKey = svc.key || "general";
    const [result, setResult] = useState(() => readSavedSystemReading(resultStorageKey));
    const [error, setError] = useState("");

    useEffect(() => {
      saveSystemReading(resultStorageKey, result);
    }, [resultStorageKey, result]);

    const topicLabel = {
      tarot: "ไพ่ทาโรต์",
      love: "ความรัก",
      phone: "เบอร์โทร",
      home_number: "บ้านเลขที่",
      career: "การงาน",
      finance: "การเงิน",
      general: "ดวงโดยรวม",
    };

    const mockResultMap = {
      career: "งานมีโอกาสขยับ แต่ต้องจัดลำดับความสำคัญให้ดี ระวังรับภาระมากเกินไป",
      finance: "การเงินยังพอหมุนได้ แต่ควรแยกเงินจำเป็นกับเงินใช้จ่ายส่วนตัวให้ชัดเจน",
      general: "ดวงโดยรวมอยู่ในช่วงค่อย ๆ ดีขึ้น เหมาะกับการวางแผนและเริ่มต้นอย่างเป็นระบบ"
    };

    const getTodayKey = () => {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    const dailyLimitMap = {
      tarot: "duangwannee_daily_tarot",
      love: "duangwannee_daily_love",
    };

    const isDailyLimited = (key) => {
      const storageKey = dailyLimitMap[key];
      if (!storageKey) return false;
      return localStorage.getItem(storageKey) === getTodayKey();
    };

    const markDailyUsed = (key) => {
      const storageKey = dailyLimitMap[key];
      if (!storageKey) return;
      localStorage.setItem(storageKey, getTodayKey());
    };

    const resetDailyLimitForTest = () => {
      if (svc.key === "tarot") localStorage.removeItem("duangwannee_daily_tarot");
      if (svc.key === "love") localStorage.removeItem("duangwannee_daily_love");
      setError("");
      alert("รีเซ็ตสิทธิ์ทดสอบแล้ว สามารถกดดูคำทำนายใหม่ได้");
    };

    const fetchSystemReading = async () => {
      localStorage.removeItem("dw_system_active_input_v97");
      setError("");
      setResult(null);

      if (isDailyLimited(svc.key)) {
        setError("ท่านได้ใช้สิทธิ์ของวันนี้ไปแล้ว โปรดลองใหม่ในวันพรุ่งนี้");
        return;
      }

      if (svc.key === "career" || svc.key === "finance") {
        try {
          setLoading(true);
          const data = await apiGet("/api/reading/tarot?topic=" + encodeURIComponent(svc.key));
          const card = (data.cards || [])[0] || {};
          const isCareer = svc.key === "career";
          const detail =
            isCareer
              ? (card.work || card.overview || mockResultMap.career)
              : (card.money || card.overview || mockResultMap.finance);
          const summary =
            isCareer
              ? `สรุปการงาน: ไพ่ ${card.thai_name || card.card_name || "ประจำวัน"} บอกว่าช่วงนี้ควรเน้นความรอบคอบ วางแผนเป็นขั้นตอน และเลือกงานที่สำคัญก่อน งานจะค่อย ๆ เดินหน้าได้ดีขึ้น`
              : `สรุปการเงิน: ไพ่ ${card.thai_name || card.card_name || "ประจำวัน"} บอกว่าการเงินควรบริหารอย่างมีวินัย ระวังรายจ่ายยิบย่อย และเหมาะกับการเก็บเงินมากกว่าการเสี่ยง`;
          setResult({
            ok: true,
            type: "single_tarot",
            sourceType: svc.key,
            title: svc.name,
            subtitle: "สุ่มไพ่ 1 ใบ เพื่ออ่านพลังโดยรวมของเรื่องนี้",
            cards: [{...card, position_name: "ไพ่ประจำเรื่อง"}],
            message: detail,
            summary,
          });
          markDailyUsed(svc.key);
        } catch (err) {
          setError("เชื่อมต่อฐานข้อมูล D1 ไม่สำเร็จ: " + (err.message || err));
        } finally {
          setLoading(false);
        }
        return;
      }

      if (!["tarot", "love", "phone", "home_number"].includes(svc.key)) {
        setResult({
          ok:true,
          type:svc.key,
          title:svc.name,
          message:mockResultMap[svc.key] || mockResultMap.general,
          summary:"สรุปผล: ดวงโดยรวมอยู่ในจังหวะค่อย ๆ ขยับดีขึ้น เหมาะกับการตั้งหลัก วางแผน และเลือกทำสิ่งที่สำคัญก่อน"
        });
        return;
      }

      let path = "/api/reading/tarot";
      if (svc.key === "love") path = "/api/reading/tarot?topic=love";
      if (svc.key === "phone") {
        const phone = (document.getElementById("dwStablePhoneInput")?.value || localStorage.getItem("dw_system_phone_input_v96") || phoneInput || "").trim();
        if (!phone) { setError("กรุณากรอกเบอร์โทรก่อนดูคำทำนาย"); return; }
        localStorage.setItem("dw_system_phone_input_v96", phone);
        setPhoneInput(phone);
        path = "/api/reading/phone?phone=" + encodeURIComponent(phone);
      }
      if (svc.key === "home_number") {
        const number = (document.getElementById("dwStableHomeInput")?.value || localStorage.getItem("dw_system_home_input_v96") || homeInput || "").trim();
        if (!number) { setError("กรุณากรอกบ้านเลขที่ก่อนดูคำทำนาย"); return; }
        localStorage.setItem("dw_system_home_input_v96", number);
        setHomeInput(number);
        path = "/api/reading/home-number?number=" + encodeURIComponent(number);
      }

      try {
        setLoading(true);
        const data = await apiGet(path);
        setResult(data);
        markDailyUsed(svc.key);
      } catch (err) {
        setError("เชื่อมต่อฐานข้อมูล D1 ไม่สำเร็จ: " + (err.message || err));
      } finally {
        setLoading(false);
      }
    };



    const cleanText = (value) => String(value || "").replace(/\s+/g, " ").trim();

    const SummaryCard = ({title="สรุปผล", children, tone="gold"}) => (
      <div className="card" style={{
        padding:14,
        marginTop:12,
        borderColor: tone === "green" ? `${C.green}55` : `${C.gold}55`,
        background: tone === "green" ? `${C.green}11` : `${C.gold}10`
      }}>
        <div style={{fontSize:14,fontWeight:900,color:tone === "green" ? C.green : C.goldLight,marginBottom:8}}>
          ✨ {title}
        </div>
        <div style={{fontSize:13,color:C.text,lineHeight:1.85}}>{children}</div>
      </div>
    );

    const buildTarotSummary = (reading) => {
      const cards = reading?.cards || [];
      if (!cards.length) return "";
      const names = cards.map(c => c.thai_name || c.card_name).filter(Boolean).join(" / ");
      if (reading.type === "love") {
        return `สรุปความรัก: ไพ่ที่ออกมา (${names}) บอกว่าความรักช่วงนี้ควรค่อย ๆ ดูใจ ใช้ความจริงใจมากกว่าการเร่งรัด หากมีเรื่องค้างใจควรพูดกันให้ชัด ความสัมพันธ์จะเดินหน้าได้ดีขึ้นเมื่อทั้งสองฝ่ายเปิดใจ`;
      }
      return `สรุปไพ่ทาโรต์: ไพ่ที่ออกมา (${names}) ชี้ว่าช่วงนี้มีทั้งโอกาสและเรื่องที่ต้องจัดการอย่างรอบคอบ ควรใช้สติ ตัดสินใจทีละขั้น และเลือกทางที่มั่นคงที่สุด ผลลัพธ์โดยรวมมีแนวโน้มค่อย ๆ ดีขึ้น`;
    };

    const buildPhoneOverallSummary = (data) => {
      const results = data?.results || [];
      const percent = data?.percent || data?.score || data?.overall_percent;
      const good = results.filter(r => String(r.level || r.title || "").includes("ดี") || String(r.meaning || "").includes("ดี")).length;
      const warn = results.filter(r => String(r.level || r.title || "").includes("ควร") || String(r.meaning || "").includes("ระวัง")).length;
      const pairs = (data?.pairs || []).join(" • ");
      const firstAdvice = results.map(r => r.advice).filter(Boolean)[0];

      return {
        percent,
        text:
          `สรุปเบอร์โทร: เบอร์นี้มีชุดเลขที่ระบบอ่านได้ ${pairs || "หลายตำแหน่ง"} ` +
          `${percent ? `คะแนนรวมประมาณ ${percent}% ` : ""}` +
          `ภาพรวมมีจุดเด่น ${good || "หลาย"} จุด และมีจุดที่ควรระวัง ${warn || "บาง"} จุด ` +
          `${firstAdvice ? `คำแนะนำหลักคือ ${firstAdvice}` : "เหมาะกับการใช้อย่างมีสติ หากต้องการเสริมพลังให้เลือกเบอร์ที่หนุนเรื่องงาน เงิน และความสัมพันธ์ให้สมดุล"}`
      };
    };

    const buildHomeOverallSummary = (data) => {
      const r = data?.result || {};
      const number = data?.calculated_number || data?.number || "";
      const good = cleanText(r.good_points || r.meaning || "");
      const warning = cleanText(r.warning || "");
      const remedy = cleanText(r.remedy || r.advice || "");

      return (
        `สรุปบ้านเลขที่: เลขรวม ${number || "-"} ให้พลังโดยรวมเกี่ยวกับ “${r.title || "การอยู่อาศัยและความมั่นคง"}” ` +
        `${good ? `จุดเด่นคือ ${good} ` : ""}` +
        `${warning ? `ข้อควรระวังคือ ${warning} ` : ""}` +
        `${remedy ? `แนวทางเสริมคือ ${remedy}` : "ควรจัดบ้านให้โปร่ง สะอาด และมีแสงสว่าง เพื่อช่วยเปิดพลังที่ดี"}`
      );
    };


    const buildLineResultText = (reading) => {
      if (!reading) return "";

      if (reading.type === "tarot" || reading.type === "love" || reading.type === "single_tarot") {
        const lines = (reading.cards || []).map((card, index) => {
          const meaning = reading.type === "love" ? (card.love || card.overview || "") : (card.overview || "");
          return `${index + 1}. ${card.position_name || "ไพ่"} - ${card.thai_name || card.card_name || ""}\n${meaning}`;
        });
        return `${reading.title || svc.name}\n${reading.subtitle || ""}\n\n${lines.join("\n\n")}`;
      }

      return `${reading.title || svc.name}\n${reading.message || ""}`;
    };

    const sendResultToLine = async () => {
      if (!result) {
        alert("ยังไม่มีผลคำทำนายให้ส่งเข้า LINE");
        return;
      }

      // ช่วงทดสอบใน localhost ยังไม่มี LIFF profile จริง จึงให้กรอก LINE userId เพื่อทดสอบได้ก่อน
      // ตอนเชื่อม LIFF จริง ให้เปลี่ยนส่วนนี้เป็น liff.getProfile().userId
      let lineUserId = localStorage.getItem("duangwannee_line_user_id") || "";
      if (!lineUserId) {
        lineUserId = prompt("ใส่ LINE userId สำหรับทดสอบส่งจริง\nเมื่อต่อ LIFF แล้ว ระบบจะดึงค่านี้อัตโนมัติ");
        if (!lineUserId) return;
        localStorage.setItem("duangwannee_line_user_id", lineUserId.trim());
      }

      try {
        console.log("LINE result payload", { lineUserId: lineUserId.trim(), sourceType: result.type || svc.key, title: result.title || svc.name, resultText: buildLineResultText(result) });
        alert("บันทึกผลคำทำนายในระบบแล้ว\nขั้นส่งเข้า LINE OA จะต่อ API push message ในรอบถัดไป");
      } catch (err) {
        alert("เชื่อมต่อระบบส่ง LINE ไม่สำเร็จ: " + err.message);
      }
    };

    function TarotCards({cards, mode}) {
      if (!cards || !cards.length) return null;
      return (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {cards.map((card, i)=>(
            <div key={i} className="card" style={{padding:14}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                {card.image_url && (
                  <img src={card.image_url} alt={card.thai_name || card.card_name} style={{width:70,borderRadius:10,border:`1px solid ${C.border}`,background:C.surface}}/>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,color:C.goldLight,marginBottom:4}}>{card.position_name || `ใบที่ ${i+1}`}</div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:5}}>{card.thai_name || card.card_name}</div>
                  <div style={{fontSize:12,color:C.purpleLight,marginBottom:8}}>{card.card_name}</div>
                  {mode === "love" ? (
                    <div style={{fontSize:13,color:C.text,lineHeight:1.75}}>{card.love || card.overview}</div>
                  ) : (
                    <>
                      <div style={{fontSize:13,color:C.text,lineHeight:1.75,marginBottom:8}}>{card.overview}</div>
                      {card.love && <div style={{fontSize:12,color:C.muted,lineHeight:1.65}}>💕 ความรัก: {card.love}</div>}
                      {card.money && <div style={{fontSize:12,color:C.muted,lineHeight:1.65}}>💰 การเงิน: {card.money}</div>}
                      {card.work && <div style={{fontSize:12,color:C.muted,lineHeight:1.65}}>💼 การงาน: {card.work}</div>}
                    </>
                  )}
                  {(card.lucky_color || card.lucky_number) && (
                    <div style={{fontSize:12,color:C.goldLight,marginTop:8}}>
                      {card.lucky_color && `สีมงคล: ${card.lucky_color} `}
                      {card.lucky_number && `เลขมงคล: ${card.lucky_number}`}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    function PhoneResult({data}) {
      if (!data) return null;
      const overall = buildPhoneOverallSummary(data);
      return (
        <>
          <div className="card" style={{padding:14}}>
            <div style={{fontSize:15,fontWeight:700,color:C.goldLight,marginBottom:8}}>เบอร์ {data.phone}</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:10}}>ระบบอ่านเลขเป็นคู่: {(data.pairs || []).join(" • ")}</div>
            {(data.results || []).map((r, i)=>(
              <div key={i} style={{padding:"10px 0",borderBottom:i < data.results.length-1 ? `1px solid ${C.border}55` : "none"}}>
                <div style={{fontSize:13,fontWeight:700,color:C.purpleLight}}>เลขคู่ {r.pair_number || r.number}</div>
                {r.level && <div style={{fontSize:12,color:C.goldLight,lineHeight:1.6}}>ระดับ: {r.level}</div>}
                <div style={{fontSize:13,color:C.text,lineHeight:1.7}}>{r.meaning || r.description || "ยังไม่มีคำทำนาย"}</div>
                {r.advice && <div style={{fontSize:12,color:C.goldLight,lineHeight:1.6}}>คำแนะนำ: {r.advice}</div>}
              </div>
            ))}
          </div>
          <SummaryCard title="ผลรวมสรุปเบอร์โทร">
            {overall.text}
          </SummaryCard>
        </>
      );
    }

    function HomeNumberResult({data}) {
      const r = data?.result;
      if (!r) return null;
      return (
        <div className="card" style={{padding:14}}>
          <div style={{fontSize:12,color:C.muted,marginBottom:4}}>บ้านเลขที่ {data.number} → เลขรวม {data.calculated_number}</div>
          <div style={{fontSize:16,fontWeight:700,color:C.goldLight,marginBottom:8}}>{r.title || "ผลวิเคราะห์บ้านเลขที่"}</div>
          {r.meaning && <div style={{fontSize:13,color:C.text,lineHeight:1.8,marginBottom:8}}>{r.meaning}</div>}
          {r.good_points && <div style={{fontSize:12,color:C.green,lineHeight:1.7}}>จุดเด่น: {r.good_points}</div>}
          {r.warning && <div style={{fontSize:12,color:C.orange,lineHeight:1.7}}>ข้อควรระวัง: {r.warning}</div>}
          {r.remedy && <div style={{fontSize:13,color:C.goldLight,lineHeight:1.8,marginTop:10,padding:10,background:`${C.gold}11`,borderRadius:8}}>วิธีแก้เคล็ด: {r.remedy}</div>}
          {(r.lucky_color || r.lucky_item) && <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:8}}>
            {r.lucky_color && `สีมงคล: ${r.lucky_color} `}
            {r.lucky_item && `ของมงคล: ${r.lucky_item}`}
          </div>}
          {r.advice && <div style={{fontSize:12,color:C.text,lineHeight:1.7,marginTop:8}}>คำแนะนำ: {r.advice}</div>}
          <SummaryCard title="ผลรวมสรุปบ้านเลขที่">
            {buildHomeOverallSummary(data)}
          </SummaryCard>
        </div>
      );
    }

    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>go("services")}>←</button>
          <span className="topbar-title">{svc.name}</span>
        </div>
        <div className="content z1">
          <div className="hero" style={{paddingBottom:10}}>
            <div className="hero-icon">{svc.ico}</div>
            <h1 style={{fontSize:22}}>{svc.name}</h1>
            <p>ผลทำนายเบื้องต้นจากฐานข้อมูลดวงวันนี้</p>
          </div>

          {svc.key === "phone" && (
            <div className="fpad" style={{paddingBottom:0}}>
              <div className="fgroup">
                <label className="flabel">กรอกเบอร์โทรศัพท์</label>
                <input
                  id="dwStablePhoneInput"
                  className="finput"
                  placeholder="เช่น 0812345678"
                  inputMode="numeric"
                  autoComplete="off"
                  defaultValue={localStorage.getItem("dw_system_phone_input_v96") || phoneInput}
                  onFocus={()=>localStorage.setItem("dw_system_active_input_v97", "dwStablePhoneInput")}
                  onInput={e=>{
                    localStorage.setItem("dw_system_active_input_v97", "dwStablePhoneInput");
                    localStorage.setItem("dw_system_phone_input_v96", e.currentTarget.value);
                  }}
                  onBlur={e=>setPhoneInput(e.currentTarget.value)}
                />
              </div>
            </div>
          )}

          {svc.key === "home_number" && (
            <div className="fpad" style={{paddingBottom:0}}>
              <div className="fgroup">
                <label className="flabel">กรอกบ้านเลขที่</label>
                <input
                  id="dwStableHomeInput"
                  className="finput"
                  placeholder="เช่น 99/24"
                  inputMode="text"
                  autoComplete="off"
                  defaultValue={localStorage.getItem("dw_system_home_input_v96") || homeInput}
                  onFocus={()=>localStorage.setItem("dw_system_active_input_v97", "dwStableHomeInput")}
                  onInput={e=>{
                    localStorage.setItem("dw_system_active_input_v97", "dwStableHomeInput");
                    localStorage.setItem("dw_system_home_input_v96", e.currentTarget.value);
                  }}
                  onBlur={e=>setHomeInput(e.currentTarget.value)}
                />
              </div>
            </div>
          )}

          <div style={{padding:"0 16px 14px"}}>
            <button className="btn btn-gold" onClick={fetchSystemReading} disabled={loading}>
              {loading ? "กำลังอ่านคำทำนาย..." : `ดูคำทำนาย${topicLabel[svc.key] ? ` ${topicLabel[svc.key]}` : ""}`}
            </button>
            {(svc.key === "tarot" || svc.key === "love") && (
              <>
                <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8,textAlign:"center"}}>
                  เมนูนี้ดูได้วันละ 1 ครั้ง และรีเซ็ตสิทธิ์หลังเที่ยงคืน
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  style={{margin:"10px auto 0",display:"block",fontSize:11,color:C.muted}}
                  onClick={resetDailyLimitForTest}
                >
                  รีเซ็ตสิทธิ์ทดสอบ
                </button>
              </>
            )}
            {error && <div style={{marginTop:10,fontSize:12,color:C.red,lineHeight:1.6}}>{error}</div>}
          </div>

          {result && (
            <div style={{margin:"0 16px 14px"}}>
              <div style={{fontSize:13,color:C.goldLight,fontWeight:700,marginBottom:10}}>{result.title || svc.name}</div>
              {result.subtitle && <div style={{fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:10}}>{result.subtitle}</div>}
              {(result.type === "tarot" || result.type === "love") && (
                <>
                  <TarotCards cards={result.cards} mode={result.type}/>
                  <SummaryCard title={result.type === "love" ? "สรุปผลความรัก" : "สรุปผลไพ่ทาโรต์"}>
                    {result.summary || buildTarotSummary(result)}
                  </SummaryCard>
                </>
              )}
              {result.type === "single_tarot" && (
                <>
                  <TarotCards cards={result.cards} mode={result.sourceType}/>
                  {result.message && (
                    <div className="card" style={{padding:14,fontSize:13,color:C.text,lineHeight:1.8,marginTop:12}}>{result.message}</div>
                  )}
                  <SummaryCard title={result.sourceType === "finance" ? "สรุปผลการเงิน" : "สรุปผลการงาน"}>
                    {result.summary}
                  </SummaryCard>
                </>
              )}
              {result.type === "phone" && <PhoneResult data={result}/>}
              {result.type === "home_number" && <HomeNumberResult data={result}/>}
              {result.message && result.type !== "single_tarot" && (
                <div className="card" style={{padding:14,fontSize:13,color:C.text,lineHeight:1.8}}>{result.message}</div>
              )}
              {result.summary && !["tarot","love","phone","home_number","single_tarot"].includes(result.type) && (
                <SummaryCard>{result.summary}</SummaryCard>
              )}
            </div>
          )}

          <div style={{margin:"0 16px",padding:14,background:`${C.purple}11`,border:`1px solid ${C.purple}33`,borderRadius:12,fontSize:12,color:C.muted,lineHeight:1.7}}>
            ผลจากระบบเป็นคำทำนายเบื้องต้น หากต้องการคำตอบเฉพาะเรื่องหรือคำแนะนำละเอียด สามารถปรึกษาหมอดูได้
          </div>
          <div style={{padding:16}}>
            <button className="btn btn-gold" onClick={()=>{setActiveNav("seers");go("seers")}}>ปรึกษาหมอดู</button>
            {(svc.key === "tarot" || svc.key === "love") && result && (
              <button
                className="btn btn-outline"
                style={{marginTop:10,color:C.green,borderColor:C.green+"66"}}
                onClick={sendResultToLine}
              >
                ส่งข้อมูลเข้าไลน์
              </button>
            )}
            {svc.key === "phone" && (
              <button
                className="btn btn-outline"
                style={{marginTop:10,color:C.goldLight,borderColor:C.gold+"66"}}
                onClick={()=>window.open("https://truestore.live/r/dk0B7", "_blank", "noopener,noreferrer")}
              >
                เลือกซื้อเบอร์มงคล
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Seers List ──────────────────────────────────────────────────────────────
  function SeersList() {
    const baseFilters = ["ทั้งหมด","ไพ่ทาโรต์","โหราศาสตร์","เลขศาสตร์","เบอร์โทร"];
    const filters = favoriteSeers.length ? ["หมอโปรดของคุณ", ...baseFilters] : baseFilters;
    const visibleSeers = allSeers.filter(s => {
      if (seerListFilter === "หมอโปรดของคุณ") return favoriteSeers.includes(s.id);
      if (seerListFilter === "ทั้งหมด") return true;
      return `${s.spec} ${s.bio}`.includes(seerListFilter);
    });
    return (
      <div className="scr on">
        <div className="topbar"><span className="topbar-title">ทำเนียบหมอดู</span></div>
        <div className="content z1">
          <div style={{padding:"8px 16px 4px",display:"flex",gap:8,overflow:"auto",scrollbarWidth:"none"}}>
            {filters.map(f=>(
              <span
                key={f}
                className="tag"
                onClick={()=>setSeerListFilter(f)}
                style={{
                  cursor:"pointer",
                  padding:"5px 12px",
                  fontSize:11,
                  whiteSpace:"nowrap",
                  background:seerListFilter===f?`${C.gold}22`:undefined,
                  borderColor:seerListFilter===f?`${C.gold}88`:undefined,
                  color:seerListFilter===f?C.goldLight:undefined
                }}
              >{f}</span>
            ))}
          </div>
          {favoriteSeers.length > 0 && (
            <div style={{margin:"6px 16px 4px",padding:"10px 12px",background:`${C.gold}0f`,border:`1px solid ${C.gold}33`,borderRadius:12,fontSize:12,color:C.muted,lineHeight:1.6}}>
              💛 มีหมอโปรดของคุณ {favoriteSeers.length} ท่าน แตะแถบ “หมอโปรดของคุณ” เพื่อดูเฉพาะหมอที่บันทึกไว้
            </div>
          )}
          <div className="sec-hd">{seerListFilter === "หมอโปรดของคุณ" ? "หมอโปรดของคุณ" : "หมอดูทั้งหมด"} {visibleSeers.length} ท่าน</div>
          {visibleSeers.length === 0 && (
            <div style={{margin:"0 16px 12px",padding:14,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,fontSize:12,color:C.muted,lineHeight:1.7}}>
              ยังไม่มีหมอในหมวดนี้ หากต้องการบันทึกหมอโปรด ให้เข้าไปที่หน้ารายละเอียดหมอดูแล้วกด “หมอโปรด”
            </div>
          )}
          {visibleSeers.map(s=>(
            <div key={s.id} className="seer-card card card-gold" onClick={()=>{setSelSeer(s);go("seer_detail",{seer_id:s.seerId || s.id})}}>
              <div className="seer-inner">
                <SeerAvatar seer={s} className="seer-ava" />
                <div className="seer-info">
                  <div className="seer-name">{s.name} {s.feedBoost&&<span style={{fontSize:10,color:C.goldLight}}>โปรโมท</span>}</div>
                  <div className="seer-spec">{s.spec}</div>
                  <div className={`status-line ${getSeerStatusClass(s.status)}`} style={{marginBottom:5}}><span className="status-dot"/> {getSeerStatusLabel(s.status)} <span style={{fontWeight:400,color:C.muted}}>• ตอบกลับเฉลี่ย {s.avgReply}</span></div>
                  {s.status==="offline" && <div style={{fontSize:11,color:C.muted,marginBottom:5}}>ออนไลน์ล่าสุด {s.lastOnline}</div>}
                  <div className="seer-bio">{s.bio}</div>
                  <div className="seer-row">
                    <span className="rating">★ {s.rating} ({s.reviews} รีวิว)</span>
                    <span className="price-tag">เริ่ม ฿{getMinPriceForSeer(s)}</span>
                  </div>
                </div>
              </div>
              <div style={{padding:"9px 14px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end"}}>
                <span style={{fontSize:12,color:C.gold}}>ดูรายละเอียด / จองคิว →</span>
              </div>
            </div>
          ))}
        </div>
        <BottomNav/>
      </div>
    );
  }

  // ── Seer Detail ─────────────────────────────────────────────────────────────
  function SeerDetail() {
    const s = selSeer; if(!s) return null;
    const svcs = getServicesForSeer(s);
    const bookingSvcsV109 = getBookingServicesForSeerV109(s);
    const liveSvc = bookingSvcsV109[0] || svcs.find(x=>x.input==="booking") || {id:"live",name:"จองคิวดูดวงสด",type:"live",price:Number(s?.minPrice||199),ico:"🎯",desc:"ปรึกษาหมอดูแบบสด",input:"booking"};
    const expertise = [
      {ico:"🔮", title:"ศาสตร์ที่ถนัด", text:s.spec},
      {ico:"✨", title:"แนวทางการทำนาย", text:s.bio},
      {ico:"📌", title:"เหมาะสำหรับ", text:"ปรึกษาเรื่องความรัก การงาน การเงิน เบอร์โทร ไพ่ และคำถามเฉพาะทางตามความถนัดของหมอดู"},
    ];
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>go("seers")}>←</button>
          <span className="topbar-title">รายละเอียดหมอดู</span>
        </div>
        <div className="content z1">
          <div className="profile-hero">
            <SeerAvatar seer={s} className="ava-lg" />
            <div className="profile-name">{s.name}</div>
            <div style={{fontSize:13,color:C.purpleLight,marginBottom:8}}>{s.spec}</div>
            <div className={`status-line ${getSeerStatusClass(s.status)}`} style={{justifyContent:"center",marginBottom:8}}><span className="status-dot"/> {getSeerStatusLabel(s.status)}</div>
            <div style={{color:C.gold,fontSize:14,marginBottom:10}}>{"★".repeat(Math.floor(s.rating))} {s.rating} ({s.reviews} รีวิว)</div>
            <p style={{fontSize:13,color:C.muted,lineHeight:1.7}}>{s.bio}</p>
            <div style={{marginTop:14,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"6px 12px"}}>
              <div className="meta-row"><span>ดูดวงแล้ว</span><b style={{color:C.text}}>{s.answered} ครั้ง</b></div>
              <div className="meta-row"><span>ดูดวงล่าสุด</span><b style={{color:C.text}}>{s.latestViews} ครั้ง</b></div>
              <div className="meta-row"><span>แชท</span><b style={{color:C.text}}>{s.chatCount} ครั้ง</b></div>
              <div className="meta-row"><span>อัตราตอบกลับเฉลี่ย</span><b style={{color:C.green}}>{s.avgReply}</b></div>
              {s.status==="offline" && <div className="meta-row"><span>เวลาออนไลน์ล่าสุด</span><b style={{color:C.text}}>{s.lastOnline}</b></div>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:14}}>
              <button
                className="btn btn-outline btn-sm"
                style={{width:"100%",padding:"9px 6px",fontSize:11,color:favoriteSeers.includes(s.id)?C.goldLight:C.muted,borderColor:favoriteSeers.includes(s.id)?`${C.gold}88`:C.border}}
                onClick={()=>toggleFavoriteSeer(s.id)}
              >{favoriteSeers.includes(s.id)?"💛 หมอโปรด":"♡ หมอโปรด"}</button>
              <button
                className="btn btn-outline btn-sm"
                style={{width:"100%",padding:"9px 6px",fontSize:11,color:notifySeers.includes(s.id)?C.green:C.muted,borderColor:notifySeers.includes(s.id)?`${C.green}88`:C.border}}
                onClick={()=>toggleNotifySeer(s.id)}
              >{notifySeers.includes(s.id)?"🔔 เปิดแจ้งเตือน":"🔕 แจ้งออนไลน์"}</button>
              <button
                className="btn btn-outline btn-sm"
                style={{width:"100%",padding:"9px 6px",fontSize:11}}
                onClick={()=>shareSeer(s)}
              >↗ แชร์หมอ</button>
            </div>
          </div>
          <div className="gold-bar" style={{margin:"16px 0 0"}}/>
          <div className="sec-hd">รายละเอียด / ความชำนาญ</div>
          {expertise.map((x,i)=>(
            <div key={i} style={{margin:"0 16px 10px",padding:14,background:C.card,border:`1px solid ${C.border}`,borderRadius:13}}>
              <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{fontSize:22,flexShrink:0}}>{x.ico}</span>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>{x.title}</div>
                  <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>{x.text}</div>
                </div>
              </div>
            </div>
          ))}
          <div style={{margin:"12px 16px 16px",padding:14,background:`linear-gradient(135deg,${C.goldDim},${C.card})`,border:`1px solid ${C.gold}44`,borderRadius:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:12}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.goldLight}}>จองคิวดูดวงสด</div>
                <div style={{fontSize:12,color:C.muted,marginTop:3}}>เลือกวันและช่วงเวลาที่สะดวก เพื่อปรึกษาหมอดูโดยตรง</div>
              </div>
              <div style={{fontSize:18,fontWeight:700,color:C.goldLight,whiteSpace:"nowrap"}}>
                {bookingSvcsV109.length > 1
                  ? `฿${Math.min(...bookingSvcsV109.map(x=>Number(x.price)||0).filter(Boolean)).toLocaleString()} - ฿${Math.max(...bookingSvcsV109.map(x=>Number(x.price)||0).filter(Boolean)).toLocaleString()}`
                  : `฿${Number(liveSvc.price || 0).toLocaleString()}`}
              </div>
            </div>
            <button className="btn btn-gold" onClick={()=>setRateModal(liveSvc)}>เลือกดูดวง / ดูเรท</button>
          </div>
          <div className="sec-hd">รีวิวจากลูกค้า</div>
          {(() => {
            const defaultReviews = [{n:"ก.",t:"ดูไพ่แม่นมาก บอกเรื่องงานได้ตรงเลยค่ะ",st:5},{n:"ธ.",t:"คุยสบาย ให้คำแนะนำดีมาก จะกลับมาอีกครับ",st:5}];
            const customReviews = seerReviews.filter(r=>String(r.seer_id)===String(s.seerId || s.id));
            const allReviews = [...customReviews, ...defaultReviews];
            const canReview = orders.some(o=>String(o.seer_id)===String(s.seerId || s.id) && ["answered","completed"].includes(o.status));
            const hasReviewed = seerReviews.some(r=>String(r.seer_id)===String(s.seerId || s.id) && r.source !== "admin");
            return (
              <>
                {canReview && !hasReviewed && (
                  <div style={{margin:"0 16px 12px",padding:14,background:C.card,border:`1px solid ${C.gold}44`,borderRadius:14}}>
                    <div style={{fontSize:14,fontWeight:700,color:C.goldLight,marginBottom:8}}>เขียนรีวิวหมอดูท่านนี้</div>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                      <span style={{fontSize:12,color:C.muted}}>ให้ดาว</span>
                      <select className="finput" style={{width:110,padding:"8px 10px"}} value={reviewForm.rating} onChange={e=>setReviewForm(f=>({...f,rating:e.target.value}))}>
                        <option value="5">5 ดาว</option><option value="4">4 ดาว</option><option value="3">3 ดาว</option><option value="2">2 ดาว</option><option value="1">1 ดาว</option>
                      </select>
                    </div>
                    <textarea className="finput" placeholder="เขียนความประทับใจหลังใช้บริการ เช่น แม่นมาก ให้คำแนะนำดี คุยสนุก" value={reviewForm.text} onChange={e=>setReviewForm(f=>({...f,text:e.target.value}))}/>
                    <button className="btn btn-gold" style={{marginTop:10}} onClick={()=>submitSeerReview(s)}>ส่งรีวิว</button>
                    <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>รีวิวได้ 1 ครั้งต่อหมอดู 1 ท่าน เฉพาะลูกค้าที่เคยจอง/ใช้บริการแล้ว</div>
                  </div>
                )}
                {hasReviewed && (
                  <div style={{margin:"0 16px 12px",padding:12,background:`${C.green}11`,border:`1px solid ${C.green}33`,borderRadius:12,fontSize:12,color:C.green,lineHeight:1.6}}>
                    คุณรีวิวหมอดูท่านนี้แล้ว ขอบคุณที่ช่วยแบ่งปันประสบการณ์ให้ลูกค้าท่านอื่น
                  </div>
                )}
                {!canReview && (
                  <div style={{margin:"0 16px 12px",padding:12,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,fontSize:12,color:C.muted,lineHeight:1.6}}>
                    หลังจากดูดวงกับหมอดูท่านนี้เสร็จแล้ว ระบบจึงจะเปิดให้เขียนรีวิวได้ 1 ครั้ง
                  </div>
                )}
                {allReviews.map((r,i)=>(
                  <div key={r.id || i} style={{margin:"0 16px 10px",padding:13,background:C.surface,border:`1px solid ${C.border}`,borderRadius:11}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                      <span style={{fontSize:13,fontWeight:600,color:C.text}}>{r.n}</span>
                      <span style={{color:C.gold,fontSize:12}}>{"★".repeat(Number(r.st)||5)}</span>
                    </div>
                    {r.verified && <div style={{fontSize:10,color:C.green,marginBottom:5}}>✓ รีวิวจากลูกค้าที่ใช้บริการแล้ว</div>}
                    <p style={{fontSize:12,color:C.muted,lineHeight:1.6}}>{r.t}</p>
                  </div>
                ))}
              </>
            );
          })()}
          <div style={{height:20}}/>
        </div>
        {rateModal && (
          <div className="rate-modal-bg" onClick={()=>setRateModal(null)}>
            <div className="rate-modal" onClick={e=>e.stopPropagation()}>
              <div className="rate-head">ดูดวงแบบไหน<button className="rate-close" onClick={()=>setRateModal(null)}>×</button></div>
              <div className="rate-body">
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                  <SeerAvatar seer={s} className="seer-ava" style={{width:58,height:58,fontSize:24}} />
                  <div>
                    <div style={{fontSize:18,fontWeight:800,color:"#5b6575"}}>{s.name}</div>
                    <div style={{fontSize:13,color:"#8d98aa"}}>ดูดวงสดแบบนัดเวลา</div>
                  </div>
                </div>
                {getBookingServicesForSeerV109(s).map((pkg, idx)=>(
                  <div className="rate-price" key={pkg.id || idx} style={{marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <div className="rate-coin">🪙</div>
                      <div>
                        <div className="rate-main">{pkg.minutes ? `${pkg.minutes} นาที` : (pkg.name || "ดูดวงสด")}</div>
                        <div className="rate-sub">{pkg.desc || "เลือกแพ็กเกจที่ต้องการ"}</div>
                      </div>
                    </div>
                    <button className="btn btn-purple btn-sm" style={{fontSize:18,padding:"13px 20px",borderRadius:24}} onClick={()=>{
                      setSelSvc(pkg);
                      setRateModal(null);
                      setBookingStep("rules");
                      setSelSlot(null);
                      setForm({name:"",phone:"",contact:"LINE",question:"",phoneNum:"",slip:null});
                      go("book_form");
                    }}>฿{Number(pkg.price || 0).toLocaleString()}</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Book Form / นัดเวลา / ชำระเงิน v91 ─────────────────────────────────────
  function BookForm() {
    const s = selSeer;
    const bookingPackagesV130 = s ? getBookingServicesForSeerV109(s).slice(0, 5) : [];
    const selectedPackageV130 = bookingPackagesV130.find(x => String(x.id) === String(bookingForm.package_id)) || selSvc || bookingPackagesV130[0] || (s ? (getServicesForSeer(s).find(x=>x.input==="booking") || {name:"จองคิวดูดวงสด", price:Number(s?.minPrice||199)}) : null);
    const sv = selectedPackageV130;
    const selectedDateV113 = bookingForm.requested_date || "";
    const seerIdKeysV113 = [s?.seerId, s?.id, s?.lineUserId, s?.line_user_id, s?.name].filter(Boolean).map(String);
    const blockedSlotKeysV113 = blockedSlotKeysForSeerV176(bookings || [], seerIdKeysV113);
    const remoteBlockedSlotsV176 = slotAvailabilityV176[bookingAvailabilityKeyV176(s, selectedDateV113)] || [];
    remoteBlockedSlotsV176.forEach(slot => blockedSlotKeysV113.add(slotKeyV113(selectedDateV113, slot)));
    // v180: ลูกค้าจะเห็นเวลา 00:00-04:00 เฉพาะวันที่หมอดูเปิดเป็นช่วงเวลาพิเศษ
    const specialOpenSlotsV180 = selectedDateV113 ? (specialNightAvailabilityV180[bookingAvailabilityKeyV176(s, selectedDateV113)] || []) : [];
    const regularOpenSlotsV191 = selectedDateV113 ? (regularAvailabilityV191[bookingAvailabilityKeyV176(s, selectedDateV113)] || BOOKING_TIME_SLOTS_V92) : BOOKING_TIME_SLOTS_V92;
    const allOpenSlotsV191 = [...regularOpenSlotsV191, ...specialOpenSlotsV180];
    const selectedDurationV191 = serviceDurationV191(sv);
    const canBookStartV191 = (slot) => !!selectedDateV113 && !isSlotTooCloseForTodayV120(selectedDateV113, slot) && canSelectBookingStartV191({date:selectedDateV113,slot,service:sv,openSlots:allOpenSlotsV191,blockedKeys:blockedSlotKeysV113});
    const displayedBookingSlotsV180 = [...regularOpenSlotsV191, ...specialOpenSlotsV180];
    if(!s||!sv) return null;
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>go("seer_detail")}>←</button>
          <span className="topbar-title">ขอนัดหมอดู</span>
        </div>
        <div className="content z1">
          <div style={{margin:16,padding:14,background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:13}}>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <SeerAvatar seer={s} className="seer-ava" style={{width:52,height:52,fontSize:22}} />
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:800,color:C.goldLight}}>{s.name}</div>
                <div style={{fontSize:13,color:C.purpleLight}}>{sv.name || `ดูดวงสด ${selectedDurationV191} นาที`}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:4}}>ระยะเวลาดูดวง {selectedDurationV191} นาที • ระบบกันเวลาในตารางครั้งละ 30 นาที เพื่อไม่ให้คิวซ้อนกัน</div>
              </div>
              <div style={{fontSize:18,fontWeight:900,color:C.goldLight}}>฿{Number(sv.price || 999).toLocaleString()}</div>
            </div>
          </div>
          <div className="sec-hd">เลือกวัน / เวลา ที่ต้องการนัด</div>
          <div className="fpad">
            <div className="warn-box" style={{marginBottom:14}}>
              Flow การจอง: ลูกค้าเลือกวัน/เวลา → โอนเงินและแนบสลิป → แอดมินตรวจ → หมอดูรับงานหรือขอเลื่อนเวลา → ลูกค้ายอมรับเวลาใหม่ (ถ้ามี) → หมอดูรับงาน
            </div>
            <div className="fgroup">
              <label className="flabel">เลือกรายการดูดวง *</label>
              <div style={{display:"grid",gap:10}}>
                {bookingPackagesV130.map((pkg, i) => {
                  const active = String((bookingForm.package_id || sv?.id || "")) === String(pkg.id);
                  return (
                    <button
                      key={pkg.id || i}
                      type="button"
                      className={`btn ${active ? "btn-purple" : "btn-outline"}`}
                      style={{textAlign:"left",display:"block",padding:12,borderRadius:14}}
                      onClick={()=>{ setSelSvc(pkg); setBookingForm(p=>({...p,package_id:pkg.id})); }}
                    >
                      <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                        <div>
                          <div style={{fontWeight:900,color:active?C.text:C.goldLight}}>{pkg.name || `รายการที่ ${i+1}`}</div>
                          <div style={{fontSize:11,color:C.muted,lineHeight:1.55,marginTop:4}}>{pkg.desc || "รายละเอียดบริการดูดวง"}</div>
                          {pkg.minutes ? <div style={{fontSize:11,color:C.purpleLight,marginTop:4}}>ระยะเวลา: {pkg.minutes} นาที</div> : null}
                        </div>
                        <div style={{fontWeight:900,color:C.goldLight,whiteSpace:"nowrap"}}>฿{Number(pkg.price||0).toLocaleString()}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>เลือกรายการที่ต้องการ ราคานี้จะใช้ตอนโอนเงินหลังหมอดูยืนยันเวลา</div>
            </div>
            <div className="fgroup">
              <label className="flabel">ชื่อผู้จอง</label>
              <input id="dwBookingNameV91" className="finput" placeholder="ชื่อของท่าน" defaultValue={currentLineUser?.displayName || form.name || ""} />
            </div>
            <div className="fgroup">
              <label className="flabel">วันที่ต้องการนัด *</label>
              <div style={{
                position:"relative",
                padding:10,
                borderRadius:18,
                background:"linear-gradient(180deg, rgba(255,215,120,0.16), rgba(116,77,255,0.12))",
                border:`1px solid ${bookingForm.requested_date ? C.gold : `${C.gold}66`}`,
                boxShadow: bookingForm.requested_date
                  ? `0 0 0 2px ${C.gold}22, 0 10px 30px rgba(0,0,0,.18)`
                  : `0 0 0 2px ${C.gold}12, inset 0 1px 0 rgba(255,255,255,.06)`
              }}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:8}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:900,color:C.goldLight,letterSpacing:.2}}>เลือกวันนัดหมาย</div>
                    <div style={{fontSize:11,color:bookingForm.requested_date ? C.purpleLight : C.muted,marginTop:2}}>
                      {bookingForm.requested_date ? `วันที่เลือก: ${bookingForm.requested_date}` : "แตะช่องด้านล่างเพื่อเปิดปฏิทินทันที"}
                    </div>
                  </div>
                  <div style={{
                    minWidth:42,height:42,borderRadius:12,display:"grid",placeItems:"center",
                    background:"rgba(255,215,120,.18)",border:`1px solid ${C.gold}55`,fontSize:22
                  }}>📅</div>
                </div>
                <input
                  id="dwBookingDateV91"
                  className="finput"
                  type="date"
                  min={localDateInputValue()}
                  value={bookingForm.requested_date}
                  onFocus={e=>e.currentTarget.showPicker?.()}
                  onClick={e=>e.currentTarget.showPicker?.()}
                  onChange={e=>setBookingForm(p=>({...p,requested_date:e.target.value,requested_time:""}))}
                  style={{
                    fontSize:18,
                    fontWeight:900,
                    color:C.text,
                    paddingRight:16,
                    minHeight:58,
                    border:`2px solid ${bookingForm.requested_date ? C.gold : `${C.gold}88`}`,
                    background:"rgba(16,10,48,.84)",
                    boxShadow: bookingForm.requested_date ? `0 0 0 3px ${C.gold}18` : "none"
                  }}
                />
              </div>
              <div style={{fontSize:11,color:C.goldLight,lineHeight:1.6,marginTop:7,fontWeight:700}}>กดช่องวันที่เพื่อเปิดปฏิทิน เลือกได้ตั้งแต่วันนี้เป็นต้นไป</div>
            </div>
            <div className="fgroup">
              <label className="flabel">ช่วงเวลาที่ต้องการนัด *</label>
              {!selectedDateV113 ? (
                <div className="warn-box" style={{marginBottom:8}}>กรุณาเลือกวันที่ก่อน แล้วระบบจะแสดงช่วงเวลาที่จองได้</div>
              ) : (
                <>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {BOOKING_TIME_SLOTS_V92.map(slot => {
                      if (!canBookStartV191(slot)) return null;
                      const selectedRange = bookingDisplayRangeV191(slot, sv);
                      return (
                        <button
                          key={slot}
                          type="button"
                          className={`btn btn-sm ${bookingForm.requested_time === selectedRange ? "btn-purple" : "btn-outline"}`}
                          style={{padding:"11px 8px",fontSize:13,borderRadius:12}}
                          onClick={()=>setBookingForm(p=>({...p,requested_time:selectedRange}))}
                        >
                          {selectedRange}
                        </button>
                      );
                    })}
                  </div>
                  {specialOpenSlotsV180.length > 0 && (
                    <div style={{marginTop:14,padding:12,borderRadius:14,background:`${C.purple}16`,border:`1px solid ${C.purple}66`}}>
                      <div style={{fontSize:12,fontWeight:900,color:C.purpleLight,marginBottom:8}}>🌙 เวลาพิเศษหลังเที่ยงคืน — หมอดูเปิดรับในวันนี้</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        {specialOpenSlotsV180.map(slot => {
                          if (!canBookStartV191(slot)) return null;
                          const selectedRange = bookingDisplayRangeV191(slot, sv);
                          return (
                            <button
                              key={slot}
                              type="button"
                              className={`btn btn-sm ${bookingForm.requested_time === selectedRange ? "btn-purple" : "btn-outline"}`}
                              style={{padding:"11px 8px",fontSize:13,borderRadius:12}}
                              onClick={()=>setBookingForm(p=>({...p,requested_time:selectedRange}))}
                            >
                              {`🌙 ${selectedRange}`}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
              {selectedDateV113 && displayedBookingSlotsV180.every(slot => !canBookStartV191(slot)) && (
                <div className="warn-box" style={{marginTop:8}}>วันนี้ไม่มีช่วงเวลาต่อเนื่องที่จองได้ตามระยะเวลาบริการ กรุณาเลือกวันอื่น</div>
              )}
              <input id="dwBookingTimeV91" type="hidden" value={bookingForm.requested_time} readOnly />
              <div style={{fontSize:11,color:bookingForm.requested_time ? C.goldLight : C.muted,lineHeight:1.6,marginTop:8}}>
                {bookingForm.requested_time ? `เลือกแล้ว: ${bookingForm.requested_time}` : "แตะเลือกช่วงเวลา ไม่ต้องพิมพ์เอง"}
              </div>
            </div>
            <div className="fgroup">
              <label className="flabel">เรื่องที่ต้องการดูดวง *</label>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginBottom:7}}>ระบุเรื่องที่อยากดูหรือคำถามเบื้องต้น เพื่อให้หมอดูเตรียมข้อมูลก่อนถึงเวลานัด</div>
              <textarea
                id="dwBookingNoteV91"
                className="finput"
                placeholder="เช่น อยากดูเรื่องความรัก การงาน การเงิน หรือมีคำถามที่อยากถาม"
                style={{height:112,resize:"vertical"}}
                defaultValue={bookingForm.customer_note || ""}
                onInput={e=>{ /* v183: ไม่ setState ทุกตัวอักษร เพื่อไม่ให้ช่องกรอกกระตุก/หลุดโฟกัส */ e.currentTarget.dataset.dirty = "1"; }}
              />
            </div>
            <button className="btn btn-gold" disabled={bookingLoading || !bookingForm.requested_date || !bookingForm.requested_time} onClick={createBookingV91}>{bookingLoading?"กำลังสร้างรายการ...":"ไปชำระเงิน / แนบสลิป ✨"}</button>
            <button className="btn btn-outline" style={{marginTop:10}} onClick={()=>{setActiveNav("my_bookings");go("my_bookings")}}>ดูนัดของฉัน</button>
          </div>
        </div>
      </div>
    );
  }


  function RescheduleRequestModalV168() {
    const b = rescheduleBookingV168;
    if (!b) return null;
    const date = rescheduleFormV168.date || "";
    const seerKeysV176 = [b.seer_id, b.seer_line_user_id, b.seer_name].filter(Boolean).map(String);
    const blockedRescheduleSlotsV176 = blockedSlotKeysForSeerV176(bookings || [], seerKeysV176);
    (slotAvailabilityV176[bookingAvailabilityKeyV176(b, date)] || []).forEach(slot => blockedRescheduleSlotsV176.add(slotKeyV113(date, slot)));
    const specialRescheduleSlotsV180 = date ? (specialNightAvailabilityV180[bookingAvailabilityKeyV176(b, date)] || []) : [];
    const regularRescheduleSlotsV191 = date ? (regularAvailabilityV191[bookingAvailabilityKeyV176(b, date)] || BOOKING_TIME_SLOTS_V92) : BOOKING_TIME_SLOTS_V92;
    const allRescheduleOpenSlotsV191 = [...regularRescheduleSlotsV191, ...specialRescheduleSlotsV180];
    const canRescheduleAtV191 = (slot) => !!date && !isSlotTooCloseForTodayV120(date, slot) && canSelectBookingStartV191({date,slot,service:b,openSlots:allRescheduleOpenSlotsV191,blockedKeys:blockedRescheduleSlotsV176});
    return (
      <div style={{position:"fixed",inset:0,zIndex:1000002,background:"rgba(0,0,0,.74)",display:"flex",alignItems:"flex-end",justifyContent:"center",padding:0}}>
        <div style={{width:"100%",maxWidth:560,maxHeight:"92dvh",overflowY:"auto",background:"linear-gradient(180deg,#20164a,#110D26)",border:`1px solid ${C.gold}66`,borderRadius:"24px 24px 0 0",boxShadow:"0 -18px 70px rgba(0,0,0,.65)",padding:"18px 16px calc(22px + env(safe-area-inset-bottom))"}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:14}}>
            <div style={{width:44,height:44,borderRadius:14,display:"grid",placeItems:"center",background:`${C.gold}18`,border:`1px solid ${C.gold}55`,fontSize:22}}>⏰</div>
            <div style={{flex:1}}>
              <div style={{fontSize:18,fontWeight:900,color:C.goldLight}}>ขอเลื่อนเวลานัด</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.6,marginTop:3}}>ระบุวันและเวลาใหม่ แล้วระบบจะแจ้งลูกค้าและแอดมินทันที</div>
            </div>
            <button type="button" className="btn btn-outline btn-sm" style={{width:42,padding:"7px 0"}} onClick={()=>setRescheduleBookingV168(null)}>×</button>
          </div>
          <div className="card" style={{padding:12,marginBottom:14,borderColor:`${C.purple}66`}}>
            <div style={{fontSize:13,fontWeight:900,color:C.text}}>{b.customer_name || "ลูกค้า"} • {b.service_name || "ดูดวง"}</div>
            <div style={{fontSize:12,color:C.purpleLight,marginTop:4}}>เวลาที่ลูกค้าเลือก: {dwBookingScheduledText(b)}</div>
          </div>
          <div className="fgroup">
            <label className="flabel">วันที่ใหม่ *</label>
            <input className="finput" type="date" min={localDateInputValue()} value={date} onFocus={e=>e.currentTarget.showPicker?.()} onClick={e=>e.currentTarget.showPicker?.()} onChange={e=>setRescheduleFormV168(p=>({...p,date:e.target.value,time:""}))} style={{fontSize:17,fontWeight:800,minHeight:54,border:`2px solid ${C.gold}88`}} />
          </div>
          <div className="fgroup">
            <label className="flabel">เวลาใหม่ *</label>
            {!date ? <div className="warn-box">กรุณาเลือกวันใหม่ก่อน แล้วจึงเลือกเวลา</div> : (
              <>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {BOOKING_TIME_SLOTS_V92.map(slot => {
                    if (!canRescheduleAtV191(slot)) return null;
                    const range = bookingDisplayRangeV191(slot, b);
                    return <button key={slot} type="button" className={`btn btn-sm ${rescheduleFormV168.time===range ? "btn-purple" : "btn-outline"}`} style={{padding:"12px 8px",borderRadius:12}} onClick={()=>setRescheduleFormV168(p=>({...p,time:range}))}>{range}</button>;
                  })}
                </div>
                {specialRescheduleSlotsV180.length > 0 && <div style={{marginTop:12,padding:10,borderRadius:12,background:`${C.purple}16`,border:`1px solid ${C.purple}66`}}>
                  <div style={{fontSize:12,fontWeight:900,color:C.purpleLight,marginBottom:8}}>🌙 เวลาพิเศษหลังเที่ยงคืนที่เปิดไว้</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {specialRescheduleSlotsV180.map(slot => {
                      if (!canRescheduleAtV191(slot)) return null;
                      const range = bookingDisplayRangeV191(slot, b);
                      return <button key={slot} type="button" className={`btn btn-sm ${rescheduleFormV168.time===range ? "btn-purple" : "btn-outline"}`} style={{padding:"12px 8px",borderRadius:12}} onClick={()=>setRescheduleFormV168(p=>({...p,time:range}))}>{`🌙 ${range}`}</button>;
                    })}
                  </div>
                </div>}
              </>
            )}
          </div>
          <div className="fgroup">
            <label className="flabel">ข้อความถึงลูกค้า</label>
            <textarea className="finput" value={rescheduleFormV168.note} onChange={e=>setRescheduleFormV168(p=>({...p,note:e.target.value}))} style={{height:98}} placeholder="เช่น เวลาที่ลูกค้าเลือกไม่สะดวก ขอเสนอเวลานี้แทนค่ะ" />
          </div>
          <div className="warn-box" style={{borderColor:`${C.green}55`,background:`${C.green}11`,color:C.green,marginBottom:12}}>ลูกค้าชำระเงินแล้ว การขอเลื่อนเวลานี้จะไม่ให้ลูกค้าโอนเงินหรือแนบสลิปซ้ำ</div>
          <button type="button" className="btn btn-gold" disabled={bookingLoading || !date || !rescheduleFormV168.time} onClick={submitSeerRescheduleV168}>{bookingLoading ? "กำลังส่งคำขอ..." : "ส่งคำขอเลื่อนเวลา"}</button>
        </div>
      </div>
    );
  }


  function CustomerReselectSlotModalV176() {
    const b = customerReselectBookingV176;
    if (!b) return null;
    const date = customerReselectFormV176.date || "";
    const seerKeys = [b.seer_id, b.seer_line_user_id, b.seer_name].filter(Boolean).map(String);
    const blockedSlots = blockedSlotKeysForSeerV176(bookings || [], seerKeys);
    (slotAvailabilityV176[bookingAvailabilityKeyV176(b, date)] || []).forEach(slot => blockedSlots.add(slotKeyV113(date, slot)));
    const specialCustomerReselectSlotsV180 = date ? (specialNightAvailabilityV180[bookingAvailabilityKeyV176(b, date)] || []) : [];
    const regularCustomerReselectSlotsV191 = date ? (regularAvailabilityV191[bookingAvailabilityKeyV176(b, date)] || BOOKING_TIME_SLOTS_V92) : BOOKING_TIME_SLOTS_V92;
    const allCustomerReselectOpenV191 = [...regularCustomerReselectSlotsV191, ...specialCustomerReselectSlotsV180];
    const canCustomerReselectAtV191 = (slot) => !!date && !isSlotTooCloseForTodayV120(date, slot) && canSelectBookingStartV191({date,slot,service:b,openSlots:allCustomerReselectOpenV191,blockedKeys:blockedSlots});
    return (
      <div style={{position:"fixed",inset:0,zIndex:1000004,background:"rgba(0,0,0,.76)",display:"flex",alignItems:"flex-end",justifyContent:"center",padding:0}}>
        <div style={{width:"100%",maxWidth:560,maxHeight:"92dvh",overflowY:"auto",background:"linear-gradient(180deg,#20164a,#110D26)",border:`1px solid ${C.gold}66`,borderRadius:"24px 24px 0 0",boxShadow:"0 -18px 70px rgba(0,0,0,.65)",padding:"18px 16px calc(22px + env(safe-area-inset-bottom))"}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:14}}>
            <div style={{width:44,height:44,borderRadius:14,display:"grid",placeItems:"center",background:`${C.red}18`,border:`1px solid ${C.red}66`,fontSize:22}}>⏰</div>
            <div style={{flex:1}}>
              <div style={{fontSize:18,fontWeight:900,color:C.goldLight}}>เวลาที่เลือกไม่ว่างแล้ว</div>
              <div style={{fontSize:12,color:C.text,lineHeight:1.65,marginTop:3}}>โปรดเลือกเวลาอื่น หรือวันอื่น แล้วจึงแนบสลิปเพื่อยืนยันการจอง</div>
            </div>
            <button type="button" className="btn btn-outline btn-sm" style={{width:42,padding:"7px 0"}} onClick={()=>setCustomerReselectBookingV176(null)}>×</button>
          </div>
          <div className="warn-box" style={{borderColor:`${C.red}66`,background:`${C.red}10`,color:C.text,marginBottom:14}}>
            เวลาที่เลือกเดิม: {dwBookingScheduledText(b)}<br/>
            {b.slot_conflict_message || "เวลานี้ไม่ว่างแล้ว โปรดเลือกเวลาอื่น หรือวันอื่น"}
          </div>
          <div className="fgroup">
            <label className="flabel">เลือกวันใหม่ *</label>
            <input className="finput" type="date" min={localDateInputValue()} value={date} onFocus={e=>e.currentTarget.showPicker?.()} onClick={e=>e.currentTarget.showPicker?.()} onChange={e=>setCustomerReselectFormV176(p=>({...p,date:e.target.value,time:""}))} style={{fontSize:17,fontWeight:800,minHeight:54,border:`2px solid ${C.gold}88`}} />
          </div>
          <div className="fgroup">
            <label className="flabel">เลือกเวลาใหม่ *</label>
            {!date ? <div className="warn-box">กรุณาเลือกวันก่อน แล้วจึงเลือกเวลา</div> : (
              <>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {BOOKING_TIME_SLOTS_V92.map(slot => {
                    if (!canCustomerReselectAtV191(slot)) return null;
                    const range = bookingDisplayRangeV191(slot, b);
                    return <button key={slot} type="button" className={`btn btn-sm ${customerReselectFormV176.time===range ? "btn-purple" : "btn-outline"}`} style={{padding:"12px 8px",borderRadius:12}} onClick={()=>setCustomerReselectFormV176(p=>({...p,time:range}))}>{range}</button>;
                  })}
                </div>
                {specialCustomerReselectSlotsV180.length > 0 && <div style={{marginTop:12,padding:10,borderRadius:12,background:`${C.purple}16`,border:`1px solid ${C.purple}66`}}>
                  <div style={{fontSize:12,fontWeight:900,color:C.purpleLight,marginBottom:8}}>🌙 เวลาพิเศษหลังเที่ยงคืนที่เปิดไว้</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {specialCustomerReselectSlotsV180.map(slot => {
                      if (!canCustomerReselectAtV191(slot)) return null;
                      const range = bookingDisplayRangeV191(slot, b);
                      return <button key={slot} type="button" className={`btn btn-sm ${customerReselectFormV176.time===range ? "btn-purple" : "btn-outline"}`} style={{padding:"12px 8px",borderRadius:12}} onClick={()=>setCustomerReselectFormV176(p=>({...p,time:range}))}>{`🌙 ${range}`}</button>;
                    })}
                  </div>
                </div>}
              </>
            )}
          </div>
          <button type="button" className="btn btn-gold" disabled={bookingLoading || !date || !customerReselectFormV176.time} onClick={submitCustomerReselectSlotV176}>{bookingLoading ? "กำลังบันทึก..." : "บันทึกเวลาใหม่ และแนบสลิป"}</button>
        </div>
      </div>
    );
  }


  function MyBookingsV91() {
    // v139D: นัดของฉันต้องแสดงเฉพาะ booking จริงจาก API/current state เท่านั้น
    // ไม่เอา local history/mock มาแสดง และไม่สร้างรายการจำลองเอง
    const list = getRealCustomerBookingsV139D(
      [...(Array.isArray(customerBookingListV104) ? customerBookingListV104 : []), ...(Array.isArray(bookings) ? bookings : [])],
      [...(selectedBooking?.booking_id && belongsToCurrentUserBookingV126(selectedBooking) ? [selectedBooking] : [])]
    );
    const _tickV104 = bookingHistoryTickV103;
    // v108: ไม่ auto refresh ระหว่างอยู่หน้านี้ เพราะ setState จาก API อาจทำให้การแตะปุ่มบนมือถือสะดุด
    // ใช้ปุ่ม “รีเฟรชรายการนัด” แทน หรือ polling หลักของระบบจะอัปเดตเอง

    return (
      <div className="scr on">
        <div className="topbar"><span className="topbar-title">นัดดูดวงของฉัน</span></div>
        <div className="content z1">
          <div className="sec-hd">ประวัติการนัด / แชทนัดหมาย</div>
          <div className="notif" style={{margin:"0 16px 12px"}}>
            ระบบจะแสดงเฉพาะรายการจองจริงจากฐานข้อมูลเท่านั้น ถ้าเพิ่งจองแล้วรายการยังไม่ขึ้น ให้กดรีเฟรช
          </div>
          <div style={{padding:"0 16px 12px",display:"flex",gap:8,flexWrap:"wrap"}}>
              <button className="btn btn-gold btn-sm" onClick={refreshCustomerBookingListV104}>รีเฟรชรายการนัด</button>

            </div>

          {list.length === 0 ? (
            <div className="empty">
              <div className="e-ico">📅</div>
              ยังไม่มีประวัติการนัด<br/>
              <span style={{fontSize:12,color:C.muted}}>ถ้าเพิ่งนัดหมอดูแล้ว กด “รีเฟรชรายการนัด” อีกครั้ง</span>
            </div>
          ) : list.map(b => {
            const needPay = ["waiting_payment"].includes(String(b.status || "")) && !(String(b.slip_review_status || "") === "approved" || !!b.slip_uploaded_at || !!b.slip_url);
            const canChat = !["completed","cancelled"].includes(String(b.status || ""));
            const roomReady = ["confirmed","in_session"].includes(String(b.status || ""));
            const alreadyPaidV168 = String(b.slip_review_status || "") === "approved" || !!b.slip_uploaded_at || !!b.slip_url;
            const canPay = ["waiting_payment"].includes(String(b.status || "")) && !alreadyPaidV168 && !(paymentDeadlineDateV113(b) && paymentDeadlineDateV113(b).getTime() <= bookingNowTickV113);
            // v183: ลูกค้ายกเลิกได้เฉพาะก่อนชำระเงินเท่านั้น
            // หลังแนบสลิป/อนุมัติ/ยืนยันคิว จะไม่แสดงปุ่มยกเลิก
            const canCustomerCancelV183 = String(b.status || "") === "waiting_payment" && !alreadyPaidV168;
            return (
              <div key={b.booking_id} className="brow" style={{padding:14,borderRadius:16,cursor:"pointer"}} onClick={()=>openBookingChatStableV108(b, "customer")}>
                <div className="brow-hd">
                  <div>
                    <div className="brow-name">{b.seer_name || "หมอดู"}</div>
                    <div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "จองคิวดูดวง"}</div>
                  </div>
                  <span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span>
                </div>
                <div className="brow-detail">
                  📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>
                  #{b.booking_id} {b.customer_note ? `• ${String(b.customer_note).slice(0,40)}` : ""}
                </div>

                {needPay && (
                  <div className="warn-box" style={{marginTop:10,borderColor:`${C.green}55`,background:`${C.green}11`,color:C.green}}>
                    กรุณาโอนเงินและแนบสลิปก่อน ระบบจึงจะส่งคำขอนัดให้หมอดู<br/>
                    ⏳ {paymentCountdownTextV113(b, bookingNowTickV113)}
                  </div>
                )}
                {b.status === "slot_unavailable" && (
                  <div className="warn-box" style={{marginTop:10,borderColor:`${C.red}66`,background:`${C.red}10`,color:C.text}}>
                    ⚠️ {b.slot_conflict_message || "เวลานี้ไม่ว่างแล้ว โปรดเลือกเวลาอื่น หรือวันอื่น"}
                  </div>
                )}
                {b.status === "seer_proposed_new_time" && (
                  <div className="warn-box" style={{marginTop:10}}>
                    ⏰ หมอดูเสนอเวลาใหม่: {b.proposed_scheduled_at || `${b.proposed_date || ""} ${b.proposed_time || ""}`}<br/>{b.proposed_note || ""}
                  </div>
                )}
                {b.status === "rejected" && (
                  <div className="warn-box" style={{marginTop:10,borderColor:`${C.red}55`,background:`${C.red}11`,color:C.red}}>
                    ❌ หมอดูปฏิเสธคำขอนัด {b.reject_reason ? `: ${b.reject_reason}` : ""}
                  </div>
                )}
                {roomReady && (
                  <div className="warn-box" style={{marginTop:10,borderColor:`${C.gold}55`,background:`${C.gold}10`,color:C.goldLight}}>
                    🔮 แอดมินอนุมัติสลิปแล้ว ห้องดูดวงพร้อมใช้งาน
                  </div>
                )}

                <div style={{display:"grid",gridTemplateColumns:"1fr",gap:10,marginTop:12}} onClick={e=>e.stopPropagation()}>
                  {canChat && <button type="button" className="btn btn-purple" style={{minHeight:48,fontSize:15,fontWeight:900}} onPointerDown={(e)=>{e.stopPropagation(); openBookingChatStableV108(b, "customer");}} onClick={(e)=>{e.stopPropagation(); openBookingChatStableV108(b, "customer");}}>💬 เปิดแชทนัดเวลา</button>}
                  {canPay && <button type="button" className="btn btn-gold" style={{minHeight:48,fontSize:15,fontWeight:900}} onPointerDown={(e)=>{e.stopPropagation(); openPaymentStableV108(b);}} onClick={(e)=>{e.stopPropagation(); openPaymentStableV108(b);}}>💳 โอนเงิน / แนบสลิป</button>}
                  {canCustomerCancelV183 && <button
                    type="button"
                    className="btn btn-outline"
                    style={{minHeight:44,fontSize:14,borderColor:`${C.red}88`,color:C.red}}
                    onPointerDown={e=>e.stopPropagation()}
                    onClick={async (e)=>{
                      e.stopPropagation();
                      if (!window.confirm("ยืนยันยกเลิกรายการจองนี้? รายการยังไม่ได้ชำระเงิน และเวลานี้จะกลับมาให้ลูกค้าคนอื่นเลือกได้")) return;
                      const result = await bookingActionV91(b, "customer_cancel");
                      if (result?.booking) {
                        setPaymentPopupBookingV106(null);
                        alert("ยกเลิกรายการจองเรียบร้อยแล้ว");
                      }
                    }}
                  >✕ ยกเลิกรายการจอง</button>}
                  {b.status === "slot_unavailable" && <button type="button" className="btn btn-gold" style={{minHeight:48,fontSize:15,fontWeight:900}} onClick={()=>openCustomerReselectSlotV176(b)}>🗓️ เลือกวัน / เวลาใหม่</button>}
                  {b.status === "seer_proposed_new_time" && <button type="button" className="btn btn-success" style={{minHeight:46,fontSize:15,fontWeight:900}} onClick={()=>bookingActionV91(b,"accept_new_time")}>✅ ยอมรับเวลาใหม่</button>}
                  {roomReady && <button type="button" className="btn btn-gold" style={{minHeight:48,fontSize:15,fontWeight:900}} onClick={()=>openBookingRoomV91(b)}>🔮 เข้าห้องดูดวง</button>}
                </div>
              </div>
            );
          })}
        </div>
        <BottomNav/>
      </div>
    );
  }

  function BookingChatV99() {
    const b = selectedBooking;
    if (!b) return null;
    const needPay = !seerMode && ["waiting_payment"].includes(b.status) && !(String(b.slip_review_status || "") === "approved" || !!b.slip_uploaded_at || !!b.slip_url);
    const roomReady = ["confirmed","in_session"].includes(String(b.status || ""));
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>{setActiveNav("my_bookings");go("my_bookings")}}>←</button>
          <span className="topbar-title">แชทนัดเวลา</span>
        </div>
        <div className="content z1">
          <div style={{padding:16}}>
            <div className="card card-gold" style={{padding:14,marginBottom:12}}>
              <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>📅 นัดดูดวง</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:6}}>
                {seerMode ? (b.customer_name || "ลูกค้า") : (b.seer_name || "หมอดู")} • {b.service_name}<br/>
                เวลานัด: {dwBookingScheduledText(b)} • ฿{Number(b.price || 0).toLocaleString()}
              </div>
              <span className={`badge ${dwBookingBadgeClass(b.status)}`} style={{marginTop:10}}>{dwBookingStatusLabel(b.status)}</span>
            </div>

            {needPay && (
              <div className="warn-box" style={{marginBottom:12,borderColor:`${C.green}55`,background:`${C.green}11`,color:C.green}}>
                กรุณาโอนเงินและอัปโหลดสลิป ระบบจึงจะส่งคำขอนัดให้หมอดู<br/>⏳ {paymentCountdownTextV113(b, bookingNowTickV113)}
              </div>
            )}
            {b.status === "slot_unavailable" && (
              <div className="warn-box" style={{marginBottom:12,borderColor:`${C.red}66`,background:`${C.red}10`,color:C.text}}>
                ⚠️ {b.slot_conflict_message || "เวลานี้ไม่ว่างแล้ว โปรดเลือกเวลาอื่น หรือวันอื่น"}
              </div>
            )}
            {b.status === "seer_proposed_new_time" && (
              <div className="warn-box" style={{marginBottom:12}}>
                ⏰ หมอดูเสนอเวลาใหม่: {b.proposed_scheduled_at || `${b.proposed_date || ""} ${b.proposed_time || ""}`}<br/>{b.proposed_note || ""}
              </div>
            )}
            {b.status === "waiting_slip_review" && (
              <div className="warn-box" style={{marginBottom:12}}>🧾 ส่งสลิปแล้ว รอแอดมินตรวจสอบ</div>
            )}
            {roomReady && (
              <div className="warn-box" style={{marginBottom:12,borderColor:`${C.gold}55`,background:`${C.gold}10`,color:C.goldLight}}>
                🔮 แอดมินอนุมัติแล้ว ห้องดูดวงพร้อมใช้งาน
              </div>
            )}

            <div className="chat-box" style={{minHeight:300,margin:"0 0 12px",padding:12}}>
              {bookingChatMessages.length === 0 && <div className="empty" style={{padding:20}}>เริ่มแชทนัดเวลากับ{seerMode ? "ลูกค้า" : "หมอดู"}ได้เลย</div>}
              {bookingChatMessages.map((m,i)=>(
                <div key={m.message_id || i} className={`chat-bubble ${m.sender_role === "customer" ? "chat-right" : "chat-left"}`}>
                  <div>{m.message_text}</div>
                  <div style={{fontSize:10,opacity:.65,marginTop:4}}>
                    {m.sender_role === "system" ? "ระบบ" : m.sender_role === "customer" ? "ลูกค้า" : "หมอดู"} • {m.created_at || "ตอนนี้"}
                  </div>
                </div>
              ))}
            </div>

            {["rejected","cancelled","completed"].includes(String(b.status || "")) ? (
              <div className="warn-box">รายการนี้ปิดการแชทนัดเวลาแล้ว</div>
            ) : (
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input className="finput" placeholder="พิมพ์ข้อความนัดเวลา..." ref={bookingChatInputRefV115} defaultValue="" onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault(); sendBookingChatV99();}}} />
                <button type="button" className="btn btn-gold btn-sm" onPointerDown={(e)=>e.preventDefault()} onClick={sendBookingChatV99}>ส่ง</button>
              </div>
            )}

            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
              {!seerMode && b.status === "seer_proposed_new_time" && <button className="btn btn-success btn-sm" onClick={()=>bookingActionV91(b,"accept_new_time")}>ยอมรับเวลาใหม่</button>}
              {bookingChatRoleV118 === "customer" && needPay && <button className="btn btn-gold btn-sm" onClick={()=>setPaymentPopupBookingV106(b)}>โอนเงิน / แนบสลิป</button>}
              {bookingChatRoleV118 === "customer" && roomReady && <button className="btn btn-purple btn-sm" onClick={()=>openBookingRoomV91(b)}>เข้าห้องดูดวง</button>}
              {bookingChatRoleV118 === "seer" && b.status === "paid_waiting_seer" && <button className="btn btn-success btn-sm" onClick={()=>seerConfirmBookingV91(b)}>รับงาน</button>}
              {bookingChatRoleV118 === "seer" && b.status === "paid_waiting_seer" && <button className="btn btn-outline btn-sm" onClick={()=>seerProposeBookingV91(b)}>ขอเลื่อนเวลา</button>}
              {bookingChatRoleV118 === "seer" && roomReady && <button className="btn btn-purple btn-sm" onClick={()=>openBookingRoomV91(b)}>เข้าห้องดูดวง</button>}
            </div>

            <div style={{fontSize:11,color:C.muted,lineHeight:1.7,marginTop:12}}>
              หมายเหตุ: แชทนัดเวลานี้ถูกบันทึกในระบบ และแอดมินสามารถตรวจสอบย้อนหลังได้ เพื่อป้องกันการนัดออกนอกแพลตฟอร์ม
            </div>
          </div>
        </div>
      </div>
    );
  }



  function PaymentPopupV106() {
    const b = paymentPopupBookingV106;
    if (!b) return null;
    const pay = paymentSettingsV106 || {};
    const bankName = pay.bank_name || pay.bankName || "ธนาคาร / พร้อมเพย์";
    const accountName = pay.account_name || pay.accountName || "ดวงวันนี้";
    const accountNumber = pay.account_number || pay.accountNumber || pay.promptpay || pay.promptPay || "กรุณาตั้งค่าเลขบัญชีในแอดมิน";
    const qrUrl = pay.qr_url || pay.qrUrl || "";
    const cardEnabled = pay.card_payment_enabled === true || pay.card_payment_enabled === "1" || pay.cardPaymentEnabled === true || pay.cardPaymentEnabled === "1";
    const cardUrl = pay.card_payment_url || pay.cardPaymentUrl || "";
    const cardNote = pay.card_payment_note || pay.cardPaymentNote || "ชำระผ่านบัตรเครดิต/เดบิต";
    const paymentExpiredV113 = paymentDeadlineDateV113(b) && paymentDeadlineDateV113(b).getTime() <= bookingNowTickV113;
    const savedPreferredMethodsV175 = Array.isArray(customerPayContactV122.preferredMethods)
      ? customerPayContactV122.preferredMethods
      : String(customerPayContactV122.preferredMethods || b.customer_contact_preference || "phone,line").split(",");
    const phonePreferredV175 = savedPreferredMethodsV175.includes("phone");
    const linePreferredV175 = savedPreferredMethodsV175.includes("line");
    return (
      <div style={{position:"fixed",inset:0,zIndex:999999,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
        <div style={{width:"100%",maxWidth:430,maxHeight:"88vh",overflowY:"auto",background:C.bg,border:`1px solid ${C.gold}66`,borderRadius:"22px 22px 0 0",boxShadow:"0 -18px 70px rgba(0,0,0,.7)"}}>
          <div style={{padding:16,borderBottom:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"center"}}>
            <div style={{flex:1}}>
              <div style={{fontSize:17,fontWeight:900,color:C.goldLight}}>💳 โอนเงินยืนยันนัด</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>{b.seer_name || "หมอดู"} • {dwBookingScheduledText(b)}</div>
            </div>
            <button className="btn btn-outline btn-sm" style={{width:42,padding:"7px 0"}} onClick={()=>setPaymentPopupBookingV106(null)}>×</button>
          </div>
          <div style={{padding:16}}>
            <div className="card card-gold" style={{padding:16,marginBottom:12}}>
              <div style={{fontSize:12,color:C.muted,marginBottom:6}}>ยอดที่ต้องชำระ</div>
              <div style={{fontSize:28,fontWeight:900,color:C.goldLight}}>฿{Number(b.price||0).toLocaleString()}</div>
              {["waiting_payment","seer_confirmed"].includes(String(b.status || "")) && (
                <div style={{fontSize:12,color:C.orange,marginTop:8}}>⏳ {paymentCountdownTextV113(b, bookingNowTickV113)}</div>
              )}
            </div>
            <div className="card" style={{padding:16,marginBottom:12}}>
              <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:10}}>บัญชีรับเงิน</div>
              <div className="irow"><span className="lbl">ธนาคาร/ช่องทาง</span><span className="val">{bankName}</span></div>
              <div className="irow"><span className="lbl">ชื่อบัญชี</span><span className="val">{accountName}</span></div>
              <div className="irow"><span className="lbl">เลขบัญชี/พร้อมเพย์</span><span className="val" style={{fontSize:16,color:C.goldLight}}>{accountNumber}</span></div>
              {qrUrl ? (
                <div style={{textAlign:"center",marginTop:14}}>
                  <div style={{fontSize:13,fontWeight:900,color:C.goldLight,marginBottom:8}}>สแกน QR เพื่อโอนเงิน</div>
                  <img src={qrUrl} alt="QR รับเงิน" style={{maxWidth:240,width:"86%",borderRadius:14,border:`1px solid ${C.border}`,background:"#fff",padding:8}} />
                  <a
                    className="btn btn-outline btn-sm"
                    href={qrUrl}
                    download={`duangwannee-payment-qr-${b.booking_id || "booking"}.png`}
                    target="_blank"
                    rel="noreferrer"
                    style={{display:"inline-block",marginTop:10,textDecoration:"none"}}
                  >
                    ⬇️ บันทึก QR ไว้สแกนจ่าย
                  </a>
                  <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>ถ้ากดบันทึกแล้วไม่ดาวน์โหลด ให้กดค้างที่รูป QR แล้วเลือก “บันทึกรูปภาพ”</div>
                </div>
              ) : (
                <div className="warn-box" style={{marginTop:12}}>ยังไม่ได้ตั้งค่า QR Code ในแอดมิน ลูกค้าสามารถโอนตามเลขบัญชี/พร้อมเพย์ด้านบนได้</div>
              )}
            </div>
            {cardEnabled && cardUrl && (
              <div className="card" style={{padding:16,marginBottom:12,borderColor:`${C.purple}66`,background:`${C.purple}12`}}>
                <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:8}}>💳 ชำระผ่านบัตรเครดิต/เดบิต</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:10}}>{cardNote}</div>
                <a className="btn btn-outline" href={cardUrl} target="_blank" rel="noreferrer" style={{textDecoration:"none",display:"block",textAlign:"center"}}>เปิดหน้าชำระบัตรเครดิต</a>
                <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>หลังชำระแล้ว กรุณากลับมาแนบหลักฐาน/สลิปในหน้านี้เพื่อส่งคำขอนัด</div>
              </div>
            )}
            <div className="warn-box" style={{marginBottom:12}}>
              หลังโอนเงินแล้ว กรุณาแนบสลิป ระบบจึงจะส่งคำขอนัดให้หมอดู
            </div>
            {paymentExpiredV113 && (
              <div className="warn-box" style={{marginBottom:12,borderColor:`${C.red}55`,background:`${C.red}11`,color:C.red}}>
                หมดเวลาโอนเงิน 15 นาทีแล้ว ระบบจะยกเลิกการจองนี้อัตโนมัติ
              </div>
            )}
            <div className="card" style={{padding:12,marginBottom:10,borderColor:`${C.purple}55`}}>
              <div style={{fontSize:13,fontWeight:900,color:C.goldLight,marginBottom:5}}>กรุณาระบุข้อมูล เพื่อการติดต่อดูดวง</div>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginBottom:10}}>เลือกช่องทางที่สะดวกให้หมอดูติดต่อกลับได้มากกว่า 1 ช่องทาง</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                <label style={{display:"flex",gap:7,alignItems:"center",padding:"10px 9px",borderRadius:12,border:`1px solid ${C.green}55`,background:`${C.green}10`,fontSize:13,color:C.text,cursor:"pointer"}}>
                  <input id={`customerContactPhonePreferenceV175_${b.booking_id || "booking"}`} type="checkbox" defaultChecked={phonePreferredV175} />
                  <span>📞 เบอร์โทร</span>
                </label>
                <label style={{display:"flex",gap:7,alignItems:"center",padding:"10px 9px",borderRadius:12,border:`1px solid ${C.purple}77`,background:`${C.purple}10`,fontSize:13,color:C.text,cursor:"pointer"}}>
                  <input id={`customerContactLinePreferenceV175_${b.booking_id || "booking"}`} type="checkbox" defaultChecked={linePreferredV175} />
                  <span>💬 LINE</span>
                </label>
              </div>
              <div className="fgroup" style={{marginBottom:10}}>
                <label className="flabel">เบอร์โทรลูกค้า <span style={{fontSize:11,color:C.muted}}>(กรอกเมื่อเลือกเบอร์โทร)</span></label>
                <input
                  id={`customerPhoneInputV123_${b.booking_id || "booking"}`}
                  className="finput"
                  defaultValue={customerPayContactV122.phone || b.customer_phone || ""}
                  onInput={e=>{ customerPayContactV122.phone = e.currentTarget.value; }}
                  placeholder="เช่น 0812345678"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>
              <div className="fgroup" style={{marginBottom:10}}>
                <label className="flabel">LINE ID ลูกค้า <span style={{fontSize:11,color:C.muted}}>(กรอกเมื่อเลือก LINE)</span></label>
                <input
                  id={`customerLineInputV123_${b.booking_id || "booking"}`}
                  className="finput"
                  defaultValue={customerPayContactV122.lineId || b.customer_line_id || ""}
                  onInput={e=>{ customerPayContactV122.lineId = e.currentTarget.value; }}
                  placeholder="เช่น lineid หรือ @lineid"
                  autoComplete="off"
                />
              </div>
              <label style={{display:"flex",gap:8,alignItems:"flex-start",fontSize:12,color:C.muted,lineHeight:1.6}}>
                <input
                  id={`customerConsentInputV123_${b.booking_id || "booking"}`}
                  type="checkbox"
                  defaultChecked={customerPayContactV122.consent !== false}
                  onChange={e=>{ customerPayContactV122.consent = e.currentTarget.checked; }}
                />
                ยินยอมให้ดวงวันนี้บันทึกชื่อ เบอร์โทร และ LINE ID เพื่อใช้ติดต่อตามนัด บริการหลังการขาย และทำการตลาด
              </label>
            </div>
            <div className="card" style={{padding:12,marginBottom:10,borderColor:`${C.gold}55`}}>
              <div style={{fontSize:13,fontWeight:900,color:C.goldLight,marginBottom:8}}>แนบสลิปโอนเงิน</div>
              <input
                key={slipInputKeyV121}
                id={`paymentSlipInputV121_${b.booking_id || "booking"}`}
                className="finput"
                type="file"
                accept="image/*,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
                disabled={bookingLoading || paymentExpiredV113}
                onClick={(e)=>{ e.currentTarget.value = ""; }}
                onChange={(e)=>{
                  const f = e.currentTarget.files?.[0] || null;
                  pendingSlipFileRefV127.current = f;
                  setSelectedSlipNameV127(f ? (f.name || "เลือกรูปสลิปแล้ว") : "");
                  e.currentTarget.value = "";
                }}
              />
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>
                เลือกรูปสลิปจากแกลเลอรี/ไฟล์ในเครื่องก่อน แล้วค่อยกด “แนบสลิปเพื่อยืนยัน”
              </div>
              {selectedSlipNameV127 && (
                <div className="warn-box" style={{marginTop:10,borderColor:`${C.gold}55`,background:`${C.gold}11`,color:C.goldLight}}>
                  เลือกไฟล์แล้ว: {selectedSlipNameV127}
                </div>
              )}
              {slipUploadStatusV110 && (
                <div className="warn-box" style={{marginTop:10,borderColor:`${C.green}55`,background:`${C.green}11`,color:C.green}}>
                  {slipUploadStatusV110}
                </div>
              )}
            </div>
            <label
              className="btn btn-outline"
              htmlFor={`paymentSlipInputV121_${b.booking_id || "booking"}`}
              style={{display:"block",textAlign:"center",opacity:(bookingLoading || paymentExpiredV113) ? .55 : 1,pointerEvents:(bookingLoading || paymentExpiredV113) ? "none" : "auto"}}
            >
              เลือกรูปสลิปจากเครื่อง
            </label>
            <button
              className="btn btn-gold"
              style={{marginTop:10,width:"100%",opacity:(bookingLoading || paymentExpiredV113) ? .55 : 1}}
              disabled={bookingLoading || paymentExpiredV113}
              onClick={()=>customerUploadSlipV91(b, pendingSlipFileRefV127.current)}
            >
              {bookingLoading ? "กำลังส่งภาพ..." : paymentExpiredV113 ? "หมดเวลาโอนเงินแล้ว" : "แนบสลิปเพื่อยืนยัน"}
            </button>
            <button className="btn btn-outline" style={{marginTop:10}} onClick={()=>setPaymentPopupBookingV106(null)}>กลับไปหน้ารายการนัด</button>
          </div>
        </div>
      </div>
    );
  }

  function SlipReceivedPopupV106() {
    if (!slipReceivedPopupV106) return null;
    return (
      <div style={{position:"fixed",inset:0,zIndex:1000000,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
        <div style={{width:"100%",maxWidth:360,background:"linear-gradient(180deg,#20164a,#110D26)",border:`1px solid ${C.green}66`,borderRadius:22,boxShadow:"0 22px 80px rgba(0,0,0,.55)",padding:22,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:10}}>🧾</div>
          <div style={{fontSize:20,fontWeight:900,color:C.green,marginBottom:8}}>ได้รับสลิปของคุณแล้ว</div>
          <div style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:16}}>โปรดรอเจ้าหน้าที่ตรวจสอบ เพื่อยืนยันการจองของคุณ</div>
          <button className="btn btn-gold" onClick={()=>setSlipReceivedPopupV106(false)}>รับทราบ</button>
        </div>
      </div>
    );
  }

  function BookingChatPopupV100() {
    const b = selectedBooking;
    if (!bookingChatPopupOpen || !b) return null;
    const chatRole = bookingChatRoleV118 === "seer" ? "seer" : "customer";
    const isSeerChat = chatRole === "seer";
    const isCustomerChat = chatRole === "customer";
    const alreadyPaidV168 = String(b.slip_review_status || "") === "approved" || !!b.slip_uploaded_at || !!b.slip_url;
    const needPay = isCustomerChat && ["waiting_payment"].includes(String(b.status || "")) && !alreadyPaidV168;
    const roomReady = ["confirmed","in_session"].includes(String(b.status || ""));
    const canPay = isCustomerChat && ["waiting_payment"].includes(String(b.status || "")) && !alreadyPaidV168 && !(paymentDeadlineDateV113(b) && paymentDeadlineDateV113(b).getTime() <= bookingNowTickV113);
    return (
      <div className="dw-chat-popup-v117" style={{position:"fixed",left:0,top:0,width:"100vw",height:"100dvh",zIndex:999998,background:"rgba(0,0,0,.78)",display:"flex",alignItems:"stretch",justifyContent:"center",padding:0,overflow:"hidden",overscrollBehavior:"contain",touchAction:"none"}}>
        <div style={{width:"100%",maxWidth:520,height:"100%",maxHeight:"none",background:C.bg,border:`1px solid ${C.gold}55`,borderRadius:"18px 18px 0 0",boxShadow:"0 -18px 70px rgba(0,0,0,.65)",overflow:"hidden",display:"flex",flexDirection:"column",touchAction:"auto",transform:"translateZ(0)"}}>
          <div style={{padding:"12px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"center",background:`linear-gradient(135deg,${C.surface},${C.card})`}}>
            <div style={{flex:1}}>
              <div style={{fontSize:15,fontWeight:900,color:C.goldLight}}>💬 แชทนัดเวลา</div>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>
                {isSeerChat ? (b.customer_name || "ลูกค้า") : (b.seer_name || "หมอดู")} • {dwBookingScheduledText(b)}
              </div>
            </div>
            <span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span>
            <button className="btn btn-outline btn-sm" style={{width:42,padding:"7px 0"}} onClick={()=>setBookingChatPopupOpen(false)}>×</button>
          </div>

          <div style={{padding:"10px 14px 0",overflowY:"auto",flex:"0 0 auto",maxHeight:roomReady ? 235 : 140}}>
            {needPay && (
              <div className="warn-box" style={{marginBottom:10,borderColor:`${C.green}55`,background:`${C.green}11`,color:C.green}}>
                กรุณาโอนเงินและอัปโหลดสลิป ระบบจึงจะส่งคำขอนัดให้หมอดู
              </div>
            )}
            {b.status === "slot_unavailable" && (
              <div className="warn-box" style={{marginBottom:10,borderColor:`${C.red}66`,background:`${C.red}10`,color:C.text}}>
                ⚠️ {b.slot_conflict_message || "เวลานี้ไม่ว่างแล้ว โปรดเลือกเวลาอื่น หรือวันอื่น"}
              </div>
            )}
            {b.status === "seer_proposed_new_time" && (
              <div className="warn-box" style={{marginBottom:10}}>
                ⏰ หมอดูเสนอเวลาใหม่: {b.proposed_scheduled_at || `${b.proposed_date || ""} ${b.proposed_time || ""}`}<br/>{b.proposed_note || ""}
              </div>
            )}
            {b.status === "waiting_slip_review" && <div className="warn-box" style={{marginBottom:10}}>🧾 ส่งสลิปแล้ว รอแอดมินตรวจสอบ</div>}
            {roomReady && (
              <div className="warn-box" style={{marginBottom:10,borderColor:`${C.gold}55`,background:`${C.gold}10`,color:C.goldLight}}>
                🔮 แอดมินอนุมัติแล้ว ห้องดูดวงพร้อมใช้งาน
              </div>
            )}
            {roomReady && (
              <BookingLineMeetingAccessV3920
                booking={b}
                isSeer={isSeerChat}
                compact
              />
            )}
          </div>

          <div className="chat-box" style={{flex:"1 1 auto",minHeight:0,margin:"0 12px 10px",padding:12,overflowY:"auto",WebkitOverflowScrolling:"touch",overscrollBehavior:"contain"}}>
            {bookingChatMessages.length === 0 && <div className="empty" style={{padding:20}}>เริ่มคุยนัดเวลาได้เลย ข้อความนี้จะถูกบันทึกให้แอดมินตรวจสอบได้</div>}
            {bookingChatMessages.map((m,i)=>(
              <div key={m.message_id || i} className={`chat-bubble ${m.sender_role === "customer" ? "chat-right" : "chat-left"}`}>
                <div>{m.message_text}</div>
                <div style={{fontSize:10,opacity:.65,marginTop:4}}>
                  {m.sender_role === "system" ? "ระบบ" : m.sender_role === "customer" ? "ลูกค้า" : "หมอดู"} • {m.created_at || "ตอนนี้"}
                </div>
              </div>
            ))}
          </div>

          <div style={{padding:"0 12px calc(10px + env(safe-area-inset-bottom))",flex:"0 0 auto",background:C.bg,borderTop:`1px solid ${C.border}`,position:"relative",zIndex:2}}>
            {["rejected","cancelled","completed"].includes(String(b.status || "")) ? (
              <div className="warn-box">รายการนี้ปิดการแชทนัดเวลาแล้ว</div>
            ) : (
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input className="finput" placeholder="พิมพ์ข้อความนัดเวลา..." ref={bookingChatInputRefV115} defaultValue="" enterKeyHint="send" autoComplete="off" autoCorrect="off" onFocus={(e)=>{setTimeout(()=>{try{e.currentTarget.scrollIntoView({block:"nearest",inline:"nearest"});}catch{}},50);}} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault(); sendBookingChatV99();}}} />
                <button type="button" className="btn btn-gold btn-sm" onPointerDown={(e)=>e.preventDefault()} onClick={sendBookingChatV99}>ส่ง</button>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr",gap:10,marginTop:10}}>
              {isCustomerChat && canPay && <button type="button" className="btn btn-gold" style={{minHeight:48,fontSize:15,fontWeight:900}} onPointerDown={(e)=>{e.stopPropagation(); openPaymentStableV108(b);}} onClick={(e)=>{e.stopPropagation(); openPaymentStableV108(b);}}>💳 โอนเงิน / แนบสลิป</button>}
              {isCustomerChat && b.status === "slot_unavailable" && <button type="button" className="btn btn-gold" style={{minHeight:46,fontSize:15,fontWeight:900}} onClick={()=>{setBookingChatPopupOpen(false); openCustomerReselectSlotV176(b);}}>🗓️ เลือกวัน / เวลาใหม่</button>}
              {isCustomerChat && b.status === "seer_proposed_new_time" && <button type="button" className="btn btn-success" style={{minHeight:46,fontSize:15,fontWeight:900}} onClick={()=>bookingActionV91(b,"accept_new_time")}>✅ ยอมรับเวลาใหม่</button>}
              {roomReady && <button type="button" className="btn btn-purple" style={{minHeight:48,fontSize:15,fontWeight:900}} onClick={()=>{setBookingChatPopupOpen(false); openBookingRoomV91(b);}}>🔮 เข้าห้องดูดวง</button>}
              {isSeerChat && b.status === "paid_waiting_seer" && <button type="button" className="btn btn-success" style={{minHeight:46,fontSize:15,fontWeight:900}} onClick={()=>seerConfirmBookingV91(b)}>✅ รับงาน</button>}
              {isSeerChat && b.status === "paid_waiting_seer" && <button type="button" className="btn btn-outline" style={{minHeight:46,fontSize:15,fontWeight:900}} onClick={()=>seerProposeBookingV91(b)}>⏰ ขอเลื่อนเวลา</button>}
            </div>
            <div style={{fontSize:10,color:C.muted,lineHeight:1.55,marginTop:8}}>
              แชทนี้ถูกบันทึกในระบบ แอดมินตรวจสอบได้เพื่อป้องกันการนัดออกนอกแพลตฟอร์ม
            </div>
          </div>
        </div>
      </div>
    );
  }

  function BookingRoomV91() {
    const b = selectedBooking;
    if (!b) return null;
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>go("my_bookings")}>←</button>
          <span className="topbar-title">ห้องดูดวง</span>
        </div>
        <div className="content z1">
          <div style={{padding:16}}>
            <div className="card card-gold" style={{padding:16,marginBottom:12}}>
              <div style={{fontSize:18,fontWeight:900,color:C.goldLight}}>🔮 ห้องดูดวงในแอพ</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:6}}>#{b.booking_id} • ห้อง {b.room_id || "กำลังสร้าง"}<br/>{b.service_name} • {dwBookingScheduledText(b)}</div>
              <span className={`badge ${dwBookingBadgeClass(b.status)}`} style={{marginTop:10}}>{dwBookingStatusLabel(b.status)}</span>
            </div>
            {!isBookingRoomOpenV112(b) && (
              <div className="warn-box" style={{marginBottom:12,borderColor:`${C.orange}55`,background:`${C.orange}11`,color:C.orange}}>
                ⏳ {bookingRoomWaitTextV112(b) || "ยังไม่ถึงเวลานัด ห้องนี้ยังเข้าไม่ได้"}
              </div>
            )}
            {b.status !== "completed" && isBookingRoomOpenV112(b) && (
              <BookingLineMeetingAccessV3920
                booking={b}
                isSeer={seerMode}
                fallbackToWebCall
              />
            )}
            {b.status === "completed" && (
              <div className="card" style={{padding:16,marginBottom:12,borderColor:`${C.green}77`,background:`${C.green}10`}}>
                <div style={{fontSize:17,fontWeight:900,color:C.green}}>✅ งานนี้ปิดเรียบร้อยแล้ว</div>
                <div style={{fontSize:13,color:C.text,lineHeight:1.75,marginTop:7}}>ระบบบันทึกรายได้ของงานนี้แล้ว ห้องโทรและแชทถูกปิดตามสถานะงาน</div>
                {b.summary ? <div style={{fontSize:12,color:C.muted,lineHeight:1.65,marginTop:7}}>สรุปจากหมอดู: {b.summary}</div> : null}
                {seerMode && <button type="button" className="btn btn-gold" style={{marginTop:12}} onClick={()=>{setSeerTab("live");setActiveNav("seer_dash");go("seer_job_history");}}>ดูประวัติงานที่รับ</button>}
              </div>
            )}
            <div className="chat-box" style={{minHeight:320,margin:"0 0 12px",padding:12}}>
              {bookingRoomMessages.length === 0 && <div className="empty" style={{padding:20}}>ยังไม่มีข้อความในห้อง</div>}
              {bookingRoomMessages.map((m,i)=>(
                <div key={m.message_id || i} className={`chat-bubble ${m.sender_role === "customer" ? "chat-right" : "chat-left"}`}>
                  <div>{m.message_text}</div>
                  <div style={{fontSize:10,opacity:.65,marginTop:4}}>{m.sender_role === "customer" ? "ลูกค้า" : "หมอดู"} • {m.created_at || "ตอนนี้"}</div>
                </div>
              ))}
            </div>
            {b.status !== "completed" && isBookingRoomOpenV112(b) ? (
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input className="finput" placeholder="พิมพ์ข้อความในห้องดูดวง..." value={roomInput} onChange={e=>setRoomInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter") sendRoomMessageV91();}} />
                <button className="btn btn-gold btn-sm" onClick={sendRoomMessageV91}>ส่ง</button>
              </div>
            ) : b.status !== "completed" ? (
              <div className="warn-box">ยังไม่ถึงเวลานัด จึงยังไม่สามารถส่งข้อความในห้องดูดวงได้</div>
            ) : (
              <div className="warn-box">งานนี้เสร็จสิ้นแล้ว{b.summary ? <><br/>สรุปจากหมอดู: {b.summary}</> : null}</div>
            )}
            {seerMode && b.status !== "completed" && (
              <button className="btn btn-danger" style={{marginTop:12}} onClick={()=>{
                if(!isBookingCloseReadyV3942(b)) {
                  alert(BOOKING_COMPLETION_WAIT_MESSAGE_V3942);
                  return;
                }
                const summary=prompt("สรุปงาน/คำทำนายส่งให้ลูกค้า","")||"";
                if(summary.trim()) bookingActionV91(b,"complete",{summary});
              }}>ปิดงาน / ส่งสรุป</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Book Success ─────────────────────────────────────────────────────────────
  function BookSuccess() {
    return (
      <div className="scr on">
        <div className="content z1" style={{display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",padding:"60px 24px",textAlign:"center",minHeight:"80vh"}}>
          <div style={{fontSize:64,marginBottom:20}}>🌟</div>
          <h2 style={{fontFamily:"Cinzel",fontSize:22,color:C.goldLight,marginBottom:10}}>ได้รับคำขอแล้ว</h2>
          <p style={{color:C.muted,lineHeight:1.8,marginBottom:28,fontSize:14}}>
            ได้รับสลิปของท่านแล้ว<br/>
            กรุณารอเจ้าหน้าที่ตรวจสอบ<br/>
            <span style={{color:C.goldLight}}>ประมาณ 15–30 นาที</span>
          </p>
          <div style={{background:`${C.green}11`,border:`1px solid ${C.green}33`,borderRadius:10,padding:14,marginBottom:24,width:"100%",maxWidth:300}}>
            <div style={{fontSize:13,color:C.green}}>✓ ระบบแจ้งเตือนผ่าน LINE เมื่ออนุมัติ</div>
          </div>
          <button className="btn btn-gold" style={{maxWidth:280}} onClick={()=>{setActiveNav("history");go("history")}}>ดูประวัติการสั่ง</button>
          <button className="btn btn-outline" style={{maxWidth:280,marginTop:10}} onClick={()=>{setActiveNav("home");go("home")}}>กลับหน้าหลัก</button>
        </div>
      </div>
    );
  }

  // ── History ─────────────────────────────────────────────────────────────────
  function History() {
    const bookingHistory = mergeBookingListsV101(
      [...(Array.isArray(customerBookingListV104) ? customerBookingListV104 : []), ...(Array.isArray(bookings) ? bookings : [])],
      [...(selectedBooking?.booking_id ? [selectedBooking] : [])]
    );
    const _tickV104History = bookingHistoryTickV103;
    const myH = orders.slice(0,8);
    return (
      <div className="scr on">
        <div className="topbar"><span className="topbar-title">ประวัติของฉัน</span></div>
        <div className="content z1">
          <div className="sec-hd">ประวัติการนัดหมอดู</div>
          <div style={{padding:"0 16px 12px",display:"flex",gap:8,flexWrap:"wrap"}}>
            <button className="btn btn-gold btn-sm" onClick={refreshCustomerBookingListV104}>รีเฟรชประวัตินัด</button>
            <button className="btn btn-outline btn-sm" onClick={()=>{setActiveNav("my_bookings");go("my_bookings")}}>ไปที่นัดของฉัน</button>
          </div>
          {bookingHistory.length === 0 ? (
            <div className="empty" style={{padding:20}}>
              <div className="e-ico">📅</div>
              ยังไม่มีประวัติการนัด
              <button className="btn btn-gold" style={{marginTop:14}} onClick={async()=>{
                await refreshCustomerBookingListV104();
                setActiveNav("my_bookings");
                go("my_bookings");
              }}>รีเฟรชรายการนัด</button>
            </div>
          ) : bookingHistory.map(b=>(
            <div key={b.booking_id} className="brow">
              <div className="brow-hd">
                <div>
                  <div className="brow-name">{b.seer_name || "หมอดู"}</div>
                  <div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "จองคิวดูดวง"}</div>
                </div>
                <span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span>
              </div>
              <div className="brow-detail">📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>#{b.booking_id}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
                <button className="btn btn-purple btn-sm" onClick={()=>openBookingChatStableV108(b, "seer")}>เปิดแชทนัดเวลา</button>
                {["confirmed","in_session"].includes(String(b.status||"")) && <button className="btn btn-gold btn-sm" onClick={()=>openBookingRoomV91(b)}>เข้าห้องดูดวง</button>}
              </div>
            </div>
          ))}
          <div className="sec-hd">รายการบริการอื่น</div>
          {myH.length===0?(
            <div className="empty"><div className="e-ico">📭</div>ยังไม่มีการสั่งบริการ</div>
          ):myH.map(o=>(
            <div key={o.id} className="brow" onClick={()=>{setViewOrder(o);go("order_detail_cust")}}>
              <div className="brow-hd">
                <div>
                  <div className="brow-name">{o.seer_name}</div>
                  <div style={{fontSize:12,color:C.purpleLight}}>{o.svc_name}</div>
                </div>
                <Badge status={o.status}/>
              </div>
              <div className="brow-detail">
                📅 {o.date}{o.time?" • "+o.time:""} • ฿{o.price}<br/>
                {o.question&&`❓ ${o.question.slice(0,40)}...`}
                {o.phone_input&&`📱 ${o.phone_input}`}
              </div>
              {o.status==="pending_slip"&&(
                <div style={{marginTop:10,background:`${C.orange}11`,border:`1px solid ${C.orange}33`,borderRadius:8,padding:10,fontSize:12,color:C.orange,lineHeight:1.6}}>
                  🧾 ส่งสลิปให้แอดมินแล้ว รอตรวจสอบเพื่อยืนยันคิว
                </div>
              )}
              {o.answer&&(
                <div style={{marginTop:10,background:C.surface,borderRadius:8,padding:10,fontSize:12,color:C.text,lineHeight:1.6}}>
                  <span style={{color:C.goldLight}}>✨ คำทำนาย: </span>{o.answer.slice(0,80)}...
                </div>
              )}
            </div>
          ))}
        </div>
        <BottomNav/>
      </div>
    );
  }

  // ── Order Detail (Customer view) ─────────────────────────────────────────────
  function OrderDetailCust() {
    const o = viewOrder; if(!o) return null;
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>go("history")}>←</button>
          <span className="topbar-title">รายละเอียดคำสั่ง</span>
        </div>
        <div className="content z1">
          <div style={{padding:16}}>
            <div className="card" style={{padding:16,marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:700,color:C.goldLight}}>#{o.id}</div>
                <Badge status={o.status}/>
              </div>
              {[["หมอดู",o.seer_name],["บริการ",o.svc_name],["วันที่",o.date+(o.time?" • "+o.time:"")],["ราคา","฿"+o.price],["ช่องทางติดต่อ",o.contact],o.question?["คำถาม",o.question]:null,o.phone_input?["เบอร์ที่วิเคราะห์",o.phone_input]:null].filter(Boolean).map(([l,v])=>(
                <div key={l} className="irow"><span className="lbl">{l}</span><span className="val">{v}</span></div>
              ))}
            </div>
            {o.svc_type === "live" && (
              <div className="card" style={{padding:16,marginBottom:14,borderColor:canEnterLiveRoom(o)?`${C.green}55`:`${C.gold}44`}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                  <div style={{fontSize:30}}>📞</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:800,color:C.goldLight}}>ห้องดูดวงสด</div>
                    <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>ระบบจะสร้างห้องให้อัตโนมัติหลังจากคิวได้รับการยืนยันแล้ว</div>
                  </div>
                </div>
                {canEnterLiveRoom(o) ? (
                  <button className="btn btn-gold" onClick={()=>openLiveRoom(o)}>เริ่มดูดวงสด</button>
                ) : (
                  <div style={{padding:12,background:C.surface,borderRadius:10,fontSize:12,color:C.muted,lineHeight:1.7}}>
                    สถานะ: รอการยืนยันคิว<br/>
                    หากยังไม่แนบสลิป หรือยังไม่ได้รับการอนุมัติ ห้องดูดวงสดจะยังไม่เปิดใช้งาน
                  </div>
                )}
              </div>
            )}
            {o.answer&&(
              <div className="card" style={{padding:16}}>
                <div style={{fontSize:13,fontWeight:600,color:C.goldLight,marginBottom:10}}>✨ คำทำนายจากหมอดู</div>
                <div style={{fontSize:13,color:C.text,lineHeight:1.8}}>{o.answer}</div>
              </div>
            )}
            {!o.answer && o.status!=="pending_slip" && o.status!=="rejected" && (
              <div style={{background:`${C.purple}11`,border:`1px solid ${C.purple}33`,borderRadius:10,padding:14,textAlign:"center",fontSize:13,color:C.muted}}>
                กำลังรอหมอดูตอบคำทำนาย...<br/>
                <span style={{fontSize:12}}>จะแจ้งผ่าน LINE เมื่อตอบแล้ว</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Live Room Mockup ───────────────────────────────────────────────────────────
  function LiveRoom() {
    const o = viewOrder;
    if (!o) return null;
    const roomId = o.room_id || buildLiveRoomId(o.id);
    const callStatus = liveCallStatus;
    const elapsed = liveSeconds;
    const overtime = Math.max(0, elapsed - 40 * 60);
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>isSeer ? go("seer_dash") : go("order_detail_cust")}>←</button>
          <span className="topbar-title">ห้องดูดวงสด</span>
        </div>
        <div className="content z1">
          <div style={{padding:16}}>
            <div className="card card-gold" style={{padding:18,marginBottom:14,textAlign:"center"}}>
              <div style={{fontSize:46,marginBottom:10}}>🔮</div>
              <div style={{fontFamily:"Cinzel",fontSize:22,color:C.goldLight,marginBottom:6}}>Live Fortune Room</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>ห้องนี้ถูกสร้างอัตโนมัติโดยระบบตามรายการจองที่ได้รับการยืนยันแล้ว</div>
            </div>
            <div className="card" style={{padding:16,marginBottom:14}}>
              <div className="irow"><span className="lbl">หมายเลขห้อง</span><span className="val">{roomId}</span></div>
              <div className="irow"><span className="lbl">รายการจอง</span><span className="val">#{o.id}</span></div>
              <div className="irow"><span className="lbl">หมอดู</span><span className="val">{o.seer_name}</span></div>
              <div className="irow"><span className="lbl">ลูกค้า</span><span className="val">{o.cname}</span></div>
              <div className="irow"><span className="lbl">แพ็กเกจ</span><span className="val">40 นาที • ถามได้เต็มที่</span></div>
              <div className="irow"><span className="lbl">เวลานัด</span><span className="val">{o.date} {o.time}</span></div>
              <div className="irow"><span className="lbl">สถานะ</span><span className="val" style={{color:C.green}}>พร้อมใช้งาน</span></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
              <button className="btn btn-purple" onClick={()=>{setLiveCallStatus("voice"); setLiveSeconds(0);}}>เริ่มโทรเสียง</button>
              <button className="btn btn-purple" onClick={()=>{setLiveCallStatus("video"); setLiveSeconds(0);}}>เริ่มวิดีโอคอล</button>
            </div>
            <div className="card" style={{padding:16,marginBottom:14,textAlign:"center"}}>
              <div style={{fontSize:12,color:C.muted,marginBottom:6}}>สถานะการใช้งาน</div>
              <div style={{fontSize:24,fontWeight:800,color:callStatus==="ready"?C.goldLight:C.green}}>
                {callStatus==="ready"?"รอเริ่มดูดวง":callStatus==="voice"?"กำลังโทรเสียง":"กำลังวิดีโอคอล"}
              </div>
              <div style={{fontSize:13,color:overtime>0?C.red:C.muted,marginTop:6,fontWeight:overtime>0?800:400}}>
                {overtime>0 ? `เกินเวลา +${formatTime(overtime)}` : `เวลาใช้งาน: ${formatTime(elapsed)} / 40:00`}
              </div>
            </div>
            <div className="warn-box" style={{marginBottom:14}}>
              <div>• Prototype นี้ยังไม่ใช่ระบบโทรจริง</div>
              <div>• ระบบจริงจะใช้ห้องของ booking นี้เท่านั้น ลูกค้าและหมอดูเข้าห้องได้เมื่อชำระเงินและอนุมัติแล้ว</div>
              <div>• แอดมินเห็น log เวลาเริ่ม จบ ระยะเวลา และสถานะห้องได้ เพื่อใช้ตรวจสอบกรณีร้องเรียน</div>
            </div>
            <button className="btn btn-danger" onClick={()=>{
              setLiveCallStatus("ended");
              updOrder(o.id,{status:"answered",room_status:"ended",live_ended_at:"ตอนนี้"});
              setViewOrder(v=>({...v,status:"answered",room_status:"ended",live_ended_at:"ตอนนี้"}));
            }}>จบการดูดวง</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Seer Dashboard ───────────────────────────────────────────────────────────
  function SeerDash({ detailPage = "" } = {}) {
    const allProfileChoices = uniqById([...readApprovedSeersStore(), ...allSeers]);
    const activeProfileId = readActiveSeerProfileId();
    const selectedBaseSeer = findSeerByAnyId(activeProfileId, allProfileChoices);
    const lineLockedSeer = lockedSeerProfile;
    const baseSeer = lineLockedSeer || selectedBaseSeer || getApprovedSeerForCurrentLineUser();
    if (!baseSeer) {
      return (
        <div className="scr on seer-light">
          <div className="topbar">
            <span className="topbar-title">🔒 แดชบอร์ดหมอดู</span>
          </div>
          <div className="content z1" style={{padding:"16px"}}>
            <div className="card" style={{padding:16,borderColor:`${C.orange}55`,background:`${C.orange}10`}}>
              <div style={{fontSize:18,fontWeight:900,color:C.goldLight,marginBottom:8}}>ยังไม่พบโปรไฟล์หมอดูของ LINE นี้</div>
              <div style={{fontSize:13,color:C.muted,lineHeight:1.8}}>
                ระบบ v54 จะไม่สุ่มดึงโปรไฟล์หมอดูคนอื่นมาแสดงแล้ว เพื่อกันข้อมูลมั่วระหว่างเครื่อง
                ถ้าเป็นหมอดู ให้สมัคร/รอแอดมินอนุมัติด้วย LINE บัญชีนี้ หรือให้แอดมินผูก LINE userId นี้กับโปรไฟล์หมอดูใน D1
              </div>
              <button className="btn btn-gold" style={{marginTop:12}} onClick={()=>setPage("seerRegister")}>ไปหน้าสมัครหมอดู</button>
            </div>
          </div>
        </div>
      );
    }
    const latestApproved = readApprovedSeersStore().find(x => String(x.id) === String(baseSeer?.id) || String(x.seerId) === String(baseSeer?.seerId) || String(x.name || "") === String(baseSeer?.name || ""));
    const seer = {...(baseSeer || {}), ...(latestApproved || {})};
    const seerProfileDraftKeyV131 = `dw_seer_profile_draft_v131_${String(seer.seerId || seer.id || seer.lineUserId || "me")}`;
    const readSeerProfileDraftV131 = () => {
      try { return JSON.parse(localStorage.getItem(seerProfileDraftKeyV131) || "{}") || {}; } catch { return {}; }
    };
    const writeSeerProfileDraftV131 = (patch = {}) => {
      try { localStorage.setItem(seerProfileDraftKeyV131, JSON.stringify({...readSeerProfileDraftV131(), ...patch})); } catch {}
    };
    const seerProfileDraftV131 = readSeerProfileDraftV131();
    const savedProfilePackagesV131 = parseBookingPackageTextV109(
      seer.priceText || seer.price_text || seer.bookingPackageText || seer.booking_packages_text || "",
      seer.seerId || seer.id || "seer"
    ).slice(0,5);
    const [packageVisibleCountV131, setPackageVisibleCountV131] = useState(() => Math.max(1, Math.min(5, Number(seerProfileDraftV131.packageVisibleCount || 0) || savedProfilePackagesV131.length || 1)));
    const platformLevel = getSeerPlatformLevel(seer, seerLevels);
    const isPlatinumSeer = platformLevel === "platinum";
    const isReferralEligibleSeer = isPlatinumSeer;
    // v191: โหลดตารางปกติ 30 นาทีจาก D1 ตามวันที่หมอดูเลือก
    useEffect(() => {
      if (seerTab !== "slots") return;
      loadRegularAvailabilityV191(seer, seerRegularDateV191);
    }, [seerTab, String(seer.seerId || seer.id || seer.lineUserId || ""), seerRegularDateV191]);
    const seerRegularOpenSlotsV191 = regularAvailabilityV191[bookingAvailabilityKeyV176(seer, seerRegularDateV191)] || BOOKING_TIME_SLOTS_V92;
    const seerRegularOpenSetV191 = new Set(seerRegularOpenSlotsV191);
    const seerRegularDayClosedV191 = seerRegularOpenSlotsV191.length === 0;
    // v180: โหลดเวลาพิเศษหลังเที่ยงคืนเฉพาะตอนเข้าหน้าจัดการเวลา หรือเปลี่ยนวัน
    useEffect(() => {
      if (seerTab !== "slots") return;
      loadSpecialNightAvailabilityV180(seer, seerSpecialDateV180);
    }, [seerTab, String(seer.seerId || seer.id || seer.lineUserId || ""), seerSpecialDateV180]);
    const seerSpecialOpenSlotsV180 = specialNightAvailabilityV180[bookingAvailabilityKeyV176(seer, seerSpecialDateV180)] || [];
    const seerSpecialOpenSetV180 = new Set(seerSpecialOpenSlotsV180);
    // v162: รายได้จากหมอดูที่ตัวเองแนะนำ (เฉพาะ Platinum / อ่านจาก D1)
    const [seerReferralIncomeV158, setSeerReferralIncomeV158] = useState(() => ({
      summary:{ referral_count:0, active_count:0, referral_limit:null, remaining:null, completed_jobs:0, total_commission:0, this_month_commission:0, can_refer:false },
      referrals:[],
      commissions:[]
    }));
    // v190: สิทธิ์สร้างลิงก์ต้องใช้ค่า D1 ตัวเดียวกับ API สร้างลิงก์
    // ไม่ผสมค่า localStorage เก่ากับข้อมูล D1 จนปุ่มสลับเป็น “โควต้าเต็มแล้ว” ทั้งที่ Platinum ไม่จำกัด
    const referralServerCanReferV190 = seerReferralIncomeV158.summary?.can_refer === true;
    const referralCanCreateV190 = isReferralEligibleSeer || referralServerCanReferV190;
    useEffect(() => {
      let alive = true;
      const empty = { summary:{ referral_count:0, active_count:0, referral_limit:null, remaining:null, completed_jobs:0, total_commission:0, this_month_commission:0, can_refer:false }, referrals:[], commissions:[] };
      const loadReferralIncome = async () => {
        try {
          const rawId = String(seer.seerId || seer.id || "").trim();
          if (!rawId) return;
          const params = new URLSearchParams({
            inviter_seer_id: rawId,
            alt_id: String(seer.id || "").trim(),
            line_user_id: String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || "").trim(),
          });
          const data = await apiGet(`/api/seer-referrals?${params.toString()}`);
          if (!alive) return;
          setSeerReferralIncomeV158(prev => {
            const next = {
              summary: data.summary || empty.summary,
              referrals: Array.isArray(data.referrals) ? data.referrals : [],
              commissions: Array.isArray(data.commissions) ? data.commissions : []
            };
            const sig = value => JSON.stringify({ summary:value.summary, referrals:value.referrals, commissions:value.commissions });
            return sig(prev) === sig(next) ? prev : next;
          });
        } catch (err) {
          console.warn("โหลดสถานะระบบแนะนำหมอดูจาก D1 ไม่สำเร็จ", err);
        }
      };
      loadReferralIncome();
      const timer = setInterval(loadReferralIncome, 45000);
      return () => { alive = false; clearInterval(timer); };
    }, [String(seer.seerId || seer.id || ""), String(seer.id || ""), String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || ""), seerTab]);
    useEffect(() => {
      let alive = true;
      const pingSeerPresenceV136 = async () => {
        try {
          if (!seer?.seerId && !seer?.id && !seer?.lineUserId && !seer?.line_user_id) return;
          await apiPost("/api/seer-presence", {
            action: "ping",
            seer_id: seer.seerId || seer.id || "",
            line_user_id: seer.lineUserId || seer.line_user_id || currentLineUser?.userId || "",
            page: "seer_dash",
          });
        } catch (err) {
          console.warn("อัปเดตสถานะออนไลน์หมอดูไม่สำเร็จ", err);
        }
      };
      pingSeerPresenceV136();
      const timer = setInterval(() => { if (alive) pingSeerPresenceV136(); }, 60000);
      return () => { alive = false; clearInterval(timer); };
    }, [String(seer.seerId || seer.id || ""), String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || "")]);
    const [inviteTick, setInviteTick] = useState(0);
    const [adminMsgTick, setAdminMsgTick] = useState(0);
    const [seerAdminMessagesD1, setSeerAdminMessagesD1] = useState([]);
    useEffect(() => {
      let alive = true;
      const loadAdminMessages = async () => {
        try {
          const seerId = encodeURIComponent(String(seer.seerId || seer.id || ""));
          const lineId = encodeURIComponent(String(seer.lineUserId || seer.line_user_id || ""));
          const data = await apiGet(`/api/seer-messages?seer_id=${seerId}&line_user_id=${lineId}`);
          if (!alive) return;
          const next = Array.isArray(data.messages) ? data.messages : [];
          setSeerAdminMessagesD1(prev => {
            const sig = (arr)=>JSON.stringify((arr||[]).map(m=>({id:m.id||m.message_id,msg:m.message,read:m.read,up:m.updated_at||m.created_at})));
            return sig(prev) === sig(next) ? prev : next;
          });
        } catch (err) {
          console.warn("โหลดข้อความแอดมินจาก D1 ไม่สำเร็จ", err);
        }
      };
      if (seerTab !== "profile") loadAdminMessages();
      const timer = setInterval(() => { if (seerTab !== "profile") loadAdminMessages(); }, 30000);
      return () => { alive = false; clearInterval(timer); };
    }, [String(seer.seerId || seer.id || ""), String(seer.lineUserId || seer.line_user_id || ""), adminMsgTick, seerTab]);
    const [seerInvitesD1, setSeerInvitesD1] = useState([]);
    // v175: state รายละเอียดงานใหม่อยู่ที่ App root เพื่อไม่ให้ popup เด้งปิดระหว่าง dashboard re-render
    useEffect(() => {
      let alive = true;
      const loadInvites = async () => {
        try {
          const params = new URLSearchParams({
            inviter_seer_id: String(seer.seerId || seer.id || ""),
            alt_id: String(seer.id || ""),
            line_user_id: String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || ""),
          });
          const data = await apiGet(`/api/seer-invites?${params.toString()}`);
          if (!alive) return;
          const next = Array.isArray(data.invites) ? data.invites : [];
          setSeerInvitesD1(prev => {
            const sig = (arr)=>JSON.stringify((arr||[]).map(x=>({code:x.code||x.invite_code,status:x.status,used:x.used_at||x.usedAt})));
            return sig(prev) === sig(next) ? prev : next;
          });
        } catch (err) {
          console.warn("โหลดลิงก์เชิญจาก D1 ไม่สำเร็จ", err);
        }
      };
      if (referralCanCreateV190) loadInvites();
      const timer = setInterval(() => { if (referralCanCreateV190) loadInvites(); }, 45000);
      return () => { alive = false; clearInterval(timer); };
    }, [String(seer.seerId || seer.id || ""), String(seer.id || ""), String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || ""), referralCanCreateV190, inviteTick, seerTab]);

    const localInviteLinks = readSeerInvitesStore().filter(x=>
      String(x.inviterSeerId)===String(seer.id || seer.seerId) ||
      (seer.seerId && String(x.inviterSeerId)===String(seer.seerId)) ||
      (seer.id && String(x.inviterSeerD1Id || "")===String(seer.id)) ||
      (seer.lineUserId && String(x.inviterLineUserId || "")===String(seer.lineUserId))
    );
    const myInviteLinks = (() => {
      const seen = new Set();
      return [...seerInvitesD1, ...localInviteLinks].filter(x => {
        const code = String(x.code || x.invite_code || "");
        if (!code || seen.has(code)) return false;
        seen.add(code);
        return true;
      });
    })();
    const localAdminMessages = readSeerAdminMessagesStore().filter(m => String(m.seerId || "") === String(seer.id || seer.seerId) || (seer.lineUserId && String(m.lineUserId || "") === String(seer.lineUserId)));
    const adminMessages = (() => {
      const seen = new Set();
      return [...seerAdminMessagesD1, ...localAdminMessages].filter(m => {
        const key = String(m.id || m.message_id || `${m.createdAt || m.created_at}_${m.message}`);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })();
    const unreadAdminMessages = adminMessages.filter(m => !m.read && m.read !== 1);
    const referralQuotaV159 = seerReferralIncomeV158.summary || {};
    const referralUsedV159 = Number(referralQuotaV159.active_count ?? referralQuotaV159.referral_count ?? 0);
    // v190: Platinum แนะนำได้ไม่จำกัด จึงไม่มีสถานะ “โควต้าเต็ม” ใน UI อีก
    const referralQuotaFullV159 = false;
    const createSeerReferralInviteV190 = async () => {
      if (!referralCanCreateV190) {
        flash("บัญชีนี้ยังไม่ได้เป็นแพลตินั่มในฐานข้อมูล กรุณาให้แอดมินตรวจระดับอีกครั้ง");
        return;
      }
      try {
        const res = await apiPost("/api/seer-invites", {
          action:"create",
          inviter_seer_id: String(seer.seerId || seer.id || "").trim(),
          alt_id: String(seer.id || "").trim(),
          line_user_id: String(seer.lineUserId || seer.line_user_id || currentLineUser?.userId || "").trim(),
          inviter_name: seer.name || "",
          commission_percent: 3
        });
        const invite = res?.invite || null;
        const code = String(invite?.invite_code || invite?.code || res?.code || "").trim();
        if (!code) throw new Error("ระบบสร้างลิงก์ไม่คืนรหัสลิงก์");
        // ใส่ลิงก์ที่สร้างลงหน้าทันที ไม่ต้องรอรอบ refresh จึงไม่กระพริบหรือสลับข้อความ
        setSeerInvitesD1(prev => {
          const list = [invite, ...(prev || []).filter(item => String(item.invite_code || item.code || "") !== code)];
          return list;
        });
        setInviteTick(value => value + 1);
        copyTextSafe(buildSeerInviteUrl(code), "สร้างลิงก์เชิญและคัดลอกแล้ว");
      } catch (err) {
        flash("สร้างลิงก์เชิญไม่สำเร็จ: " + (err.message || err));
      }
    };
    // v170: แยกงานตาม flow จริงของการนัดดูดวง
    // paid_waiting_seer = มีงานใหม่ รอหมอดูรับ / seer_proposed_new_time = รอลูกค้ายอมรับเวลาใหม่
    const newWaitingBookingsV170 = mySeerBookingsV91
      .filter(b => ["paid_waiting_seer", "seer_proposed_new_time"].includes(String(b.status || "")))
      .sort((a,b)=>String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
    const readyToAcceptBookingsV170 = newWaitingBookingsV170.filter(b => String(b.status || "") === "paid_waiting_seer");
    // อย่าคืน null ระหว่าง API refresh ชั่วคราว เพราะจะทำให้หน้ารายละเอียดเด้งปิดเอง
    // เก็บ snapshot เดิมไว้จนกว่าหมอดูจะกดปิด หรือรับงาน/ขอเลื่อนเวลาเสร็จ
    const selectedNewJobDetailsV175 = newJobDetailsV175
      ? (readyToAcceptBookingsV170.find(b => String(b.booking_id || "") === String(newJobDetailsV175.booking_id || "")) || newJobDetailsV175)
      : null;
    const openNewJobDetailsV175 = (booking = null) => {
      const target = booking || readyToAcceptBookingsV170[0] || null;
      if (target) setNewJobDetailsV175(target);
    };
    const customerContactPreferenceLabelV175 = (booking = {}) => {
      const raw = String(booking.customer_contact_preference || booking.customerContactPreference || "").toLowerCase();
      const values = raw.split(/[,|\s]+/).filter(Boolean);
      const labels = [];
      if (values.includes("phone")) labels.push("📞 เบอร์โทร");
      if (values.includes("line")) labels.push("💬 LINE");
      return labels.length ? labels.join(" / ") : "ไม่ได้ระบุ (ใช้ข้อมูลด้านล่าง)";
    };
    const approvedLiveBookingsV128 = mySeerBookingsV91.filter(b => ["confirmed","in_session"].includes(String(b.status || "")));
    const completedLiveBookingsV128 = mySeerBookingsV91.filter(b => String(b.status || "") === "completed");
    const todayQueueBookingsV170 = [...approvedLiveBookingsV128]
      .sort((a,b)=>String(a.scheduled_at || a.requested_date || "").localeCompare(String(b.scheduled_at || b.requested_date || "")));
    // v178: คิวที่หมอดูเปิดดูรายละเอียด เก็บ snapshot ไว้เพื่อไม่ให้เด้งปิดจากการ refresh รายการ
    // v182: ใช้ snapshot ที่ถูกเลือกทันที ห้ามแทน object ระหว่าง background refresh
    // เพื่อให้หน้ารายละเอียดไม่เห็นอาการเหมือนรีเฟรช/เด้งใน LINE WebView
    const selectedSeerQueueDetailsV178 = seerQueueDetailsV178 || null;
    const openSeerQueueDetailsV178 = (booking = null) => {
      const target = booking || todayQueueBookingsV170[0] || null;
      if (target) setSeerQueueDetailsV178(prev =>
        String(prev?.booking_id || "") === String(target.booking_id || "") ? prev : { ...target }
      );
    };
    const formatCustomerBirthdateV178 = (value) => {
      const parsed = parseBirthdateV139(value);
      if (!parsed) return "ลูกค้ายังไม่ได้ระบุ";
      const thaiYear = parsed.year + 543;
      return `${String(parsed.day).padStart(2,"0")}/${String(parsed.month).padStart(2,"0")}/${thaiYear}`;
    };
    const completedHistoryBookingsV170 = [...completedLiveBookingsV128]
      .sort((a,b)=>String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
    // v3.9.2: รายได้บนแดชบอร์ดหมอดูต้องอ่านจาก booking ที่ปิดงานแล้วใน D1
    // ไม่ใช้ myOrders เพราะชุดนั้นถูกเลิกใช้กับ flow การจองจริงและเป็น [] เสมอ
    const seerIncomeForBookingV392 = (booking = {}) => {
      const savedIncome = booking?.seer_income_amount;
      if (savedIncome !== null && savedIncome !== undefined && String(savedIncome).trim() !== "") {
        const value = Number(savedIncome);
        if (Number.isFinite(value)) return Math.max(0, value);
      }
      // fallback สำหรับงานเก่าที่เคยปิดแล้ว แต่ยังไม่มี seer_income_amount
      const gross = Number(booking?.price || 0);
      const savedFee = booking?.platform_fee_amount;
      if (savedFee !== null && savedFee !== undefined && String(savedFee).trim() !== "") {
        const fee = Number(savedFee);
        if (Number.isFinite(fee)) return Math.max(0, gross - fee);
      }
      return 0;
    };
    const total = completedLiveBookingsV128.reduce((sum, booking) => sum + seerIncomeForBookingV392(booking), 0);
    const tabs = [
      {k:"booking_requests",icon:"💼",l:`มีงานใหม่/รอรับ${readyToAcceptBookingsV170.length?` (${readyToAcceptBookingsV170.length})`:""}`},
      {k:"jobs",icon:"📅",l:`จำนวนคิวงานวันนี้${todayQueueBookingsV170.length?` (${todayQueueBookingsV170.length})`:""}`},
      {k:"live",icon:"📋",l:`ประวัติงานที่รับ${completedHistoryBookingsV170.length?` (${completedHistoryBookingsV170.length})`:""}`},
      {k:"slots",icon:"◷",l:"จัดการเวลา"},
      {k:"income",icon:"💼",l:"รายได้"},
      {k:"admin_msg",icon:"💬",l:`ข้อความแอดมิน${unreadAdminMessages.length?` (${unreadAdminMessages.length})`:""}`},
      {k:"promo",icon:"📣",l:"แชร์โปรไฟล์"},
      {k:"profile",icon:"▣",l:"โปรไฟล์"}
    ];
    // v3.9.11: Each dashboard overview card now opens its own real detail
    // screen.  It no longer merely changes a tab or scrolls the user down the
    // dashboard, which was easy to miss in LINE mobile WebView.
    const openSeerDashboardMenuV3910 = (tab) => {
      setSeerTab(tab);
      const scrollToMenu = () => {
        document.getElementById("dw-seer-dashboard-menu-v3910")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      };
      if (typeof window !== "undefined") window.setTimeout(scrollToMenu, 0);
    };

    const detailConfigV3911 = {
      seer_new_jobs: { tab: "booking_requests", title: "งานใหม่ / รอรับงาน", icon: "💼" },
      seer_today_jobs: { tab: "jobs", title: "นัดของฉันวันนี้", icon: "📅" },
      seer_job_history: { tab: "live", title: "ประวัติงานที่รับ", icon: "📋" },
      seer_income: { tab: "income", title: "รายได้ของฉัน", icon: "฿" },
    };
    const detailConfig = detailConfigV3911[detailPage] || null;
    const openSeerDashboardDetailV3911 = (screen, tab) => {
      setSeerTab(tab);
      setActiveNav("seer_dash");
      go(screen);
      if (typeof window !== "undefined") window.setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 0);
    };
    const closeSeerDashboardDetailV3911 = () => {
      if (detailConfig) setSeerTab(detailConfig.tab);
      setActiveNav("seer_dash");
      go("seer_dash");
    };
    const moneyV3911 = (value) => Number(value || 0).toLocaleString("th-TH", { minimumFractionDigits:0, maximumFractionDigits:2 });

    if (detailConfig) {
      const ownIncomeV3911 = completedLiveBookingsV128.reduce((sum, b) => sum + seerIncomeForBookingV392(b), 0);
      let detailBody = null;

      if (detailPage === "seer_new_jobs") {
        detailBody = <>
          <div className="notif" style={{margin:"0 0 12px",borderColor:`${C.gold}66`,background:`${C.gold}10`,color:C.goldLight}}>
            แสดงรายการที่ลูกค้าชำระเงินแล้วและแอดมินอนุมัติสลิปเรียบร้อย สามารถรับงานหรือขอเลื่อนเวลาได้จากหน้านี้
          </div>
          {newWaitingBookingsV170.length === 0 ? <div className="empty"><div className="e-ico">✅</div>ยังไม่มีงานใหม่รอรับ</div> : newWaitingBookingsV170.map(b => (
            <div key={b.booking_id} className="brow" style={{marginBottom:12,borderColor:b.status === "paid_waiting_seer" ? `${C.gold}77` : undefined}}>
              <div className="brow-hd"><div><div className="brow-name">{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "จองคิวดูดวงสด"}</div></div><span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span></div>
              <div className="brow-detail">📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>📌 สะดวกติดต่อกลับ: {customerContactPreferenceLabelV175(b)}<br/>📞 โทร: {b.customer_phone || "-"} • 💬 LINE: {b.customer_line_id || "-"}<br/>📝 {b.customer_note || "ไม่มีข้อความเพิ่มเติม"}</div>
              {b.status === "seer_proposed_new_time" ? <div className="warn-box" style={{marginTop:10}}>⏳ ส่งคำขอเลื่อนเวลาแล้ว รอลูกค้ายอมรับเวลาใหม่</div> : <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:12}}>
                <button type="button" className="btn btn-success" disabled={bookingLoading} onClick={()=>seerConfirmBookingV91(b)}>{bookingLoading ? "กำลังรับงาน..." : "รับงาน"}</button>
                <button type="button" className="btn btn-outline" disabled={bookingLoading} onClick={()=>seerProposeBookingV91(b)}>ขอเลื่อนเวลา</button>
              </div>}
            </div>
          ))}
        </>;
      } else if (detailPage === "seer_today_jobs") {
        detailBody = <>
          <div className="notif" style={{margin:"0 0 12px"}}>รายการคิวที่ยืนยันแล้วของวันนี้ แตะ “ดูรายละเอียดลูกค้า” เพื่อดูข้อมูลและเข้าห้องดูดวงเมื่อถึงเวลา</div>
          {todayQueueBookingsV170.length === 0 ? <div className="empty"><div className="e-ico">📅</div>ยังไม่มีคิวงานวันนี้</div> : todayQueueBookingsV170.map(b => {
            const inSession = String(b.status || "") === "in_session";
            return <div key={b.booking_id} className="brow" style={{marginBottom:12,borderColor:`${C.green}55`}}>
              <div className="brow-hd"><div><div className="brow-name">{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "จองคิวดูดวงสด"}</div></div><span className="badge b-green">{inSession ? "กำลังดูดวง" : "ยืนยันคิวแล้ว"}</span></div>
              <div className="brow-detail">📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>📌 สะดวกติดต่อ: {customerContactPreferenceLabelV175(b)}</div>
              <button type="button" className="btn btn-gold" style={{marginTop:10}} onClick={()=>openSeerQueueDetailsV178(b)}>{inSession ? "ดูรายละเอียด / เข้าห้องดูดวง" : "ดูรายละเอียดลูกค้า"}</button>
            </div>;
          })}
        </>;
      } else if (detailPage === "seer_job_history") {
        detailBody = <>
          <div className="notif" style={{margin:"0 0 12px"}}>แสดงเฉพาะงานที่หมอดูรับและปิดงานเรียบร้อยแล้ว</div>
          {completedHistoryBookingsV170.length === 0 ? <div className="empty"><div className="e-ico">📜</div>ยังไม่มีประวัติงานที่ปิดแล้ว</div> : completedHistoryBookingsV170.map(b => (
            <div key={b.booking_id} className="brow" style={{marginBottom:12}}>
              <div className="brow-hd"><div><div className="brow-name">{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "จองคิวดูดวงสด"}</div></div><span className="badge b-green">ปิดงานแล้ว</span></div>
              <div className="brow-detail">📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>{b.summary ? `สรุปงาน: ${b.summary}` : "ปิดงานเรียบร้อย"}</div>
            </div>
          ))}
        </>;
      } else {
        const refSummaryV3911 = seerReferralIncomeV158.summary || {};
        detailBody = <>
          <div className="card card-gold" style={{padding:16,textAlign:"center",marginBottom:14}}>
            <div style={{fontSize:12,color:C.muted,marginBottom:4}}>รายได้จากงานของฉันที่ปิดแล้ว</div>
            <div style={{fontFamily:"Cinzel",fontSize:32,color:C.goldLight}}>฿{moneyV3911(ownIncomeV3911)}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>นับเฉพาะงานที่กดปิดงานสำเร็จแล้ว</div>
          </div>
          {isReferralEligibleSeer ? <div className="card" style={{padding:14,marginBottom:14,borderColor:`${C.gold}66`}}>
            <div style={{fontWeight:900,color:C.goldLight,fontSize:16}}>👑 รายได้จากหมอดูที่ฉันแนะนำ</div>
            <div className="stats-grid" style={{marginTop:10}}>
              <div className="stat"><div className="stat-l">ค่าคอมสะสม</div><div className="stat-v" style={{color:C.green}}>฿{moneyV3911(refSummaryV3911.total_commission)}</div></div>
              <div className="stat"><div className="stat-l">เดือนนี้</div><div className="stat-v">฿{moneyV3911(refSummaryV3911.this_month_commission)}</div></div>
            </div>
          </div> : null}
          {completedLiveBookingsV128.length === 0 ? <div className="empty"><div className="e-ico">💰</div>ยังไม่มีรายได้จากงานจริงที่ปิดแล้ว</div> : completedLiveBookingsV128.map(b => (
            <div key={b.booking_id} className="brow" style={{marginBottom:10}}>
              <div className="brow-hd"><div><div className="brow-name">{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:11,color:C.muted}}>{b.service_name || "บริการ"} • {b.seer_income_released_at || b.updated_at || ""}</div></div><span style={{color:C.green,fontWeight:900}}>+฿{moneyV3911(seerIncomeForBookingV392(b))}</span></div>
            </div>
          ))}
        </>;
      }

      return <div className="scr on seer-light">
        <div className="topbar">
          <button className="back-btn" type="button" aria-label="กลับแดชบอร์ดหมอดู" onClick={closeSeerDashboardDetailV3911}>←</button>
          <span className="topbar-title">{detailConfig.icon} {detailConfig.title}</span>
        </div>
        <div className="content z1" style={{padding:"16px 16px 96px"}}>
          {detailBody}
        </div>
        <BottomNav/>
      </div>;
    }

    return (
      <div className="scr on seer-light">
        <div className="topbar">
          <span className="topbar-title">{seer.emoji} แดชบอร์ดหมอดู</span>
        </div>
        <div className="content z1">
          <div className="stats-grid seer-dashboard-stats">
            <button
              type="button"
              className="stat-card seer-stat-card seer-stat-card-button"
              aria-label="เปิดเมนูมีงานใหม่หรือรอรับ"
              onClick={()=>openSeerDashboardDetailV3911("seer_new_jobs", "booking_requests")}
              style={{cursor:"pointer",touchAction:"manipulation",borderColor:readyToAcceptBookingsV170.length ? "#d69b12" : undefined}}
            >
              <div className="seer-stat-icon" aria-hidden="true">💼</div>
              <div className="seer-stat-copy"><div className="stat-num" style={{color:readyToAcceptBookingsV170.length ? "#8b4b08" : "#47248c"}}>{readyToAcceptBookingsV170.length}</div><div className="stat-lbl">มีงานใหม่/รอรับ</div></div>
            </button>
            <button
              type="button"
              className="stat-card seer-stat-card seer-stat-card-button"
              aria-label="เปิดเมนูจำนวนคิวงานวันนี้"
              onClick={()=>openSeerDashboardDetailV3911("seer_today_jobs", "jobs")}
              style={{cursor:"pointer",touchAction:"manipulation",borderColor:todayQueueBookingsV170.length ? "#22876a" : undefined}}
            >
              <div className="seer-stat-icon" aria-hidden="true">📅</div>
              <div className="seer-stat-copy"><div className="stat-num" style={{color:"#2f166f"}}>{todayQueueBookingsV170.length}</div><div className="stat-lbl">จำนวนคิวงานวันนี้</div>{todayQueueBookingsV170.length > 0 && <div style={{fontSize:12,color:"#14775b",marginTop:5,fontWeight:900}}>แตะเพื่อดูรายละเอียด</div>}</div>
            </button>
            <button
              type="button"
              className="stat-card seer-stat-card seer-stat-card-button"
              aria-label="เปิดเมนูประวัติงานที่รับ"
              onClick={()=>openSeerDashboardDetailV3911("seer_job_history", "live")}
              style={{cursor:"pointer",touchAction:"manipulation"}}
            >
              <div className="seer-stat-icon" aria-hidden="true">📋</div>
              <div className="seer-stat-copy"><div className="stat-num">{completedHistoryBookingsV170.length}</div><div className="stat-lbl">ประวัติงานที่รับ</div></div>
            </button>
            <button
              type="button"
              className="stat-card seer-stat-card seer-stat-card-button"
              aria-label="เปิดเมนูรายได้"
              onClick={()=>openSeerDashboardDetailV3911("seer_income", "income")}
              style={{cursor:"pointer",touchAction:"manipulation"}}
            >
              <div className="seer-stat-icon" aria-hidden="true">฿</div>
              <div className="seer-stat-copy"><div className="stat-num" style={{fontSize:28}}>฿{total.toLocaleString("th-TH", {minimumFractionDigits:0, maximumFractionDigits:2})}</div><div className="stat-lbl">รายได้รวม</div></div>
            </button>
          </div>
          <div className="card seer-share-card">
            <div className="seer-share-head">
              <div className="seer-share-copy">
                <div className="seer-share-title"><span className="seer-share-icon" aria-hidden="true">📣</span><span>แชร์โปรไฟล์หมอดู</span></div>
                <div className="seer-share-desc">แชร์โปรไฟล์ตัวเองไป LINE / Facebook / แอปอื่นได้จากตรงนี้</div>
              </div>
              <span className="badge seer-level-badge">{referralCanCreateV190 ? "👑 ระดับแพลตินั่ม" : `◈ ระดับ${getSeerLevelLabel(platformLevel)}`}</span>
            </div>
            <div className="seer-share-actions">
              <button className="btn btn-gold btn-sm" onClick={() => copyTextSafe(buildSeerProfileShareUrl(seer), "คัดลอกลิงก์โปรไฟล์แล้ว")}>⇧ คัดลอกโปรไฟล์</button>
              <button className="btn btn-outline btn-sm" onClick={async()=>{
                const url = buildSeerProfileShareUrl(seer);
                const shareData = { title:`ดูดวงกับ ${seer.name} บนดวงวันนี้`, text:`โปรไฟล์หมอดู ${seer.name} — ${seer.spec}`, url };
                if (navigator.share) {
                  try { await navigator.share(shareData); } catch {}
                } else {
                  copyTextSafe(url, "อุปกรณ์นี้ไม่รองรับปุ่มแชร์ คัดลอกลิงก์ให้แล้ว");
                }
              }}>↗ แชร์</button>
              <button className="btn btn-purple btn-sm" onClick={()=>openSeerDashboardMenuV3910("promo")}>📣 เปิดเมนูโปรโมต</button>
              {referralCanCreateV190 && (
                <button className="btn btn-gold btn-sm seer-invite-button" onClick={createSeerReferralInviteV190}>👤+ สร้างลิงก์เชิญ</button>
              )}
            </div>
            {!referralCanCreateV190 && (
              <div className="seer-share-desc" style={{fontSize:15,marginTop:13}}>
                หากแอดมินปรับเป็นแพลตินั่ม จะมีปุ่มสร้างลิงก์เชิญหมอดูขึ้นตรงนี้
              </div>
            )}
          </div>

          {readyToAcceptBookingsV170.length > 0 && (
            <div
              className="card"
              role="button"
              tabIndex={0}
              aria-label="เปิดเมนูมีงานใหม่รอรับ"
              onClick={()=>openNewJobDetailsV175()}
              onKeyDown={(e)=>{ if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openNewJobDetailsV175(); } }}
              style={{
                margin:"12px 16px 8px", padding:15,
                border:`2px solid ${C.gold}`,
                background:"linear-gradient(135deg, rgba(255,213,89,.24), rgba(126,77,255,.22))",
                boxShadow:"0 8px 26px rgba(255,199,61,.18)",
                cursor:"pointer"
              }}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:42,height:42,borderRadius:13,display:"grid",placeItems:"center",fontSize:23,background:"rgba(255,223,111,.20)",border:`1px solid ${C.gold}88`}}>🔔</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:16,fontWeight:950,color:C.goldLight}}>มีงานใหม่ รอรับงาน</div>
                  <div style={{fontSize:12,color:C.text,marginTop:3}}>ตรวจสอบลูกค้าชำระเงินเรียบร้อยแล้ว และอนุมัติ {readyToAcceptBookingsV170.length} งาน</div>
                  <div style={{fontSize:11,color:C.purpleLight,marginTop:4}}>แตะกรอบนี้เพื่อเปิดเมนูงานใหม่ / รับงาน • ล่าสุด: {readyToAcceptBookingsV170[0]?.customer_name || "ลูกค้า"} • {dwBookingScheduledText(readyToAcceptBookingsV170[0])}</div>
                </div>
              </div>
              <button className="btn btn-gold" type="button" style={{marginTop:12,minHeight:44,fontWeight:900}} onClick={(e)=>{ e.stopPropagation(); openNewJobDetailsV175(); }}>ดูงานใหม่ / รับงาน</button>
            </div>
          )}
          {selectedNewJobDetailsV175 && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="รายละเอียดงานใหม่"
              onClick={()=>setNewJobDetailsV175(null)}
              style={{
                position:"fixed", inset:0, zIndex:1000001, background:"rgba(0,0,0,.74)",
                display:"flex", alignItems:"flex-end", justifyContent:"center", padding:0
              }}
            >
              <div
                onClick={e=>e.stopPropagation()}
                style={{
                  width:"100%", maxWidth:560, maxHeight:"88dvh", overflowY:"auto",
                  background:"linear-gradient(180deg, #211541, #120b2a)",
                  border:`1px solid ${C.gold}88`, borderRadius:"24px 24px 0 0",
                  padding:"18px 16px calc(22px + env(safe-area-inset-bottom, 0px))",
                  boxShadow:"0 -16px 46px rgba(0,0,0,.48)"
                }}
              >
                <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize:19,fontWeight:950,color:C.goldLight}}>🔔 รายละเอียดงานใหม่</div>
                    <div style={{fontSize:12,color:C.purpleLight,marginTop:4}}>ลูกค้าชำระเงินแล้วและระบบอนุมัติเรียบร้อย</div>
                  </div>
                  <button className="btn btn-outline btn-sm" type="button" onClick={()=>setNewJobDetailsV175(null)}>ปิด</button>
                </div>
                {readyToAcceptBookingsV170.length > 1 && (
                  <div style={{display:"flex",gap:8,overflowX:"auto",padding:"14px 0 2px"}}>
                    {readyToAcceptBookingsV170.map((job, index)=>(
                      <button
                        key={job.booking_id || index}
                        type="button"
                        className={`btn btn-sm ${String(job.booking_id)===String(selectedNewJobDetailsV175.booking_id) ? "btn-gold" : "btn-outline"}`}
                        style={{whiteSpace:"nowrap"}}
                        onClick={()=>setNewJobDetailsV175(job)}
                      >งานที่ {index+1}</button>
                    ))}
                  </div>
                )}
                <div className="brow" style={{margin:"16px 0 0",borderColor:`${C.gold}66`,background:"rgba(255,214,100,.07)"}}>
                  <div className="brow-hd">
                    <div>
                      <div className="brow-name">{selectedNewJobDetailsV175.customer_name || "ลูกค้า"}</div>
                      <div style={{fontSize:13,color:C.purpleLight,marginTop:3}}>{selectedNewJobDetailsV175.service_name || "จองคิวดูดวงสด"}</div>
                    </div>
                    <span className={`badge ${dwBookingBadgeClass(selectedNewJobDetailsV175.status)}`}>{dwBookingStatusLabel(selectedNewJobDetailsV175.status)}</span>
                  </div>
                  <div className="brow-detail" style={{fontSize:14,lineHeight:1.8}}>
                    📅 {dwBookingScheduledText(selectedNewJobDetailsV175)}<br/>
                    💰 ฿{Number(selectedNewJobDetailsV175.price || 0).toLocaleString()}<br/>
                    📌 สะดวกติดต่อกลับ: {customerContactPreferenceLabelV175(selectedNewJobDetailsV175)}<br/>
                    📞 โทร: {selectedNewJobDetailsV175.customer_phone || "-"}<br/>
                    💬 LINE: {selectedNewJobDetailsV175.customer_line_id || "-"}<br/>
                    📝 {selectedNewJobDetailsV175.customer_note || "ไม่มีข้อความเพิ่มเติม"}
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:16}}>
                  <button
                    className="btn btn-success"
                    type="button"
                    disabled={bookingLoading}
                    style={{minHeight:52,fontSize:16,fontWeight:950}}
                    onClick={async()=>{
                      const data = await seerConfirmBookingV91(selectedNewJobDetailsV175);
                      if (data?.booking) setNewJobDetailsV175(null);
                    }}
                  >{bookingLoading ? "กำลังรับงาน..." : "รับงาน"}</button>
                  <button
                    className="btn btn-outline"
                    type="button"
                    disabled={bookingLoading}
                    style={{minHeight:52,fontSize:16,fontWeight:900}}
                    onClick={()=>{
                      const selected = selectedNewJobDetailsV175;
                      setNewJobDetailsV175(null);
                      seerProposeBookingV91(selected);
                    }}
                  >ขอเลื่อนเวลา</button>
                </div>
                <button className="btn btn-outline" type="button" style={{marginTop:10}} onClick={()=>{ setNewJobDetailsV175(null); setSeerTab("booking_requests"); }}>ดูรายการงานใหม่ทั้งหมด</button>
              </div>
            </div>
          )}
          {unreadAdminMessages.length > 0 && (
            <div className="notif" style={{margin:"12px 16px 6px",borderColor:`${C.orange}66`,background:`${C.orange}12`}}>
              ⚠️ มีข้อความจากแอดมิน {unreadAdminMessages.length} รายการ<br/>
              <span style={{fontSize:11,opacity:.85}}>ล่าสุด: {unreadAdminMessages[0]?.message}</span>
              <button className="btn btn-outline btn-sm" style={{marginTop:8}} onClick={()=>setSeerTab("admin_msg")}>เปิดอ่านข้อความ</button>
            </div>
          )}
          <div id="dw-seer-dashboard-menu-v3910" className="tab-bar" style={{overflow:"visible",flexWrap:"wrap",paddingBottom:4,scrollMarginTop:12}}>
            {tabs.map(t=>(
              <button
                key={t.k}
                type="button"
                className={`tab${seerTab===t.k?" on":""}`}
                aria-current={seerTab===t.k ? "page" : undefined}
                onClick={()=>openSeerDashboardMenuV3910(t.k)}
              ><span className="seer-tab-icon" aria-hidden="true">{t.icon}</span>{t.l}</button>
            ))}
          </div>

          {seerTab==="booking_requests" && (
            <div style={{paddingBottom:90}}>
              <div className="sec-hd">มีงานใหม่ / รอรับงาน</div>
              <div className="notif" style={{margin:"0 16px 12px",borderColor:`${C.gold}66`,background:`${C.gold}10`,color:C.goldLight}}>
                แสดงเฉพาะงานที่ลูกค้าชำระเงินแล้วและระบบอนุมัติแล้ว งานจะหายจากหน้านี้ทันทีหลังหมอดูกดรับงาน หรือเมื่อลูกค้ายอมรับเวลาใหม่
              </div>
              {newWaitingBookingsV170.length===0 ? <div className="empty"><div className="e-ico">✅</div>ยังไม่มีงานใหม่รอรับ</div> : newWaitingBookingsV170.map(b=>(
                <div key={b.booking_id} className="brow" style={{borderColor:b.status === "paid_waiting_seer" ? `${C.gold}77` : undefined}}>
                  <div className="brow-hd"><div><div className="brow-name">{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:12,color:C.purpleLight}}>{b.service_name}</div></div><span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span></div>
                  <div className="brow-detail">📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>📌 สะดวกติดต่อกลับ: {customerContactPreferenceLabelV175(b)}<br/>โทร: {b.customer_phone || "-"} • LINE: {b.customer_line_id || "-"}<br/>{b.customer_note || "ไม่มีข้อความเพิ่มเติม"}</div>
                  {b.status === "seer_proposed_new_time" && <div className="warn-box" style={{marginTop:10}}>⏳ ส่งคำขอเลื่อนเวลาแล้ว รอลูกค้ากดยอมรับเวลาใหม่</div>}

                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
                    {b.status==="paid_waiting_seer" && <button className="btn btn-success btn-sm" onClick={()=>seerConfirmBookingV91(b)}>รับงาน</button>}
                    {b.status==="paid_waiting_seer" && <button className="btn btn-outline btn-sm" onClick={()=>seerProposeBookingV91(b)}>ขอเลื่อนเวลา</button>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {seerTab==="jobs" && (
            <div style={{paddingBottom:90}}>
              <div className="sec-hd">จำนวนคิวงานวันนี้</div>
              <div className="notif" style={{margin:"0 16px 12px"}}>
                งานที่หมอดูกดรับแล้ว หรือรายการที่ลูกค้ายอมรับเวลาใหม่แล้ว จะย้ายมาอยู่ที่นี่ทันที ระบบยืนยันคิวแล้ว รอถึงเวลานัดเพื่อเปิดห้องดูดวง
              </div>
              {todayQueueBookingsV170.length===0
                ? <div className="empty"><div className="e-ico">📅</div>ยังไม่มีคิวงานวันนี้</div>
                : todayQueueBookingsV170.map(b => {
                  const inSession = String(b.status || "") === "in_session";
                  return (
                    <div
                      key={b.booking_id}
                      className="brow"
                      role="button"
                      tabIndex={0}
                      onClick={()=>openSeerQueueDetailsV178(b)}
                      onKeyDown={(e)=>{ if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openSeerQueueDetailsV178(b); } }}
                      style={{cursor:"pointer",borderColor:`${C.green}55`}}
                    >
                      <div className="brow-hd">
                        <div>
                          <div className="brow-name">{b.customer_name || "ลูกค้า"}</div>
                          <div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "จองคิวดูดวงสด"}</div>
                        </div>
                        <span className="badge b-green">{inSession ? "กำลังดูดวง" : "ยืนยันคิวแล้ว"}</span>
                      </div>
                      <div className="brow-detail">📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>📌 สะดวกติดต่อ: {customerContactPreferenceLabelV175(b)}<br/>แตะการ์ดนี้เพื่อดูรายละเอียดลูกค้าและขั้นตอนปิดงาน</div>
                      <button
                        className="btn btn-gold"
                        type="button"
                        style={{marginTop:10}}
                        onClick={(e)=>{ e.stopPropagation(); openSeerQueueDetailsV178(b); }}
                      >{inSession ? "ดูรายละเอียด / เข้าห้องดูดวง" : "ดูรายละเอียดลูกค้า"}</button>
                    </div>
                  );
                })}
            </div>
          )}

          {seerTab==="live" && (
            <div style={{paddingBottom:90}}>
              <div className="sec-hd">ประวัติงานที่รับ</div>
              <div className="notif" style={{margin:"0 16px 12px"}}>
                แสดงเฉพาะงานที่หมอดูรับและปิดงานเรียบร้อยแล้ว
              </div>
              {completedHistoryBookingsV170.length===0
                ? <div className="empty"><div className="e-ico">📜</div>ยังไม่มีประวัติงานที่ปิดแล้ว</div>
                : completedHistoryBookingsV170.map(b => (
                  <div key={b.booking_id} className="brow">
                    <div className="brow-hd">
                      <div>
                        <div className="brow-name">{b.customer_name || "ลูกค้า"}</div>
                        <div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "จองคิวดูดวงสด"}</div>
                      </div>
                      <span className="badge b-green">ปิดงานแล้ว</span>
                    </div>
                    <div className="brow-detail">📅 {dwBookingScheduledText(b)} • ฿{Number(b.price||0).toLocaleString()}<br/>{b.summary ? `สรุปงาน: ${b.summary}` : "ปิดงานเรียบร้อย"}</div>
                  </div>
                ))}
            </div>
          )}

          {seerTab==="slots" && (
            <>
              <div className="sec-hd">เปิด / ปิดช่วงเวลา</div>
              <div style={{margin:"0 16px 12px",padding:14,background:C.card,border:`1px solid ${seerRegularDayClosedV191 ? C.red : C.green}55`,borderRadius:14}}>
                <div className="fgroup" style={{marginBottom:12}}>
                  <label className="flabel">เลือกวันที่ต้องการตั้งเวลา</label>
                  <input className="finput" type="date" min={localDateInputValue()} value={seerRegularDateV191} onFocus={e=>e.currentTarget.showPicker?.()} onClick={e=>e.currentTarget.showPicker?.()} onChange={e=>setSeerRegularDateV191(e.target.value)} style={{fontSize:16,fontWeight:800,minHeight:50,border:`1px solid ${C.green}88`}} />
                </div>
                <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:900,color:seerRegularDayClosedV191 ? C.red : C.green}}>{seerRegularDayClosedV191 ? "ปิดรับงานทั้งวัน" : "เปิดรับงานตามช่วงที่เลือก"}</div>
                    <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:4}}>ทุกช่องเป็น 30 นาที และบันทึกลงระบบจริง ลูกค้าจะเห็นเฉพาะช่วงที่เปิดและต่อเนื่องพอกับระยะเวลาบริการ</div>
                  </div>
                  <button type="button" disabled={seerRegularSavingV191} className={`btn btn-sm ${seerRegularDayClosedV191 ? "btn-purple" : "btn-outline"}`} style={{width:120,whiteSpace:"nowrap"}} onClick={()=>saveSeerRegularHoursV191(seer, seerRegularDateV191, "set_all", seerRegularDayClosedV191)}>
                    {seerRegularSavingV191 ? "กำลังบันทึก..." : (seerRegularDayClosedV191 ? "เปิดทั้งวัน" : "ปิดทั้งวัน")}
                  </button>
                </div>
              </div>

              <div style={{margin:"0 16px 16px",padding:14,borderRadius:16,background:`linear-gradient(180deg, ${C.purple}18, ${C.surface})`,border:`1px solid ${C.purple}77`}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:900,color:C.purpleLight}}>🌙 ช่วงเวลาพิเศษหลังเที่ยงคืน</div>
                    <div style={{fontSize:11,color:C.muted,lineHeight:1.65,marginTop:4}}>เวลาปกติเปิดรับถึง 00:00 น. ช่วง 00:00–04:00 จะปิดไว้เป็นค่าเริ่มต้น และเปิดได้เฉพาะวันที่หมอดูต้องการรับงานเท่านั้น</div>
                  </div>
                  <span className={`badge ${seerSpecialOpenSlotsV180.length ? "b-green" : "b-purple"}`}>{seerSpecialOpenSlotsV180.length ? `เปิด ${seerSpecialOpenSlotsV180.length} ช่วง` : "ปิดทั้งหมด"}</span>
                </div>
                <div className="fgroup" style={{marginBottom:10}}>
                  <label className="flabel">เลือกวันที่ต้องการเปิดเวลาพิเศษ</label>
                  <input className="finput" type="date" min={localDateInputValue()} value={seerSpecialDateV180} onFocus={e=>e.currentTarget.showPicker?.()} onClick={e=>e.currentTarget.showPicker?.()} onChange={e=>setSeerSpecialDateV180(e.target.value)} style={{fontSize:16,fontWeight:800,minHeight:50,border:`1px solid ${C.purple}88`}} />
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                  <button type="button" className="btn btn-purple btn-sm" disabled={seerSpecialSavingV180} onClick={()=>saveSeerSpecialNightHoursV180(seer, seerSpecialDateV180, "set_all", true)}>{seerSpecialSavingV180 ? "กำลังบันทึก..." : "เปิดทุกช่วง 00:00–04:00"}</button>
                  <button type="button" className="btn btn-outline btn-sm" disabled={seerSpecialSavingV180} onClick={()=>saveSeerSpecialNightHoursV180(seer, seerSpecialDateV180, "set_all", false)}>ปิดทุกช่วง</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {SPECIAL_NIGHT_TIME_SLOTS_V180.map(slot => {
                    const open = seerSpecialOpenSetV180.has(slot);
                    return <button key={slot} type="button" disabled={seerSpecialSavingV180} className={`btn btn-sm ${open ? "btn-purple" : "btn-outline"}`} style={{minHeight:48,borderRadius:12,opacity:seerSpecialSavingV180 ? .65 : 1}} onClick={()=>saveSeerSpecialNightHoursV180(seer, seerSpecialDateV180, "set_slot", !open, slot)}>{open ? `🌙 ${slot} • เปิด` : `${slot} • ปิด`}</button>;
                  })}
                </div>
                <div style={{fontSize:11,color:C.goldLight,lineHeight:1.6,marginTop:10}}>ลูกค้าจะเห็นเฉพาะช่วงเวลาพิเศษที่เปิดไว้ในวันที่เลือก และระบบกันเวลาซ้ำเหมือนช่วงเวลาปกติ</div>
              </div>

              {BOOKING_TIME_SLOTS_V92.map(sl=>{
                const open = seerRegularOpenSetV191.has(sl);
                const hasBook = (bookings || []).some(b=>String(b.requested_date || "").slice(0,10)===seerRegularDateV191 && isSlotBlockedStatusV113(b.status) && bookingSlotKeysV191(b).includes(slotKeyV113(seerRegularDateV191, sl)));
                return (
                  <div key={sl} className="tog-wrap">
                    <div>
                      <div style={{fontSize:14,color:C.text}}>{sl}</div>
                      {hasBook&&<div style={{fontSize:11,color:C.orange}}>มีการจอง — ปิดไม่ได้</div>}
                      {!hasBook&&!open&&<div style={{fontSize:11,color:C.muted}}>ปิดอยู่</div>}
                      {!hasBook&&open&&<div style={{fontSize:11,color:C.green}}>เปิดรับงาน</div>}
                    </div>
                    <div className={`tog${(!hasBook&&open)?" on":""}`} onClick={()=>{ if(!hasBook) saveSeerRegularHoursV191(seer, seerRegularDateV191, "set_slot", !open, sl); }}/>
                  </div>
                );
              })}
            </>
          )}



          {seerTab==="admin_msg" && (
            <div style={{padding:16}}>
              <div className="card" style={{padding:16,marginBottom:14,borderColor:`${C.orange}55`,background:`${C.orange}10`}}>
                <div style={{fontSize:15,fontWeight:900,color:C.goldLight,marginBottom:6}}>ข้อความจากแอดมิน</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.7}}>
                  ข้อความนี้เป็นการแจ้งเตือนฝ่ายเดียวจากแอดมิน เช่น เรื่องกฎการใช้งาน การแก้ไขพฤติกรรม หรือข้อมูลที่ต้องปรับปรุง
                </div>
                {unreadAdminMessages.length > 0 && (
                  <button className="btn btn-gold btn-sm" style={{marginTop:12}} onClick={async()=>{
                    markAdminMessagesReadForSeer(seer);
                    try {
                      await apiPost("/api/seer-messages", {
                        action:"mark_read",
                        seer_id: seer.seerId || seer.id || "",
                        line_user_id: seer.lineUserId || seer.line_user_id || ""
                      });
                    } catch (err) {
                      console.warn("บันทึกอ่านแล้วเข้า D1 ไม่สำเร็จ", err);
                    }
                    setAdminMsgTick(x=>x+1);
                    alert("ทำเครื่องหมายว่าอ่านแล้ว");
                  }}>ทำเครื่องหมายว่าอ่านแล้วทั้งหมด</button>
                )}
              </div>

              {adminMessages.length === 0 ? (
                <div className="empty"><div className="e-ico">📭</div>ยังไม่มีข้อความจากแอดมิน</div>
              ) : adminMessages.map(m=>(
                <div key={m.id} className="card" style={{padding:14,marginBottom:12,borderColor:m.read?C.border:`${C.gold}66`}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:8}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:900,color:m.read?C.text:C.goldLight}}>📣 แจ้งเตือนจากแอดมิน</div>
                      <div style={{fontSize:11,color:C.muted}}>โดย {m.adminId || m.admin_id || "admin"} • {m.createdAt || m.created_at}</div>
                    </div>
                    <span className={`badge ${(m.read || m.read === 1) ? "b-green" : "b-orange"}`}>{(m.read || m.read === 1) ? "อ่านแล้ว" : "ใหม่"}</span>
                  </div>
                  <div style={{fontSize:13,color:C.text,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{m.message}</div>
                </div>
              ))}
            </div>
          )}

          {seerTab==="promo" && (
            <div style={{padding:16}}>
              <div className="card card-gold" style={{padding:16,marginBottom:14}}>
                <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:12}}>
                  <SeerAvatar seer={seer} className="seer-ava" style={{width:68,height:68,fontSize:28}} />
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>{seer.name}</div>
                    <div style={{fontSize:12,color:C.purpleLight}}>{seer.spec}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:4}}>ใช้สำหรับแชร์ไป LINE / Facebook / แอปอื่น ๆ</div>
                  </div>
                </div>
                <div className="fgroup">
                  <label className="flabel">ลิงก์โปรไฟล์หมอดู</label>
                  <input className="finput" readOnly value={buildSeerProfileShareUrl(seer)} onFocus={e=>e.target.select()} />
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button className="btn btn-gold btn-sm" onClick={() => copyTextSafe(buildSeerProfileShareUrl(seer), "คัดลอกลิงก์โปรไฟล์แล้ว")}>คัดลอกลิงก์</button>
                  <button className="btn btn-outline btn-sm" onClick={async()=>{
                    const url = buildSeerProfileShareUrl(seer);
                    const shareData = { title:`ดูดวงกับ ${seer.name} บนดวงวันนี้`, text:`โปรไฟล์หมอดู ${seer.name} — ${seer.spec}`, url };
                    if (navigator.share) {
                      try { await navigator.share(shareData); } catch {}
                    } else {
                      copyTextSafe(url, "อุปกรณ์นี้ไม่รองรับปุ่มแชร์ คัดลอกลิงก์ให้แล้ว");
                    }
                  }}>แชร์โปรไฟล์</button>
                  <button className="btn btn-outline btn-sm" onClick={()=>window.open(buildSeerProfileShareUrl(seer), "_blank", "noopener,noreferrer")}>ดูหน้าโปรไฟล์</button>
                </div>
              </div>

              <div className="card" style={{padding:16,borderColor:referralCanCreateV190?`${C.gold}66`:C.border}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:900,color:C.goldLight}}>ระบบเชิญหมอดู</div>
                    <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>เฉพาะหมอดูระดับแพลตินั่มเท่านั้น</div>
                  </div>
                  <span className={`badge ${referralCanCreateV190 ? "b-gold" : "b-purple"}`}>{getSeerLevelLabel(referralCanCreateV190 ? "platinum" : platformLevel)}</span>
                </div>

                {referralCanCreateV190 ? (
                  <>
                    <div className="warn-box" style={{marginBottom:12}}>
                      หมอดูที่เชิญเข้ามาด้วยลิงก์นี้ เมื่อได้รับอนุมัติและกดปิดงานสำเร็จ ผู้เชิญจะได้รับ 3% ของยอดชำระเต็มจำนวนทันที โดยเงินค่าคอมตัดจากรายได้ระบบ ไม่หักเพิ่มจากรายได้ของหมอดูผู้รับงาน
                    </div>
                    <div style={{fontSize:12,color:C.goldLight,marginBottom:10,fontWeight:800}}>
                      จำนวนหมอดูที่แนะนำ: {referralUsedV159} คน • แนะนำได้ไม่จำกัด
                    </div>
                    <button className="btn btn-gold" onClick={createSeerReferralInviteV190}>สร้างลิงก์เชิญใช้ครั้งเดียว</button>

                    <div className="sec-hd" style={{padding:"16px 0 8px"}}>ลิงก์เชิญของฉัน</div>
                    {myInviteLinks.length===0 ? (
                      <div className="empty" style={{padding:16}}>ยังไม่มีลิงก์เชิญ</div>
                    ) : myInviteLinks.slice(0,8).map(inv=>(
                      <div key={inv.code} className="brow" style={{margin:"8px 0"}}>
                        <div className="brow-hd">
                          <div>
                            <div className="brow-name">{inv.code || inv.invite_code}</div>
                            <div style={{fontSize:11,color:C.muted}}>สร้างเมื่อ {inv.createdAt || inv.created_at}</div>
                          </div>
                          <span className={`badge ${inv.status==="used"?"b-green":"b-orange"}`}>{inv.status==="used"?"ใช้แล้ว":"ยังไม่ใช้"}</span>
                        </div>
                        <div style={{fontSize:11,color:C.muted,wordBreak:"break-all",lineHeight:1.6}}>{buildSeerInviteUrl(inv.code || inv.invite_code)}</div>
                        <button className="btn btn-outline btn-sm" style={{marginTop:8}} onClick={()=>copyTextSafe(buildSeerInviteUrl(inv.code || inv.invite_code), "คัดลอกลิงก์เชิญแล้ว")}>คัดลอก</button>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="warn-box">
                    บัญชีนี้เป็นหมอดูทั่วไป ยังไม่สามารถสร้างลิงก์เชิญหมอดูได้ หากต้องการใช้ระบบเชิญหมอดู ต้องให้แอดมินปรับเป็นระดับแพลตินั่มก่อน
                  </div>
                )}
              </div>
            </div>
          )}


          {seerTab==="profile" && (
            <div style={{padding:16}}>
              <div className="card" style={{padding:16,marginBottom:14}}>
                <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:12}}>
                  <SeerAvatar seer={seer} className="seer-ava" style={{width:74,height:74,fontSize:28}} />
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:800,color:C.goldLight}}>{seer.name}</div>
                    <div style={{fontSize:12,color:C.purpleLight}}>{seer.spec}</div>
                    <div style={{fontSize:11,color:seer.profileLocked?C.red:C.green,marginTop:4}}>{seer.profileLocked?"🔒 แอดมินล็อกข้อมูลไว้":"🔓 แก้ไขข้อมูลได้"}</div>
                  </div>
                </div>
                {seer.profileLocked ? (
                  <div className="warn-box">แอดมินล็อกการแก้ไขข้อมูลไว้ชั่วคราว หากต้องการเปลี่ยนรูปหรือข้อมูล โปรดติดต่อแอดมิน</div>
                ) : (
                  <>
                    <div className="fgroup">
                      <label className="flabel">เปลี่ยนภาพหมอดู</label>
                      <input id="seerSelfPhoto" className="finput" type="file" accept="image/*" onFocus={markSeerProfileEditingV132B} onChange={(e)=>{
                        markSeerProfileEditingV132B();
                        const f = e.currentTarget.files?.[0];
                        if (!f) return;
                        fileToDataUrl(f, (dataUrl)=>{
                          const v = String(dataUrl || "");
                          try {
                            document.getElementById("seerSelfPhotoDataV131").value = v;
                            document.getElementById("seerSelfPhotoNameV131").value = f.name || "profile";
                            const img = document.getElementById("seerSelfPhotoPreviewV132B");
                            const box = document.getElementById("seerSelfPhotoPreviewBoxV132B");
                            if (img) img.src = v;
                            if (box) box.style.display = "flex";
                          } catch {}
                          writeSeerProfileDraftV131({photoUrl:v, photoName:f.name || "profile"});
                        });
                      }}/>
                      <input id="seerSelfPhotoDataV131" type="hidden" defaultValue={seerProfileDraftV131.photoUrl || ""}/>
                      <input id="seerSelfPhotoNameV131" type="hidden" defaultValue={seerProfileDraftV131.photoName || ""}/>
                      <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:6}}>เลือกรูปแล้วกด “บันทึกโปรไฟล์” รูปจึงจะถูกบันทึกจริง</div>
                      <div id="seerSelfPhotoPreviewBoxV132B" style={{marginTop:10,display:(seerProfileDraftV131.photoUrl || seer.photoUrl) ? "flex" : "none",alignItems:"center",gap:10}}>
                        <img id="seerSelfPhotoPreviewV132B" src={seerProfileDraftV131.photoUrl || seer.photoUrl || ""} alt="preview" style={{width:56,height:56,borderRadius:14,objectFit:"cover",border:`1px solid ${C.gold}55`}}/>
                        <div style={{fontSize:12,color:C.green}}>มีรูปพร้อมบันทึกแล้ว</div>
                      </div>
                    </div>
                    <div className="fgroup"><label className="flabel">ชื่อหมอดู</label><input id="seerSelfName" className="finput" defaultValue={seerProfileDraftV131.name ?? seer.name ?? ""} onFocus={markSeerProfileEditingV132B} onInput={e=>{ markSeerProfileEditingV132B(); writeSeerProfileDraftV131({name:e.currentTarget.value}); }}/></div>
                    <div className="fgroup"><label className="flabel">ศาสตร์ที่ถนัด</label><input id="seerSelfSpec" className="finput" defaultValue={seerProfileDraftV131.spec ?? seer.spec ?? ""} onFocus={markSeerProfileEditingV132B} onInput={e=>{ markSeerProfileEditingV132B(); writeSeerProfileDraftV131({spec:e.currentTarget.value}); }}/></div>
                    <div className="fgroup"><label className="flabel">คำแนะนำตัว</label><textarea id="seerSelfBio" className="finput" style={{height:110}} defaultValue={seerProfileDraftV131.bio ?? seer.bio ?? ""} onFocus={markSeerProfileEditingV132B} onInput={e=>{ markSeerProfileEditingV132B(); writeSeerProfileDraftV131({bio:e.currentTarget.value}); }}/></div>
                    <div className="fgroup">
                      <label className="flabel">รายการบริการดูดวง (ไม่เกิน 5 รายการ)</label>
                      <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginBottom:8}}>
                        ใส่เป็นรายการแยกกัน เช่น “ดูดวงความรัก” / 15 นาที / 99 บาท ลูกค้าจะเห็นเป็นตัวเลือกตอนจอง
                      </div>
                      {(() => {
                        const pkgs = savedProfilePackagesV131;
                        const rows = Array.from({length:5}, (_,i)=>pkgs[i] || {});
                        const visibleCount = packageVisibleCountV131;
                        return <div id="seerSelfPackageRowsV130" style={{display:"grid",gap:10}}>
                          {rows.map((pkg,i)=>(
                            <div key={i} data-package-row-v130={i} className="card" style={{padding:10,display:i < visibleCount ? "block" : "none",borderColor:`${C.purple}55`}}>
                              <div style={{fontWeight:900,color:C.goldLight,marginBottom:8}}>รายการที่ {i+1}</div>
                              <div className="fgroup" style={{marginBottom:8}}>
                                <label className="flabel">ชื่อรายการ</label>
                                <input id={`seerPkgNameV130_${i}`} className="finput" defaultValue={(seerProfileDraftV131[`pkgName${i}`] ?? pkg.name) || ""} placeholder="เช่น ดูดวงความรัก" onFocus={markSeerProfileEditingV132B} onInput={e=>{ markSeerProfileEditingV132B(); writeSeerProfileDraftV131({[`pkgName${i}`]:e.currentTarget.value}); }}/>
                              </div>
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                                <div className="fgroup" style={{marginBottom:8}}>
                                  <label className="flabel">ระยะเวลาดูดวง</label>
                                  <select id={`seerPkgMinutesV130_${i}`} className="finput" defaultValue={String((seerProfileDraftV131[`pkgMinutes${i}`] ?? pkg.minutes) || "30")} onFocus={markSeerProfileEditingV132B} onChange={e=>{ markSeerProfileEditingV132B(); writeSeerProfileDraftV131({[`pkgMinutes${i}`]:e.currentTarget.value}); }}>
                                    <option value="15">15 นาที</option>
                                    <option value="30">30 นาที</option>
                                    <option value="60">60 นาที</option>
                                  </select>
                                  <div style={{fontSize:10,color:C.muted,lineHeight:1.45,marginTop:5}}>15 นาทีจะกันคิว 30 นาที / 60 นาทีจะกันคิวต่อเนื่อง 2 ช่วง</div>
                                </div>
                                <div className="fgroup" style={{marginBottom:8}}>
                                  <label className="flabel">ราคา (บาท)</label>
                                  <input id={`seerPkgPriceV130_${i}`} className="finput" inputMode="numeric" defaultValue={(seerProfileDraftV131[`pkgPrice${i}`] ?? pkg.price) || ""} placeholder="99" onFocus={markSeerProfileEditingV132B} onInput={e=>{ markSeerProfileEditingV132B(); writeSeerProfileDraftV131({[`pkgPrice${i}`]:e.currentTarget.value}); }}/>
                                </div>
                              </div>
                              <div className="fgroup" style={{marginBottom:0}}>
                                <label className="flabel">รายละเอียด</label>
                                <textarea id={`seerPkgDescV130_${i}`} className="finput" style={{height:78}} defaultValue={(seerProfileDraftV131[`pkgDesc${i}`] ?? pkg.desc) || ""} placeholder="เช่น ถามเรื่องความรัก ไม่เกิน 15 นาที" onFocus={markSeerProfileEditingV132B} onInput={e=>{ markSeerProfileEditingV132B(); writeSeerProfileDraftV131({[`pkgDesc${i}`]:e.currentTarget.value}); }}/>
                              </div>
                            </div>
                          ))}
                        </div>;
                      })()}
                      <button type="button" className="btn btn-outline" style={{marginTop:10}} onClick={()=>{
                        setPackageVisibleCountV131(c => {
                          if (c >= 5) { alert("เพิ่มได้ไม่เกิน 5 รายการ"); return c; }
                          const nextCount = c + 1;
                          writeSeerProfileDraftV131({packageVisibleCount: nextCount});
                          markSeerProfileEditingV132B();
                          return nextCount;
                        });
                      }}>+ เพิ่มรายการ</button>
                    </div>
                    <button className="btn btn-gold" onClick={async ()=>{
                      const saveWithPhoto = async (photoUrl="", photoName="") => {
                        const packageRows = Array.from({length:5}, (_,i) => {
                          const name = String(document.getElementById(`seerPkgNameV130_${i}`)?.value || "").trim();
                          const minutes = String(document.getElementById(`seerPkgMinutesV130_${i}`)?.value || "").replace(/[^0-9]/g,"").trim();
                          const price = String(document.getElementById(`seerPkgPriceV130_${i}`)?.value || "").replace(/[^0-9]/g,"").trim();
                          const desc = String(document.getElementById(`seerPkgDescV130_${i}`)?.value || "").trim();
                          if (!name && !minutes && !price && !desc) return "";
                          if (!name || !price) return "";
                          const timeText = minutes ? `${minutes} นาที` : "";
                          // v134: เก็บแบบแยกช่องด้วย | เพื่อไม่ให้ชื่อ/ราคา/รายละเอียดถูกเอาไปปนกันจนยาวซ้ำ
                          return [name, timeText, `${price} บาท`, desc].filter(Boolean).join(" | ");
                        }).filter(Boolean).slice(0,5);
                        const packageText = packageRows.join("\n");
                        const updated = {
                          ...seer,
                          name: document.getElementById("seerSelfName")?.value || seer.name,
                          spec: document.getElementById("seerSelfSpec")?.value || seer.spec,
                          bio: document.getElementById("seerSelfBio")?.value || seer.bio,
                          priceText: packageText,
                          price_text: packageText,
                          bookingPackageText: packageText,
                          booking_packages_text: packageText,
                          photoUrl: photoUrl || document.getElementById("seerSelfPhotoDataV131")?.value || seerProfileDraftV131.photoUrl || seer.photoUrl || "",
                          photoName: photoName || document.getElementById("seerSelfPhotoNameV131")?.value || seerProfileDraftV131.photoName || seer.photoName || "",
                        };
                        const next = uniqById([updated, ...readApprovedSeersStore().filter(x=>x.id!==seer.id)]);
                        writeApprovedSeersStore(next);
                        setApprovedSeers(next);
                        try { setLockedSeerProfile(prev => prev ? {...prev, ...updated, profileLocked: updated.profileLocked} : updated); } catch {}
                        try {
                          await apiPost("/api/seers", {action:"upsert", seer_id: updated.seerId || updated.id, display_name: updated.name, specialties: updated.spec, short_bio: updated.bio, profile_image_url: safePhotoForD1(updated.photoUrl), price_text: packageText, line_user_id: updated.lineUserId || updated.line_user_id || "", line_url: updated.lineUrl || updated.line_url || "", status:"active", profile_locked: updated.profileLocked ? 1 : 0});
                        } catch (err) { console.warn("บันทึกโปรไฟล์เข้า D1 ไม่สำเร็จ", err); }
                        try { localStorage.removeItem(seerProfileDraftKeyV131); } catch {}
                        clearSeerProfileEditingV133();
                        alert("บันทึกโปรไฟล์หมอดูแล้ว");
                      };
                      const file = document.getElementById("seerSelfPhoto")?.files?.[0];
                      if (file) {
                        try {
                          const ownerId = String(seer.seerId || seer.id || currentLineUser?.userId || "seer");
                          const up = await uploadFileToR2(file, { ownerType:"seer", ownerId, fileRole:"profile" });
                          const photoUrl = up.url || up.public_url || up.file_url || up.path || "";
                          if (!photoUrl) throw new Error("อัปโหลดรูปแล้วไม่ได้ URL กลับมา");
                          await saveWithPhoto(photoUrl, file.name || "profile");
                        } catch (err) {
                          alert("อัปโหลดรูปโปรไฟล์ไม่สำเร็จ: " + (err.message || err));
                          return;
                        }
                      } else {
                        await saveWithPhoto();
                      }
                    }}>บันทึกโปรไฟล์</button>
                  </>
                )}
              </div>
            </div>
          )}

          {seerTab==="income" && (() => {
            const ownIncome = completedLiveBookingsV128.reduce((sum, b) => sum + seerIncomeForBookingV392(b), 0);
            const refSummary = seerReferralIncomeV158.summary || {};
            const money = (value) => Number(value || 0).toLocaleString("th-TH", { minimumFractionDigits:0, maximumFractionDigits:2 });
            return <div style={{padding:16}}>
              <div className="card card-gold" style={{padding:16,textAlign:"center",marginBottom:14}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:4}}>รายได้จากงานของฉันที่ปิดแล้ว</div>
                <div style={{fontFamily:"Cinzel",fontSize:30,color:C.goldLight}}>฿{money(ownIncome)}</div>
                <div style={{fontSize:11,color:C.muted,marginTop:6}}>นับเฉพาะงานที่กดปิดงานสำเร็จแล้ว</div>
              </div>

              {isReferralEligibleSeer ? (
                <div className="card" style={{padding:16,marginBottom:14,borderColor:`${C.gold}66`}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
                    <div>
                      <div style={{fontWeight:900,color:C.goldLight,fontSize:16}}>👑 รายได้จากหมอดูที่ฉันแนะนำ</div>
                      <div style={{fontSize:11,color:C.muted,lineHeight:1.65,marginTop:4}}>ระบบเครดิต 3% จากยอดชำระเต็มจำนวน เมื่อหมอดูที่แนะนำกดปิดงานสำเร็จ เงินค่าคอมตัดจากรายได้ระบบ ไม่หักเพิ่มจากรายได้ของหมอดูผู้รับงาน</div>
                    </div>
                    <span className="badge b-gold">{getSeerLevelLabel(platformLevel)}</span>
                  </div>
                  <div className="stats-grid" style={{marginBottom:12}}>
                    <div className="stat"><div className="stat-l">ค่าคอมสะสม</div><div className="stat-v" style={{color:C.green}}>฿{money(refSummary.total_commission)}</div></div>
                    <div className="stat"><div className="stat-l">เดือนนี้</div><div className="stat-v">฿{money(refSummary.this_month_commission)}</div></div>
                    <div className="stat"><div className="stat-l">หมอดูที่แนะนำ</div><div className="stat-v">{Number(refSummary.active_count ?? refSummary.referral_count ?? 0)} คน</div></div>
                    <div className="stat"><div className="stat-l">งานที่สร้างค่าคอม</div><div className="stat-v">{Number(refSummary.completed_jobs || 0)}</div></div>
                  </div>

                  {(seerReferralIncomeV158.referrals || []).length === 0 ? (
                    <div className="empty" style={{margin:"6px 0 0"}}><div className="e-ico">🤝</div>ยังไม่มีหมอดูที่สมัครผ่านลิงก์ของคุณ</div>
                  ) : (
                    <>
                      <div style={{fontWeight:800,color:C.text,fontSize:13,margin:"4px 0 8px"}}>หมอดูที่แนะนำ</div>
                      {(seerReferralIncomeV158.referrals || []).map((item, i) => (
                        <div key={item.referral_id || item.invited_seer_id || i} className="brow" style={{margin:"0 0 8px"}}>
                          <div className="brow-hd"><div><div className="brow-name">{item.invited_name || "หมอดูที่แนะนำ"}</div><div style={{fontSize:11,color:C.muted}}>รหัสหมอดู: {item.invited_seer_id || "-"}</div></div><span style={{color:C.green,fontWeight:900}}>฿{money(item.total_commission)}</span></div>
                          <div className="brow-detail">งานปิดแล้ว {Number(item.completed_jobs || 0)} งาน • ค่าคอม {Number(item.commission_percent || 3)}% จากยอดชำระ • สถานะ: {item.status === "active" ? "ใช้งาน" : "ปิด/ไม่เริ่มใช้งาน"}</div>
                        </div>
                      ))}
                    </>
                  )}

                  {(seerReferralIncomeV158.commissions || []).length > 0 && <>
                    <div style={{fontWeight:800,color:C.text,fontSize:13,margin:"12px 0 8px"}}>รายการค่าคอมล่าสุด</div>
                    {(seerReferralIncomeV158.commissions || []).slice(0,10).map((item, i) => (
                      <div key={item.commission_id || item.booking_id || i} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"9px 0",borderBottom:`1px solid ${C.border}22`,fontSize:12}}>
                        <div><div style={{color:C.text,fontWeight:700}}>{item.invited_name || "หมอดูที่แนะนำ"}</div><div style={{fontSize:11,color:C.muted}}>Booking: {item.booking_id || "-"} • ปิดงาน {item.released_at ? new Date(item.released_at).toLocaleString("th-TH") : "-"}</div></div>
                        <div style={{textAlign:"right",color:C.green,fontWeight:900}}>+฿{money(item.referral_commission_amount)}<div style={{fontSize:10,color:C.muted,fontWeight:400}}>จากยอดชำระ ฿{money(item.booking_gross_amount)}</div></div>
                      </div>
                    ))}
                  </>}
                </div>
              ) : (
                <div className="notif" style={{margin:"0 0 14px"}}>รายได้จากการแนะนำหมอดูจะแสดงสำหรับหมอดูระดับแพลตินั่มเท่านั้น</div>
              )}

              {completedLiveBookingsV128.length===0 ? (
                <div className="empty" style={{margin:"12px 0"}}><div className="e-ico">💰</div>ยังไม่มีรายได้จากงานจริงที่ปิดแล้ว</div>
              ) : completedLiveBookingsV128.map(b=>(
                <div key={b.booking_id} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${C.border}22`,fontSize:13}}>
                  <div><div style={{color:C.text}}>{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:11,color:C.muted}}>{b.service_name || "บริการ"} • {b.seer_income_released_at || b.updated_at || ""}</div></div>
                  <span style={{color:C.green,fontWeight:600}}>+฿{money(seerIncomeForBookingV392(b))}</span>
                </div>
              ))}
            </div>;
          })()}
        </div>
        <BottomNav/>
      </div>
    );
  }

  // ── Seer Order Detail (reply form) ───────────────────────────────────────────
  function SeerOrderDetail() {
    const o = viewOrder; if(!o) return null;
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>go("seer_dash")}>←</button>
          <span className="topbar-title">รายละเอียดงาน</span>
        </div>
        <div className="content z1">
          <div style={{padding:16}}>
            <div className="card" style={{padding:16,marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
                <span style={{fontSize:13,fontWeight:700,color:C.goldLight}}>#{o.id}</span>
                <Badge status={o.status}/>
              </div>
              {[["บริการ",o.svc_name],["วันที่",o.date+(o.time?" • "+o.time:"")],["ราคา","฿"+o.price],o.question?["คำถาม",o.question]:null,o.phone_input?["เบอร์โทร",o.phone_input]:null].filter(Boolean).map(([l,v])=>(
                <div key={l} className="irow"><span className="lbl">{l}</span><span className="val">{v}</span></div>
              ))}
              <div style={{marginTop:10,padding:8,background:`${C.purple}11`,borderRadius:8,fontSize:12,color:C.muted}}>
                🔒 ข้อมูลลูกค้าถูกปกป้อง — ห้ามติดต่อนอกระบบ
              </div>
            </div>

            {(o.status==="approved_pending"||o.status==="answering") && (
              <div className="ans-box">
                <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:8}}>
                  {o.question?"ตอบคำถาม:":"วิเคราะห์เบอร์โทร:"}
                </div>
                <div className="q-box">{o.question||o.phone_input}</div>
                <textarea className="finput" style={{height:100,marginBottom:10}} placeholder="พิมพ์คำทำนาย / ผลการวิเคราะห์..." value={reply} onChange={e=>setReply(e.target.value)}/>
                <button className="btn btn-gold" onClick={()=>{
                  if(!reply.trim()) return;
                  updOrder(o.id,{status:"answered",answer:reply});
                  setViewOrder(v=>({...v,status:"answered",answer:reply}));
                  setReply("");
                }}>ส่งคำตอบให้ลูกค้า ✨</button>
              </div>
            )}
            {o.answer&&(
              <div className="card" style={{padding:14}}>
                <div style={{fontSize:13,fontWeight:600,color:C.goldLight,marginBottom:8}}>คำตอบที่ส่งแล้ว</div>
                <div style={{fontSize:13,color:C.text,lineHeight:1.7}}>{o.answer}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Seer Register ────────────────────────────────────────────────────────────
  function SeerRegister() {
    // v8: หน้านี้ไม่บังคับ login/redirect ตอนกดส่ง เพื่อไม่ให้ฟอร์มเด้งออกและข้อมูลหาย
    // ใช้ DOM + draft autosave เพื่อให้พิมพ์ต่อเนื่องและเลือกความชำนาญแล้วข้อมูลยังอยู่ครบ
    const [submitPopup, setSubmitPopup] = useState(false);
    const [submitMessage, setSubmitMessage] = useState("ระบบได้รับใบสมัครของคุณแล้ว โปรดรอเจ้าหน้าที่ตรวจสอบ");
    const draft = readSeerRegisterDraftStore();

    useEffect(() => {
      captureLiffProfileForRegister({ allowLogin: false });
    }, []);

    const getRegisterInputValue = (id) => {
      const el = document.getElementById(id);
      return (el?.value || "").trim();
    };

    const getSelectedServices = () => {
      return Array.from(document.querySelectorAll("[data-seer-service][data-selected='yes']"))
        .map((el) => el.getAttribute("data-seer-service"))
        .filter(Boolean);
    };

    const collectRegisterDraft = () => ({
      name:getRegisterInputValue("seerRegName"),
      spec:getRegisterInputValue("seerRegSpec"),
      price:getRegisterInputValue("seerRegPrice"),
      bank:getRegisterInputValue("seerRegBank"),
      contactPhone:getRegisterInputValue("seerRegContactPhone"),
      lineId:getRegisterInputValue("seerRegLineId"),
      lineUrl:getRegisterInputValue("seerRegLineUrl"),
      bio:getRegisterInputValue("seerRegBio"),
      services:getSelectedServices(),
    });

    const saveRegisterDraftFromDom = () => {
      writeSeerRegisterDraftStore(collectRegisterDraft());
    };

    const clearRegisterFormDom = () => {
      ["seerRegName", "seerRegSpec", "seerRegPrice", "seerRegBank", "seerRegContactPhone", "seerRegLineId", "seerRegLineUrl", "seerRegBio"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.value = "";
      });
      document.querySelectorAll("[data-seer-service]").forEach((el) => {
        el.setAttribute("data-selected", "no");
        el.classList.remove("btn-purple");
        el.classList.add("btn-outline");
      });
      clearSeerRegisterDraftStore();
    };

    const submitSeerRegister = () => {
      const nextForm = collectRegisterDraft();

      if(!nextForm.name){alert("กรุณากรอกชื่อหมอดู");return;}
      if(!nextForm.spec){alert("กรุณากรอกศาสตร์ที่ถนัด");return;}
      if(!nextForm.price){alert("กรุณากรอกราคาเริ่มต้น");return;}
      if(!nextForm.bank){alert("กรุณากรอกบัญชีรับเงิน");return;}
      if(!nextForm.contactPhone){alert("กรุณากรอกเบอร์ติดต่อสำหรับแอดมิน");return;}
      if(!nextForm.lineId && !nextForm.lineUrl){alert("กรุณากรอก LINE ID หรือ LINE URL สำหรับใช้โทรหลังลูกค้าชำระเงิน");return;}

      if(!nextForm.services || nextForm.services.length === 0){alert("กรุณาเลือกประเภทบริการที่รับดูอย่างน้อย 1 รายการ");return;}

      // ห้ามเรียก liff.login ตอนกดส่ง เพราะจะ redirect ออกและทำให้ข้อมูลหาย
      // ถ้ามี LINE profile อยู่แล้วจะผูกให้, ถ้าไม่มีให้แอดมินอนุมัติจากข้อมูลที่กรอกก่อนได้
      const lineUser = getSavedLineUser();
      const inviteCode = new URLSearchParams(window.location.search).get("invite") || "";

      const applicant = {
        id:"REQ" + Date.now(),
        inviteCode,
        sourceInviteCode: inviteCode,
        name:nextForm.name,
        spec:nextForm.spec || "-",
        phone:lineUser?.displayName || "LINE: รอผูกบัญชี",
        contactPhone:nextForm.contactPhone || "",
        adminContactPhone:nextForm.contactPhone || "",
        lineId:nextForm.lineId || "",
        lineUrl:nextForm.lineUrl || "",
        docs:`ราคาเริ่มต้น: ${nextForm.price || "-"} / บัญชีรับเงิน: ${nextForm.bank || "-"}`,
        bio:nextForm.bio || "",
        services:nextForm.services || [],
        status:"รอตรวจสอบ",
        lineUserId:lineUser?.userId || "",
        lineDisplayName:lineUser?.displayName || "",
        linePictureUrl:lineUser?.pictureUrl || "",
        createdAt:new Date().toLocaleString("th-TH"),
      };

      const oldApplicants = readSeerApplicantsStore();
      const nextApplicants = [applicant, ...oldApplicants];
      writeSeerApplicantsStore(nextApplicants);

      const saved = readSeerApplicantsStore();
      const ok = saved.some((x) => x.id === applicant.id);

      if (!ok) {
        alert("บันทึกใบสมัครไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        return;
      }

      setSubmitMessage("ระบบได้รับใบสมัครของคุณแล้ว โปรดรอเจ้าหน้าที่ตรวจสอบ");
      setSubmitPopup(true);
      clearRegisterFormDom();
      setRegForm({name:"",spec:"",bio:"",price:"",bank:"",contactPhone:"",lineId:"",lineUrl:"",services:[]});
    };

    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back-btn" onClick={()=>go("home")}>←</button>
          <span className="topbar-title">สมัครเป็นหมอดู</span>
        </div>
        <div className="content z1">
          <div style={{padding:"16px 16px 0",fontSize:13,color:C.muted,lineHeight:1.7}}>
            กรอกข้อมูลเพื่อสมัครเป็นหมอดูในระบบ<br/>ทีมงานจะตรวจสอบและแจ้งผลภายใน 1-3 วัน
          </div>
          <div className="fpad">
            <div className="fgroup">
              <label className="flabel">ชื่อหมอดู / ชื่อที่ใช้แสดง *</label>
              <input
                id="seerRegName"
                className="finput"
                type="text"
                placeholder="เช่น อ.มณีรัตน์"
                defaultValue={draft.name || registerForm.name || ""}
                onInput={saveRegisterDraftFromDom}
                autoComplete="off"
              />
            </div>

            <div className="fgroup">
              <label className="flabel">ศาสตร์ที่ถนัด *</label>
              <input
                id="seerRegSpec"
                className="finput"
                type="text"
                placeholder="เช่น ไพ่ทาโรต์, โหราศาสตร์"
                defaultValue={draft.spec || registerForm.spec || ""}
                onInput={saveRegisterDraftFromDom}
                autoComplete="off"
              />
            </div>

            <div className="fgroup">
              <label className="flabel">ราคาเริ่มต้น *</label>
              <input
                id="seerRegPrice"
                className="finput"
                type="text"
                placeholder="เช่น 99 บาท"
                defaultValue={draft.price || registerForm.price || ""}
                onInput={saveRegisterDraftFromDom}
                autoComplete="off"
              />
            </div>

            <div className="fgroup">
              <label className="flabel">บัญชีรับเงิน *</label>
              <input
                id="seerRegBank"
                className="finput"
                type="text"
                placeholder="เลขบัญชี / PromptPay"
                defaultValue={draft.bank || registerForm.bank || ""}
                onInput={saveRegisterDraftFromDom}
                autoComplete="off"
              />
            </div>

            <div className="fgroup">
              <label className="flabel">เบอร์ติดต่อสำหรับแอดมิน *</label>
              <input
                id="seerRegContactPhone"
                className="finput"
                type="tel"
                placeholder="เช่น 0812345678"
                defaultValue={draft.contactPhone || registerForm.contactPhone || ""}
                onInput={saveRegisterDraftFromDom}
                autoComplete="tel"
              />
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:6}}>เบอร์นี้แอดมินเห็นเท่านั้น ลูกค้าไม่เห็น</div>
            </div>
            <div className="fgroup">
              <label className="flabel">LINE ID สำหรับโทรหลังลูกค้าชำระเงิน *</label>
              <input
                id="seerRegLineId"
                className="finput"
                type="text"
                placeholder="เช่น lineid หรือ @lineid"
                defaultValue={draft.lineId || registerForm.lineId || ""}
                onInput={saveRegisterDraftFromDom}
                autoComplete="off"
              />
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:6}}>จะไม่โชว์ให้ลูกค้าเห็นจนกว่าจะจ่ายเงินและเข้าห้องดูดวง</div>
            </div>
            <div className="fgroup">
              <label className="flabel">LINE URL ถ้ามี</label>
              <input
                id="seerRegLineUrl"
                className="finput"
                type="url"
                placeholder="https://line.me/ti/p/..."
                defaultValue={draft.lineUrl || registerForm.lineUrl || ""}
                onInput={saveRegisterDraftFromDom}
                autoComplete="off"
              />
            </div>

            <div className="fgroup">
              <label className="flabel">คำแนะนำตัวสั้น ๆ</label>
              <textarea
                id="seerRegBio"
                className="finput"
                placeholder="บอกเล่าประสบการณ์และความเชี่ยวชาญ..."
                defaultValue={draft.bio || registerForm.bio || ""}
                onInput={saveRegisterDraftFromDom}
                style={{height:120}}
              />
            </div>

            <div className="fgroup">
              <label className="flabel">ประเภทบริการที่รับดู *</label>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {[["tarot","ไพ่ทาโรต์"],["phone","ดูเบอร์โทร"],["live","จองคิวสด"],["love","ดูดวงความรัก"],["career","ดูดวงการงาน"],["finance","ดูดวงการเงิน"]].map(([k,l])=>{
                  const isSelected = Array.isArray(draft.services) && draft.services.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      data-seer-service={k}
                      data-selected={isSelected ? "yes" : "no"}
                      className={`btn btn-sm ${isSelected ? "btn-purple" : "btn-outline"}`}
                      onClick={(e)=>{
                        e.preventDefault();
                        const btn = e.currentTarget;
                        const selected = btn.getAttribute("data-selected") === "yes";
                        btn.setAttribute("data-selected", selected ? "no" : "yes");
                        btn.classList.toggle("btn-purple", !selected);
                        btn.classList.toggle("btn-outline", selected);
                        saveRegisterDraftFromDom();
                      }}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>

            <button type="button" className="btn btn-gold" onClick={submitSeerRegister}>ส่งใบสมัคร ✨</button>

            <div style={{fontSize:11,color:C.muted,lineHeight:1.7,marginTop:10,textAlign:"center"}}>
              ข้อมูลจะถูกบันทึกให้แอดมินตรวจสอบ หลังส่งสำเร็จจะมีหน้าต่างแจ้งเตือนขึ้นมา
            </div>
          </div>
        </div>

        {submitPopup && (
          <div style={{position:"fixed",inset:0,zIndex:999999,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
            <div style={{width:"100%",maxWidth:360,background:"linear-gradient(180deg,#20164a,#110D26)",border:`1px solid ${C.gold}66`,borderRadius:22,boxShadow:"0 22px 80px rgba(0,0,0,.55)",padding:22,textAlign:"center"}}>
              <div style={{width:70,height:70,borderRadius:"50%",margin:"0 auto 14px",display:"flex",alignItems:"center",justifyContent:"center",background:`${C.green}22`,border:`1px solid ${C.green}66`,fontSize:38}}>✓</div>
              <div style={{fontSize:21,fontWeight:800,color:C.goldLight,marginBottom:8}}>ระบบได้รับใบสมัครของคุณแล้ว</div>
              <div style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:16}}>โปรดรอเจ้าหน้าที่ตรวจสอบ</div>
              <button className="btn btn-gold" onClick={()=>{setSubmitPopup(false); go("home");}}>กลับหน้าหลัก</button>
            </div>
          </div>
        )}
      </div>
    );
  }


  function InfoPage({title, subtitle, children}) {
    return (
      <div className="scr on">
        <div className="topbar">
          <button className="back" onClick={()=>go("home")}>←</button>
          <span className="topbar-title">{title}</span>
        </div>
        <div className="content z1" style={{paddingTop:18}}>
          <div className="hero" style={{paddingTop:14,paddingBottom:10}}>
            <div className="hero-icon">✨</div>
            <h1 style={{fontSize:22}}>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {children}
          <button className="btn btn-gold" style={{marginTop:18}} onClick={()=>go("home")}>กลับหน้าหลัก</button>
        </div>
        <BottomNav/>
      </div>
    );
  }

  function InfoBox({title, children, icon}) {
    return (
      <div className="card card-gold" style={{padding:18,marginBottom:14}}>
        <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:10}}>
          {icon && <div style={{fontSize:24}}>{icon}</div>}
          <div style={{fontSize:17,fontWeight:800,color:C.goldLight}}>{title}</div>
        </div>
        <div style={{fontSize:14,lineHeight:1.9,color:"#eee9ff"}}>
          {children}
        </div>
      </div>
    );
  }


  function MyInfoV139() {
    const lineUser = currentLineUser || getSavedLineUser() || {};
    const profile = customerProfileV138 || readCustomerProfileV138(lineUser.userId || "") || {};
    const astro = getCustomerAstroV139(customerProfileFormV138.birthdate || profile.birthdate);
    const setF = (k,v) => setCustomerProfileFormV138(p => {
      const next = {...p, [k]: v};
      try { localStorage.setItem("dw_customer_register_draft_v138c", JSON.stringify(next)); } catch {}
      return next;
    });
    const saveMyInfo = async () => {
      const f = customerProfileFormV138 || {};
      const name = String(f.name || "").trim();
      const phone = String(f.phone || "").replace(/[^0-9]/g, "");
      const birthdate = String(f.birthdate || "").trim();
      const lineId = String(f.lineId || "").trim();
      if (!name) { alert("กรุณากรอกชื่อ - นามสกุล"); return; }
      if (!phone || phone.length < 9) { alert("กรุณากรอกเบอร์โทรให้ถูกต้อง"); return; }
      if (!birthdate) { alert("กรุณาเลือกวันเดือนปีเกิด"); return; }
      const nextProfile = {
        line_user_id: lineUser.userId || "",
        line_display_name: lineUser.displayName || "",
        line_picture_url: lineUser.pictureUrl || "",
        name,
        phone,
        birthdate,
        line_id: lineId,
        consent: true,
        registered_at: profile.registered_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      try {
        await apiPost("/api/customer-profile", nextProfile);
      } catch (err) {
        console.warn("บันทึกข้อมูลของฉันลง D1 ไม่สำเร็จ แต่จะบันทึกในเครื่องไว้ก่อน", err);
      }
      writeCustomerProfileV138(nextProfile);
      try { localStorage.removeItem("dw_customer_register_draft_v138c"); } catch {}
      setCustomerProfileV138(nextProfile);
      alert("บันทึกข้อมูลของฉันแล้ว");
    };

    // v139D: แสดงเฉพาะ booking จริงจากฐานข้อมูล/current state ไม่เอา local/mock
    const bookingHistory = getRealCustomerBookingsV139D(
      [...(Array.isArray(customerBookingListV104) ? customerBookingListV104 : []), ...(Array.isArray(bookings) ? bookings : [])],
      [...(selectedBooking?.booking_id && belongsToCurrentUserBookingV126(selectedBooking) ? [selectedBooking] : [])]
    );

    return (
      <div className="scr on">
        <div className="topbar"><span className="topbar-title">ข้อมูลของฉัน</span></div>
        <div className="content z1" style={{paddingBottom:90}}>
          <div className="card card-gold" style={{padding:16,margin:"10px 16px 12px"}}>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              <SeerAvatar seer={{photoUrl:lineUser.pictureUrl, name:lineUser.displayName || "LINE"}} className="seer-ava"/>
              <div>
                <div style={{fontWeight:900,color:C.goldLight}}>ข้อมูลสมาชิก</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>แก้ไขข้อมูลเพื่อใช้จองคิว ดูฐานดวง และติดต่อกับอาจารย์หมอดู</div>
              </div>
            </div>
          </div>

          {astro && (
            <div className="card" style={{padding:16,margin:"0 16px 12px",borderColor:`${C.gold}55`,background:`${C.gold}10`}}>
              <div style={{fontSize:16,fontWeight:900,color:C.goldLight,marginBottom:8}}>🔮 ราศีพื้นดวงเบื้องต้น</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div className="card" style={{padding:10}}><div style={{fontSize:11,color:C.muted}}>ราศี</div><div style={{fontWeight:900,color:C.text}}>ราศี{astro.zodiac}</div></div>
                <div className="card" style={{padding:10}}><div style={{fontSize:11,color:C.muted}}>ธาตุราศี</div><div style={{fontWeight:900,color:C.text}}>ธาตุ{astro.zodiacElement}</div></div>
                <div className="card" style={{padding:10}}><div style={{fontSize:11,color:C.muted}}>ปีนักษัตร</div><div style={{fontWeight:900,color:C.text}}>{astro.animal}</div></div>
                <div className="card" style={{padding:10}}><div style={{fontSize:11,color:C.muted}}>ธาตุนักษัตร</div><div style={{fontWeight:900,color:C.text}}>ธาตุ{astro.yearElement}</div></div>
              </div>
              <div style={{fontSize:13,color:C.text,lineHeight:1.8}}>คุณเป็นคนราศี{astro.zodiac} ธาตุ{astro.zodiacElement} ปีนักษัตร{astro.animal} ธาตุ{astro.yearElement}</div>
              <div style={{fontSize:13,color:C.goldLight,lineHeight:1.8,marginTop:8}}>ควรเสริมดวงด้วย: {astro.advice}</div>
              {astro.yearAdvice && <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:6}}>เสริมตามธาตุนักษัตร: {astro.yearAdvice}</div>}
            </div>
          )}

          <div className="card" style={{padding:16,margin:"0 16px 12px"}}>
            <div style={{fontSize:16,fontWeight:900,color:C.goldLight,marginBottom:10}}>ข้อมูลของฉัน</div>
            <div className="fgroup">
              <label className="flabel">ชื่อ - นามสกุล (เพื่อใช้ดูดวง ฐานดวง) *</label>
              <input className="finput" name="dw_customer_fullname_v141" autoComplete="name" value={customerProfileFormV138.name || ""} onChange={e=>setF("name",e.target.value)} placeholder="เช่น นายสมชาย ใจดี"/>
            </div>
            <div className="fgroup">
              <label className="flabel">เบอร์โทร (โปรดระบุเบอร์ที่ติดต่อได้ เพื่อให้ติดต่อกับอาจารย์หมอดู) *</label>
              <input className="finput" name="dw_customer_phone_v141" autoComplete="tel" inputMode="tel" value={customerProfileFormV138.phone || ""} onChange={e=>setF("phone",e.target.value)} placeholder="โปรดระบุเบอร์ที่ติดต่อได้"/>
            </div>
            <div className="fgroup">
              <label className="flabel">วันเดือนปีเกิด (โปรดระบุ วันเดือนปีเกิดจริง เพื่อ ดูตามปฎิทินมงคล) *</label>
              <input className="finput" name="dw_customer_birthdate_v141" type="date" value={customerProfileFormV138.birthdate || ""} onChange={e=>setF("birthdate",e.target.value)}/>
            </div>
            <div className="fgroup">
              <label className="flabel">LINE ID ถ้ามี</label>
              <input className="finput" name="dw_customer_lineid_v141" autoComplete="username" value={customerProfileFormV138.lineId || ""} onChange={e=>setF("lineId",e.target.value)} placeholder="เช่น mylineid"/>
            </div>
            <button className="btn btn-gold" onClick={()=>{
              if (!myInfoEditModeV139C) {
                setMyInfoEditModeV139C(true);
                return;
              }
              saveMyInfo().then(()=>setMyInfoEditModeV139C(false));
            }}>{myInfoEditModeV139C ? "บันทึกข้อมูลของฉัน" : "แก้ไขข้อมูล"}</button>
          </div>

          <div className="sec-hd">ประวัติการดู / ประวัติการนัด</div>
          <div style={{padding:"0 16px 12px",display:"flex",gap:8,flexWrap:"wrap"}}>
            <button className="btn btn-gold btn-sm" onClick={refreshCustomerBookingListV104}>รีเฟรชประวัติ</button>
            <button className="btn btn-outline btn-sm" onClick={()=>{setActiveNav("my_bookings");go("my_bookings")}}>ไปที่นัดของฉัน</button>
          </div>
          {bookingHistory.length === 0 ? (
            <div className="empty" style={{padding:20,margin:"0 16px"}}>
              <div className="e-ico">📋</div>
              ยังไม่มีประวัติการจองหมอดู
            </div>
          ) : (
            <>
              {bookingHistory.slice(0,8).map((b,i)=>(
                <div key={b.booking_id || i} className="card" style={{padding:12,margin:"0 16px 10px"}}>
                  <div style={{fontWeight:900,color:C.goldLight}}>{b.service_name || "นัดดูดวง"} • ฿{Number(b.price || 0).toLocaleString()}</div>
                  <div style={{fontSize:12,color:C.muted,lineHeight:1.7}}>หมอดู: {b.seer_name || "-"} | เวลา: {dwBookingScheduledText(b) || b.requested_time || "-"}</div>
                  <div style={{fontSize:12,color:C.text,marginTop:4}}>สถานะ: {dwBookingStatusLabel(b.status)}</div>
                </div>
              ))}

            </>
          )}
        </div>
        <BottomNav/>
      </div>
    );
  }

  function PointsDetail() {
    return <History/>;
  }


  function MemberLevelDetail() {
    return <History/>;
  }


  const screenMap = {
    home:<Home/>, movement:<MovementList/>, movement_detail:<MovementDetail/>, services:<Services/>, system_reading:<SystemReading/>, seers:<SeersList/>,
    my_bookings:<MyBookingsV91/>, booking_chat:<BookingChatV99/>, booking_room:<BookingRoomV91/>,
    seer_detail:<SeerDetail/>, book_success:<BookSuccess/>,
    history:<History/>, order_detail_cust:<OrderDetailCust/>,
    seer_dash:<SeerDash/>,
    seer_new_jobs:<SeerDash detailPage="seer_new_jobs"/>,
    seer_today_jobs:<SeerDash detailPage="seer_today_jobs"/>,
    seer_job_history:<SeerDash detailPage="seer_job_history"/>,
    seer_income:<SeerDash detailPage="seer_income"/>,
    seer_order_detail:<SeerOrderDetail/>,
    seer_register:<SeerRegister/>, points_detail:<PointsDetail/>, member_level_detail:<MemberLevelDetail/>, live_room:<LiveRoom/>,
  };
  const SeerApprovedModal = () => (seerApprovedPopup && scr !== "seer_dash") ? (
    <div style={{position:"fixed",inset:0,zIndex:999999,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
      <div style={{width:"100%",maxWidth:360,background:"linear-gradient(180deg,#20164a,#110D26)",border:`1px solid ${C.gold}66`,borderRadius:22,boxShadow:"0 22px 80px rgba(0,0,0,.55)",padding:22,textAlign:"center"}}>
        <div style={{width:70,height:70,borderRadius:"50%",margin:"0 auto 14px",display:"flex",alignItems:"center",justifyContent:"center",background:`${C.green}22`,border:`1px solid ${C.green}66`,fontSize:38}}>🔮</div>
        <div style={{fontSize:21,fontWeight:800,color:C.goldLight,marginBottom:8}}>บัญชีของคุณได้รับอนุมัติเป็นหมอดูแล้ว</div>
        <div style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:16}}>คุณสามารถเข้าแดชบอร์ดหมอดูได้จากเมนูแดชบอร์ด</div>
        <button type="button" className="btn btn-gold" onClick={()=>closeSeerApprovedPopupV155(true)}>เข้าสู่แดชบอร์ดหมอดู</button>
        <button type="button" className="btn btn-outline" style={{marginTop:8}} onClick={()=>closeSeerApprovedPopupV155(false)}>ปิดข้อความนี้</button>
      </div>
    </div>
  ) : null;

  const LocalLogoutButton = () => null;

  if (scr === "my_info") return <>{MyInfoV139()}{BookingChatPopupV100()}{PaymentPopupV106()}{SlipReceivedPopupV106()}{RescheduleRequestModalV168()}<CustomerReselectSlotModalV176/><SeerQueueDetailsModalV182
      booking={seerQueueDetailsV178}
      onClose={()=>setSeerQueueDetailsV178(null)}
      onOpenRoom={(booking)=>{ setSeerQueueDetailsV178(null); openBookingRoomV91(booking); }}
    /><SeerApprovedModal/><LocalLogoutButton/></>;
  if (scr === "book_form") return <>{BookForm()}{BookingChatPopupV100()}{PaymentPopupV106()}{SlipReceivedPopupV106()}{RescheduleRequestModalV168()}<CustomerReselectSlotModalV176/><SeerQueueDetailsModalV182
      booking={seerQueueDetailsV178}
      onClose={()=>setSeerQueueDetailsV178(null)}
      onOpenRoom={(booking)=>{ setSeerQueueDetailsV178(null); openBookingRoomV91(booking); }}
    /><SeerApprovedModal/><LocalLogoutButton/></>;
  return <>{screenMap[scr]||<Home/>}{BookingChatPopupV100()}{PaymentPopupV106()}{SlipReceivedPopupV106()}{RescheduleRequestModalV168()}<CustomerReselectSlotModalV176/><SeerQueueDetailsModalV182
      booking={seerQueueDetailsV178}
      onClose={()=>setSeerQueueDetailsV178(null)}
      onOpenRoom={(booking)=>{ setSeerQueueDetailsV178(null); openBookingRoomV91(booking); }}
    /><SeerApprovedModal/><LocalLogoutButton/></>;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  ADMIN APP
// ═══════════════════════════════════════════════════════════════════════════════
function AdminApp({orders, setOrders, chatAlerts=[], initialMenu=""}) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginF, setLoginF] = useState({user:"",pass:""});
  const [adminAccessV3923, setAdminAccessV3923] = useState({loading:true, error:"", isAdmin:false, isSuperAdmin:false, needsSetup:false, account:null});
  const [adminBootstrapV3923, setAdminBootstrapV3923] = useState({username:"", display_name:"", password:"", confirm_password:""});
  const [adminRecoveryV3930, setAdminRecoveryV3930] = useState({username:"", display_name:"", password:"", confirm_password:"", recovery_code:""});
  const [adminInviteFormV3923, setAdminInviteFormV3923] = useState({display_name:"", username:"", password:"", confirm_password:"", role:"super_admin", expires_in_minutes:"60"});
  const [adminInviteResultV3923, setAdminInviteResultV3923] = useState(null);
  const [adminDirectoryV3923, setAdminDirectoryV3923] = useState([]);
  const [adminPendingInvitesV3923, setAdminPendingInvitesV3923] = useState([]);
  const [adminAccessBusyV3923, setAdminAccessBusyV3923] = useState(false);
  const adminInviteTokenV3923 = getAdminInviteTokenV3923();
  const [menuRaw, setMenuRaw] = useState(initialMenu || "overview");
  const menu = menuRaw;
  const setMenu = (next) => {
    const value = typeof next === "function" ? next(menuRaw) : next;
    setMenuRaw(value || "overview");
    try {
      const url = new URL(window.location.href);
      const qs = new URLSearchParams(url.search);
      if (value && value !== "overview") qs.set("page", value);
      else qs.delete("page");
      const nextUrl = `${url.pathname}${qs.toString() ? "?" + qs.toString() : ""}${url.hash || ""}`;
      window.history.pushState({adminPage:value || "overview"}, "", nextUrl);
    } catch {}
  };
  useEffect(() => {
    const onPopAdminV137 = () => {
      try {
        const url = new URL(window.location.href);
        setMenuRaw(url.searchParams.get("page") || "overview");
      } catch { setMenuRaw("overview"); }
    };
    window.addEventListener("popstate", onPopAdminV137);
    return () => window.removeEventListener("popstate", onPopAdminV137);
  }, []);
  const [toast, setToast] = useState("");
  const [currentAdmin, setCurrentAdmin] = useState("");
  const [orderFilter, setOrderFilter] = useState("all");
  const [pageByKey, setPageByKey] = useState({});
  const [adminInvite, setAdminInvite] = useState("");
  const [adminOneTimeSeerInviteLink, setAdminOneTimeSeerInviteLink] = useState("");
  const [newSeerForm, setNewSeerForm] = useState({name:"",spec:"",price:"199",bio:"",bank:"",lineId:"",lineUrl:"",services:"ไพ่ทาโรต์, ดูดวงสด",photoUrl:"",photoName:""});
  const [editSeerForm, setEditSeerForm] = useState(null);
  const [viewApplicant, setViewApplicant] = useState(null);
  const [reviewForm, setReviewForm] = useState({open:false, seer:null, customer:"ลูกค้าจริง", rating:"5", text:""});
  const adminMenuKeys = ["overview","orders","bookings","line_meetings","payment_account","income_settings","customer_database","seer_income","seer_applicants","seers","articles","settings","admins","audit","chat_monitor","income"];
  const adminMenuLabels = {
    overview:"ภาพรวม", orders:"ออเดอร์", bookings:"ระบบจอง", payment_account:"เลขบัญชี", income_settings:"รายได้และการตั้งค่า", customer_database:"ฐานลูกค้า", seer_income:"รายได้หมอดู", seer_applicants:"สมัครหมอดู", seers:"จัดการหมอดู", articles:"บทความ",
    settings:"ตั้งค่าระบบ", admins:"แอดมิน", audit:"ประวัติแอดมิน", chat_monitor:"ตรวจแชท", income:"รายได้รวม", line_meetings:"ห้อง LINE Meeting"
  };
  const defaultPerms = Object.fromEntries(adminMenuKeys.map(k=>[k,true]));
  // v3.9.23: admin accounts are read from D1; no demo admin rows or local-only permissions.
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({username:"", role:"Admin Level 1"});
  const [articles, setArticles] = useState([
    {id:"ART001", title:"วิธีเตรียมตัวก่อนดูดวงสด", status:"เผยแพร่", author:"super_admin", createdAt:new Date().toLocaleString("th-TH")},
    {id:"ART002", title:"ความหมายเบื้องต้นของไพ่ทาโรต์", status:"แบบร่าง", author:"admin1", createdAt:new Date().toLocaleString("th-TH")},
  ]);
  const [articleF, setArticleF] = useState({title:"", body:"", status:"แบบร่าง", scheduledAt:"", imageUrl:"", imageName:"", linkUrl:"", linkText:"อ่านต่อ"});
  const [articleUploading, setArticleUploading] = useState(false);
  const [adminBookingsV91, setAdminBookingsV91] = useState([]);
  // v3.9.7: keep slip review state in the UI so a slow network cannot trigger
  // duplicate approval requests and any real error remains visible on the row.
  const [reviewingBookingIdV397, setReviewingBookingIdV397] = useState("");
  const [slipReviewErrorV397, setSlipReviewErrorV397] = useState({ bookingId:"", message:"" });
  const [adminPaymentSettingsV106, setAdminPaymentSettingsV106] = useState({bank_name:"", account_name:"", account_number:"", promptpay:"", qr_url:"", platform_fee_percent:35});
  const [adminChatViewV106, setAdminChatViewV106] = useState({open:false, booking:null, messages:[]});
  const [lineMeetingRoomsV3936, setLineMeetingRoomsV3936] = useState({loading:false, error:"", rooms:[], stats:{total:0,ready:0,in_use:0,pending_review:0,blocked:0,retired:0}});
  const [lineMeetingActionBusyV3936, setLineMeetingActionBusyV3936] = useState("");
  const [seerIncomeDetailV156E, setSeerIncomeDetailV156E] = useState(null);
  const [seerWithdrawalsV156E, setSeerWithdrawalsV156E] = useState([]);
  const loadAdminPaymentSettingsV106 = async () => {
    try {
      const data = await apiGet("/api/payment-settings");
      setAdminPaymentSettingsV106(data.settings || data || {});
    } catch (err) { console.warn("โหลดเลขบัญชีในแอดมินไม่สำเร็จ", err); }
  };
  const saveAdminPaymentSettingsV106 = async () => {
    try {
      await apiPost("/api/payment-settings", { ...adminPaymentSettingsV106, platform_fee_percent: Math.min(60, Math.max(0, Number(adminPaymentSettingsV106.platform_fee_percent || 0))), admin_id: currentAdmin });
      await loadAdminPaymentSettingsV106();
      flash("บันทึกเลขบัญชีรับเงินแล้ว");
    } catch (err) { flash("บันทึกเลขบัญชีไม่สำเร็จ: " + (err.message || err)); }
  };
  const loadSeerWithdrawalsV156E = async () => {
    try {
      const data = await apiGet("/api/seer-withdrawals");
      setSeerWithdrawalsV156E(Array.isArray(data.withdrawals) ? data.withdrawals : []);
    } catch (err) { console.warn("โหลดประวัติเบิกเงินหมอดูไม่สำเร็จ", err); }
  };
  const saveSeerWithdrawalV156E = async (seer, amount) => {
    const value = Number(amount || 0);
    if (!seer?.seer_key) { flash("ไม่พบรหัสหมอดู"); return; }
    if (!value || value <= 0) { flash("กรุณาใส่ยอดเบิกที่ถูกต้อง"); return; }
    const available = Math.max(0, Number(seer.net || 0) - Number(seer.withdrawn || 0));
    if (value > available) {
      flash(`ยอดถอนมากกว่ายอดคงเหลือ (${available.toLocaleString("th-TH")} บาท) จึงไม่สามารถบันทึกได้`);
      return;
    }
    try {
      await apiPost("/api/seer-withdrawals", {
        action:"add",
        admin_id: currentAdmin,
        seer_key: seer.seer_key,
        seer_name: seer.seer_name || "หมอดู",
        amount: value,
        note: "บันทึกเบิกจากหน้าแอดมินรายได้หมอดู"
      });
      await loadSeerWithdrawalsV156E();
      flash("บันทึกยอดเบิกหมอดูแล้ว");
    } catch (err) { flash("บันทึกยอดเบิกไม่สำเร็จ: " + (err.message || err)); }
  };

  const openAdminBookingChatV106 = async (b) => {
    try {
      const qs = new URLSearchParams({ booking_id:b.booking_id || "", admin:"1", limit:"300" });
      const data = await apiGet(`/api/booking-chat?${qs.toString()}`);
      setAdminChatViewV106({open:true, booking:b, messages:data.messages || []});
    } catch (err) { flash("โหลดแชทนัดเวลาไม่สำเร็จ: " + (err.message || err)); }
  };

  const loadAdminBookingsV91 = async () => {
    try {
      const data = await apiGet("/api/bookings?admin=1&limit=200");
      const nextBookings = Array.isArray(data.bookings) ? data.bookings : [];
      // v165: ห้าม setState ซ้ำเมื่อข้อมูลไม่เปลี่ยน เพราะหน้าแอดมินจะ remount และกระพริบ
      setAdminBookingsV91(prev => sameDataV165(prev, nextBookings) ? prev : nextBookings);
    } catch (err) { console.warn("โหลด booking v91 ใน admin ไม่สำเร็จ", err); }
  };
  const loadLineMeetingRoomsV3936 = async () => {
    setLineMeetingRoomsV3936(prev=>({...prev,loading:true,error:""}));
    try {
      const data = await apiGet("/api/line-meeting-admin");
      setLineMeetingRoomsV3936({
        loading:false,
        error:"",
        rooms:Array.isArray(data.rooms) ? data.rooms : [],
        stats:data.stats || {total:0,ready:0,in_use:0,pending_review:0,blocked:0,retired:0},
      });
    } catch (err) {
      setLineMeetingRoomsV3936(prev=>({...prev,loading:false,error:String(err?.message || err || "โหลดห้อง LINE Meeting ไม่สำเร็จ")}));
    }
  };
  const updateLineMeetingRoomV3936 = async (roomId, action) => {
    const room = (lineMeetingRoomsV3936.rooms || []).find(item=>item.room_id===roomId);
    if (!room) return;
    const labels = {
      confirm_deleted:"ยืนยันว่าได้เข้า LINE บัญชีเจ้าของห้องและลบ Meeting นี้แล้วหรือไม่? หลังยืนยัน ห้องนี้จะปิดถาวรและห้ามนำกลับเข้าสุ่ม",
      mark_blocked:"บันทึกว่าพบผู้เข้าร่วมค้างอยู่ ห้องนี้จะถูกกันออกจากการใช้งานและต้องลบ Meeting จาก LINE",
    };
    if (labels[action] && !confirm(labels[action])) return;
    setLineMeetingActionBusyV3936(`${roomId}:${action}`);
    try {
      const data = await apiPost("/api/line-meeting-admin", {room_id:roomId, action});
      flash(data.message || "บันทึกสถานะห้องแล้ว");
      await loadLineMeetingRoomsV3936();
    } catch (err) {
      flash("บันทึกสถานะห้องไม่สำเร็จ: " + (err?.message || err));
    } finally {
      setLineMeetingActionBusyV3936("");
    }
  };

  const refreshAdminAccessV3923 = async () => {
    const data = await adminAccessApiV3924("session");
    const next = {
      loading:false,
      error:"",
      isAdmin:!!data.is_admin,
      isSuperAdmin:!!data.is_super_admin,
      needsSetup:!!data.needs_setup,
      account:data.account || null,
      bootstrapAvailable:!!data.bootstrap_available,
    };
    setAdminAccessV3923(next);
    if (next.account?.username) {
      setLoginF(prev => ({ ...prev, user: prev.user || next.account.username }));
      setCurrentAdmin(next.account.username);
    }
    return next;
  };
  const loadAdminDirectoryV3923 = async () => {
    const data = await adminAccessApiV3924("list");
    const next = Array.isArray(data.accounts) ? data.accounts : [];
    setAdminDirectoryV3923(next);
    setAdmins(next.map((item) => ({
      id:item.admin_id,
      username:item.username,
      role:item.role_label,
      permission:item.role === "super_admin" ? "ทุกอย่าง" : item.role === "admin_level_1" ? "ตรวจสอบและจัดการตามสิทธิ์" : "ดูข้อมูลตามสิทธิ์",
      active:!!item.is_active,
      menuPermissions:{...defaultPerms},
    })));
    setAdminPendingInvitesV3923(Array.isArray(data.pending_invites) ? data.pending_invites : []);
  };
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await adminAccessApiV3924("session");
        if (!alive) return;
        const next = {
          loading:false,
          error:"",
          isAdmin:!!data.is_admin,
          isSuperAdmin:!!data.is_super_admin,
          needsSetup:!!data.needs_setup,
          account:data.account || null,
          bootstrapAvailable:!!data.bootstrap_available,
        };
        setAdminAccessV3923(next);
        if (next.account?.username) {
          setLoginF(prev => ({ ...prev, user: prev.user || next.account.username }));
          setCurrentAdmin(next.account.username);
        }
      } catch (error) {
        if (!alive) return;
        setAdminAccessV3923({loading:false, error:String(error?.message || error), isAdmin:false, isSuperAdmin:false, needsSetup:false, account:null, bootstrapAvailable:false});
      }
    })();
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!loggedIn) return;
    // v167: โหลดข้อมูลครั้งแรกเมื่อเข้าแอดมินเท่านั้น
    // ไม่มี polling / ไม่มี refresh เมื่อสลับแอป เพื่อให้หน้าจอนิ่ง ไม่กระตุก และไม่โหลดรูปสลิปซ้ำ
    loadAdminBookingsV91();
    loadAdminPaymentSettingsV106();
    loadSeerWithdrawalsV156E();
    if (adminAccessV3923.isSuperAdmin) loadAdminDirectoryV3923().catch((error) => console.warn("โหลดรายชื่อแอดมินไม่สำเร็จ", error));
    return undefined;
  }, [loggedIn]);
  const adminReviewBookingSlipV91 = async (b, approve=true) => {
    const bookingId = String(b?.booking_id || "").trim();
    if (!bookingId || reviewingBookingIdV397) return;
    setReviewingBookingIdV397(bookingId);
    setSlipReviewErrorV397({ bookingId:"", message:"" });
    try {
      const result = await apiPost("/api/booking-action", {
        booking_id: bookingId,
        action: approve ? "admin_approve_slip" : "admin_reject_slip",
        admin_id: currentAdmin,
        note: approve ? "ตรวจสลิปผ่าน" : "สลิปไม่ผ่าน",
      });
      await loadAdminBookingsV91();
      if (!approve) {
        flash("ปฏิเสธสลิปแล้ว");
      } else if (result?.already_approved) {
        flash("รายการนี้อนุมัติสลิปแล้ว");
      } else if (result?.dispatch?.ok) {
        flash("อนุมัติสลิปแล้ว ส่งงานให้หมอดูแล้ว");
      } else {
        flash("อนุมัติสลิปแล้ว หมอดูเปิดแดชบอร์ดเพื่อรับงานได้");
      }
    } catch (err) {
      const message = String(err?.message || err || "ไม่สามารถอนุมัติสลิปได้");
      setSlipReviewErrorV397({ bookingId, message });
      flash((approve ? "อนุมัติสลิปไม่สำเร็จ: " : "ปฏิเสธสลิปไม่สำเร็จ: ") + message);
    } finally {
      setReviewingBookingIdV397("");
    }
  };
  const adminResendBookingToSeerV3 = async (b) => {
    try {
      await apiPost("/api/booking-action", { booking_id:b.booking_id, action:"admin_resend_to_seer", admin_id:currentAdmin });
      await loadAdminBookingsV91();
      flash("ส่งงานให้หมอดูอีกครั้งแล้ว");
    } catch (err) { flash("ส่งงานให้หมอดูไม่สำเร็จ: " + (err.message || err)); }
  };
  const [platformSettings, setPlatformSettings] = useState({
    siteName:"ดวงวันนี้",
    pointRate:"1, = 100 บาท",
    goldRule:"Gold มีสิทธิ์พิเศษมากกว่า",
    paymentAccount:"พร้อมเพย์ 123-4-56789-0 บริษัท ดวงวันนี้ จำกัด",
    slipMode:"แอดมินตรวจสลิปก่อนยืนยันคิว",
    minWithdraw:"500",
  });
  const [seerLevelsAdmin, setSeerLevelsAdmin] = useState(() => readSeerLevelsStore());
  const [seerLineBindInput, setSeerLineBindInput] = useState({});
  const [lineContactsAdmin, setLineContactsAdmin] = useState([]);
  const loadLineContactsAdmin = async () => {
    try {
      const data = await apiGet("/api/line-webhook?limit=30");
      setLineContactsAdmin(Array.isArray(data.contacts) ? data.contacts : []);
      flash("โหลดรายชื่อผู้ใช้จาก LINE OA ล่าสุดแล้ว");
    } catch (err) {
      flash("โหลดรายชื่อ LINE OA ไม่สำเร็จ: " + (err.message || err));
    }
  };

  const testSendLineContact = async (contact) => {
    const userId = String(contact?.userId || contact?.user_id || "").trim();
    if (!userId) { flash("ไม่พบ userId ของ contact นี้"); return; }
    const text = prompt(`ทดสอบส่ง LINE ตรงถึง ${contact.displayName || contact.display_name || userId}`, "ทดสอบส่งตรงจากแอดมิน");
    if (!text) return;
    try {
      const res = await apiPost("/api/line-push", {
        to: userId,
        text,
        seer_name: contact.displayName || contact.display_name || "",
        admin_id: currentAdmin,
      });
      flash(res?.sent ? "ส่ง LINE ตรงถึง contact สำเร็จ" : "ส่งคำขอแล้ว แต่ยังไม่ยืนยันสถานะ");
    } catch (err) {
      flash("ส่ง LINE ตรงไม่สำเร็จ: " + (err.message || err));
    }
  };

  const checkLineContactProfile = async (contact) => {
    const userId = String(contact?.userId || contact?.user_id || "").trim();
    if (!userId) { flash("ไม่พบ userId ของ contact นี้"); return; }
    try {
      const data = await apiGet(`/api/line-profile?userId=${encodeURIComponent(userId)}`);
      alert(JSON.stringify(data, null, 2));
    } catch (err) {
      alert("เช็ก profile ไม่สำเร็จ: " + (err.message || err));
    }
  };

  const createAdminOneTimeSeerRegisterLink = async () => {
    try {
      const res = await apiPost("/api/seer-invites", {
        action: "create",
        inviter_seer_id: "ADMIN",
        alt_id: currentAdmin || "admin",
        inviter_name: "แอดมินดวงวันนี้",
        created_by: currentAdmin || "admin",
        commission_percent: 0,
        admin_only: true,
      });
      const code = res?.invite?.code || res?.invite?.invite_code || "";
      if (!code) {
        flash("สร้างลิงก์ไม่สำเร็จ: ไม่พบ invite code");
        return;
      }
      const url = buildSeerInviteUrl(code);
      setAdminOneTimeSeerInviteLink(url);
      addAudit("สร้างลิงก์สมัครหมอดูใช้ครั้งเดียว", code);
      flash("สร้างลิงก์สมัครหมอดูแล้ว กดคัดลอกลิงก์จากกล่องด้านล่าง");
    } catch (err) {
      flash("สร้างลิงก์สมัครหมอดูไม่สำเร็จ: " + (err.message || err));
    }
  };
  useEffect(() => {
    let alive = true;
    const loadLevels = async () => {
      try {
        const data = await apiGet("/api/seer-levels");
        const map = data.levels || {};
        if (!alive) return;
        const merged = map;
        setSeerLevelsAdmin(merged);
        try { localStorage.setItem(SEER_LEVELS_KEY, JSON.stringify(merged)); } catch {}
      } catch (err) {
        console.warn("โหลดระดับหมอดูแอดมินจาก D1 ไม่สำเร็จ", err);
      }
    };
    loadLevels();
    return () => { alive = false; };
  }, []);
  // Admin seer management is D1-only. Do not seed this list from demo data,
  // LINE OA webhook contacts, or browser storage.
  const [managedSeers, setManagedSeers] = useState([]);
  const [seerApplicants, setSeerApplicants] = useState(() => readSeerApplicantsStore());
  const [auditLogs, setAuditLogs] = useState([
    {id:"LOG001", admin:"super_admin", action:"เข้าสู่ระบบหลังบ้าน", target:"Admin Portal", time:new Date().toLocaleString("th-TH")},
  ]);
  const [seerTermsAdminV150, setSeerTermsAdminV150] = useState(() => readSeerTermsConfigV150());

  const loadSeerTermsAdminV150 = async () => {
    try {
      const data = await apiGet("/api/seer-terms");
      const cfg = writeSeerTermsConfigV150(data?.terms || data || {});
      setSeerTermsAdminV150(cfg);
    } catch {
      setSeerTermsAdminV150(readSeerTermsConfigV150());
    }
  };

  useEffect(() => { loadSeerTermsAdminV150(); }, []);

  const loadAdminD1Data = async () => {
    // v147: โหลดแต่ละชุดแยกกัน ไม่ให้ API ตัวเดียวพังแล้วลากใบสมัครหมอดูหายทั้งหน้า
    let appsOk = false;

    try {
      const apps = await apiGet("/api/seer-applications?limit=200");
      const mappedApps = (apps.applications || []).map(mapApiApplication);
      setSeerApplicants(prev => sameDataV165(prev, mappedApps) ? prev : mappedApps);
      try { writeSeerApplicantsStore(mappedApps); } catch {}
      appsOk = true;
    } catch (err) {
      console.warn("โหลดใบสมัครหมอดูจาก D1 ไม่สำเร็จ ใช้ข้อมูลในเครื่องแทน", err);
      const latest = readSeerApplicantsStore();
      setSeerApplicants(prev => JSON.stringify(prev) === JSON.stringify(latest) ? prev : latest);
    }

    try {
      const seersData = await apiGet("/api/seers?all=1");
      const mappedSeers = (seersData.seers || []).map(mapApiSeer).map((s,i)=>({
        ...s, status:s.adminStatus || "active", feedBoost:i===0 || !!s.feedBoost, warning:s.warning || 0,
        // v165: ห้ามสร้างวันเวลาใหม่ทุก polling เพราะจะทำให้ข้อมูลดูเปลี่ยนตลอดและหน้าแอดมินกระพริบ
        joined:s.joined || s.approvedAt || ""
      }));
      const cleanMappedSeers = uniqById(mappedSeers);
      setManagedSeers(prev => sameDataV165(prev, cleanMappedSeers) ? prev : cleanMappedSeers);
      setSeerLineBindInput(prev => {
        const allowed = new Set(cleanMappedSeers.map(s => `${String(s.seerId || s.id || "").trim()}::${String(s.lineUserId || s.line_user_id || "").trim()}`));
        const next = {};
        Object.keys(prev || {}).forEach(k => { if (allowed.has(k)) next[k] = prev[k]; });
        return sameDataV165(prev, next) ? prev : next;
      });
    } catch (err) {
      // Never replace D1 profiles with demo/local/webhook contacts. Showing an
      // explicit empty/error state is safer than letting an admin edit a false profile.
      console.warn("โหลดโปรไฟล์หมอดูจาก D1 ไม่สำเร็จ", err);
      setManagedSeers([]);
      setSeerLineBindInput({});
    }

    try {
      const articleData = await apiGet("/api/articles?status=all&limit=100");
      const mappedArticles = (articleData.articles || []).map(a => ({
        id: a.article_id || a.id,
        articleId: a.article_id || a.id,
        title: a.title || "ไม่มีชื่อข่าว",
        body: a.body || "",
        status: a.status === "published" ? "เผยแพร่" : a.status === "scheduled" ? "ตั้งเวลา" : a.status === "unpublished" ? "ไม่เผยแพร่" : "แบบร่าง",
        author: a.author || "admin",
        imageUrl: a.image_url || a.imageUrl || "",
        linkUrl: a.external_url || a.linkUrl || "",
        linkText: a.link_text || a.linkText || "อ่านข่าวต่อ",
        scheduledAt: a.scheduled_at || a.scheduledAt || "",
        publishedAt: a.published_at || a.publishedAt || "",
        unpublishedAt: a.unpublished_at || "",
        // v165: ใช้ค่าว่างเมื่อข้อมูลเดิมไม่มีวันสร้าง แทนการสร้างเวลาใหม่ทุก polling
        createdAt: a.created_at || a.createdAt || "",
      }));
      setArticles(prev => sameDataV165(prev, mappedArticles) ? prev : mappedArticles);
    } catch (err) {
      console.warn("โหลดบทความจาก D1 ไม่สำเร็จ แต่ไม่กระทบใบสมัครหมอดู", err);
    }
  };

  useEffect(() => {
    if (!loggedIn) return;
    // v167: โหลดข้อมูลรวมครั้งแรกเมื่อเข้าแอดมินเท่านั้น
    // หลังจากนั้นโหลดเมื่อแอดมินกดปุ่มรีเฟรช หรือเมื่อทำรายการสำเร็จในระบบ
    loadAdminD1Data();
    return undefined;
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn || menu !== "line_meetings") return;
    loadLineMeetingRoomsV3936();
  }, [loggedIn, menu]);

  const refreshAdminDataNowV167 = async () => {
    try {
      await Promise.all([
        loadAdminBookingsV91(),
        loadAdminPaymentSettingsV106(),
        loadSeerWithdrawalsV156E(),
        loadAdminD1Data(),
        loadLineMeetingRoomsV3936(),
      ]);
      flash("รีเฟรชข้อมูลแอดมินล่าสุดแล้ว");
    } catch (err) {
      console.warn("รีเฟรชข้อมูลแอดมินไม่สำเร็จ", err);
      flash("รีเฟรชข้อมูลบางส่วนไม่สำเร็จ กรุณาลองใหม่");
    }
  };

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""), 1800); };
  const addAudit = (action, target) => setAuditLogs(prev=>[{id:"LOG"+(prev.length+1).toString().padStart(3,"0"), admin:currentAdmin, action, target, time:new Date().toLocaleString("th-TH"), createdAt:new Date().toISOString()}, ...prev]);
  const isSuperAdmin = !!adminAccessV3923.isSuperAdmin;
  const currentAdminData = admins.find(a=>a.username===currentAdmin) || admins[0];
  const canSee = (key) => isSuperAdmin || currentAdminData?.menuPermissions?.[key];

  const getMonthKey = (item) => {
    const raw = item?.createdAt || item?.created || item?.time || item?.joined || item?.approvedAt || "";
    const d = new Date(raw);
    const now = new Date();
    if (!Number.isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
  };
  const currentMonthKey = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`;
  };
  const thisMonth = (list) => (Array.isArray(list)?list:[]).filter(x=>getMonthKey(x)===currentMonthKey());
  const page = (key) => pageByKey[key] || 1;
  const pageItems = (key, list, per=10) => {
    const p = page(key);
    return list.slice((p-1)*per, p*per);
  };
  const setPage = (key, p) => setPageByKey(prev=>({...prev,[key]:Math.max(1,p)}));
  const Pager = ({k, list, per=10}) => {
    const total = Math.max(1, Math.ceil((list.length || 0) / per));
    const cur = Math.min(page(k), total);
    if (total <= 1) return null;
    return <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center",padding:"12px 0"}}>
      {Array.from({length:total},(_,i)=>i+1).map(n=><button key={n} className={`btn btn-sm ${cur===n?"btn-purple":"btn-outline"}`} style={{width:"auto",minWidth:36}} onClick={()=>setPage(k,n)}>{n}</button>)}
    </div>;
  };
  const MonthInfo = ({list}) => <div style={{fontSize:11,color:C.muted,margin:"3px 0 10px"}}>ตัดรอบรายเดือน: เดือนนี้มี {list.length} รายการ เลขลำดับเริ่มใหม่ทุกเดือน</div>;

  const pending = orders.filter(o=>o.status==="pending_slip");
  const approved = orders.filter(o=>["approved","approved_pending","answering"].includes(o.status));
  const totalRev = orders.filter(o=>o.pay==="paid").reduce((a,o)=>a+o.price,0);
  const filteredOrdersAll = orders.filter(o=>orderFilter==="all" ? true : orderFilter==="pending" ? o.status==="pending_slip" : orderFilter==="approved" ? ["approved","approved_pending","answering"].includes(o.status) : orderFilter==="rejected" ? o.status==="rejected" : o.status==="answered");
  const filteredOrders = thisMonth(filteredOrdersAll);
  const monthApplicants = thisMonth(seerApplicants);
  const monthSeers = thisMonth(managedSeers);
  const monthArticles = thisMonth(articles);
  const monthAudits = thisMonth(auditLogs);

  const MENUS = [
    // v125: เมนูบนสุดให้เหลือเฉพาะงานหลักที่ใช้บ่อย เมนูย่อย/เมนูที่เพิ่มเรื่อย ๆ ย้ายไปอยู่ใน “ตั้งค่า”
    {k:"overview",ico:"📊",l:"ภาพรวม"},
    {k:"orders",ico:"🧾",l:`ออเดอร์ (${orders.length + (adminBookingsV91||[]).filter(b=>["waiting_slip_review","confirmed","in_session","completed"].includes(String(b.status||"")) || ["approved","rejected","pending"].includes(String(b.slip_review_status||""))).length})`},
    {k:"bookings",ico:"📅",l:`จอง (${adminBookingsV91.length})`},
    {k:"line_meetings",ico:"🎙️",l:`ห้อง LINE (${Number(lineMeetingRoomsV3936.stats?.pending_review||0)+Number(lineMeetingRoomsV3936.stats?.blocked||0)})`},
    {k:"seers",ico:"🔮",l:"จัดการหมอดู"},
    {k:"seer_applicants",ico:"📝",l:`ใบสมัคร (${seerApplicants.filter(s=>s.status==="รอตรวจสอบ").length})`},
    {k:"income_settings",ico:"💰",l:"รายได้"},
    {k:"seer_terms",ico:"📜",l:"ข้อกำหนดหมอดู"},
    {k:"settings",ico:"⚙️",l:"ตั้งค่า / เมนูทั้งหมด"},
  ].filter(m=>canSee(m.k));

  const updOrder = (id,ch, actionLabel="แก้ไขออเดอร์") => {
    setOrders(prev=>prev.map(o=>o.id===id?{...o,...ch}:o));
    addAudit(actionLabel, id);
  };
  const approveOrder = (o) => {
    const isLive=o.svc_type==="live";
    const ch = {status:isLive?"approved":"approved_pending",pay:"paid",admin_action:"อนุมัติ",admin_action_by:currentAdmin,admin_action_at:new Date().toLocaleString("th-TH"),...(isLive?{room_id:o.room_id||`live_${o.id}`,room_status:"ready"}:{})};
    updOrder(o.id,ch,"อนุมัติออเดอร์");
    flash("อนุมัติออเดอร์แล้ว");
  };
  const rejectOrder = (o) => {
    const ch={status:"rejected",pay:"rejected",admin_action:"ไม่อนุมัติ",admin_action_by:currentAdmin,admin_action_at:new Date().toLocaleString("th-TH")};
    updOrder(o.id,ch,"ไม่อนุมัติออเดอร์");
    flash("บันทึกไม่อนุมัติแล้ว");
  };

  function AdminNotice(){ if(!toast) return null; return <div style={{position:"sticky",top:0,zIndex:60,margin:"10px 16px 0"}}><div className="badge b-green" style={{width:"100%",justifyContent:"center",padding:10}}>{toast}</div></div> }
  function RowNum({index}) { return <span style={{fontSize:11,color:C.goldLight,marginRight:6}}>#{index+1}</span>; }
  function OrderRow({o, index=0, showActions=false}) {
    return <div className="brow">
      <div className="brow-hd"><div><div className="brow-name"><RowNum index={index}/> {o.id} • {o.cname}</div><div style={{fontSize:12,color:C.purpleLight}}>{o.seer_name} • {o.svc_name}</div></div><Badge status={o.status}/></div>
      <div className="brow-detail">📅 {o.date}{o.time?" • "+o.time:""} • ฿{o.price}<br/>📞 {o.cphone} • {o.contact}</div>
      {o.admin_action_by && <div style={{fontSize:11,color:C.goldLight,marginTop:6}}>ดำเนินการล่าสุด: {o.admin_action} โดย {o.admin_action_by}</div>}
      {showActions && o.status==="pending_slip" && <div style={{display:"flex",gap:8,marginTop:10}}><button className="btn btn-success btn-sm" onClick={()=>approveOrder(o)}>✓ อนุมัติ</button><button className="btn btn-danger btn-sm" onClick={()=>rejectOrder(o)}>✗ ไม่อนุมัติ</button></div>}
    </div>;
  }

  function AdminInviteAcceptV3923(){
    const [inviteInfo, setInviteInfo] = useState(null);
    const [inviteError, setInviteError] = useState("");
    const [inviteBusy, setInviteBusy] = useState(false);
    useEffect(() => {
      let alive = true;
      (async () => {
        try {
          const data = await adminAccessApiV3924("preview_invite", { invite_token: adminInviteTokenV3923 });
          if (alive) setInviteInfo(data.invite || null);
        } catch (error) {
          if (alive) setInviteError(String(error?.message || error));
        }
      })();
      return () => { alive = false; };
    }, []);
    const accept = async () => {
      setInviteBusy(true); setInviteError("");
      try {
        const data = await adminAccessApiV3924("accept_invite", { invite_token: adminInviteTokenV3923 });
        setInviteInfo({ ...(data.account || {}), accepted:true });
      } catch (error) {
        setInviteError(String(error?.message || error));
      } finally { setInviteBusy(false); }
    };
    return <div className="scr on" data-dw-admin-root="true" style={{background:`linear-gradient(180deg,${C.purple}0D 0%,${C.bg} 60%)`,padding:16}}><Stars/><div className="login-wrap z1"><div style={{fontSize:52,marginBottom:14}}>🛡️</div><h2 style={{fontFamily:"Cinzel",fontSize:22,color:C.goldLight,marginBottom:4}}>เปิดใช้งานแอดมิน</h2><p style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:18}}>ยืนยันบัญชี LINE ของเครื่องนี้ก่อนเปิดสิทธิ์แอดมิน</p>{inviteInfo?.accepted?<div className="card" style={{padding:16,width:"100%"}}><div style={{fontWeight:900,color:C.green,marginBottom:8}}>เปิดใช้งานสำเร็จ</div><div style={{fontSize:13,color:C.text,lineHeight:1.7}}>Username: <b>{inviteInfo.username}</b><br/>ระดับ: {inviteInfo.role_label || inviteInfo.role}</div><div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:10}}>ใช้ Username และ Password ที่ Super Admin ตั้งให้เพื่อเข้าสู่แดชบอร์ด</div><button className="btn btn-gold" style={{marginTop:14,width:"100%"}} onClick={()=>{ window.location.href = "/?screen=admin"; }}>ไปหน้าเข้าสู่ระบบ</button></div>:<div className="card" style={{padding:16,width:"100%"}}>{inviteInfo?<><div style={{fontWeight:900,color:C.goldLight,marginBottom:8}}>{inviteInfo.display_name || "บัญชีแอดมิน"}</div><div style={{fontSize:13,color:C.text,lineHeight:1.75}}>Username ที่ได้รับ: <b>{inviteInfo.username}</b><br/>ระดับ: {inviteInfo.role_label || inviteInfo.role}<br/>หมดอายุ: {inviteInfo.expires_at}</div><div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:10}}>เมื่อกดยืนยัน ระบบจะผูก LINE ของเครื่องนี้กับสิทธิ์แอดมินดังกล่าว ลิงก์ใช้ได้ครั้งเดียว</div><button className="btn btn-gold" disabled={inviteBusy} style={{marginTop:14,width:"100%"}} onClick={accept}>{inviteBusy?"กำลังเปิดใช้งาน...":"ยืนยันเป็นแอดมินเครื่องนี้"}</button></>:<div style={{fontSize:13,color:C.muted}}>กำลังตรวจสอบลิงก์เชิญ...</div>}{inviteError&&<div className="badge b-red" style={{marginTop:12,width:"100%",whiteSpace:"normal",lineHeight:1.5}}>{inviteError}</div>}</div>}</div></div>;
  }

  if(adminInviteTokenV3923) return <AdminInviteAcceptV3923/>;

  const submitAdminRecoveryV3930 = async () => {
    setAdminAccessBusyV3923(true);
    try {
      const data = await adminAccessApiV3924("bootstrap_initial_super_admin", adminRecoveryV3930);
      const account = data.account || null;
      setAdminAccessV3923(prev=>({...prev, account, isAdmin:true, isSuperAdmin:true, needsSetup:false, bootstrapAvailable:false, error:""}));
      setCurrentAdmin(account?.username || "super_admin");
      setAdminRecoveryV3930({username:"", display_name:"", password:"", confirm_password:"", recovery_code:""});
      setLoggedIn(true);
      flash("กู้คืนสิทธิ์ Super Admin เรียบร้อยแล้ว");
    } catch (error) { flash(String(error?.message || error)); }
    finally { setAdminAccessBusyV3923(false); }
  };
  const submitAdminLoginV3923 = async () => {
    setAdminAccessBusyV3923(true);
    try {
      const data = await adminAccessApiV3924("login", { username:loginF.user, password:loginF.pass });
      const account = data.account || null;
      setAdminAccessV3923(prev=>({...prev, account, isAdmin:true, isSuperAdmin:!!data.is_super_admin, needsSetup:false, error:""}));
      setCurrentAdmin(account?.username || loginF.user);
      setLoggedIn(true);
      setLoginF(prev=>({...prev, pass:""}));
      addAudit("เข้าสู่ระบบ", account?.username || "Admin Portal");
    } catch (error) { flash(String(error?.message || error)); }
    finally { setAdminAccessBusyV3923(false); }
  };
  const submitAdminSetupV3923 = async () => {
    setAdminAccessBusyV3923(true);
    try {
      const data = await adminAccessApiV3924("setup_self", adminBootstrapV3923);
      const account = data.account || null;
      setAdminAccessV3923(prev=>({...prev, account, needsSetup:false, isAdmin:true, error:""}));
      setLoginF({user:account?.username || "",pass:""});
      setCurrentAdmin(account?.username || "");
      setLoggedIn(true);
      flash("ตั้งค่า Username และ Password เรียบร้อยแล้ว");
    } catch (error) { flash(String(error?.message || error)); }
    finally { setAdminAccessBusyV3923(false); }
  };
  if(!loggedIn) return <div className="scr on" data-dw-admin-root="true" style={{background:`linear-gradient(180deg,${C.purple}0D 0%,${C.bg} 60%)`}}><Stars/><div className="login-wrap z1"><div style={{fontSize:52,marginBottom:14}}>🛡️</div><h2 style={{fontFamily:"Cinzel",fontSize:22,color:C.goldLight,marginBottom:4}}>Admin Portal</h2><p style={{fontSize:12,color:C.muted,marginBottom:22}}>ดวงวันนี้ — ระบบจัดการหลังบ้าน</p><div style={{width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:12}}>{adminAccessV3923.loading?<div className="card" style={{padding:14,textAlign:"center",color:C.muted}}>กำลังยืนยันบัญชี LINE...</div>:adminAccessV3923.error?<div className="card" style={{padding:14,color:C.orange,lineHeight:1.7}}>{adminAccessV3923.error}</div>:!adminAccessV3923.isAdmin?(adminAccessV3923.bootstrapAvailable?<><div className="card" style={{padding:14,color:C.orange,lineHeight:1.7}}>กู้คืน Super Admin ครั้งแรก<br/><span style={{fontSize:11,color:C.muted}}>ใช้ได้เฉพาะเมื่อยังไม่มีบัญชีแอดมิน และต้องใช้รหัสกู้คืนที่ตั้งไว้ใน Cloudflare Pages</span></div><input className="finput" placeholder="ชื่อเรียกแอดมิน" value={adminRecoveryV3930.display_name} onChange={e=>setAdminRecoveryV3930(p=>({...p,display_name:e.target.value}))}/><input className="finput" placeholder="Username ภาษาอังกฤษ/ตัวเลข" autoCapitalize="none" value={adminRecoveryV3930.username} onChange={e=>setAdminRecoveryV3930(p=>({...p,username:e.target.value}))}/><input className="finput" type="password" placeholder="Password อย่างน้อย 10 ตัวอักษร" value={adminRecoveryV3930.password} onChange={e=>setAdminRecoveryV3930(p=>({...p,password:e.target.value}))}/><input className="finput" type="password" placeholder="ยืนยัน Password" value={adminRecoveryV3930.confirm_password} onChange={e=>setAdminRecoveryV3930(p=>({...p,confirm_password:e.target.value}))}/><input className="finput" type="password" placeholder="รหัสกู้คืนจาก Cloudflare Pages" value={adminRecoveryV3930.recovery_code} onChange={e=>setAdminRecoveryV3930(p=>({...p,recovery_code:e.target.value}))}/><button className="btn btn-gold" disabled={adminAccessBusyV3923} onClick={submitAdminRecoveryV3930}>{adminAccessBusyV3923?"กำลังกู้คืน...":"กู้คืน Super Admin"}</button></>:<div className="card" style={{padding:14,color:C.orange,lineHeight:1.7}}>LINE บัญชีนี้ยังไม่มีสิทธิ์แอดมิน<br/><span style={{fontSize:11,color:C.muted}}>กรุณาเปิดลิงก์เชิญจาก Super Admin ใน LINE บัญชีนี้</span></div>):adminAccessV3923.needsSetup?<><div className="card" style={{padding:14,color:C.muted,lineHeight:1.7}}>ตั้งค่า Username และ Password ครั้งแรกสำหรับ LINE บัญชีนี้ รหัสผ่านจะถูกเก็บแบบเข้ารหัสใน D1</div><input className="finput" placeholder="ชื่อเรียกแอดมิน" value={adminBootstrapV3923.display_name} onChange={e=>setAdminBootstrapV3923(p=>({...p,display_name:e.target.value}))}/><input className="finput" placeholder="Username ภาษาอังกฤษ/ตัวเลข" autoCapitalize="none" value={adminBootstrapV3923.username} onChange={e=>setAdminBootstrapV3923(p=>({...p,username:e.target.value}))}/><input className="finput" type="password" placeholder="Password อย่างน้อย 10 ตัวอักษร" value={adminBootstrapV3923.password} onChange={e=>setAdminBootstrapV3923(p=>({...p,password:e.target.value}))}/><input className="finput" type="password" placeholder="ยืนยัน Password" value={adminBootstrapV3923.confirm_password} onChange={e=>setAdminBootstrapV3923(p=>({...p,confirm_password:e.target.value}))}/><button className="btn btn-gold" disabled={adminAccessBusyV3923} onClick={submitAdminSetupV3923}>{adminAccessBusyV3923?"กำลังบันทึก...":"ตั้งค่าบัญชีแอดมิน"}</button></>:<><div className="card" style={{padding:14,color:C.muted,lineHeight:1.6}}>ยืนยัน LINE แล้ว: <b style={{color:C.text}}>{adminAccessV3923.account?.display_name || "แอดมิน"}</b></div><input className="finput" placeholder="Username" autoCapitalize="none" value={loginF.user} onChange={e=>setLoginF(p=>({...p,user:e.target.value}))}/><input className="finput" type="password" placeholder="Password" value={loginF.pass} onChange={e=>setLoginF(p=>({...p,pass:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter") submitAdminLoginV3923();}}/><button className="btn btn-gold" disabled={adminAccessBusyV3923} onClick={submitAdminLoginV3923}>{adminAccessBusyV3923?"กำลังเข้าสู่ระบบ...":"เข้าสู่ระบบ"}</button><p style={{fontSize:11,color:C.dim,textAlign:"center",lineHeight:1.6}}>ต้องยืนยันทั้ง LINE บัญชีที่ได้รับสิทธิ์ และ Username / Password ของแอดมิน</p></>}{toast&&<div className="badge b-orange" style={{alignSelf:"center",whiteSpace:"normal",lineHeight:1.5}}>{toast}</div>}</div></div></div>;

  function Overview(){
    const latest = [
      ...orders.map(x=>({...x,_type:"ออเดอร์",_title:`${x.cname} • ${x.svc_name}`,_time:x.created || x.createdAt || ""})),
      ...seerApplicants.map(x=>({...x,_type:"ใบสมัครหมอดู",_title:x.name,_time:x.createdAt || ""})),
      ...auditLogs.map(x=>({...x,_type:"ประวัติแอดมิน",_title:x.action,_time:x.time || ""})),
    ].slice(0,10);
    return <><div className="stats-grid" style={{marginTop:14}}><div className="stat-card" onClick={()=>{setMenu("orders");setOrderFilter("pending");setPage("orders",1);}} style={{cursor:"pointer",borderColor:`${C.orange}66`}}><div className="stat-num" style={{color:C.orange}}>{pending.length + (adminBookingsV91||[]).filter(b=>b.status==="waiting_slip_review").length}</div><div className="stat-lbl">รอตรวจสลิป</div></div><div className="stat-card"><div className="stat-num" style={{color:C.green}}>{approved.length}</div><div className="stat-lbl">อนุมัติแล้ว</div></div><div className="stat-card" onClick={()=>{setMenu("seer_applicants");setPage("seer_applicants",1);loadAdminD1Data();}} style={{cursor:"pointer",borderColor:`${C.gold}66`}}><div className="stat-num">{seerApplicants.filter(s=>s.status==="รอตรวจสอบ").length}</div><div className="stat-lbl">หมอดูรอตรวจ</div><div style={{fontSize:10,color:C.muted,marginTop:4}}>กดเพื่อเปิดใบสมัคร</div></div><div className="stat-card"><div className="stat-num" style={{fontSize:15}}>฿{totalRev.toLocaleString()}</div><div className="stat-lbl">รายได้รวม</div></div></div><div className="sec-hd">รายการล่าสุด 10 รายการ</div>{latest.map((x,i)=><div key={(x.id||x._title)+i} className="card" style={{padding:12,margin:"0 16px 10px"}}><div style={{fontSize:13,fontWeight:800,color:C.text}}><RowNum index={i}/>{x._type}: {x._title}</div><div style={{fontSize:11,color:C.muted,marginTop:4}}>{x._time}</div></div>)}</>;
  }
  function AdminBookingsPageV91(){
    const all = adminBookingsV91 || [];
    const waitingSlip = all.filter(b=>b.status === "waiting_slip_review");
    const list = all.filter(b=>["pending_request","seer_confirmed","seer_proposed_new_time","waiting_payment","waiting_slip_review","paid_waiting_seer","confirmed","in_session"].includes(String(b.status || "")));
    const items = pageItems("bookings", list, 20);
    return <div style={{paddingTop:12}}>
      <div className="stats-grid" style={{marginTop:4}}>
        <div className="stat-card" onClick={()=>{setMenu("orders");setOrderFilter("pending");setPage("orders",1);}} style={{cursor:"pointer",borderColor:`${C.orange}66`}}>
          <div className="stat-num" style={{color:C.orange}}>{waitingSlip.length}</div>
          <div className="stat-lbl">รอตรวจสลิป</div>
          <div style={{fontSize:10,color:C.muted,marginTop:4}}>กดเพื่อไปเมนูออเดอร์</div>
        </div>
        <div className="stat-card">
          <div className="stat-num" style={{color:C.green}}>{all.filter(b=>b.status==="confirmed").length}</div>
          <div className="stat-lbl">ห้องพร้อมใช้</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{list.length}</div>
          <div className="stat-lbl">รอลูกค้าโอน/รอหมอตอบ</div>
        </div>
      </div>
      <div className="sec-hd">รายการจองและสถานะส่งงานหมอดู</div>
      <div style={{padding:"0 16px 12px",display:"flex",gap:8,flexWrap:"wrap"}}>
        <button className="btn btn-outline btn-sm" onClick={loadAdminBookingsV91}>โหลดใหม่</button>
        <button className="btn btn-gold btn-sm" onClick={()=>{setMenu("orders");setOrderFilter("pending");setPage("orders",1);}}>ไปตรวจสลิปในออเดอร์</button>
      </div>
      {items.length===0?<div className="empty"><div className="e-ico">📅</div>ยังไม่มีรายการที่รอการโอนเงิน</div>:items.map((b,i)=><div key={b.booking_id} className="brow">
        <div className="brow-hd"><div><div className="brow-name"><RowNum index={(page("bookings")-1)*20+i}/>{b.customer_name || "ลูกค้า"} → {b.seer_name || "หมอดู"}</div><div style={{fontSize:12,color:C.purpleLight}}>{b.service_name}</div></div><span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span></div>
        <div className="brow-detail">เลขออเดอร์: {b.order_number || "-"}<br/>Booking: {b.booking_id} • ฿{Number(b.price||0).toLocaleString()} • {dwBookingScheduledText(b)}<br/>Room: {b.room_id || "ยังไม่สร้าง"}</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
          <button className="btn btn-outline btn-sm" onClick={()=>openAdminBookingChatV106(b)}>ส่องแชทนัดเวลา</button>
          {b.status === "paid_waiting_seer" && <button className="btn btn-gold btn-sm" onClick={()=>adminResendBookingToSeerV3(b)}>ส่งงานให้หมอดูอีกครั้ง</button>}
        </div>
      </div>)}
      <Pager k="bookings" list={list} per={20}/>
      {adminChatViewV106.open && (
        <div style={{position:"fixed",inset:0,zIndex:999999,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{width:"100%",maxWidth:520,maxHeight:"86vh",overflowY:"auto",background:C.bg,border:`1px solid ${C.gold}55`,borderRadius:20,boxShadow:"0 22px 80px rgba(0,0,0,.65)"}}>
            <div style={{padding:14,borderBottom:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"center"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>ส่องแชทนัดเวลา</div>
                <div style={{fontSize:11,color:C.muted}}>#{adminChatViewV106.booking?.booking_id}</div>
              </div>
              <button className="btn btn-outline btn-sm" style={{width:42,padding:"7px 0"}} onClick={()=>setAdminChatViewV106({open:false, booking:null, messages:[]})}>×</button>
            </div>
            <div style={{padding:14}}>
              {(adminChatViewV106.messages || []).length === 0 ? <div className="empty"><div className="e-ico">💬</div>ยังไม่มีข้อความ</div> : adminChatViewV106.messages.map((m,i)=>(
                <div key={m.message_id || i} className={`chat-bubble ${m.sender_role === "customer" ? "chat-right" : "chat-left"}`}>
                  <div>{m.message_text}</div>
                  <div style={{fontSize:10,opacity:.65,marginTop:4}}>{m.sender_role === "system" ? "ระบบ" : m.sender_role === "customer" ? "ลูกค้า" : "หมอดู"} • {m.created_at}</div>
                </div>
              ))}
              <div className="warn-box" style={{marginTop:12}}>ใช้ตรวจสอบการนัดออกนอกแพลตฟอร์ม / การทุจริต</div>
            </div>
          </div>
        </div>
      )}
    </div>;
  }

  function AdminPaymentAccountPageV106(){
    const f = adminPaymentSettingsV106 || {};
    const setF = (k,v)=>setAdminPaymentSettingsV106(p=>({...p,[k]:v}));
    const uploadPaymentQrV157 = async (file) => {
      if (!file) return;
      try {
        flash("กำลังอัปโหลด QR Code...");
        const up = await uploadFileToR2(file, { ownerType:"payment", ownerId:"account", fileRole:"promptpay_qr" });
        setF("qr_url", up.public_url || up.url || "");
        flash("อัปโหลด QR Code แล้ว อย่าลืมกดบันทึกเลขบัญชี");
      } catch (err) {
        alert("อัปโหลด QR Code ไม่สำเร็จ: " + (err?.message || err));
      }
    };
    const cardEnabled = f.card_payment_enabled === true || f.card_payment_enabled === "1";
    return <div style={{paddingTop:12}}>
      <div className="sec-hd">เลขบัญชีรับเงินลูกค้า</div>
      <div className="card" style={{padding:16,margin:"0 16px 14px"}}>
        <div className="warn-box" style={{marginBottom:14}}>เมนูนี้สำหรับแอดมินสูงสุด ใช้ตั้งค่าเลขบัญชี/พร้อมเพย์/QR Code ที่ลูกค้าเห็นตอนกดโอนเงินและแนบสลิป</div>
        <div className="card" style={{padding:12,margin:"0 0 14px",borderColor:`${C.gold}55`,background:`${C.gold}10`}}>
          <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:6}}>📷 อัปโหลดรูป QR รับเงินแม่มณี SCB / พร้อมเพย์</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>ให้อัปโหลดรูป QR แม่มณี SCB หรือ QR พร้อมเพย์ของบัญชีรับเงิน ระบบจะแสดงในหน้าชำระเงินให้ลูกค้าโอนและแนบสลิป</div>
        </div>
        <div className="fgroup"><label className="flabel">ธนาคาร / ช่องทาง</label><input className="finput" value={f.bank_name || ""} onChange={e=>setF("bank_name",e.target.value)} placeholder="เช่น SCB แม่มณี / พร้อมเพย์"/></div>
        <div className="fgroup"><label className="flabel">ชื่อบัญชี</label><input className="finput" value={f.account_name || ""} onChange={e=>setF("account_name",e.target.value)} placeholder="ชื่อบัญชีรับเงิน"/></div>
        <div className="fgroup"><label className="flabel">เลขบัญชี / พร้อมเพย์</label><input className="finput" value={f.account_number || ""} onChange={e=>setF("account_number",e.target.value)} placeholder="เลขบัญชีหรือพร้อมเพย์"/></div>
        <div className="fgroup"><label className="flabel">PromptPay เพิ่มเติม</label><input className="finput" value={f.promptpay || ""} onChange={e=>setF("promptpay",e.target.value)} placeholder="เบอร์พร้อมเพย์ / เลขบัตร / Tax ID"/></div>
        <div className="fgroup"><label className="flabel">ลิงก์รูป QR พร้อมเพย์</label><input className="finput" value={f.qr_url || ""} onChange={e=>setF("qr_url",e.target.value)} placeholder="https://... หรือ /api/files?key=..."/></div>
        <div className="fgroup">
          <label className="flabel">อัปโหลดรูป QR พร้อมเพย์</label>
          <input
            id="paymentQrUploadV157"
            className="finput"
            type="file"
            accept="image/*"
            onChange={e=>uploadPaymentQrV157(e.target.files?.[0])}
          />
          <div style={{fontSize:11,color:C.muted,marginTop:6}}>เลือกรูป QR พร้อมเพย์จากเครื่อง ระบบจะอัปโหลดเข้า R2 แล้วเติมลิงก์ให้อัตโนมัติ</div>
        </div>
        {f.qr_url && <div style={{textAlign:"center",marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:900,color:C.goldLight,marginBottom:8}}>ตัวอย่าง QR ที่ลูกค้าจะเห็น</div>
          <img src={f.qr_url} alt="QR พร้อมเพย์ Preview" style={{maxWidth:220,width:"80%",borderRadius:14,border:`1px solid ${C.border}`,background:"#fff",padding:8}}/>
          <div style={{fontSize:11,color:C.muted,marginTop:6}}>หลังอัปโหลดแล้ว ต้องกด “บันทึกเลขบัญชี” เพื่อให้ลูกค้าเห็น QR นี้</div>
        </div>}
        <div className="card" style={{padding:12,margin:"12px 0",borderColor:`${C.purple}66`,background:`${C.purple}12`}}>
          <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:8}}>💳 ชำระผ่านบัตรเครดิต / เดบิต</div>
          <label style={{display:"flex",gap:8,alignItems:"center",fontSize:13,color:C.text,marginBottom:10}}>
            <input type="checkbox" checked={cardEnabled} onChange={e=>setF("card_payment_enabled", e.target.checked ? "1" : "0")}/>
            เปิดแสดงปุ่มชำระผ่านบัตรเครดิตให้ลูกค้า
          </label>
          <div className="fgroup"><label className="flabel">ลิงก์ชำระบัตรเครดิต</label><input className="finput" value={f.card_payment_url || ""} onChange={e=>setF("card_payment_url",e.target.value)} placeholder="ลิงก์จาก Payment Gateway ที่จะใช้ในอนาคต"/></div>
          <div className="fgroup"><label className="flabel">ข้อความอธิบายบัตรเครดิต</label><input className="finput" value={f.card_payment_note || ""} onChange={e=>setF("card_payment_note",e.target.value)} placeholder="เช่น ชำระผ่านบัตรเครดิต/เดบิต ระบบจะเปิดหน้า Payment Gateway"/></div>
          <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>ถ้ายังไม่ได้รับอนุมัติ Payment Gateway ให้ปิดไว้ก่อน ระบบจะไม่แสดงปุ่มนี้แก่ลูกค้า</div>
        </div>
        <div className="fgroup"><label className="flabel">% หักแพลตฟอร์ม (สูงสุด 60%)</label><input className="finput" type="number" min="0" max="60" value={f.platform_fee_percent ?? 35} onChange={e=>setF("platform_fee_percent",Math.min(60,Math.max(0,Number(e.target.value||0))))} placeholder="เช่น 35"/><div style={{fontSize:11,color:C.muted,marginTop:6}}>ตัวอย่าง: ลูกค้าจ่าย 1,000 บาท หัก 35% = แพลตฟอร์ม 350 บาท หมอดูได้ 650 บาท</div></div>
        <button className="btn btn-gold" onClick={saveAdminPaymentSettingsV106}>บันทึกเลขบัญชี</button>
      </div>
    </div>;
  }


  function AdminSlipPreviewV119({ b }) {
    const raw = b?.slip_url || b?.slipUrl || "";
    if (!raw) return null;
    const src = String(raw);
    const lower = src.toLowerCase();
    const isPdf = lower.includes(".pdf") || lower.startsWith("data:application/pdf");
    const isImageLikely = src.startsWith("data:image/") || !isPdf;
    return (
      <div className="card" style={{padding:10,marginTop:10,borderColor:`${C.gold}55`,background:"rgba(255,213,74,.06)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <div style={{fontSize:13,fontWeight:900,color:C.goldLight,flex:1}}>🧾 รูปสลิปที่ลูกค้าแนบ</div>
          <a className="btn btn-outline btn-sm" href={src} target="_blank" rel="noreferrer" style={{width:"auto",textDecoration:"none"}}>เปิดเต็ม</a>
        </div>
        {isPdf ? (
          <div className="warn-box">ไฟล์นี้เป็น PDF กด “เปิดเต็ม” เพื่อตรวจสอบสลิป</div>
        ) : (
          <img
            src={src}
            alt="payment slip"
            loading="lazy"
            style={{
              display:"block",
              width:"100%",
              maxHeight:360,
              objectFit:"contain",
              borderRadius:14,
              border:`1px solid ${C.border}`,
              background:"#05030f"
            }}
            onError={(e)=>{
              e.currentTarget.style.display="none";
              const next = e.currentTarget.nextSibling;
              if (next) next.style.display = "block";
            }}
          />
        )}
        <div style={{display:"none",fontSize:12,color:C.orange,lineHeight:1.6,marginTop:8,wordBreak:"break-all"}}>
          โหลดรูปสลิปไม่สำเร็จ กด “เปิดเต็ม” เพื่อตรวจสอบ<br/>
          {src}
        </div>
        <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8,wordBreak:"break-all"}}>
          ไฟล์สลิป: {src}
        </div>
      </div>
    );
  }

  function OrdersPage(){
    const bookingOrderRows = (adminBookingsV91 || [])
      .filter(b=>["waiting_slip_review","confirmed","in_session","completed"].includes(String(b.status || "")) || ["approved","rejected","pending"].includes(String(b.slip_review_status || "")))
      .map(b=>({
        _kind:"booking",
        id:b.booking_id,
        createdAt:b.updated_at || b.slip_uploaded_at || b.created_at,
        status:b.status,
        booking:b,
      }));
    const serviceOrderRows = (filteredOrders || []).map(o=>({_kind:"service", id:o.id, createdAt:o.createdAt || o.created_at, order:o, status:o.status}));
    const combinedAll = [...bookingOrderRows, ...serviceOrderRows].sort((a,b)=>String(b.createdAt||"").localeCompare(String(a.createdAt||"")));
    const combined = combinedAll.filter(x=>{
      if (orderFilter === "all") return true;
      if (orderFilter === "pending") return x._kind === "booking" ? x.booking.status === "waiting_slip_review" : x.order.status === "pending_slip";
      if (orderFilter === "approved") return x._kind === "booking" ? ["confirmed","in_session"].includes(String(x.booking.status || "")) : ["approved","approved_pending","answering"].includes(x.order.status);
      if (orderFilter === "rejected") return x._kind === "booking" ? String(x.booking.slip_review_status || "") === "rejected" : x.order.status === "rejected";
      if (orderFilter === "answered") return x._kind === "booking" ? x.booking.status === "completed" : x.order.status === "answered";
      return true;
    });
    const items = pageItems("orders", combined, 20);
    const BookingOrderRow = ({b,index}) => (
      <div className="brow">
        <div className="brow-hd">
          <div>
            <div className="brow-name"><RowNum index={index}/>{b.customer_name || "ลูกค้า"} → {b.seer_name || "หมอดู"}</div>
            <div style={{fontSize:12,color:C.purpleLight}}>นัดดูดวง: {b.service_name || "-"}</div>
          </div>
          <span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span>
        </div>
        <div className="brow-detail">
          #{b.booking_id} • ฿{Number(b.price||0).toLocaleString()} • {dwBookingScheduledText(b)}<br/>
          สถานะสลิป: {b.slip_review_status || "-"} • Room: {b.room_id || "ยังไม่สร้าง"}
        </div>
        <AdminSlipPreviewV119 b={b}/>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
          <button className="btn btn-outline btn-sm" onClick={()=>openAdminBookingChatV106(b)}>ส่องแชทนัดเวลา</button>
          {b.status==="waiting_slip_review" && <button
            className="btn btn-success btn-sm"
            disabled={reviewingBookingIdV397 === b.booking_id}
            onClick={()=>adminReviewBookingSlipV91(b,true)}
          >{reviewingBookingIdV397 === b.booking_id ? "กำลังอนุมัติ..." : "อนุมัติสลิป / ส่งให้หมอดู"}</button>}
          {b.status==="waiting_slip_review" && <button
            className="btn btn-danger btn-sm"
            disabled={reviewingBookingIdV397 === b.booking_id}
            onClick={()=>adminReviewBookingSlipV91(b,false)}
          >ไม่อนุมัติ</button>}
        </div>
        {slipReviewErrorV397.bookingId === b.booking_id && (
          <div className="warn-box" style={{marginTop:10,borderColor:`${C.red}66`,color:C.red}}>
            ดำเนินการไม่สำเร็จ: {slipReviewErrorV397.message}
          </div>
        )}
      </div>
    );
    return <div style={{paddingTop:12}}>
      <div className="tab-bar">{[["all","ทั้งหมด"],["pending","รอตรวจสลิป"],["approved","อนุมัติแล้ว"],["rejected","ไม่อนุมัติ"],["answered","เสร็จแล้ว"]].map(([k,l])=><button key={k} className={`tab ${orderFilter===k?'on':''}`} onClick={()=>{setOrderFilter(k);setPage("orders",1);}}>{l}</button>)}</div>
      <div className="sec-hd">ออเดอร์ — {combined.length} รายการ</div>
      <div style={{padding:"0 16px"}}><MonthInfo list={combined}/></div>
      {items.length===0?<div className="empty"><div className="e-ico">📭</div>ไม่มีรายการในหน้านี้</div>:items.map((x,i)=> x._kind === "booking"
        ? <BookingOrderRow key={"b-"+x.id} b={x.booking} index={(page("orders")-1)*20+i}/>
        : <OrderRow key={"o-"+x.id} o={x.order} index={(page("orders")-1)*20+i} showActions={x.order.status==="pending_slip"}/>
      )}
      <Pager k="orders" list={combined} per={20}/>
      {adminChatViewV106.open && (
        <div style={{position:"fixed",inset:0,zIndex:999999,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{width:"100%",maxWidth:520,maxHeight:"86vh",overflowY:"auto",background:C.bg,border:`1px solid ${C.gold}55`,borderRadius:20,boxShadow:"0 22px 80px rgba(0,0,0,.65)"}}>
            <div style={{padding:14,borderBottom:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"center"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>ส่องแชทนัดเวลา</div>
                <div style={{fontSize:11,color:C.muted}}>#{adminChatViewV106.booking?.booking_id}</div>
              </div>
              <button className="btn btn-outline btn-sm" style={{width:42,padding:"7px 0"}} onClick={()=>setAdminChatViewV106({open:false, booking:null, messages:[]})}>×</button>
            </div>
            <div style={{padding:14}}>
              {(adminChatViewV106.messages || []).length === 0 ? <div className="empty"><div className="e-ico">💬</div>ยังไม่มีข้อความ</div> : adminChatViewV106.messages.map((m,i)=>(
                <div key={m.message_id || i} className={`chat-bubble ${m.sender_role === "customer" ? "chat-right" : "chat-left"}`}>
                  <div>{m.message_text}</div>
                  <div style={{fontSize:10,opacity:.65,marginTop:4}}>{m.sender_role === "system" ? "ระบบ" : m.sender_role === "customer" ? "ลูกค้า" : "หมอดู"} • {m.created_at}</div>
                </div>
              ))}
              <div className="warn-box" style={{marginTop:12}}>ใช้ตรวจสอบการนัดออกนอกแพลตฟอร์ม / การทุจริต</div>
            </div>
          </div>
        </div>
      )}
    </div>;
  }
  const approveSeerApplicant = async (a) => {
    try {
      const approval = await apiPost("/api/seer-applications", {
        action:"approve",
        application_id:a.applicationId || a.id,
        adminId:currentAdmin
      });
      await loadAdminD1Data();
      setViewApplicant(null);
      setPage("seers", 1);
      setMenu("seers");
      addAudit("อนุมัติหมอดูใหม่", a.name);
      flash(approval?.referral
        ? "อนุมัติแล้ว และบันทึกสายเชิญ/คอม 3% เข้า D1 แล้ว • เปิดหน้าจัดการหมอดู"
        : "อนุมัติแล้ว • เปิดหน้าจัดการหมอดู");
    } catch (err) {
      // D1 is the only source of truth for approval.  Never mark an applicant
      // approved only in browser storage when the server request fails.
      console.warn("approve seer application failed", err);
      flash("อนุมัติไม่สำเร็จ: สถานะใน D1 ยังไม่เปลี่ยน กรุณาลองใหม่ • " + (err.message || err));
    }
  };

  const rejectSeerApplicant = async (a) => {
    try {
      await apiPost("/api/seer-applications", { action:"reject", application_id:a.applicationId || a.id, adminId:currentAdmin });
      await loadAdminD1Data();
      addAudit("ปฏิเสธหมอดูใหม่", a.name);
      flash("ปฏิเสธใบสมัครแล้ว");
    } catch (err) {
      setSeerApplicants(prev=>prev.map(x=>x.id===a.id?{...x,status:"ปฏิเสธ"}:x));
      flash("ปฏิเสธในเครื่องแล้ว แต่ D1 ไม่สำเร็จ: " + (err.message || err));
    }
  };

  function SeerApplicantDetailModal(){
    const a = viewApplicant;
    if (!a) return null;
    const docs = a.documents || [];
    return <div style={{position:"fixed",inset:0,zIndex:99999,background:"rgba(0,0,0,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:14}} onClick={()=>setViewApplicant(null)}>
      <div className="card" style={{width:"100%",maxWidth:520,maxHeight:"86vh",overflow:"auto",padding:16}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",marginBottom:12}}>
          <div style={{fontWeight:900,color:C.goldLight,fontSize:16}}>รายละเอียดใบสมัครหมอดู</div>
          <button className="btn btn-outline btn-sm" onClick={()=>setViewApplicant(null)}>ปิด</button>
        </div>
        {a.photoUrl ? (
          <div style={{textAlign:"center",marginBottom:12}}>
            <img src={a.photoUrl} alt={a.name} style={{width:150,height:150,borderRadius:18,objectFit:"cover",border:`2px solid ${C.gold}66`,display:"block",margin:"0 auto 8px"}}/>
            <a className="btn btn-outline btn-sm" href={a.photoUrl} target="_blank" rel="noreferrer">เปิดภาพโปรไฟล์</a>
          </div>
        ) : <div className="warn-box" style={{marginBottom:12}}>ยังไม่มีภาพโปรไฟล์ หรือไฟล์เดิมยังไม่ได้อัปโหลดเข้า R2</div>}
        <div style={{fontSize:13,color:C.text,lineHeight:1.9}}>
          <b>ชื่อหมอดู:</b> {a.name}<br/>
          <b>ศาสตร์ที่ถนัด:</b> {a.spec || "-"}<br/>
          <b>LINE:</b> {a.lineDisplayName || a.phone || "-"}<br/>
          <b>LINE userId:</b> {a.lineUserId || "-"}<br/>
          <b>เบอร์ติดต่อหมอดู (แอดมินเท่านั้น):</b> {a.contactPhone || a.adminContactPhone || "-"}<br/>
          <b>ข้อมูลราคา/บัญชี:</b> {a.docs || "-"}<br/>
          <b>บริการที่เลือก:</b> {(a.services||[]).join(" / ") || "-"}<br/>
          <b>คำแนะนำตัว:</b><br/><span style={{color:C.muted}}>{a.bio || "-"}</span><br/>
          <b>ส่งใบสมัคร:</b> {a.createdAt || "-"}<br/>
          {a.adminNote && <><b>หมายเหตุระบบ:</b><br/><span style={{color:C.muted}}>{a.adminNote}</span><br/></>}
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
          <button className="btn btn-outline btn-sm" onClick={()=>copyTextSafe(a.lineUserId || "", "คัดลอก LINE userId แล้ว")}>คัดลอก LINE userId</button>
          <button className="btn btn-outline btn-sm" disabled title="อนุมัติและผูก LINE OA ก่อน จึงส่ง LINE ได้">ส่ง LINE หลังอนุมัติ</button>
          {(a.contactPhone || a.adminContactPhone) && <button className="btn btn-outline btn-sm" onClick={()=>copyTextSafe(a.contactPhone || a.adminContactPhone, "คัดลอกเบอร์ติดต่อแล้ว")}>คัดลอกเบอร์ติดต่อ</button>}
        </div>
        <div style={{fontWeight:800,color:C.goldLight,margin:"14px 0 8px"}}>เอกสารแนบ / หลักฐาน</div>
        {docs.length===0 && <div className="warn-box">ยังไม่มีเอกสารแนบ</div>}
        {docs.map((d,i)=><div key={i} className="card" style={{padding:10,marginBottom:10,background:C.surface}}>
          <div style={{fontSize:12,color:C.text,fontWeight:800,marginBottom:8}}>{i+1}. {d.name || d}</div>
          {String(d.url||"").match(/\.(png|jpg|jpeg|webp|gif)(\?|$)/i) || String(d.url||"").startsWith("data:image") || String(d.content_type||"").startsWith("image/")
            ? <img src={d.url} alt={d.name || "เอกสาร"} style={{width:"100%",maxHeight:320,objectFit:"contain",borderRadius:10,border:`1px solid ${C.border}`,background:"#05020f"}}/>
            : null}
          {String(d.url||"").startsWith("data:application/pdf") || String(d.content_type||"").includes("pdf") || String(d.name||"").toLowerCase().endsWith(".pdf")
            ? <a className="btn btn-outline btn-sm" href={d.url} target="_blank" rel="noreferrer">เปิด PDF</a>
            : null}
          {d.url ? <a className="btn btn-outline btn-sm" href={d.url} target="_blank" rel="noreferrer" style={{marginTop:8}}>เปิดไฟล์แนบ</a> : null}
          {!d.url && <div style={{fontSize:11,color:C.muted}}>ไฟล์นี้มีชื่อไฟล์ในระบบแล้ว แต่ยังไม่เห็นตัวไฟล์จริง อาจเป็นใบสมัครเก่าก่อนเปิด R2</div>}
        </div>)}
      </div>
    </div>;
  }

  function SeerApplicantsPage(){
    // This screen is an approval queue, not an application history.  Keeping
    // approved/rejected rows here made a successful approval appear to return
    // to the same "waiting for approval" screen.
    const list = [...(seerApplicants || [])]
      .filter((a) => String(a?.status || "") === "รอตรวจสอบ")
      .sort((a,b)=>String(b.createdAt||"").localeCompare(String(a.createdAt||"")));
    const items = pageItems("seer_applicants", list, 20);
    return (
      <div style={{padding:16}}>
        <SeerApplicantDetailModal/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:10}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:C.goldLight}}>หมอดูสมัครใหม่รอตรวจสอบ</div>
            <div style={{fontSize:11,color:C.muted,marginTop:3}}>รอตรวจสอบ: {list.length} ใบสมัคร</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
            <button className="btn btn-outline btn-sm" onClick={()=>{const link="https://duangwannee-web.pages.dev/seer-register"; if(navigator.clipboard) navigator.clipboard.writeText(link); flash("คัดลอกลิงก์สมัครหมอดูแล้ว");}}>คัดลอกลิงก์สมัคร</button>
            <button className="btn btn-outline btn-sm" onClick={()=>{loadAdminD1Data();flash("โหลดใบสมัครจาก D1 แล้ว");}}>โหลดใหม่</button>
          </div>
        </div>
        <MonthInfo list={list}/>
        {items.length===0 && <div className="empty"><div className="e-ico">📝</div>ยังไม่มีใบสมัครหมอดูใหม่</div>}
        {items.map((a,i)=>(
          <div key={a.id} className="card" style={{padding:14,marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                {a.photoUrl && <img src={a.photoUrl} alt={a.name} style={{width:52,height:52,borderRadius:"50%",objectFit:"cover",border:`1px solid ${C.gold}66`}}/>}
                <div>
                  <div style={{fontWeight:800,color:C.text}}><RowNum index={(page("seer_applicants")-1)*20+i}/>{a.name}</div>
                  <div style={{fontSize:12,color:C.purpleLight,marginTop:2}}>{a.spec}</div>
                </div>
              </div>
              <span className={`badge ${a.status==="อนุมัติแล้ว"?'b-green':a.status==="ปฏิเสธ"?'b-red':'b-orange'}`}>{a.status}</span>
            </div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.8,marginTop:8}}>
              LINE: {a.lineDisplayName||a.phone}<br/>
              LINE userId: {a.lineUserId||"-"}<br/>
              เบอร์ติดต่อหมอดู <span style={{color:C.goldLight}}>(แอดมินเท่านั้น)</span>: {a.contactPhone || a.adminContactPhone || "-"}<br/>
              เอกสาร/ข้อมูล: {a.docs}<br/>
              ภาพหมอดู: {a.photoName || (a.photoUrl ? "แนบแล้ว" : "-")}<br/>
              หลักฐานแนบ: {(a.documents||[]).map(d=>d.name||d).join(" / ") || "-"}<br/>
              {a.createdAt ? `ส่งใบสมัคร: ${a.createdAt}` : ""}
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
                {a.photoUrl && <img src={a.photoUrl} alt="ภาพโปรไฟล์" style={{width:72,height:72,borderRadius:12,objectFit:"cover",border:`1px solid ${C.gold}66`}}/>}
                {(a.documents||[]).filter(d=>d.url).slice(0,3).map((d,di)=>
                  <div key={di} style={{width:72,height:72,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",background:C.surface}}>
                    {String(d.url||"").match(/\.(png|jpg|jpeg|webp|gif)(\?|$)/i) || String(d.content_type||"").startsWith("image/")
                      ? <img src={d.url} alt={d.name||"เอกสาร"} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      : <span style={{fontSize:22}}>📄</span>}
                  </div>
                )}
              </div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setViewApplicant(a)}>ดูรายละเอียด/เอกสาร</button>
              <button className="btn btn-outline btn-sm" onClick={()=>copyTextSafe(a.lineUserId || "", "คัดลอก LINE userId แล้ว")}>คัดลอก LINE userId</button>
              <button className="btn btn-outline btn-sm" disabled title="อนุมัติและผูก LINE OA ก่อน จึงส่ง LINE ได้">ส่ง LINE หลังอนุมัติ</button>
              {a.status==="รอตรวจสอบ" && <>
                <button className="btn btn-success btn-sm" onClick={()=>approveSeerApplicant(a)}>อนุมัติ</button>
                <button className="btn btn-danger btn-sm" onClick={()=>rejectSeerApplicant(a)}>ปฏิเสธ</button>
              </>}
            </div>
          </div>
        ))}
        <Pager k="seer_applicants" list={list} per={20}/>
      </div>
    );
  }

  function AddSeerPage() {
    if (!isSuperAdmin) return <div className="empty"><div className="e-ico">🔒</div>เฉพาะ Super Admin เท่านั้น</div>;
    const setPhoto = (file) => fileToDataUrl(file, (dataUrl)=>setNewSeerForm(p=>({...p,photoUrl:String(dataUrl),photoName:file.name})));
    return <div style={{padding:16}}><div className="card" style={{padding:14}}><div style={{fontWeight:800,color:C.goldLight,marginBottom:10}}>เพิ่มหมอดูโดยแอดมินสูงสุด</div>
      <div className="fgroup"><label className="flabel">ภาพหมอดู</label><input className="finput" type="file" accept="image/*" onChange={e=>{const f=e.currentTarget.files?.[0]; if(f) setPhoto(f);}}/>{newSeerForm.photoUrl&&<img src={newSeerForm.photoUrl} alt="ภาพหมอดู" style={{width:84,height:84,borderRadius:"50%",objectFit:"cover",border:`2px solid ${C.gold}66`,marginTop:8}}/>}</div>
      <div className="fgroup"><label className="flabel">ชื่อหมอดู</label><input className="finput" value={newSeerForm.name} onChange={e=>setNewSeerForm(p=>({...p,name:e.target.value}))}/></div>
      <div className="fgroup"><label className="flabel">ศาสตร์ที่ถนัด</label><input className="finput" value={newSeerForm.spec} onChange={e=>setNewSeerForm(p=>({...p,spec:e.target.value}))}/></div>
      <div className="fgroup"><label className="flabel">ราคาเริ่มต้น</label><input className="finput" value={newSeerForm.price} onChange={e=>setNewSeerForm(p=>({...p,price:e.target.value}))}/></div>
      <div className="fgroup"><label className="flabel">บัญชีรับเงิน/เอกสาร</label><input className="finput" value={newSeerForm.bank} onChange={e=>setNewSeerForm(p=>({...p,bank:e.target.value}))}/></div>
      <div className="fgroup"><label className="flabel">LINE ID หมอดู</label><input className="finput" value={newSeerForm.lineId||""} onChange={e=>setNewSeerForm(p=>({...p,lineId:e.target.value}))} placeholder="lineid หรือ @lineid"/></div>
      <div className="fgroup"><label className="flabel">LINE URL หมอดู</label><input className="finput" value={newSeerForm.lineUrl||""} onChange={e=>setNewSeerForm(p=>({...p,lineUrl:e.target.value}))} placeholder="https://line.me/ti/p/..."/></div>
      <div className="fgroup"><label className="flabel">คำแนะนำตัว</label><textarea className="finput" style={{height:100}} value={newSeerForm.bio} onChange={e=>setNewSeerForm(p=>({...p,bio:e.target.value}))}/></div>
      <div className="fgroup"><label className="flabel">บริการ</label><input className="finput" value={newSeerForm.services} onChange={e=>setNewSeerForm(p=>({...p,services:e.target.value}))}/></div>
      <button className="btn btn-gold" onClick={()=>{if(!newSeerForm.name.trim()){flash("กรุณากรอกชื่อหมอดู");return;}const applicant={id:"MANUAL"+Date.now(),name:newSeerForm.name,spec:newSeerForm.spec||"หมอดูออนไลน์",bio:newSeerForm.bio,photoUrl:newSeerForm.photoUrl,photoName:newSeerForm.photoName,profileLocked:false,lineId:newSeerForm.lineId||"",lineUrl:newSeerForm.lineUrl||"",docs:`ราคาเริ่มต้น: ${newSeerForm.price} / บัญชีรับเงิน: ${newSeerForm.bank} / LINE ID: ${newSeerForm.lineId || "-"}`,services:["live","tarot"],status:"อนุมัติแล้ว",lineUserId:"MANUAL_"+Date.now(),lineDisplayName:"เพิ่มโดยแอดมิน",createdAt:new Date().toLocaleString("th-TH")};const seer=applicantToApprovedSeer(applicant);const nextApproved=[seer,...readApprovedSeersStore()];writeApprovedSeersStore(nextApproved);setManagedSeers(prev=>uniqById([{...seer,status:"active",feedBoost:false,warning:0,joined:seer.approvedAt},...prev]));addAudit("เพิ่มหมอดูโดยแอดมิน",seer.name);setNewSeerForm({name:"",spec:"",price:"199",bio:"",bank:"",lineId:"",lineUrl:"",services:"ไพ่ทาโรต์, ดูดวงสด",photoUrl:"",photoName:""});flash("เพิ่มหมอดูแล้ว");setMenu("seers");}}>บันทึกเพิ่มหมอดู</button>
    </div></div>;
  }

  function EditSeerPage() {
    if (!isSuperAdmin) return <div className="empty"><div className="e-ico">🔒</div>เฉพาะ Super Admin เท่านั้น</div>;
    if (!editSeerForm) return <div className="empty"><div className="e-ico">🔮</div>ยังไม่ได้เลือกหมอดู</div>;

    const setPhoto = async (file) => {
      if (!file) return;
      try {
        const ownerId = String(editSeerForm.seerId || editSeerForm.id || "seer").replace(/[^a-zA-Z0-9_-]/g, "_");
        const uploaded = await uploadFileToR2(file, { ownerType:"seer", ownerId, fileRole:"profile" });
        const url = String(uploaded?.url || uploaded?.public_url || "").trim();
        if (!url) throw new Error("ระบบอัปโหลดไม่ได้ส่ง URL กลับมา");
        setEditSeerForm(p=>({...p,photoUrl:url,photoName:file.name}));
        flash("อัปโหลดรูปโปรไฟล์แล้ว กดบันทึกข้อมูลเพื่อใช้รูปนี้");
      } catch (err) {
        flash("อัปโหลดรูปโปรไฟล์ไม่สำเร็จ: " + (err.message || err));
      }
    };

    const save = async () => {
      const seerId = String(editSeerForm.seerId || editSeerForm.id || "").trim();
      if (!seerId) { flash("ไม่พบรหัสหมอดู"); return; }
      if (!String(editSeerForm.name || "").trim()) { flash("กรุณาใส่ชื่อหมอดู"); return; }
      const minPrice = Math.max(0, Number(editSeerForm.minPrice || 0));
      const updated = {
        ...editSeerForm,
        minPrice: minPrice || 199,
        priceText: String(editSeerForm.priceText || `เริ่มต้น ${minPrice || 199} บาท`),
        photoUrl: safePhotoForD1(editSeerForm.photoUrl || ""),
      };
      try {
        await apiPost("/api/seers", {
          action:"upsert",
          adminId:currentAdmin,
          seer_id:seerId,
          display_name:String(updated.name || "").trim(),
          profile_image_url:updated.photoUrl,
          specialties:String(updated.spec || "").trim(),
          short_bio:String(updated.bio || "").trim(),
          price_text:updated.priceText,
          rating:Number(updated.rating || 0),
          review_count:Number(updated.reviews || 0),
          status:updated.adminStatus || "active",
          line_url:String(updated.lineUrl || "").trim(),
          button_text:String(updated.buttonText || "ปรึกษาหมอดูคนนี้"),
          sort_order:Number(updated.sortOrder || 999),
          call_type:String(updated.callType || "line"),
          available_time:String(updated.availableTime || ""),
          commission_rate:Number(updated.commissionRate ?? 0.3),
          line_user_id:String(updated.lineUserId || updated.line_user_id || "").trim(),
          login_role:String(updated.loginRole || "seer"),
          profile_status:String(updated.profileStatus || "approved"),
          bank_name:String(updated.bankName || ""),
          bank_account_name:String(updated.bankAccountName || ""),
          bank_account_number:String(updated.bankAccountNumber || ""),
          created_at:updated.joined || updated.approvedAt || "",
          terms_accepted:String(updated.termsAccepted || "yes"),
          terms_accepted_at:String(updated.termsAcceptedAt || ""),
          terms_version:String(updated.termsVersion || "v1.0"),
          profile_locked:!!updated.profileLocked,
          source_application_id:String(updated.sourceApplicationId || ""),
        });
        setManagedSeers(prev=>prev.map(x=>String(x.seerId || x.id)===seerId?{...x,...updated}:x));
        await loadAdminD1Data();
        addAudit("แก้ไขข้อมูลหมอดู", updated.name);
        flash("บันทึกโปรไฟล์หมอดูเข้า D1 แล้ว");
        setMenu("seers");
      } catch (err) {
        flash("บันทึกโปรไฟล์หมอดูไม่สำเร็จ: " + (err.message || err));
      }
    };

    return <div style={{padding:16}}><div className="card" style={{padding:14}}><div style={{fontWeight:800,color:C.goldLight,marginBottom:10}}>จัดการโปรไฟล์หมอดู</div>
      <div className="warn-box" style={{marginBottom:12}}>ทุกช่องในหน้านี้บันทึกเข้า D1 จริงเมื่อกด “บันทึกโปรไฟล์” • LINE userId เปลี่ยนจากหน้าจัดการหมอดูช่อง “ผูก LINE userId” เพื่อป้องกันผูกผิดบัญชี</div>
      <div className="fgroup"><label className="flabel">ภาพหมอดู</label><input className="finput" type="file" accept="image/*" onChange={e=>{const f=e.currentTarget.files?.[0]; if(f) setPhoto(f);}}/>{editSeerForm.photoUrl&&<img src={editSeerForm.photoUrl} alt="ภาพหมอดู" style={{width:84,height:84,borderRadius:"50%",objectFit:"cover",border:`2px solid ${C.gold}66`,marginTop:8}}/>}</div>
      <div className="fgroup"><label className="flabel">ชื่อหมอดู</label><input className="finput" value={editSeerForm.name||""} onChange={e=>setEditSeerForm(p=>({...p,name:e.target.value}))}/></div>
      <div className="fgroup"><label className="flabel">ศาสตร์ที่ถนัด</label><input className="finput" value={editSeerForm.spec||""} onChange={e=>setEditSeerForm(p=>({...p,spec:e.target.value}))}/></div>
      <div className="fgroup"><label className="flabel">ราคาเริ่มต้น</label><input className="finput" type="number" min="0" value={editSeerForm.minPrice||""} onChange={e=>setEditSeerForm(p=>({...p,minPrice:e.target.value,priceText:`เริ่มต้น ${e.target.value || 0} บาท`}))}/></div>
      <div className="fgroup"><label className="flabel">LINE URL หมอดู</label><input className="finput" value={editSeerForm.lineUrl||""} onChange={e=>setEditSeerForm(p=>({...p,lineUrl:e.target.value}))} placeholder="https://line.me/ti/p/..."/></div>
      <div className="fgroup"><label className="flabel">คำแนะนำตัว</label><textarea className="finput" style={{height:120}} value={editSeerForm.bio||""} onChange={e=>setEditSeerForm(p=>({...p,bio:e.target.value}))}/></div>
      <div className="warn-box" style={{marginBottom:12}}>สถานะการล็อก: {editSeerForm.profileLocked?"ล็อกอยู่ หมอดูแก้ไขข้อมูลเองไม่ได้":"เปิดอยู่ หมอดูแก้ไขข้อมูลเองได้"}</div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><button className="btn btn-gold btn-sm" onClick={save}>บันทึกโปรไฟล์</button><button className="btn btn-outline btn-sm" onClick={()=>setMenu("seers")}>กลับ</button></div>
    </div></div>;
  }

  async function deleteSeerD1(s) {
    if (!confirm(`ลบหมอดู ${s.name} ออกจากระบบจริงใช่ไหม?`)) return;
    try {
      await apiPost("/api/seers", {action:"delete", seer_id:s.seerId || s.id, adminId:currentAdmin});
      await loadAdminD1Data();
      addAudit("ลบหมอดู", s.name);
      flash("ลบหมอดูออกจาก D1 แล้ว");
    } catch (err) {
      flash("ลบใน D1 ไม่สำเร็จ: " + (err.message || err));
    }
  }

  async function sendAdminMessageToSeer(s) {
    const message = prompt(`ส่งข้อความแจ้งเตือนไปยังหมอดู ${s.name}\nเช่น แจ้งเตือนเรื่องผิดกฎ / ขอให้แก้ไขพฤติกรรม`);
    if (!message) return;

    const localMessage = {
      id: "AMSG" + Date.now(),
      seerId: s.id || s.seerId || "",
      seerD1Id: s.seerId || "",
      lineUserId: s.lineUserId || "",
      seerName: s.name,
      message,
      read: false,
      adminId: currentAdmin,
      createdAt: new Date().toLocaleString("th-TH"),
    };
    writeSeerAdminMessagesStore([localMessage, ...readSeerAdminMessagesStore()]);

    try {
      await apiPost("/api/seer-messages", {
        action:"create",
        seer_id:s.seerId || s.id,
        alt_id:s.id || s.seerId || "",
        line_user_id:s.lineUserId || s.line_user_id || "",
        seer_name:s.name || "",
        message,
        admin_id:currentAdmin
      });
      addAudit("ส่งข้อความแจ้งเตือนหมอดู", s.name);
      flash("ส่งข้อความแล้ว และบันทึกเข้า D1 แล้ว");
    } catch (err) {
      addAudit("ส่งข้อความแจ้งเตือนหมอดู (บันทึกในเว็บ)", s.name);
      flash("บันทึกข้อความในเว็บแล้ว แต่ส่งเข้า D1/LINE ไม่สำเร็จ: " + (err.message || err));
    }
  }

  async function sendLineMessageToSeer(s) {
    const lineId = String(s.lineUserId || s.line_user_id || "").trim();
    if (!lineId) {
      flash("หมอดูคนนี้ยังไม่มี LINE userId จึงส่ง LINE ไม่ได้");
      return;
    }
    const message = prompt(`ส่งข้อความ LINE ไปยังหมอดู ${s.name || s.lineDisplayName || ""}\nข้อความนี้จะส่งผ่าน LINE OA จริง`);
    if (!message) return;

    try {
      const res = await apiPost("/api/line-push", {
        to: lineId,
        text: message,
        seer_id: s.seerId || s.id || s.applicationId || "",
        seer_name: s.name || s.displayName || s.lineDisplayName || "",
        admin_id: currentAdmin,
      });
      addAudit("ส่ง LINE ถึงหมอดู", `${s.name || s.lineDisplayName || lineId}: ${message.slice(0, 60)}`);
      flash(res?.sent ? "ส่งข้อความ LINE ถึงหมอดูแล้ว" : "ส่งคำขอแล้ว แต่ยังไม่ยืนยันสถานะ");
    } catch (err) {
      const msg = String(err?.message || err || "");
      if (msg.includes("403") || msg.includes("Forbidden")) {
        flash("ส่ง LINE ไม่สำเร็จ: หมอดูอาจยังไม่ได้เพิ่มเพื่อน LINE OA หรือ token ไม่ถูกต้อง");
      } else if (msg.includes("401") || msg.includes("Unauthorized")) {
        flash("ส่ง LINE ไม่สำเร็จ: LINE_CHANNEL_ACCESS_TOKEN ไม่ถูกต้องหรือหมดอายุ");
      } else {
        flash("ส่ง LINE ไม่สำเร็จ: ต้องใช้ userId จาก Webhook ของ OA จริง และต้องผูกกับโปรไฟล์หลังอนุมัติแล้วเท่านั้น / รายละเอียด: " + msg);
      }
    }
  }

  async function boostSeerD1(s) {
    try {
      await apiPost("/api/seers", {action:"boost", seer_id:s.seerId || s.id, adminId:currentAdmin});
      await loadAdminD1Data();
      addAudit("ดันหมอดูขึ้นฟีด", s.name);
      flash("ดันหมอดูขึ้นฟีดหน้าแรกแล้ว");
    } catch (err) {
      flash("ดันขึ้นฟีดไม่สำเร็จ: " + (err.message || err));
    }
  }

  async function addAdminReview(s) {
    const customer = window.prompt("ชื่อผู้รีวิว", "ลูกค้าจริง");
    if (customer === null) return;
    const cleanCustomer = String(customer || "").trim();
    if (!cleanCustomer) { flash("กรุณาใส่ชื่อผู้รีวิว"); return; }

    const ratingText = window.prompt("ให้ดาว 1-5", "5");
    if (ratingText === null) return;
    const rating = Math.max(1, Math.min(5, Number(String(ratingText || "5").replace(/[^0-9]/g, "")) || 5));

    const reviewText = window.prompt("ข้อความรีวิว", "แม่นมาก ให้คำแนะนำดี คุยสบาย");
    if (reviewText === null) return;
    const cleanText = String(reviewText || "").trim();
    if (!cleanText) { flash("กรุณาเขียนข้อความรีวิว"); return; }

    try {
      await apiPost("/api/seer-reviews", {
        seer_id: s.seerId || s.id,
        seer_name: s.name,
        customer_name: cleanCustomer,
        rating,
        review_text: cleanText,
        booking_id: "ADMIN",
        adminId: currentAdmin,
      });
      await loadAdminD1Data();
      addAudit("เพิ่มรีวิวหมอดู", s.name);
      flash("ส่งรีวิวแล้ว รีวิวจะไปขึ้นหน้าหมอดูให้ลูกค้าเห็น");
    } catch (err) {
      flash("เพิ่มรีวิวไม่สำเร็จ: " + (err.message || err));
    }
  }

  async function submitAdminReview() {
    return null;
  }

  function AdminReviewModal() { return null; }


  function SeersManagePage(){
    // Profiles are long-lived master data; a monthly cutoff hid every seer
    // approved in an earlier month and made the manage page look empty.
    const list = Array.isArray(managedSeers) ? managedSeers : [];
    const items=pageItems("seers",list,20);
    const persistManaged = (next) => { setManagedSeers(next); };
    const updateSeerLevel = async (seer, level) => {
      if (!isSuperAdmin) return;
      const seerId = String(seer.seerId || seer.id || "").trim();
      const lineId = String(seer.lineUserId || seer.line_user_id || "").trim();
      const nameKey = String(seer.name || seer.displayName || seer.display_name || "").trim();
      if (!seerId) { flash("ไม่พบรหัสหมอดูใน D1"); return; }
      try {
        await apiPost("/api/seer-levels", {
          seer_id: seerId,
          alt_id: lineId,
          line_user_id: lineId,
          display_name: nameKey,
          name_key: nameKey ? `NAME:${nameKey}` : "",
          level,
          admin_id: currentAdmin,
        });
        await Promise.all([loadAdminD1Data(), (async()=> {
          try {
            const data = await apiGet("/api/seer-levels");
            setSeerLevelsAdmin(data.levels || {});
          } catch {}
        })()]);
        addAudit("ปรับระดับหมอดู", `${seer.name} → ${getSeerLevelLabel(level)}`);
        flash(`ปรับระดับ ${seer.name} เป็น ${getSeerLevelLabel(level)} แล้ว`);
      } catch (err) {
        flash(`บันทึกระดับหมอดูไม่สำเร็จ: ${err.message || err}`);
      }
    };

    const getSeerLineBindKey = (seer) => {
      const seerId = String(seer?.seerId || seer?.id || "").trim();
      const line = String(seer?.lineUserId || seer?.line_user_id || "").trim();
      return `${seerId}::${line}`;
    };

  const bindLineUserToSeer = async (seer) => {
      if (!isSuperAdmin) return;
      const seerId = String(seer.seerId || seer.id || "").trim();
      const currentLine = String(seer.lineUserId || seer.line_user_id || "").trim();
      const bindKey = getSeerLineBindKey(seer);
      const typed = String(seerLineBindInput[bindKey] ?? currentLine ?? "").trim();

      if (!typed) {
        flash("กรุณาใส่ LINE userId ก่อน");
        return;
      }
      if (!seerId) {
        flash("ไม่พบรหัสหมอดู");
        return;
      }

      try {
        await apiPost("/api/seer-profile", {
          action: "bind",
          seer_id: seerId,
          line_user_id: typed,
          admin_id: currentAdmin,
          note: `bind from admin page: ${seer.name || ""}`,
        });

        const nextSeers = managedSeers.map(x => {
          const xId = String(x.seerId || x.id || "");
          return xId === seerId ? {...x, lineUserId: typed, line_user_id: typed} : x;
        });
        persistManaged(nextSeers);
        setSeerLineBindInput(prev => {
          const next = {...prev};
          delete next[bindKey];
          next[`${seerId}::${typed}`] = typed;
          return next;
        });
        addAudit("ผูก LINE userId กับหมอดู", `${seer.name} → ${typed}`);
        flash(`ผูก LINE userId ให้ ${seer.name} แล้ว`);
      } catch (err) {
        flash("ผูก LINE userId ไม่สำเร็จ: " + (err.message || err));
      }
    };

    return <div style={{padding:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8}}>
        <div><div style={{fontSize:14,fontWeight:800,color:C.goldLight}}>จัดการหมอดู</div><div style={{fontSize:11,color:C.muted,margin:"3px 0 10px"}}>โปรไฟล์หมอดูทั้งหมด {list.length} คน • แสดงทุกโปรไฟล์ ไม่ตัดตามเดือน</div></div>
        {isSuperAdmin&&<div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
          <button className="btn btn-outline btn-sm" onClick={()=>{setSeerLineBindInput({});loadAdminD1Data();}}>รีโหลดใบสมัคร/หมอดู</button>
          <button className="btn btn-purple btn-sm" onClick={()=>setMenu("seer_add")}>+ เพิ่มหมอดูเอง</button>
        </div>}
      </div>
      {seerApplicants.filter(a=>a.status==="รอตรวจสอบ").length > 0 && (
        <div className="card" style={{padding:14,marginBottom:14,borderColor:`${C.orange}66`,background:`${C.orange}12`}}>
          <div style={{fontSize:14,fontWeight:900,color:C.goldLight}}>มีใบสมัครหมอดูรอตรวจ {seerApplicants.filter(a=>a.status==="รอตรวจสอบ").length} รายการ</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:4}}>หน้านี้คือจัดการหมอดูที่อนุมัติแล้ว ส่วนใบสมัครใหม่ต้องเปิดเมนู “ใบสมัคร”</div>
          <button className="btn btn-gold btn-sm" style={{marginTop:10}} onClick={()=>{setMenu("seer_applicants");setPage("seer_applicants",1);loadAdminD1Data();}}>เปิดใบสมัครหมอดู</button>
        </div>
      )}
      {isSuperAdmin && (
        <div className="card" style={{padding:14,marginBottom:14,borderColor:`${C.gold}55`,background:`${C.gold}10`}}>
          <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:6}}>ลิงก์ LIFF สมัครหมอดูแบบใช้ครั้งเดียว</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:10}}>
            แอดมินออกลิงก์ให้หมอดูสมัครได้เป็นรายคน ลิงก์นี้จะถูก mark เป็นใช้แล้วเมื่อแอดมินอนุมัติใบสมัครที่มาจากลิงก์นี้
          </div>
          <button className="btn btn-gold btn-sm" onClick={createAdminOneTimeSeerRegisterLink}>สร้างลิงก์สมัครหมอดู 1 ครั้ง</button>
          {adminOneTimeSeerInviteLink && (
            <div style={{marginTop:10,padding:10,border:`1px solid ${C.gold}66`,borderRadius:12,background:"rgba(12,7,38,.72)"}}>
              <div style={{fontSize:11,fontWeight:900,color:C.goldLight,marginBottom:6}}>ลิงก์สมัครที่สร้างล่าสุด</div>
              <div style={{fontSize:11,color:C.text,wordBreak:"break-all",lineHeight:1.55}}>{adminOneTimeSeerInviteLink}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:9}}>
                <button className="btn btn-outline btn-sm" onClick={()=>copyTextSafe(adminOneTimeSeerInviteLink, "คัดลอกลิงก์สมัครหมอดูแล้ว")}>คัดลอกลิงก์</button>
                <button className="btn btn-purple btn-sm" onClick={()=>window.open(adminOneTimeSeerInviteLink, "_blank", "noopener,noreferrer")}>เปิดลิงก์ทดสอบ</button>
              </div>
            </div>
          )}
          <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>
            ส่งลิงก์ LIFF นี้ให้หมอดูคนเดียวเท่านั้น • LINE จะจัดการหน้าอนุญาตแอปและ Add friend option เอง • ลิงก์มีตัวกันแคช
          </div>
        </div>
      )}
      {isSuperAdmin && (
        <div className="card" style={{padding:14,marginBottom:14,borderColor:`${C.purple}55`,background:`${C.purple}10`}}>
          <div style={{fontSize:14,fontWeight:900,color:C.goldLight,marginBottom:6}}>LINE OA userId จริงจาก Webhook</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:10}}>
            ให้หมอดูเพิ่มเพื่อนและทัก LINE OA ก่อน แล้วกดโหลด ระบบจะแสดง userId จริงของ Messaging API ให้ทดสอบ “เช็ก profile/ส่ง LINE ทดสอบ” ตรงจาก contact ก่อน แล้วค่อยผูกกับโปรไฟล์หมอดู
          </div>
          <button className="btn btn-purple btn-sm" onClick={loadLineContactsAdmin}>โหลดผู้ใช้ LINE OA ล่าสุด</button>
          {lineContactsAdmin.length > 0 && (
            <div style={{marginTop:10,display:"grid",gap:8}}>
              {lineContactsAdmin.slice(0,8).map(c=>(
                <div key={c.userId || c.user_id} style={{padding:10,border:`1px solid ${C.border}`,borderRadius:12,background:C.surface}}>
                  <div style={{fontSize:12,fontWeight:800,color:C.text}}>{c.displayName || c.display_name || "LINE user"}</div>
                  <div style={{fontSize:11,color:C.muted,wordBreak:"break-all"}}>{c.userId || c.user_id}</div>
                  <div style={{fontSize:10,color:C.muted}}>ล่าสุด: {c.lastSeenAt || c.last_seen_at || "-"}</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>copyTextSafe(c.userId || c.user_id || "", "คัดลอก LINE userId จริงแล้ว")}>คัดลอก userId นี้</button>
                    <button className="btn btn-gold btn-sm" onClick={()=>checkLineContactProfile(c)}>เช็ก profile</button>
                    <button className="btn btn-purple btn-sm" onClick={()=>testSendLineContact(c)}>ส่ง LINE ทดสอบ</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div id="seer-profile-list-v3940" style={{scrollMarginTop:72}}>
      <div className="sec-hd" style={{padding:"8px 0 10px"}}>โปรไฟล์หมอดูที่จัดการได้</div>
      {items.length===0 ? <div className="empty"><div className="e-ico">🔮</div>ยังไม่พบโปรไฟล์หมอดูใน D1<button className="btn btn-outline btn-sm" style={{marginTop:10}} onClick={()=>loadAdminD1Data()}>โหลดโปรไฟล์ใหม่</button></div> : items.map((s,i)=><div key={s.id} className="card" style={{padding:14,marginBottom:12,borderColor:s.feedBoost?C.gold:C.border}}>
        <div style={{display:"flex",gap:12,alignItems:"center"}}>
          <SeerAvatar seer={s} className="seer-ava" style={{width:58,height:58,fontSize:24}}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:800,color:C.text}}><RowNum index={(page("seers")-1)*20+i}/>{s.name} {s.feedBoost&&<span style={{color:C.goldLight,fontSize:11}}>ดันขึ้นฟีด</span>}</div>
            <div style={{fontSize:12,color:C.purpleLight}}>{s.spec}</div>
            <div style={{fontSize:11,color:C.muted}}>★{s.rating} • {s.reviews} รีวิว</div>
            <div style={{fontSize:11,color:C.goldLight,marginTop:3}}>ระดับภายใน: {getSeerLevelLabel(getSeerPlatformLevel(s, seerLevelsAdmin))}</div>
            {isSuperAdmin && <div style={{fontSize:11,color:C.muted,marginTop:3}}>เบอร์ติดต่อแอดมินเท่านั้น: {s.contactPhone || s.adminContactPhone || "-"}</div>}
            <div style={{fontSize:11,color:s.profileLocked?C.red:C.green,marginTop:3}}>{s.profileLocked?"🔒 ล็อกข้อมูล หมอดูแก้เองไม่ได้":"🔓 เปิดให้หมอดูแก้ข้อมูลเองได้"}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:4,lineHeight:1.6}}>ราคา/บริการ: {s.priceText || "ยังไม่ได้ตั้งราคา"} • {s.spec || "หมอดูออนไลน์"}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:3,lineHeight:1.6}}>ตารางเวลา: {Number(s.scheduleSummary?.open_slot_count || 0) > 0 ? `เปิด ${Number(s.scheduleSummary.open_slot_count).toLocaleString()} ช่วง • ถัดไป ${s.scheduleSummary.next_available_date || "-"}` : (s.availableTime || "ยังไม่ได้เปิดช่วงเวลา")}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:3,lineHeight:1.6}}>ผู้แนะนำ: {s.referralStatus === "active" ? `${s.referrerName || s.referrerSeerId || "มีผู้แนะนำ"} • คอม ${Number(s.referralCommissionPercent || 3)}%` : s.referralStatus === "none" ? "ไม่มี" : "ปิด/ไม่พร้อมใช้งาน"}</div>
            <div style={{fontSize:11,color:C.green,marginTop:3,lineHeight:1.6}}>รายได้สะสม: ฿{Number(s.incomeSummary?.seer_income_amount || 0).toLocaleString("th-TH")} • งานปิดแล้ว {Number(s.incomeSummary?.completed_count || 0).toLocaleString("th-TH")} งาน</div>
          </div>
          <span className={`badge ${s.status==="active"||s.status==="online"?'b-green':s.status==="banned"?'b-red':'b-orange'}`}>{s.status==="active"||s.status==="online"?'ใช้งาน':s.status==="banned"?'แบน':'พักงาน'}</span>
        </div>
        {isSuperAdmin && (
          <div style={{marginTop:12,padding:10,border:`1px solid ${C.gold}33`,borderRadius:12,background:`${C.gold}09`}}>
            <label className="flabel">ระดับหมอดูภายในระบบ</label>
            <select
              className="finput"
              value={getSeerPlatformLevel(s, seerLevelsAdmin)}
              onChange={e=>updateSeerLevel(s, e.target.value)}
            >
              <option value="standard">หมอดูทั่วไป</option>
              <option value="platinum">แพลตินั่ม (แนะนำได้ไม่จำกัด)</option>
            </select>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:6}}>
              ระดับนี้ไม่แสดงให้ลูกค้าเห็น • แพลตินั่มแนะนำหมอดูได้ไม่จำกัด • ผู้แนะนำได้รับค่าคอม 3% จากยอดชำระของงานที่หมอดูที่แนะนำกดปิดสำเร็จ
            </div>

            <div style={{marginTop:12,padding:10,border:`1px dashed ${C.purple}66`,borderRadius:12,background:`${C.purple}10`}}>
              <label className="flabel">ล็อกโปรไฟล์ด้วย LINE userId</label>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.7,marginBottom:6}}>
                รหัสหมอดู: <b style={{color:C.goldLight}}>{s.seerId || s.id}</b><br/>
                LINE userId เดิม: <b style={{color:C.goldLight}}>{s.lineUserId || s.line_user_id || "ยังไม่มี"}</b>
              </div>
              <input
                className="finput"
                placeholder="ใส่ LINE userId ของหมอดู เช่น Uxxxxxxxx"
                value={seerLineBindInput[getSeerLineBindKey(s)] ?? String(s.lineUserId || s.line_user_id || "")}
                onChange={e=>setSeerLineBindInput(prev=>({...prev,[getSeerLineBindKey(s)]:e.target.value}))}
              />
              <button className="btn btn-purple btn-sm" style={{marginTop:8}} onClick={()=>bindLineUserToSeer(s)}>
                ผูก LINE userId กับโปรไฟล์นี้
              </button>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:6}}>
                ช่องนี้ยึดตาม LINE userId ของโปรไฟล์/ใบสมัครนี้โดยตรง ไม่ใช้ค่า LINE ของเครื่องแอดมิน • 1 LINE userId ควรผูกได้ 1 โปรไฟล์เท่านั้น
              </div>
            </div>
          </div>
        )}
        <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
          <button className="btn btn-gold btn-sm" onClick={()=>{setEditSeerForm(s);setMenu("seer_edit");}}>จัดการโปรไฟล์</button>
          <button className="btn btn-outline btn-sm" onClick={async()=>{try{await apiPost("/api/seers",{action:"lock",seer_id:s.seerId||s.id,locked:!s.profileLocked,adminId:currentAdmin});await loadAdminD1Data();flash(s.profileLocked?"ปลดล็อกแล้ว":"ล็อกข้อมูลแล้ว");}catch(err){flash("บันทึกสถานะล็อกใน D1 ไม่สำเร็จ: "+(err.message||err));}}}>{s.profileLocked?"ปลดล็อกให้หมอแก้":"ล็อกไม่ให้หมอแก้"}</button>
          <button className="btn btn-purple btn-sm" onClick={()=>boostSeerD1(s)}>ดันขึ้นฟีด</button>
          <button className="btn btn-outline btn-sm" onClick={()=>sendAdminMessageToSeer(s)}>ส่งข้อความถึงหมอ</button>
          <button className="btn btn-purple btn-sm" onClick={()=>sendLineMessageToSeer(s)}>ส่ง LINE ถึงหมอ (หลังผูก OA)</button>
          <button className="btn btn-outline btn-sm" onClick={()=>addAdminReview(s)}>+ รีวิว</button>
          <button className="btn btn-danger btn-sm" onClick={()=>{const next=managedSeers.map(x=>x.id===s.id?{...x,status:x.status==="banned"?"active":"banned"}:x);persistManaged(next);addAudit(s.status==="banned"?"ปลดแบนหมอดู":"แบนหมอดู",s.name);}}>{s.status==="banned"?"ปลดแบน":"แบน"}</button>
          <button className="btn btn-danger btn-sm" onClick={()=>deleteSeerD1(s)}>ลบถาวร</button>
        </div>
        <div style={{fontSize:11,color:C.muted,lineHeight:1.7,marginTop:10}}>
          ปุ่ม “ดันขึ้นฟีด” จะปรับลำดับหมอให้ขึ้นหน้าแรกของทำเนียบ/จองคิวสด เหมือนโปรโมท
        </div>
      </div>)}
      <Pager k="seers" list={list} per={20}/>
      </div>
    </div>;
  }

  async function saveArticle() {
    if (!articleF.title.trim()) { flash("กรุณาใส่ชื่อบทความ"); return; }
    if (articleF.status === "ตั้งเวลา" && !articleF.scheduledAt) { flash("กรุณาเลือกวันเวลาเผยแพร่"); return; }
    if (articleUploading) { flash("รอให้อัปโหลดรูปเสร็จก่อน"); return; }
    if (String(articleF.imageUrl || "").startsWith("blob:")) { flash("รูปยังอัปโหลดไม่เสร็จ กรุณารอสักครู่หรือใส่ลิงก์รูปแทน"); return; }
    const a = {
      id: "ART" + (articles.length + 1).toString().padStart(3, "0"),
      title: articleF.title.trim(),
      body: articleF.body || "",
      status: articleF.status,
      author: currentAdmin,
      imageUrl: articleF.imageUrl || "",
      linkUrl: articleF.linkUrl || "",
      linkText: articleF.linkText || "อ่านต่อ",
      scheduledAt: articleF.scheduledAt ? new Date(articleF.scheduledAt).toISOString() : "",
      createdAt: new Date().toLocaleString("th-TH"),
    };
    setArticles(prev => [a, ...prev]);
    try {
      const res = await apiPost("/api/articles", { action:"create", title:a.title, body:a.body, status:a.status, scheduled_at:a.scheduledAt, author:currentAdmin, image_url:a.imageUrl, external_url:a.linkUrl, link_text:a.linkText });
      if (res?.article?.article_id) {
        setArticles(prev => prev.map(x => x.id === a.id ? {...x, id:res.article.article_id, articleId:res.article.article_id, status:res.article.status === "published" ? "เผยแพร่" : res.article.status === "scheduled" ? "ตั้งเวลา" : res.article.status === "unpublished" ? "ไม่เผยแพร่" : "แบบร่าง", scheduledAt:res.article.scheduled_at || a.scheduledAt} : x));
      }
      if (a.status === "เผยแพร่") flash(`เผยแพร่ข่าวแล้ว และแจ้งเตือนสมาชิกแล้ว ${Number(res?.notifications_created || 0).toLocaleString()} คน`);
      else if (a.status === "ตั้งเวลา") flash("ตั้งเวลาเผยแพร่ข่าวแล้ว");
      else flash("บันทึกข่าวแบบร่างแล้ว");
      loadAdminD1Data();
    } catch (err) {
      flash("บันทึกข่าวใน D1 ไม่สำเร็จ แต่บันทึกไว้ในหน้านี้แล้ว: " + (err.message || err));
    }
    setArticleF({title:"", body:"", status:"แบบร่าง", scheduledAt:"", imageUrl:"", imageName:"", linkUrl:"", linkText:"อ่านข่าวต่อ"});
    addAudit(a.status === "เผยแพร่" ? "เผยแพร่ข่าวและแจ้งเตือนผู้ใช้" : a.status === "ตั้งเวลา" ? "ตั้งเวลาเผยแพร่ข่าว" : "เพิ่มข่าว", a.title);
  }


  async function unpublishArticle(a) {
    const id = a.articleId || a.article_id || a.id;
    if (!id) return;
    if (!confirm(`ปิดการเผยแพร่ข่าวนี้ใช่ไหม?
${a.title}

ข่าวจะยังอยู่ในระบบ แต่ลูกค้าจะไม่เห็นข่าวนี้`)) return;
    setArticles(prev => prev.map(x => (x.articleId || x.article_id || x.id) === id ? {...x,status:"ไม่เผยแพร่",unpublishedAt:new Date().toISOString()} : x));
    try {
      await apiPost("/api/articles", { action:"unpublish", article_id:id, author:currentAdmin });
      flash("ปิดการเผยแพร่ข่าวแล้ว");
      loadAdminD1Data();
    } catch (err) {
      flash("ปิดการเผยแพร่ใน D1 ไม่สำเร็จ: " + (err.message || err));
    }
    addAudit("ปิดการเผยแพร่ข่าว", a.title);
  }

  async function publishArticle(a) {
    const id = a.articleId || a.article_id || a.id;
    if (!id) return;
    if (!confirm(`เผยแพร่ข่าวนี้อีกครั้งใช่ไหม?
${a.title}`)) return;
    setArticles(prev => prev.map(x => (x.articleId || x.article_id || x.id) === id ? {...x,status:"เผยแพร่",publishedAt:new Date().toISOString(),unpublishedAt:""} : x));
    try {
      const res = await apiPost("/api/articles", { action:"publish", article_id:id, author:currentAdmin });
      flash(`เผยแพร่ข่าวแล้ว และแจ้งเตือนสมาชิกแล้ว ${Number(res?.notifications_created || 0).toLocaleString()} คน`);
      loadAdminD1Data();
    } catch (err) {
      flash("เผยแพร่ใน D1 ไม่สำเร็จ: " + (err.message || err));
    }
    addAudit("เผยแพร่ข่าว", a.title);
  }

  async function deleteArticle(a) {
    const id = a.articleId || a.article_id || a.id;
    if (!confirm(`ลบข่าวนี้ใช่ไหม?\n${a.title}`)) return;
    setArticles(prev => prev.filter(x => (x.articleId || x.article_id || x.id) !== id));
    try {
      await apiPost("/api/articles", { action:"delete", article_id:id, author:currentAdmin });
      flash("ลบข่าวแล้ว");
      loadAdminD1Data();
    } catch (err) {
      flash("ลบข่าวใน D1 ไม่สำเร็จ: " + (err.message || err));
    }
    addAudit("ลบข่าว", a.title);
  }

  function ArticlesPage(){
    const list=[...(articles || [])].sort((a,b)=>String(b.createdAt || b.created_at || b.publishedAt || "").localeCompare(String(a.createdAt || a.created_at || a.publishedAt || "")));
    const items=pageItems("articles",list,20);
    return <div style={{padding:16}}>
      <div className="card" style={{padding:14,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:10}}>
          <div style={{fontWeight:800,color:C.goldLight}}>เพิ่มข่าว / เขียนบทความ / จัดการบทความเก่า</div>
          <span className="badge b-purple">ความเคลื่อนไหวประจำวัน</span>
        </div>
        <input className="finput" placeholder="หัวข้อข่าว / ชื่อบทความ" value={articleF.title} onChange={e=>setArticleF(p=>({...p,title:e.target.value}))} style={{marginBottom:8}}/>
        <textarea className="finput" placeholder="เนื้อหาข่าว / บทความ / ประกาศ / ข้อความแจ้งเตือน" value={articleF.body} onChange={e=>setArticleF(p=>({...p,body:e.target.value}))} style={{marginBottom:8,minHeight:130}}/>
        <div className="fgroup">
          <label className="flabel">ภาพประกอบข่าว</label>
          <input
            className="finput"
            type="file"
            accept="image/*"
            disabled={articleUploading}
            onChange={async e=>{
              const f = e.currentTarget.files?.[0];
              if (!f) return;
              if (!String(f.type || "").startsWith("image/")) { flash("กรุณาเลือกรูปภาพเท่านั้น"); return; }
              if (f.size > 8 * 1024 * 1024) { flash("รูปใหญ่เกินไป กรุณาเลือกรูปไม่เกิน 8MB"); return; }
              const localPreview = URL.createObjectURL(f);
              setArticleF(p=>({...p,imageUrl:localPreview,imageName:f.name}));
              setArticleUploading(true);
              try {
                const up = await uploadFileToR2(f, { ownerType:"article", ownerId:"draft", fileRole:"cover" });
                setArticleF(p=>({...p,imageUrl:up.url || up.public_url || p.imageUrl,imageName:f.name}));
                flash("อัปโหลดรูปข่าวเข้า R2 แล้ว");
              } catch (err) {
                flash("อัปโหลด R2 ไม่สำเร็จ ใช้ลิงก์รูป/ลองใหม่: " + (err.message || err));
              } finally {
                setArticleUploading(false);
              }
            }}
            style={{marginBottom:8}}
          />
          {articleUploading && <div className="badge b-purple" style={{marginBottom:8}}>กำลังอัปโหลดรูปเข้า R2...</div>}
          <input className="finput" placeholder="หรือวางลิงก์รูปภาพ เช่น https://...jpg / https://...png" value={articleF.imageUrl && String(articleF.imageUrl).startsWith('blob:') ? (articleF.imageName || 'กำลังอัปโหลดรูป...') : articleF.imageUrl} onChange={e=>setArticleF(p=>({...p,imageUrl:e.target.value,imageName:''}))}/>
          <div style={{fontSize:10,color:C.dim,marginTop:4}}>อัปโหลดรูปจากเครื่องได้แล้ว รูปจะเก็บใน Cloudflare R2 และ D1 จะเก็บเป็นลิงก์ /api/files เท่านั้น</div>
        </div>
        {articleF.imageUrl && <img src={articleF.imageUrl} alt="preview" onError={e=>{e.currentTarget.style.display='none'}} style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:12,marginBottom:8,border:`1px solid ${C.border}`}}/>}
        <div className="fgroup">
          <label className="flabel">ลิงก์ในข่าว</label>
          <input className="finput" placeholder="วางลิงก์ เช่น LINE OA / โปรโมชัน / หน้าเว็บอื่น" value={articleF.linkUrl} onChange={e=>setArticleF(p=>({...p,linkUrl:e.target.value}))}/>
        </div>
        <input className="finput" placeholder="ข้อความบนปุ่มลิงก์ เช่น อ่านข่าวต่อ / ดูโปรโมชัน / ติดต่อหมอดู" value={articleF.linkText} onChange={e=>setArticleF(p=>({...p,linkText:e.target.value}))} style={{marginBottom:8}}/>
        <select className="finput" value={articleF.status} onChange={e=>setArticleF(p=>({...p,status:e.target.value}))} style={{marginBottom:8}}><option>แบบร่าง</option><option>เผยแพร่</option><option>ตั้งเวลา</option></select>
        {articleF.status === "ตั้งเวลา" && <div className="fgroup" style={{marginBottom:8}}>
          <label className="flabel">ตั้งเวลาเผยแพร่</label>
          <input className="finput" type="datetime-local" value={articleF.scheduledAt || ""} onChange={e=>setArticleF(p=>({...p,scheduledAt:e.target.value}))}/>
          <div style={{fontSize:10,color:C.dim,marginTop:4}}>ถึงเวลาที่ตั้งไว้ ระบบจะเผยแพร่ข่าวและสร้างแจ้งเตือนให้ผู้ใช้เมื่อมีการเปิดระบบ/เรียก API หลังเวลานั้น</div>
        </div>}
        <button className="btn btn-gold" disabled={articleUploading} onClick={saveArticle}>{articleUploading?"กำลังอัปโหลดรูป...":"+ เพิ่มข่าว / บันทึกบทความ"}</button>
        <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>ถ้าเลือก “เผยแพร่” ระบบจะแจ้งเตือนทันที ถ้าเลือก “ตั้งเวลา” ระบบจะรอถึงเวลาที่กำหนดก่อนแสดงข่าว</div>
      </div>
      <MonthInfo list={list}/>
      {items.map((a,i)=><div key={a.id} className="brow">
        <div style={{display:"flex",justifyContent:"space-between",gap:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div className="brow-name"><RowNum index={(page("articles")-1)*20+i}/>{a.title}</div>
            <div style={{fontSize:11,color:C.muted}}>ผู้เขียน: {a.author} • {a.createdAt ? new Date(a.createdAt).toLocaleString("th-TH") : ""}</div>
            {a.scheduledAt && <div style={{fontSize:11,color:C.goldLight,marginTop:3}}>ตั้งเวลาเผยแพร่: {new Date(a.scheduledAt).toLocaleString("th-TH")}</div>}
            {a.unpublishedAt && <div style={{fontSize:11,color:C.orange,marginTop:3}}>ปิดเผยแพร่: {new Date(a.unpublishedAt).toLocaleString("th-TH")}</div>}
            {a.imageUrl&&<img src={a.imageUrl} alt="" onError={e=>{e.currentTarget.style.display='none'}} style={{width:"100%",maxHeight:120,objectFit:"cover",borderRadius:10,marginTop:8,border:`1px solid ${C.border}`}}/>}
            {a.body&&<div style={{fontSize:12,color:C.muted,lineHeight:1.6,marginTop:6,whiteSpace:"pre-wrap"}}>{a.body}</div>}
            {a.linkUrl&&<div style={{fontSize:11,color:C.goldLight,marginTop:6,wordBreak:"break-all"}}>ลิงก์: {a.linkUrl}</div>}
            {a.linkText&&<div style={{fontSize:11,color:C.purpleLight,marginTop:2}}>ปุ่ม: {a.linkText}</div>}
          </div>
          <span className={`badge ${a.status==="เผยแพร่"?'b-green':a.status==="ตั้งเวลา"?'b-purple':a.status==="ไม่เผยแพร่"?'b-red':'b-orange'}`}>{a.status}</span>
        </div>
        <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
          {a.status === "เผยแพร่" && (a.articleId || a.id) && <button className="btn btn-outline btn-sm" onClick={()=>window.open(`/news/${encodeURIComponent(a.articleId || a.id)}`, "_blank", "noopener,noreferrer")}>ดูหน้าอ่านข่าว</button>}
          {a.status === "เผยแพร่" ? <button className="btn btn-outline btn-sm" onClick={()=>unpublishArticle(a)}>ไม่เผยแพร่</button> : <button className="btn btn-success btn-sm" onClick={()=>publishArticle(a)}>เผยแพร่</button>}
          <button className="btn btn-danger btn-sm" onClick={()=>deleteArticle(a)}>ลบข่าว</button>
        </div>
      </div>)}
      <Pager k="articles" list={list} per={20}/>
    </div>
  }

    function SeerTermsSettingsPageV150() {
    if (!isSuperAdmin) return <div className="empty"><div className="e-ico">🔒</div>เฉพาะ Super Admin เท่านั้น</div>;
    const markEditingV151 = () => { try { window.__DW_ADMIN_TERMS_EDITING_V151__ = true; } catch {} };
    const unmarkEditingV151 = () => { try { setTimeout(()=>{ window.__DW_ADMIN_TERMS_EDITING_V151__ = false; }, 600); } catch {} };
    const setField = (k, v) => {
      markEditingV151();
      setSeerTermsAdminV150(prev => ({...prev, [k]: v}));
    };
    const resetDefaults = () => {
      const cfg = writeSeerTermsConfigV150(DEFAULT_SEER_TERMS_CONFIG_V150);
      setSeerTermsAdminV150(cfg);
      flash("รีเซ็ตข้อกำหนดเป็นค่าเริ่มต้นแล้ว");
    };
    const save = async () => {
      const cfg = writeSeerTermsConfigV150({
        ...seerTermsAdminV150,
        commissionPercent: Number(seerTermsAdminV150.commissionPercent || 35) || 35,
        updatedAt: new Date().toISOString(),
      });
      setSeerTermsAdminV150(cfg);
      try {
        const res = await apiPost("/api/seer-terms", { action:"save", terms: cfg, admin_id: currentAdmin });
        const saved = writeSeerTermsConfigV150(res?.terms || cfg);
        setSeerTermsAdminV150(saved);
        addAudit("แก้ไขข้อกำหนดหมอดู", "seer terms");
        flash("บันทึกข้อกำหนดหมอดูเข้า D1 แล้ว");
      } catch (err) {
        flash("บันทึกในเครื่องแล้ว แต่เข้า D1 ไม่สำเร็จ: " + (err.message || err));
      }
    };
    return <div style={{padding:16}}>
      <div className="card card-gold" style={{padding:16,marginBottom:14}}>
        <div style={{fontSize:18,fontWeight:900,color:C.goldLight}}>ข้อกำหนดหมอดู</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:4}}>
          แก้ได้เฉพาะ Super Admin ข้อความนี้จะแสดงก่อนหมอดูเข้าหน้าสมัคร
        </div>
      </div>
      <div className="card" style={{padding:16}}>
        <div className="fgroup"><label className="flabel">หัวข้อหน้า</label><input className="finput" onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.title || ""} onChange={e=>setField("title", e.target.value)}/></div>
        <div className="fgroup"><label className="flabel">คำอธิบายสั้น</label><textarea className="finput" style={{height:80}} onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.intro || ""} onChange={e=>setField("intro", e.target.value)}/></div>
        <div className="fgroup"><label className="flabel">รายละเอียดข้อกำหนด</label><textarea className="finput" style={{height:260}} onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.body || ""} onChange={e=>setField("body", e.target.value)}/></div>
        <div className="fgroup"><label className="flabel">ข้อความเตือนสีแดงในหน้าข้อกำหนด</label><textarea className="finput" style={{height:90}} onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.warning || ""} onChange={e=>setField("warning", e.target.value)}/></div>
        <div className="fgroup"><label className="flabel">ข้อความสีแดงใต้ช่องราคาเริ่มต้น</label><input className="finput" onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.priceWarning || ""} onChange={e=>setField("priceWarning", e.target.value)}/></div>
        <div className="fgroup"><label className="flabel">ข้อความสีแดงใต้ช่องแนบเอกสาร</label><input className="finput" onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.documentWarning || ""} onChange={e=>setField("documentWarning", e.target.value)}/></div>
        <div className="fgroup"><label className="flabel">% หักแพลตฟอร์มที่แสดงในข้อกำหนด</label><input className="finput" type="number" min="0" max="60" onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.commissionPercent || 35} onChange={e=>setField("commissionPercent", e.target.value)}/></div>
        <div className="fgroup"><label className="flabel">ข้อความปุ่มยอมรับ</label><input className="finput" onFocus={markEditingV151} onBlur={unmarkEditingV151} value={seerTermsAdminV150.buttonText || ""} onChange={e=>setField("buttonText", e.target.value)}/></div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button className="btn btn-gold" onClick={save}>บันทึกข้อกำหนด</button>
          <button className="btn btn-outline" onClick={loadSeerTermsAdminV150}>โหลดค่าล่าสุด</button>
          <button className="btn btn-danger" onClick={resetDefaults}>รีเซ็ตค่าเริ่มต้น</button>
        </div>
      </div>
    </div>;
  }

function SettingsPage(){
    const settingsToolsV125 = [
      {k:"payment_account", ico:"🏦", title:"เลขบัญชีรับเงิน", desc:"ตั้งบัญชี/พร้อมเพย์/QR ที่ลูกค้าเห็นตอนโอนเงิน"},
      {k:"income_settings", ico:"💰", title:"รายได้และการตั้งค่า", desc:"รวมรายได้หมอดู รายได้รวม และตั้งค่าหักเปอร์เซ็นต์ของหมอดู"},
      {k:"articles", ico:"📰", title:"บทความ / ข่าววันนี้", desc:"สร้าง จัดการบทความเก่า และแจ้งเตือนสมาชิก"},
      {k:"seer_applicants", ico:"📝", title:`ใบสมัครหมอดู (${seerApplicants.filter(s=>s.status==="รอตรวจสอบ").length})`, desc:"ตรวจใบสมัคร อนุมัติ/ปฏิเสธหมอดู"},
      {k:"customer_database", ico:"📇", title:"ฐานลูกค้า", desc:"ดูเบอร์ LINE ID และดาวน์โหลด Excel / CSV"},
      {k:"chat_monitor", ico:"🛡️", title:"ตรวจแชท", desc:"ส่องแชทนัดหมายเพื่อกันนัดนอกระบบ"},
      {k:"admins", ico:"👥", title:"จัดการแอดมิน", desc:"สร้างลิงก์เชิญ พร้อมกำหนด Username / Password / สิทธิ์"},
      {k:"audit", ico:"📌", title:"ประวัติแอดมิน", desc:"ดูประวัติการกระทำของแอดมิน"},
    ].filter(x => canSee(x.k));
    return <div style={{padding:16}}>
      <div className="sec-hd" style={{padding:"0 0 12px"}}>ตั้งค่า / เมนูทั้งหมด</div>
      <div className="card" style={{padding:14,marginBottom:14,borderColor:`${C.gold}55`}}>
        <div style={{fontWeight:900,color:C.goldLight,marginBottom:6}}>เมนูจัดการระบบ</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:12}}>เมนูที่มีการเพิ่ม/อัปเดตเรื่อย ๆ จะรวมไว้ตรงนี้ เพื่อไม่ให้แถบบนรกหรือหายไปด้านขวา</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>{settingsToolsV125.map(t => <button key={t.k} type="button" onClick={()=>setMenu(t.k)} className="card" style={{padding:12,textAlign:"left",border:`1px solid ${C.border}`,background:"rgba(255,255,255,.03)",cursor:"pointer",borderRadius:16,fontFamily:"Sarabun",color:C.text}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}><span style={{fontSize:20}}>{t.ico}</span><span style={{fontWeight:900,color:C.goldLight,fontSize:13}}>{t.title}</span></div><div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>{t.desc}</div></button>)}</div>
      </div>
      <div className="sec-hd" style={{padding:"4px 0 12px"}}>ตั้งค่าระบบ แก้ได้เฉพาะ Super Admin</div>
      <div className="card" style={{padding:14,marginBottom:14}}>{Object.entries(platformSettings).map(([k,v])=><div className="fgroup" key={k}><label className="flabel">{({siteName:"ชื่อเว็บไซต์",pointRate:"อัตราแต้ม",goldRule:"เงื่อนไข Gold",paymentAccount:"บัญชีรับเงิน",slipMode:"การตรวจสลิป",minWithdraw:"ถอนขั้นต่ำหมอดู"})[k]||k}</label><input className="finput" value={v} onChange={e=>setPlatformSettings(p=>({...p,[k]:e.target.value}))}/></div>)}<button className="btn btn-gold" onClick={()=>{addAudit("บันทึกตั้งค่าระบบ","System Settings");flash("บันทึกตั้งค่าระบบแล้ว");}}>บันทึกการตั้งค่า</button></div>
      <div className="card" style={{padding:14}}><div style={{fontWeight:800,color:C.goldLight,marginBottom:8}}>จัดการบัญชีแอดมิน</div><div style={{fontSize:12,color:C.muted,lineHeight:1.65,marginBottom:12}}>กำหนด Username และ Password ก่อนสร้างลิงก์เชิญ ระบบจะผูกสิทธิ์เข้ากับ LINE ของผู้ที่เปิดลิงก์</div><button className="btn btn-purple" onClick={()=>setMenu("admins")}>ไปที่จัดการแอดมิน</button></div>
    </div>;
  }
  function LineMeetingRoomsPageV3937(){
    const data = lineMeetingRoomsV3936;
    const rooms = Array.isArray(data.rooms) ? data.rooms : [];
    const stats = data.stats || {};
    const statusView = (room) => {
      if (room.pool_status === "retired" || room.review_status === "retired") return {label:"ลบจาก LINE แล้ว / ปิดใช้", tone:"b-red", detail:"ห้องนี้ถูกปลดจากระบบถาวรและจะไม่ถูกสุ่มใช้อีก"};
      if (room.review_status === "blocked") return {label:"พบคนค้าง / ต้องลบ", tone:"b-red", detail:"ห้ามใช้ซ้ำ ให้เคลียร์ผู้เข้าร่วมและลบ Meeting จาก LINE"};
      if (room.review_status === "pending_review") return {label:"ใช้แล้ว / ต้องลบจาก LINE", tone:"b-orange", detail:"นัดหมายจบแล้ว ลิงก์เคยถูกส่งให้ผู้ใช้ จึงห้ามนำกลับเข้าสุ่ม"};
      if (room.pool_status === "reserved") return {label:"กำลังใช้งาน", tone:"b-purple", detail:"ห้องถูกจองให้นัดนี้ และจะถูกส่งเข้าคิวลบหลังเวลานัดจบ"};
      return {label:"ห้องใหม่พร้อมใช้", tone:"b-green", detail:"ยังไม่เคยส่งลิงก์ให้ลูกค้าหรือหมอดู ระบบสุ่มให้การจองใหม่ได้"};
    };
    const openRoom = (url) => {
      if (!url) { flash("ไม่พบลิงก์ห้อง"); return; }
      try { window.location.assign(url); } catch { window.open(url, "_blank", "noopener,noreferrer"); }
    };
    return <div style={{padding:16}}>
      <div className="card card-gold" style={{padding:16,marginBottom:14}}>
        <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>🎙️ จัดการห้อง LINE Meeting</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:5}}>ห้อง LINE Meeting ใช้ได้เพียงครั้งเดียว: เมื่อจบนัด ห้องจะเข้า “ใช้แล้ว / ต้องลบจาก LINE” ทันที ระบบจะไม่สุ่มห้องเดิมซ้ำอีก แม้ไม่มีคนค้างอยู่ก็ตาม</div>
        <div className="warn-box" style={{marginTop:10}}>ขั้นตอน: เข้า LINE บัญชีเจ้าของห้อง → ตรวจ Participants และ Remove คนที่ค้าง (ถ้ามี) → ลบ Meeting ห้องนั้นใน LINE → กลับมากด “ยืนยันลบห้องจาก LINE แล้ว” → สร้างห้องใหม่และนำเข้ามาเติมคลัง</div>
        <div className="stats-grid" style={{marginTop:12}}>
          <div className="stat"><div className="stat-l">ห้องใหม่พร้อมใช้</div><div className="stat-v" style={{color:C.green}}>{Number(stats.ready||0)}</div></div>
          <div className="stat"><div className="stat-l">กำลังใช้งาน</div><div className="stat-v">{Number(stats.in_use||0)}</div></div>
          <div className="stat"><div className="stat-l">ต้องลบจาก LINE</div><div className="stat-v" style={{color:C.orange}}>{Number(stats.pending_review||0)+Number(stats.blocked||0)}</div></div>
          <div className="stat"><div className="stat-l">ลบแล้ว / ปิดใช้</div><div className="stat-v" style={{color:C.red}}>{Number(stats.retired||0)}</div></div>
        </div>
        <button className="btn btn-outline btn-sm" style={{marginTop:10}} onClick={loadLineMeetingRoomsV3936}>{data.loading?"กำลังโหลด...":"↻ รีเฟรชสถานะห้อง"}</button>
      </div>
      {data.error ? <div className="warn-box" style={{marginBottom:14}}>{data.error}</div> : null}
      {rooms.length===0 && !data.loading ? <div className="empty"><div className="e-ico">🎙️</div>ยังไม่มีห้อง LINE Meeting ในคลัง</div> : rooms.map((room, index)=>{
        const view=statusView(room);
        const busy=(action)=>lineMeetingActionBusyV3936===`${room.room_id}:${action}`;
        const schedule=room.scheduled_at || [room.requested_date,room.requested_time].filter(Boolean).join(" ") || "ยังไม่เคยถูกจอง";
        const needsDelete = room.review_status === "pending_review" || room.review_status === "blocked";
        return <div key={room.room_id || index} className="brow" style={{marginBottom:10,borderColor:needsDelete?`${C.orange}88`:undefined}}>
          <div className="brow-hd"><div><div className="brow-name">{room.room_label || room.room_id}</div><div style={{fontSize:11,color:C.muted,marginTop:3}}>รหัสห้อง: {room.room_id}</div></div><span className={`badge ${view.tone}`}>{view.label}</span></div>
          <div className="brow-detail">{view.detail}<br/>Booking ล่าสุด: {room.booking_id || "-"}<br/>ช่วงใช้งานล่าสุด: {schedule}<br/>ตรวจ/ลบล่าสุด: {room.reviewed_at || "-"}</div>
          {needsDelete && <div className="warn-box" style={{marginTop:10}}>ลิงก์นี้ถูกส่งให้ผู้ใช้แล้ว จึงห้ามนำกลับเข้าสุ่ม แม้เคลียร์คนค้างได้แล้ว ต้องลบ Meeting ใน LINE ก่อน แล้วค่อยกดยืนยันด้านล่าง</div>}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
            <button className="btn btn-outline btn-sm" onClick={()=>openRoom(room.meeting_url)}>เปิดห้องใน LINE</button>
            {needsDelete && <button className="btn btn-danger btn-sm" disabled={busy("confirm_deleted")} onClick={()=>updateLineMeetingRoomV3936(room.room_id,"confirm_deleted")}>{busy("confirm_deleted")?"กำลังบันทึก...":"✓ ลบ Meeting จาก LINE แล้ว"}</button>}
            {room.review_status === "pending_review" && <button className="btn btn-outline btn-sm" disabled={busy("mark_blocked")} onClick={()=>updateLineMeetingRoomV3936(room.room_id,"mark_blocked")}>{busy("mark_blocked")?"กำลังบันทึก...":"พบคนค้าง / รอเคลียร์"}</button>}
          </div>
        </div>;
      })}
    </div>;
  }

  function AdminsPage(){
    const roleOptions = [
      {value:"super_admin",label:"Super Admin — สิทธิ์เต็มทั้งหมด"},
      {value:"admin_level_1",label:"Admin Level 1 — จัดการงานตามสิทธิ์"},
      {value:"admin_level_2",label:"Admin Level 2 — ตรวจสอบข้อมูลตามสิทธิ์"},
    ];
    const createInvite = async () => {
      setAdminAccessBusyV3923(true);
      try {
        const data = await adminAccessApiV3924("create_invite", adminInviteFormV3923);
        const invite = data.invite || null;
        const url = buildAdminInviteUrlV3926(invite?.invite_token || "");
        setAdminInviteResultV3923(invite && url ? {...invite, url} : null);
        setAdminInviteFormV3923({display_name:"", username:"", password:"", confirm_password:"", role:"super_admin", expires_in_minutes:"60"});
        await loadAdminDirectoryV3923();
        flash("สร้างลิงก์เชิญแอดมินแล้ว");
      } catch (error) { flash(String(error?.message || error)); }
      finally { setAdminAccessBusyV3923(false); }
    };
    const copyInvite = async () => {
      try {
        await navigator.clipboard.writeText(String(adminInviteResultV3923?.url || ""));
        flash("คัดลอกลิงก์เชิญแล้ว");
      } catch { flash("คัดลอกไม่สำเร็จ กรุณากดค้างที่ลิงก์แล้วคัดลอก"); }
    };
    const toggleAccount = async (account) => {
      if (!confirm(`${account.is_active ? "ปิดใช้งาน" : "เปิดใช้งาน"} @${account.username} ใช่หรือไม่?`)) return;
      setAdminAccessBusyV3923(true);
      try {
        await adminAccessApiV3924("set_status", {admin_id:account.admin_id, is_active:!account.is_active});
        await loadAdminDirectoryV3923();
        flash("บันทึกสถานะแอดมินแล้ว");
      } catch (error) { flash(String(error?.message || error)); }
      finally { setAdminAccessBusyV3923(false); }
    };
    if (!adminAccessV3923.isSuperAdmin) return <div style={{padding:16}}><div className="warn-box">เมนูนี้ใช้ได้เฉพาะ Super Admin</div></div>;
    return <div style={{padding:16}}>
      <div className="card" style={{padding:14,marginBottom:14,borderColor:`${C.gold}55`}}>
        <div style={{fontWeight:900,color:C.goldLight,marginBottom:6}}>สร้างลิงก์เชิญแอดมิน</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.65,marginBottom:12}}>ตั้ง Username และ Password ให้เรียบร้อยก่อนส่งลิงก์ ผู้รับต้องเปิดลิงก์จาก LINE บัญชีที่จะใช้เป็นแอดมิน ลิงก์ใช้ได้ครั้งเดียวและหมดอายุตามเวลาที่เลือก</div>
        <div className="fgroup"><label className="flabel">ชื่อเรียกแอดมิน</label><input className="finput" placeholder="เช่น แอดมินหลักเครื่องใหม่" value={adminInviteFormV3923.display_name} onChange={e=>setAdminInviteFormV3923(p=>({...p,display_name:e.target.value}))}/></div>
        <div className="fgroup"><label className="flabel">Username</label><input className="finput" placeholder="ภาษาอังกฤษ/ตัวเลข เช่น admin_new" autoCapitalize="none" value={adminInviteFormV3923.username} onChange={e=>setAdminInviteFormV3923(p=>({...p,username:e.target.value}))}/></div>
        <div className="fgroup"><label className="flabel">Password</label><input className="finput" type="password" placeholder="อย่างน้อย 10 ตัวอักษร" value={adminInviteFormV3923.password} onChange={e=>setAdminInviteFormV3923(p=>({...p,password:e.target.value}))}/></div>
        <div className="fgroup"><label className="flabel">ยืนยัน Password</label><input className="finput" type="password" placeholder="พิมพ์ Password ซ้ำ" value={adminInviteFormV3923.confirm_password} onChange={e=>setAdminInviteFormV3923(p=>({...p,confirm_password:e.target.value}))}/></div>
        <div className="fgroup"><label className="flabel">ระดับสิทธิ์</label><select className="finput" value={adminInviteFormV3923.role} onChange={e=>setAdminInviteFormV3923(p=>({...p,role:e.target.value}))}>{roleOptions.map(option=><option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
        <div className="fgroup"><label className="flabel">ลิงก์มีอายุ</label><select className="finput" value={adminInviteFormV3923.expires_in_minutes} onChange={e=>setAdminInviteFormV3923(p=>({...p,expires_in_minutes:e.target.value}))}><option value="30">30 นาที</option><option value="60">1 ชั่วโมง</option><option value="120">2 ชั่วโมง</option><option value="180">3 ชั่วโมง</option></select></div>
        <button className="btn btn-gold" disabled={adminAccessBusyV3923} onClick={createInvite}>{adminAccessBusyV3923?"กำลังสร้างลิงก์...":"สร้างลิงก์เชิญ"}</button>
      </div>
      {adminInviteResultV3923&&<div className="card" style={{padding:14,marginBottom:14,borderColor:`${C.green}66`}}><div style={{fontWeight:900,color:C.green,marginBottom:8}}>ลิงก์เชิญพร้อมส่ง</div><div style={{fontSize:12,color:C.muted,lineHeight:1.65,marginBottom:8}}>Username: <b style={{color:C.text}}>{adminInviteResultV3923.username}</b> • {adminInviteResultV3923.role_label}<br/>หมดอายุ: {adminInviteResultV3923.expires_at}</div><div style={{fontSize:12,color:C.text,wordBreak:"break-all",lineHeight:1.65,background:"rgba(255,255,255,.04)",padding:10,borderRadius:10}}>{adminInviteResultV3923.url}</div><button className="btn btn-purple" style={{marginTop:10}} onClick={copyInvite}>คัดลอกลิงก์เพื่อส่งให้แอดมิน</button><div style={{fontSize:11,color:C.orange,lineHeight:1.6,marginTop:8}}>ส่ง Password แยกจากลิงก์ เช่นโทรบอกหรือแชตส่วนตัว ห้ามวาง Password ไว้พร้อมลิงก์</div></div>}
      <div className="sec-hd" style={{padding:"6px 0 10px"}}>บัญชีแอดมินที่เปิดใช้งาน</div>
      {adminDirectoryV3923.length===0?<div className="empty"><div className="e-ico">👥</div>ยังไม่มีบัญชีแอดมินที่ตั้งค่า Username / Password</div>:adminDirectoryV3923.map(account=><div key={account.admin_id} className="brow"><div className="brow-hd"><div><div className="brow-name">@{account.username}</div><div style={{fontSize:12,color:C.purpleLight}}>{account.display_name || "-"} • {account.role_label || account.role}</div></div><span className={`badge ${account.is_active?"b-green":"b-red"}`}>{account.is_active?"ใช้งาน":"ปิดใช้"}</span></div><div className="brow-detail">สร้าง: {account.created_at || "-"}<br/>เข้าใช้ล่าสุด: {account.last_login_at || "ยังไม่เคยเข้าใช้"}</div><div style={{display:"flex",gap:8,marginTop:10}}><button className={`btn btn-sm ${account.is_active?"btn-danger":"btn-success"}`} disabled={adminAccessBusyV3923 || account.username===currentAdmin} onClick={()=>toggleAccount(account)}>{account.is_active?"ปิดใช้":"เปิดใช้"}</button></div></div>)}
      {adminPendingInvitesV3923.length>0&&<><div className="sec-hd" style={{padding:"16px 0 10px"}}>ลิงก์ที่ยังรอเปิดใช้งาน</div>{adminPendingInvitesV3923.map(invite=><div key={invite.invite_id} className="brow"><div className="brow-name">@{invite.username}</div><div style={{fontSize:12,color:C.purpleLight}}>{invite.display_name} • {invite.role_label}</div><div style={{fontSize:11,color:C.muted,marginTop:5}}>หมดอายุ: {invite.expires_at}</div></div>)}</>}
    </div>;
  }
  function AuditPage(){ const items=pageItems("audit",monthAudits,20); return <div style={{padding:16}}><div className="sec-hd" style={{padding:"0 0 12px"}}>ประวัติการกระทำของแอดมิน</div><MonthInfo list={monthAudits}/>{items.map((l,i)=><div key={l.id} className="card" style={{padding:12,marginBottom:10}}><div style={{fontSize:13,color:C.text,fontWeight:700}}><RowNum index={(page("audit")-1)*20+i}/>{l.action}</div><div style={{fontSize:12,color:C.muted,marginTop:4}}>โดย {l.admin} • {l.target}</div><div style={{fontSize:11,color:C.dim,marginTop:4}}>{l.time}</div></div>)}<Pager k="audit" list={monthAudits} per={20}/></div> }
  function ChatMonitorPage(){ const logs=(chatAlerts||[]).map(a=>{const msgs=(a.messages||[{from:"customer",text:a.text,time:a.created}]).map(m=>`${m.from==="customer"?"ลูกค้า":"หมอดู"}: ${m.text}`);const raw=msgs.join(" ");const risky=/(0[689]\d{8}|โทร|เบอร์|line|ไลน์|แอด|@)/i.test(raw);return{id:a.id,customer:a.customer||"ลูกค้า",seer:a.seer_name||"หมอดู",flag:risky?"พบคำเสี่ยงเกี่ยวกับช่องทางติดต่อ / นัดนอกระบบ":"ปกติ",level:risky?"ต้องตรวจ":"ปกติ",msgs,createdAt:a.created||new Date().toLocaleString("th-TH")};});const list=thisMonth(logs.length?logs:[{id:"C-DEMO",customer:"ตัวอย่าง",seer:"อ.มณีรัตน์",flag:"ยังไม่มีแชทจริง",level:"ปกติ",msgs:["ระบบ: เมื่อลูกค้าและหมอดูแชทนัดเวลา ข้อความจริงจะมาแสดงตรงนี้"],createdAt:new Date().toLocaleString("th-TH")}]);const items=pageItems("chat_monitor",list,20);return <div style={{padding:16}}><div className="warn-box" style={{marginBottom:14}}>แอดมินสามารถเห็นข้อความแชทระหว่างลูกค้าและหมอดูทุกข้อความ เพื่อป้องกันการให้เบอร์ส่วนตัว การชวนออกนอกระบบ หรือพฤติกรรมที่ผิดกฎ</div><MonthInfo list={list}/>{items.map((log,i)=><div key={log.id} className="card" style={{padding:14,marginBottom:12}}><div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:8}}><div><div style={{fontSize:14,fontWeight:800,color:C.goldLight}}><RowNum index={(page("chat_monitor")-1)*20+i}/>#{log.id} • {log.seer}</div><div style={{fontSize:12,color:C.muted,marginTop:3}}>ลูกค้า: {log.customer}</div></div><span className={`badge ${log.level==="ปกติ"?"b-green":"b-orange"}`}>{log.level}</span></div><div style={{fontSize:12,color:log.level==="ปกติ"?C.green:C.orange,marginBottom:10}}>สถานะตรวจจับ: {log.flag}</div><div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:10,marginBottom:10}}>{log.msgs.map((m,j)=><div key={j} style={{fontSize:12,color:C.text,lineHeight:1.7,borderBottom:j<log.msgs.length-1?`1px solid ${C.border}33`:"none",padding:"5px 0"}}>{m}</div>)}</div></div>)}<Pager k="chat_monitor" list={list} per={20}/></div> }

  function CustomerDatabasePageV122(){
    const rows = (adminBookingsV91 || []).filter(b=>b.customer_phone || b.customer_line_id || b.slip_uploaded_at);
    const items = pageItems("customer_database", rows, 20);
    const exportUrl = "/api/customer-export?format=csv";
    return <div style={{padding:16}}>
      <div className="card card-gold" style={{padding:16,marginBottom:14}}>
        <div style={{fontSize:12,color:C.muted}}>ฐานลูกค้าจากการโอนเงิน/แนบสลิป</div>
        <div style={{fontFamily:"Cinzel",fontSize:30,color:C.goldLight,margin:"8px 0"}}>{rows.length.toLocaleString()} รายชื่อ</div>
        <a className="btn btn-gold" href={exportUrl} target="_blank" rel="noreferrer" style={{textDecoration:"none",display:"block",textAlign:"center"}}>ดาวน์โหลด Excel / CSV</a>
        <div style={{fontSize:11,color:C.muted,lineHeight:1.6,marginTop:8}}>ไฟล์ CSV เปิดด้วย Excel ได้ มีชื่อ เบอร์โทร LINE ID หมอดู บริการ ราคา และวันเวลา</div>
      </div>
      {items.length===0?<div className="empty"><div className="e-ico">📇</div>ยังไม่มีข้อมูลลูกค้า</div>:items.map((b,i)=>(
        <div key={b.booking_id} className="brow">
          <div className="brow-hd"><div><div className="brow-name"><RowNum index={(page("customer_database")-1)*20+i}/>{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:12,color:C.purpleLight}}>LINE: {b.customer_line_id || "-"} • โทร: {b.customer_phone || "-"}</div></div><span className={`badge ${dwBookingBadgeClass(b.status)}`}>{dwBookingStatusLabel(b.status)}</span></div>
          <div className="brow-detail">{b.seer_name || "หมอดู"} • {b.service_name || "-"} • ฿{Number(b.price||0).toLocaleString()}<br/>นัด: {dwBookingScheduledText(b)} • แนบสลิป: {b.slip_uploaded_at || "-"}</div>
        </div>
      ))}
      <Pager k="customer_database" list={rows} per={20}/>
    </div>
  }

  function moneyV156E(n){ return Number(n || 0).toLocaleString("th-TH"); }
  function bookingDateV156E(b={}){
    const raw = String(b.completed_at || b.seer_income_released_at || b.updated_at || b.created_at || b.scheduled_at || b.requested_date || "").trim();
    const d = raw ? new Date(raw.includes("T") ? raw : raw.replace(" ","T")) : new Date();
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }
  function periodRangeV156E(type="month"){
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    if(type === "day") {
      start.setHours(0,0,0,0); end.setHours(23,59,59,999);
    } else if(type === "week") {
      const day = (now.getDay()+6)%7;
      start.setDate(now.getDate()-day); start.setHours(0,0,0,0);
      end.setTime(start.getTime()); end.setDate(start.getDate()+6); end.setHours(23,59,59,999);
    } else {
      start.setDate(1); start.setHours(0,0,0,0);
      end.setMonth(start.getMonth()+1,0); end.setHours(23,59,59,999);
    }
    return {start,end,label:type==="day"?"วันนี้":type==="week"?"สัปดาห์นี้":"เดือนนี้"};
  }
  function inPeriodV156E(b,type="month"){
    const d = bookingDateV156E(b).getTime();
    const r = periodRangeV156E(type);
    return d >= r.start.getTime() && d <= r.end.getTime();
  }
  function getCompletedBookingsV156E(){
    return (adminBookingsV91 || []).filter(b=>String(b.status || "") === "completed");
  }
  // v3.9.40: reports prefer immutable ledger values.  Older booking rows can
  // contain default 0 fields even though the seer income was saved correctly,
  // so derive the missing system amount from gross minus seer income instead of
  // displaying a false ฿0 platform income.
  function incomeAmountsV3940(booking={}){
    const number = value => {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    const money2 = value => Math.round(number(value) * 100) / 100;
    const gross = money2(booking.gross_amount ?? booking.price ?? booking.payment_amount ?? 0);
    const savedSeerIncome = money2(booking.seer_income_amount);
    const savedPlatformFee = money2(booking.platform_fee_amount);
    const referral = money2(booking.referral_commission_amount);
    const savedPercentRaw = booking.platform_fee_percent;
    const fallbackPercent = number(savedPercentRaw ?? adminPaymentSettingsV106?.platform_fee_percent ?? 35);
    const percent = Math.min(60, Math.max(0, fallbackPercent));
    const inferredFeeFromSeer = savedSeerIncome > 0 && gross >= savedSeerIncome ? money2(gross - savedSeerIncome) : null;
    const configuredFee = money2(gross * percent / 100);
    const platformBeforeReferral = savedPlatformFee > 0 || gross === 0
      ? savedPlatformFee
      : (inferredFeeFromSeer !== null ? inferredFeeFromSeer : configuredFee);
    const derivedPlatformNet = money2(Math.max(0, platformBeforeReferral - referral));
    const savedPlatformNet = money2(booking.platform_net_income_amount);
    const platform = savedPlatformNet > 0 || derivedPlatformNet === 0 ? savedPlatformNet : derivedPlatformNet;
    const seer = savedSeerIncome > 0 || gross === 0
      ? savedSeerIncome
      : money2(Math.max(0, gross - platformBeforeReferral));
    return {gross, platformBeforeReferral, platform, referral, seer};
  }

  function summarizeBookingsV156E(rows=[]){
    return rows.reduce((a,b)=>{
      const amounts = incomeAmountsV3940(b);
      a.gross += amounts.gross; a.platform += amounts.platform; a.referral += amounts.referral; a.net += amounts.seer; a.count += 1;
      return a;
    },{gross:0, platform:0, referral:0, net:0, count:0});
  }
  function withdrawalsForSeerV156E(seerKey=""){
    return (seerWithdrawalsV156E || []).filter(w=>String(w.seer_key || "") === String(seerKey || ""));
  }
  function buildSeerIncomeRowsV156E(rows=getCompletedBookingsV156E()){
    const map = {};
    rows.forEach(b=>{
      const key = b.seer_id || b.seer_line_user_id || b.seer_name || "unknown";
      if(!map[key]) map[key] = {seer_key:key, seer_name:b.seer_name || "หมอดู", gross:0, platform:0, net:0, count:0, rows:[]};
      const amounts = incomeAmountsV3940(b);
      map[key].gross += amounts.gross; map[key].platform += amounts.platform; map[key].net += amounts.seer; map[key].count += 1; map[key].rows.push(b);
    });
    return Object.values(map).map(x=>{
      const withdrawn = withdrawalsForSeerV156E(x.seer_key).reduce((a,w)=>a+Number(w.amount || 0),0);
      return {...x, withdrawn, available: Math.max(0, Number(x.net || 0)-withdrawn)};
    }).sort((a,b)=>b.net-a.net);
  }

  // v157: จำค่าปุ่มช่วงเวลารายได้ไว้ ไม่ให้หน้ารีเรนเดอร์แล้วเด้งกลับ “รายเดือน”
  function useIncomePeriodV157(storageKey){
    const readSavedPeriod = () => {
      try {
        const v = localStorage.getItem(storageKey);
        return ["day","week","month"].includes(v) ? v : "month";
      } catch (e) {
        return "month";
      }
    };
    const [period,setPeriodState] = useState(readSavedPeriod);
    const setPeriod = (v) => {
      const next = ["day","week","month"].includes(v) ? v : "month";
      try { localStorage.setItem(storageKey,next); } catch (e) {}
      setPeriodState(next);
    };
    return [period,setPeriod];
  }

  function IncomePeriodSelectorV156E({value,setValue}){
    return <div style={{display:"flex",gap:8,flexWrap:"wrap",margin:"10px 0 14px"}}>
      {[{k:"day",l:"รายวัน"},{k:"week",l:"รายสัปดาห์"},{k:"month",l:"รายเดือน"}].map(x=><button key={x.k} className={value===x.k?"btn btn-gold btn-sm":"btn btn-outline btn-sm"} onClick={()=>setValue(x.k)}>{x.l}</button>)}
    </div>;
  }
  function IncomeSummaryCardsV156E({sum}){
    return <div className="stats-grid" style={{marginBottom:14}}>
      <div className="stat"><div className="stat-l">ยอดลูกค้าชำระรวม</div><div className="stat-v">฿{moneyV156E(sum.gross)}</div></div>
      <div className="stat"><div className="stat-l">รายได้ระบบสุทธิ</div><div className="stat-v" style={{color:C.goldLight}}>฿{moneyV156E(sum.platform)}</div></div>
      <div className="stat"><div className="stat-l">ค่าคอมแนะนำหมอดู</div><div className="stat-v" style={{color:C.purpleLight}}>฿{moneyV156E(sum.referral)}</div></div>
      <div className="stat"><div className="stat-l">รายได้หมอดูรวม</div><div className="stat-v" style={{color:C.green}}>฿{moneyV156E(sum.net)}</div></div>
      <div className="stat"><div className="stat-l">จำนวนงานปิดแล้ว</div><div className="stat-v">{sum.count}</div></div>
    </div>;
  }

  function SeerIncomeDetailPageV156E({seer}){
    const [period,setPeriod] = useIncomePeriodV157("dw_admin_seer_income_detail_period_v157");
    const [withdrawAmount,setWithdrawAmount] = useState("");
    if(!seer) return null;
    const periodRows = (seer.rows || []).filter(b=>inPeriodV156E(b, period));
    const sum = summarizeBookingsV156E(periodRows);
    const allWithdrawals = withdrawalsForSeerV156E(seer.seer_key).sort((a,b)=>String(b.created_at||"").localeCompare(String(a.created_at||"")));
    const periodWithdrawals = allWithdrawals.filter(w=>{
      const d = new Date(w.created_at || Date.now()).getTime();
      const r = periodRangeV156E(period);
      return d >= r.start.getTime() && d <= r.end.getTime();
    });
    const withdrawnAll = allWithdrawals.reduce((a,w)=>a+Number(w.amount||0),0);
    const withdrawnPeriod = periodWithdrawals.reduce((a,w)=>a+Number(w.amount||0),0);
    const availableAll = Math.max(0, Number(seer.net || 0)-withdrawnAll);
    return <div style={{padding:16}}>
      <button className="btn btn-outline btn-sm" onClick={()=>setSeerIncomeDetailV156E(null)}>← กลับรายได้หมอดู</button>
      <div className="card card-gold" style={{padding:16,marginTop:12,marginBottom:14}}>
        <div style={{fontSize:18,fontWeight:900,color:C.goldLight}}>💸 รายได้ของ {seer.seer_name}</div>
        <div style={{fontSize:12,color:C.muted,marginTop:6,lineHeight:1.7}}>ดูยอดตามวัน/สัปดาห์/เดือน และยอดเบิกแล้วของหมอดูคนนี้</div>
        <IncomePeriodSelectorV156E value={period} setValue={setPeriod}/>
        <IncomeSummaryCardsV156E sum={sum}/>
        <div className="stats-grid">
          <div className="stat"><div className="stat-l">เบิกแล้วทั้งหมด</div><div className="stat-v">฿{moneyV156E(withdrawnAll)}</div></div>
          <div className="stat"><div className="stat-l">เบิกแล้วในช่วงนี้</div><div className="stat-v">฿{moneyV156E(withdrawnPeriod)}</div></div>
          <div className="stat"><div className="stat-l">คงเหลือยังไม่ถอน</div><div className="stat-v" style={{color:C.green}}>฿{moneyV156E(availableAll)}</div></div>
        </div>
      </div>

      <div className="card" style={{padding:14,marginBottom:14}}>
        <div style={{fontWeight:900,color:C.goldLight,marginBottom:8}}>บันทึกการเบิกเงินหมอดู</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:10}}>ใช้บันทึกว่าโอนออกให้หมอดูแล้วเท่าไหร่ ระบบจะเอาไปหักจาก “คงเหลือยังไม่ถอน”</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input className="finput" style={{flex:1,minWidth:160}} type="number" min="0" value={withdrawAmount} onChange={e=>setWithdrawAmount(e.target.value)} placeholder="ยอดที่เบิก เช่น 500" />
          <button className="btn btn-gold" onClick={async()=>{ await saveSeerWithdrawalV156E(seer, withdrawAmount); setWithdrawAmount(""); }}>บันทึกยอดเบิก</button>
        </div>
      </div>

      <div className="card" style={{padding:14,marginBottom:14}}>
        <div style={{fontWeight:900,color:C.goldLight,marginBottom:8}}>รายการงานในช่วงนี้</div>
        {periodRows.length===0?<div className="empty"><div className="e-ico">📅</div>ยังไม่มีงานในช่วงนี้</div>:periodRows.map((b,i)=><div key={b.booking_id||i} className="brow">
          {(() => { const amounts = incomeAmountsV3940(b); return <><div className="brow-hd"><div><div className="brow-name"><RowNum index={i}/>{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:12,color:C.purpleLight}}>{b.service_name || "บริการ"} • {bookingDateV156E(b).toLocaleDateString("th-TH")}</div></div><span style={{color:C.green,fontWeight:900}}>฿{moneyV156E(amounts.seer)}</span></div>
          <div className="brow-detail">ยอดลูกค้าจ่าย ฿{moneyV156E(amounts.gross)} • รายได้ระบบ ฿{moneyV156E(amounts.platform)} • Booking: {b.booking_id}</div></>; })()}
        </div>)}
      </div>

      <div className="card" style={{padding:14}}>
        <div style={{fontWeight:900,color:C.goldLight,marginBottom:8}}>ประวัติการเบิกเงิน</div>
        {allWithdrawals.length===0?<div className="empty"><div className="e-ico">🏦</div>ยังไม่มีประวัติเบิกเงิน</div>:allWithdrawals.map((w,i)=><div key={w.withdrawal_id||i} className="brow">
          <div className="brow-hd"><div><div className="brow-name">#{i+1} เบิกเงิน</div><div style={{fontSize:12,color:C.purpleLight}}>{new Date(w.created_at || Date.now()).toLocaleString("th-TH")} • {w.admin_id || "admin"}</div></div><span style={{color:C.goldLight,fontWeight:900}}>฿{moneyV156E(w.amount)}</span></div>
          {w.note?<div className="brow-detail">{w.note}</div>:null}
        </div>)}
      </div>
    </div>;
  }

  function SeerIncomePageV122(){
    const [period,setPeriod] = useIncomePeriodV157("dw_admin_seer_income_period_v157");
    if(seerIncomeDetailV156E) return <SeerIncomeDetailPageV156E seer={seerIncomeDetailV156E}/>;
    const completed = getCompletedBookingsV156E();
    const periodCompleted = completed.filter(b=>inPeriodV156E(b, period));
    const list = buildSeerIncomeRowsV156E(completed);
    const items = pageItems("seer_income", list, 20);
    const periodSum = summarizeBookingsV156E(periodCompleted);
    const totalNet = list.reduce((a,x)=>a+x.net,0);
    const totalWithdrawn = list.reduce((a,x)=>a+Number(x.withdrawn||0),0);
    const totalAvailable = list.reduce((a,x)=>a+Number(x.available||0),0);
    return <div style={{padding:16}}>
      <div className="card card-gold" style={{padding:16,marginBottom:14}}>
        <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>💸 รายได้หมอดู</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:4}}>กดชื่อหมอดูเพื่อดูรายละเอียดรายวัน/รายสัปดาห์/รายเดือน และบันทึกยอดเบิก</div>
        <IncomePeriodSelectorV156E value={period} setValue={setPeriod}/>
        <IncomeSummaryCardsV156E sum={periodSum}/>
        <div className="stats-grid">
          <div className="stat"><div className="stat-l">รายได้หมอดูทั้งหมด</div><div className="stat-v">฿{moneyV156E(totalNet)}</div></div>
          <div className="stat"><div className="stat-l">หมอดูเบิกแล้ว</div><div className="stat-v">฿{moneyV156E(totalWithdrawn)}</div></div>
          <div className="stat"><div className="stat-l">คงเหลือยังไม่ถอน</div><div className="stat-v" style={{color:C.green}}>฿{moneyV156E(totalAvailable)}</div></div>
        </div>
      </div>
      {items.length===0?<div className="empty"><div className="e-ico">💸</div>ยังไม่มีงานที่หมอดูกดปิดงาน</div>:items.map((x,i)=>(
        <div key={x.seer_key+i} className="brow" onClick={()=>setSeerIncomeDetailV156E(x)} style={{cursor:"pointer"}}>
          <div className="brow-hd"><div><div className="brow-name"><RowNum index={(page("seer_income")-1)*20+i}/>{x.seer_name}</div><div style={{fontSize:12,color:C.purpleLight}}>{x.count} งานที่ปิดแล้ว • กดดูรายละเอียด</div></div><span style={{color:C.green,fontWeight:900}}>คงเหลือ ฿{moneyV156E(x.available)}</span></div>
          <div className="brow-detail">ยอดขายรวม ฿{moneyV156E(x.gross)} • รายได้หมอดู ฿{moneyV156E(x.net)} • เบิกแล้ว ฿{moneyV156E(x.withdrawn)}</div>
        </div>
      ))}
      <Pager k="seer_income" list={list} per={20}/>
    </div>
  }

  function IncomeSettingsPageV135(){
    const f = adminPaymentSettingsV106 || {};
    const setF = (k,v)=>setAdminPaymentSettingsV106(p=>({...p,[k]:v}));
    const pct = Math.min(60, Math.max(0, Number(f.platform_fee_percent ?? 35)));
    return <div style={{padding:16}}>
      <div className="card card-gold" style={{padding:16,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
          <div>
            <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>💰 รายได้และการตั้งค่า</div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:4}}>รวมรายได้หมอดู รายได้รวม และตั้งค่าหักเปอร์เซ็นต์ของหมอดู</div>
          </div>
          <span className="badge b-gold">หัก {pct}%</span>
        </div>
        <div className="warn-box" style={{marginBottom:12}}>
          เมนู “ตั้งค่าหักเปอร์เซ็นต์ของหมอดู” เดิมอยู่ในเลขบัญชีรับเงิน ตอนนี้ย้ายมารวมไว้ตรงนี้ด้วย ตั้งได้สูงสุด 60%
        </div>
        <div className="fgroup">
          <label className="flabel">ตั้งค่าหักเปอร์เซ็นต์ของหมอดู</label>
          <input
            className="finput"
            type="number"
            min="0"
            max="60"
            value={pct}
            onChange={e=>setF("platform_fee_percent",Math.min(60,Math.max(0,Number(e.target.value||0))))}
            placeholder="เช่น 20"
          />
          <div style={{fontSize:11,color:C.muted,marginTop:6,lineHeight:1.6}}>
            ตัวอย่าง: ลูกค้าจ่าย 1,000 บาท หัก {pct}% = แพลตฟอร์ม {Math.round(1000*pct/100).toLocaleString()} บาท หมอดูได้ {(1000-Math.round(1000*pct/100)).toLocaleString()} บาท
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button className="btn btn-gold" onClick={saveAdminPaymentSettingsV106}>บันทึกเปอร์เซ็นต์</button>
          <button className="btn btn-danger" onClick={async()=>{
            if(!confirm("ยืนยันรีเซ็ตรายได้รวม/ออเดอร์ทดสอบเก่าทั้งหมดหรือไม่?")) return;
            try {
              localStorage.setItem("dw_orders", JSON.stringify([]));
              localStorage.setItem("dw_v152_income_reset_done", "yes");
              setOrders([]);
              setPage("income",1);
              flash("รีเซ็ตรายได้รวมแล้ว พร้อมเริ่มทดสอบใหม่");
            } catch (err) {
              flash("รีเซ็ตรายได้ไม่สำเร็จ: " + (err.message || err));
            }
          }}>รีเซ็ตรายได้รวม</button>
        </div>
      </div>

      <div className="card" style={{padding:14,marginBottom:14}}>
        <div style={{fontWeight:900,color:C.goldLight,marginBottom:8}}>💸 รายได้หมอดู</div>
        <SeerIncomePageV122/>
      </div>

      <div className="card" style={{padding:14,marginBottom:14}}>
        <div style={{fontWeight:900,color:C.goldLight,marginBottom:8}}>💰 รายได้รวม</div>
        <IncomePage/>
      </div>
    </div>;
  }

  function AdminReferralAuditV159(){
    const [audit, setAudit] = useState({referrals:[], commissions:[]});
    const [loading, setLoading] = useState(true);
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiGet("/api/seer-referrals?admin=1");
        setAudit({ referrals:Array.isArray(data.referrals) ? data.referrals : [], commissions:Array.isArray(data.commissions) ? data.commissions : [] });
      } catch (err) {
        console.warn("โหลดตรวจสอบสายแนะนำหมอดูไม่สำเร็จ", err);
      } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const refs = audit.referrals || [];
    return <div className="card" style={{padding:14,marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"center",marginBottom:8}}>
        <div><div style={{fontWeight:900,color:C.goldLight}}>🤝 ตรวจสอบระบบแนะนำหมอดู</div><div style={{fontSize:11,color:C.muted,marginTop:3}}>แอดมินตรวจสอบผู้แนะนำ โควตา วันอนุมัติ วันเข้าใช้ครั้งแรก สถานะ และค่าคอมได้</div></div>
        <button className="btn btn-outline btn-sm" onClick={load}>{loading ? "กำลังโหลด" : "รีเฟรช"}</button>
      </div>
      {refs.length===0 ? <div className="empty" style={{padding:14}}>ยังไม่มีสายแนะนำหมอดู</div> : refs.slice(0,50).map((r,i)=>{
        const cap = r.inviter_capacity || {};
        const quota = cap.referral_limit === null ? "ไม่จำกัด" : `${Number(cap.active_count || 0)}/${Number(cap.referral_limit || 20)}`;
        const inactive = r.referral_status !== "active" || r.invited_seer_status === "inactive_no_activity";
        return <div key={r.referral_id || i} className="brow" style={{marginBottom:8}}>
          <div className="brow-hd"><div><div className="brow-name">{r.inviter_name || r.inviter_seer_id || "ผู้แนะนำ"} → {r.invited_name || r.invited_seer_id || "หมอดูที่ถูกแนะนำ"}</div><div style={{fontSize:11,color:C.muted}}>โควตาผู้แนะนำ: {quota} • อนุมัติ: {r.approved_at ? new Date(r.approved_at).toLocaleDateString("th-TH") : "-"} • เข้าใช้ครั้งแรก: {r.first_seen_at ? new Date(r.first_seen_at).toLocaleDateString("th-TH") : "ยังไม่เข้า"}</div></div><span className={`badge ${inactive ? "b-red" : "b-green"}`}>{inactive ? "ปิด/ไม่เริ่มใช้งาน" : "ใช้งาน"}</span></div>
          <div className="brow-detail">งานปิดแล้ว {Number(r.completed_jobs || 0)} งาน • ค่าคอมรวม ฿{moneyV156E(r.total_commission)} • {r.deactivated_reason || "ปกติ"}</div>
          {inactive && <button className="btn btn-outline btn-sm" style={{marginTop:8}} onClick={async()=>{ try { await apiPost("/api/seer-referrals", { action:"reactivate", seer_id:r.invited_seer_id, admin_id:currentAdmin }); flash("เปิดสถานะหมอดูแล้ว"); load(); } catch(err){ flash("เปิดสถานะไม่สำเร็จ: " + (err.message || err)); } }}>เปิดกลับ</button>}
        </div>;
      })}
    </div>;
  }

  function IncomePage(){
    const [period,setPeriod] = useIncomePeriodV157("dw_admin_income_total_period_v157");
    const [report,setReport] = useState({summary:null,rows:[],error:"",loading:true});
    const loadReport = async () => {
      setReport(prev=>({...prev,loading:true,error:""}));
      try {
        const data = await apiGet(`/api/admin-income?period=${encodeURIComponent(period)}`);
        setReport({ summary:data.summary || null, rows:Array.isArray(data.rows) ? data.rows : [], error:"", loading:false });
      } catch (err) {
        setReport({ summary:null, rows:[], error:String(err?.message || err || "โหลดรายได้จาก D1 ไม่สำเร็จ"), loading:false });
      }
    };
    useEffect(() => { loadReport(); }, [period]);
    const source = report.summary || {};
    const sum = {
      gross:Number(source.gross_amount || 0),
      platform:Number(source.platform_net_income_amount || 0),
      referral:Number(source.referral_commission_amount || 0),
      net:Number(source.seer_income_amount || 0),
      count:Number(source.completed_count || 0),
    };
    const periodRows = report.rows || [];
    const items = pageItems("income", periodRows, 20);
    return <div style={{padding:16}}>
      <div className="card card-gold" style={{padding:16,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:16,fontWeight:900,color:C.goldLight}}>💰 รายได้รวม</div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:4}}>สรุปจาก D1 income ledger เท่านั้น โดยรายได้ระบบเป็นยอดหลังหักค่าคอมมิชชันผู้แนะนำแล้ว</div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={loadReport} disabled={report.loading}>{report.loading ? "กำลังโหลด" : "รีเฟรช"}</button>
        </div>
        <IncomePeriodSelectorV156E value={period} setValue={setPeriod}/>
        {report.error ? <div className="warn-box" style={{marginBottom:12}}>โหลดรายงานไม่ได้: {report.error}</div> : <><IncomeSummaryCardsV156E sum={sum}/>
          <div className="stats-grid">
            <div className="stat"><div className="stat-l">ยอดถอนหมอดูแล้วทั้งหมด</div><div className="stat-v">฿{moneyV156E(source.withdrawn_total_amount)}</div></div>
            <div className="stat"><div className="stat-l">ยอดถอนหมอดูในช่วงนี้</div><div className="stat-v">฿{moneyV156E(source.withdrawn_period_amount)}</div></div>
            <div className="stat"><div className="stat-l">รายได้หมอดูคงเหลือทั้งหมด</div><div className="stat-v" style={{color:C.green}}>฿{moneyV156E(source.seer_outstanding_total_amount)}</div></div>
          </div>
        </>}
      </div>
      {report.loading ? <div className="empty"><div className="e-ico">⏳</div>กำลังโหลดรายได้จาก D1</div> : items.length===0 ? <div className="empty"><div className="e-ico">💰</div>ยังไม่มีรายได้ในช่วงนี้</div> : items.map((b,i)=>{
        const gross = Number(b.gross_amount || 0);
        const platform = Number(b.platform_net_income_amount || 0);
        const referral = Number(b.referral_commission_amount || 0);
        const seer = Number(b.seer_income_amount || 0);
        return <div key={b.booking_id||i} className="brow">
          <div className="brow-hd"><div><div className="brow-name"><RowNum index={(page("income")-1)*20+i}/>{b.customer_name || "ลูกค้า"}</div><div style={{fontSize:12,color:C.purpleLight}}>{b.seer_name || "หมอดู"} • {b.service_name || "บริการ"} • {bookingDateV156E(b).toLocaleDateString("th-TH")}</div></div><span style={{color:C.green,fontWeight:900}}>฿{moneyV156E(gross)}</span></div>
          <div className="brow-detail">ยอดลูกค้าจ่าย ฿{moneyV156E(gross)} • ส่วนหมอดู ฿{moneyV156E(seer)} • ค่าคอมแนะนำ ฿{moneyV156E(referral)} • รายได้ระบบสุทธิ ฿{moneyV156E(platform)} • Booking: {b.booking_id}</div>
        </div>;
      })}
      <Pager k="income" list={periodRows} per={20}/>
    </div>
  }

  // v3.9.22: Page helpers are defined inside AdminApp so rendering them as <Page />
  // creates a new component type whenever AdminApp state changes. Controlled inputs then
  // lose focus after each keystroke. Non-hook pages are invoked as render helpers instead,
  // so React reconciles their DOM in place and the active field/caret stays intact.
  // Pages that own React hooks remain mounted as components to preserve hook ordering.
  return <div className="scr on" data-dw-admin-root="true" style={{flexDirection:"column"}}><div style={{background:`rgba(17,13,38,.98)`,borderBottom:`1px solid ${C.border}`,padding:"13px 16px",display:"flex",alignItems:"center",gap:8,position:"sticky",top:0,zIndex:50}}><span style={{fontSize:16}}>🛡️</span><span style={{fontFamily:"Cinzel",fontSize:15,color:C.goldLight,flex:1,minWidth:0}}>Admin — ดวงวันนี้</span><button className="btn btn-outline btn-sm" style={{padding:"6px 9px",whiteSpace:"nowrap"}} onClick={refreshAdminDataNowV167}>↻ รีเฟรช</button><span className="badge b-purple" style={{fontSize:10}}>{currentAdmin}</span><button className="btn btn-outline btn-sm" onClick={()=>setLoggedIn(false)}>ออก</button></div><div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,overflowX:"visible",display:"flex",flexWrap:"wrap",scrollbarWidth:"auto",padding:"0 4px 4px"}}>{MENUS.map(m=><button key={m.k} onClick={()=>setMenu(m.k)} style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"9px 10px",border:"none",background:"transparent",cursor:"pointer",color:menu===m.k?C.purpleLight:C.muted,borderBottom:menu===m.k?`2px solid ${C.purple}`:"2px solid transparent",fontFamily:"Sarabun",fontSize:11,flexShrink:0,gap:3,minWidth:68,transition:"color .15s"}}><span style={{fontSize:18}}>{m.ico}</span><span style={{whiteSpace:"nowrap"}}>{m.l}</span></button>)}</div><AdminReviewModal/>{AdminNotice()}<div style={{flex:1,overflowY:"auto",paddingBottom:20}}>{menu==="overview"&&Overview()}{menu==="orders"&&OrdersPage()}{menu==="bookings"&&AdminBookingsPageV91()}{menu==="line_meetings"&&LineMeetingRoomsPageV3937()}{menu==="payment_account"&&AdminPaymentAccountPageV106()}{menu==="customer_database"&&CustomerDatabasePageV122()}{menu==="income_settings"&&IncomeSettingsPageV135()}{menu==="seer_terms"&&SeerTermsSettingsPageV150()}{menu==="seer_income"&&<SeerIncomePageV122/>}{menu==="seer_applicants"&&SeerApplicantsPage()}{menu==="seers"&&SeersManagePage()}{menu==="seer_add"&&AddSeerPage()}{menu==="seer_edit"&&EditSeerPage()}{menu==="articles"&&ArticlesPage()}{menu==="settings"&&SettingsPage()}{menu==="admins"&&AdminsPage()}{menu==="audit"&&AuditPage()}{menu==="chat_monitor"&&ChatMonitorPage()}{menu==="income"&&<IncomePage/>}</div></div>;
}


// ═══════════════════════════════════════════════════════════════════════════════
//  ROOT — Demo switcher (for prototype only)
// ═══════════════════════════════════════════════════════════════════════════════

// ─── v3.9.22 Admin Input Focus Guard ─────────────────────────────────────────
// Safety net for admin fields that may be replaced by a future page/modal render.
// It is intentionally limited to the admin root and never stores form values or identity.
function InputFocusKeeper() {
  const latestRef = useRef(null);
  const restoreFrameRef = useRef(0);

  useEffect(() => {
    const controlsSelector = 'input, textarea, select, [contenteditable="true"]';
    const isEditable = (node) => node instanceof HTMLElement && node.matches(controlsSelector);
    const getAdminRoot = (node) => node instanceof HTMLElement ? node.closest('[data-dw-admin-root="true"]') : null;
    const labelTextFor = (node) => {
      const group = node.closest('.fgroup');
      const label = group?.querySelector('.flabel, label');
      return String(label?.textContent || '').trim();
    };
    const makeSnapshot = (node) => {
      const root = getAdminRoot(node);
      if (!root) return null;
      const controls = Array.from(root.querySelectorAll(controlsSelector));
      const selectionStart = typeof node.selectionStart === 'number' ? node.selectionStart : null;
      const selectionEnd = typeof node.selectionEnd === 'number' ? node.selectionEnd : null;
      return {
        tag: node.tagName.toLowerCase(),
        type: String(node.getAttribute('type') || ''),
        id: node.id || '',
        name: node.getAttribute('name') || '',
        placeholder: node.getAttribute('placeholder') || '',
        label: labelTextFor(node),
        index: controls.indexOf(node),
        selectionStart,
        selectionEnd,
      };
    };
    const findTarget = (root, snapshot) => {
      const controls = Array.from(root.querySelectorAll(controlsSelector));
      const matchesBase = (node) => node.tagName.toLowerCase() === snapshot.tag
        && String(node.getAttribute('type') || '') === snapshot.type;
      if (snapshot.id) {
        const escapedId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(snapshot.id)
          : snapshot.id.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
        const byId = root.querySelector(`#${escapedId}`);
        if (byId && isEditable(byId)) return byId;
      }
      if (snapshot.name) {
        const byName = controls.find(node => matchesBase(node) && node.getAttribute('name') === snapshot.name);
        if (byName) return byName;
      }
      if (snapshot.label) {
        const byLabel = controls.find(node => matchesBase(node) && labelTextFor(node) === snapshot.label);
        if (byLabel) return byLabel;
      }
      if (snapshot.placeholder) {
        const byPlaceholder = controls.find(node => matchesBase(node) && node.getAttribute('placeholder') === snapshot.placeholder);
        if (byPlaceholder) return byPlaceholder;
      }
      const byIndex = controls[snapshot.index];
      return byIndex && matchesBase(byIndex) ? byIndex : null;
    };
    const clearPending = () => {
      latestRef.current = null;
      if (restoreFrameRef.current) cancelAnimationFrame(restoreFrameRef.current);
      restoreFrameRef.current = 0;
    };
    const restore = () => {
      restoreFrameRef.current = 0;
      const snapshot = latestRef.current;
      if (!snapshot) return;
      const active = document.activeElement;
      if (active && active !== document.body && active instanceof HTMLElement
        && active.matches('button, a, [role="button"], input, textarea, select, [contenteditable="true"]')) return;
      const root = document.querySelector('[data-dw-admin-root="true"]');
      if (!root) return;
      const target = findTarget(root, snapshot);
      if (!target || target.disabled || target.readOnly) return;
      target.focus({ preventScroll: true });
      if (typeof target.setSelectionRange === 'function' && snapshot.selectionStart !== null) {
        const length = String(target.value || '').length;
        const start = Math.min(snapshot.selectionStart, length);
        const end = Math.min(snapshot.selectionEnd ?? start, length);
        try { target.setSelectionRange(start, end); } catch {}
      }
    };
    const scheduleRestore = () => {
      if (!latestRef.current || restoreFrameRef.current) return;
      restoreFrameRef.current = requestAnimationFrame(restore);
    };
    const onFocusIn = (event) => {
      if (isEditable(event.target) && getAdminRoot(event.target)) latestRef.current = makeSnapshot(event.target);
      else clearPending();
    };
    const onInput = (event) => {
      if (isEditable(event.target) && getAdminRoot(event.target)) latestRef.current = makeSnapshot(event.target);
    };
    const onPointerDown = (event) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;
      if (!isEditable(target) && target.closest('button, a, [role="button"]')) clearPending();
    };
    const observer = new MutationObserver(scheduleRestore);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('input', onInput, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      clearPending();
    };
  }, []);

  return null;
}


export default function Root() {
  const [orders, _setOrders] = useState(() => {
    try {
      // v152: reset รายได้ demo/เก่าทั้งหมด เพื่อเริ่มทดสอบรายได้จริงใหม่
      if (localStorage.getItem("dw_v152_income_reset_done") !== "yes") {
        localStorage.setItem("dw_orders", JSON.stringify([]));
        localStorage.setItem("dw_v152_income_reset_done", "yes");
      }
      return JSON.parse(localStorage.getItem("dw_orders") || "[]") || [];
    }
    catch { return []; }
  });
  const setOrders = (updater) => {
    _setOrders(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("dw_orders", JSON.stringify(next));
      return next;
    });
  };
  const [chatAlerts, _setChatAlerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dw_chat_alerts") || "[]"); }
    catch { return []; }
  });
  const setChatAlerts = (updater) => {
    _setChatAlerts(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("dw_chat_alerts", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    // v179: เลิกอ่าน localStorage ทุก 0.9 วินาที เพราะทำให้ Root re-render ต่อเนื่อง
    // และทำให้แดชหมอดูที่เป็น component ภายในกระพริบใน LINE WebView
    const onStorageV179 = (event) => {
      try {
        if (event.key === "dw_orders") _setOrders(JSON.parse(event.newValue || "[]") || []);
        if (event.key === "dw_chat_alerts") _setChatAlerts(JSON.parse(event.newValue || "[]") || []);
      } catch {}
    };
    window.addEventListener("storage", onStorageV179);
    return () => window.removeEventListener("storage", onStorageV179);
  }, []);

  // Local prototype routing only. No deploy/publish is included.
  // /               = Customer app
  // /seer-register  = Hidden seer application form for admin/rich-menu link
  // /seer           = Seer app after admin approval
  // /admin          = Admin app
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const cleanPath = path.toLowerCase();
  const newsParts = path.split("/").filter(Boolean);
  const isNewsPath = cleanPath === "/news" || cleanPath.startsWith("/news/");
  const isSeerProfilePath = cleanPath.startsWith("/seer-profile/");
  const params = new URLSearchParams(window.location.search);
  const stateParams = getLiffStateParamsV142B();
  const getParamV142B = (key) => params.get(key) || stateParams.get(key) || "";
  const queryPage = getParamV142B("page") || "";
  const querySeerId = getParamV142B("seer_id") || "";
  const queryArticleId = getParamV142B("article_id") || "";
  const initialCustomerScreen = queryPage || (isNewsPath ? (newsParts[1] ? "movement_detail" : "movement") : (isSeerProfilePath ? "seer_detail" : null));
  const initialArticleId = queryArticleId || (isNewsPath ? (newsParts[1] || null) : null);
  const initialSeerId = querySeerId || (isSeerProfilePath ? (newsParts[1] || null) : null);
  const openAction = getParamV142B("open") || "";
  const openBookingId = getParamV142B("booking_id") || "";
  const adminInviteTokenV3923 = getParamV142B("admin_invite") || "";
  const wantsAdminPortalV3926 = Boolean(adminInviteTokenV3923) || getParamV142B("screen") === "admin" || cleanPath.startsWith("/admin");
  const wantsSeerRegister = !wantsAdminPortalV3926 && (getParamV142B("screen") === "seer_register" || params.has("invite") || stateParams.has("invite") || queryPage === "seer_register");
  if (wantsSeerRegister) {
    try {
      sessionStorage.setItem("DW_FORCE_SEER_REGISTER", "yes");
      localStorage.removeItem("DW_ACTIVE_SEER_PROFILE_ID_V52");
    } catch {}
  }
  // v142: ลิงก์สมัครหมอดูที่แอดมินส่ง เช่น
  // /?screen=seer_register&invite=...
  // หรือ /?page=seer_register
  // ต้องเข้า SeerRegisterStable ทันที ไม่ใช่แดชลูกค้า
  const appView = wantsAdminPortalV3926
    ? "admin"
    : (wantsSeerRegister || cleanPath.startsWith("/seer-register"))
      ? "seer_register"
      : isSeerProfilePath
        ? "customer"
        : cleanPath.startsWith("/seer")
          ? "seer"
          : "customer";

  return (
    <>
      <style>{css}</style>
      <InputFocusKeeper/>
      <Stars/>
      <div className="root z1">
        {appView==="customer"&&<CustomerApp orders={orders} setOrders={setOrders} isSeer={false} initialScreen={initialCustomerScreen} initialArticleId={initialArticleId} initialSeerId={initialSeerId} initialOpenAction={openAction} initialBookingId={openBookingId} chatAlerts={chatAlerts} setChatAlerts={setChatAlerts}/>}
        {appView==="seer_register"&&<SeerRegisterStable go={(s)=>{ if (s === "home") window.location.href = "/"; }}/>}
        {appView==="seer"&&<CustomerApp orders={orders} setOrders={setOrders} isSeer={true} initialScreen={queryPage || null} initialOpenAction={openAction} initialBookingId={openBookingId} chatAlerts={chatAlerts} setChatAlerts={setChatAlerts}/>}
        {appView==="admin"&&<AdminApp orders={orders} setOrders={setOrders} chatAlerts={chatAlerts} initialMenu={queryPage || ""}/>}
      </div>
    </>
  );
}
